/**
 * TimelineV2 - the timeline editor (DEC-240, 2026-10-01).
 *
 * Operator: "i need the timeline to replace the current timeline. make sure to
 * keep and rename the current timeline to legacy timeline", and on scope:
 * "build v2, but it doesn't need to match everything the v1 does, cos the v1 is
 * alpha stage". The first editor lives on as the Legacy timeline
 * (`TimelineEditor.tsx`), one click away in this toolbar.
 *
 * WHERE THE DESIGN COMES FROM. `public/timeline-desk.html` and the answers
 * behind it (`.notepads/timeline-overhaul.md` 20-23, `.notepads/timeline-desk-
 * review.md`): Ableton's arrangement - a toolbar with an LCD on top, a browser
 * on the left, lanes in the middle, a detail panel along the bottom for
 * whatever is selected, a performance strip closed by default, and a status
 * line that says what the thing under the pointer does. Every lane one height
 * (drag a lane's bottom edge). A neutral grey in value steps; colour is spent on
 * the literal light colours and ONE amber accent for what is playing. The bar
 * and beat lines are drawn over everything, faint. A new timeline is a blank
 * slate built up one lane at a time.
 *
 * WHAT IT IS BUILT ON. The same store, autosync, push and launch paths as the
 * legacy editor, so the two edit the same timelines and play them the same way.
 * Nothing here talks to OPBridge directly.
 *
 * WHAT IT DOES NOT DO YET (the desk page draws them, the model or OPBridge
 * cannot play them): curve lanes, the lead-in, genre bases, linked blocks,
 * freeze / hand-me-a-layer keys. Nothing is drawn that would not happen.
 *
 * Every drag has a click path, so it works with a finger: click a browser item
 * to pick it up, then click a bar in a lane to put it there.
 */

import { OPV_CELL_FX, OPV_COLUMNS as OPV_COLUMN_LIST, OPV_LAYERS, OPV_LAYER_CONTROLS, OPV_PICTURE_CONTROLS, VIZ_KNOB_NAMES, opvCellLabel, opvControlDef, vizKnobNames } from '../timeline/opvControls.ts';
import { navigateDocked } from '../navigate/dock.ts';
import { openNavigateAt } from '../navigate/store.ts';
import { onBack } from '../navigate/backButton.ts';
import { followSongTempos, tempoText } from '../timeline/tempoLabel.ts';
import { recordActivity } from '../navigate/recentActivity.ts';
import { createEffect, createMemo, createSignal, For, on, onCleanup, onMount, Show, untrack, type Accessor, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import type {
  Timeline, TimelineLane, TimelineLaneKind, TimelineStep, TimelineTier, ColorMarkerStep, ValuePointCurve, ButtonRefStep, AutomationPointStep,
  LightReleaseStep,
} from '@opbuttons/schema';
import { isColorMarkerStep, isLightReleaseStep, isPatternStep, unrollPatterns, patternOf, timelineLoops, timelineTotalBars, timelineLeadIn, timelineOrigin, timelineStartsDark, timelineEndsDark, timelinePlaySections, leadInSectionId, type PatternStep } from '@opbuttons/schema';
import { isHitBlockStep, isHitRouteStep, isHitStep, hitBlockCycles, hitBlockFill, type HitBlockStep } from '@opbuttons/schema';
import { drumPresets, sourceLabel, cssColour, type DrumPreset } from '../timeline/drumLights.ts';
import { DrumBlockPanel, DrumRoutePanel } from './DrumLightsPanel.tsx';
import { HueSceneList } from './HueScenePicker.tsx';
import { ensureAllHueScenes, findHueScene, hueRefOf, hueSceneAction, hueSceneEntries, hueSceneLabel, hueSwatchCss, type HueSceneRef } from '../lighting/hueScenes.ts';
import { takeoversOf, takeoverWeight, takeoverColour, lightGroupOf, masterReaches, mastersReaching, darkStartSteps } from '../timeline/lightMix.ts';
import { lightStream, masterStream, streamTiles, type StreamRun } from '../timeline/colourStream.ts';
import {
  freeGroupName, groupKeyOf, groupName, groupOfKey, lanesInGroup, laneRows, lightGroupsOf, LIGHTING, moveInTree, orderedLanes,
  renameLightGroup, setLightGroup,
} from '../timeline/laneGroups.ts';
import { isParked } from '../lighting/liveState.ts';
import {
  timelines, timelinesInActiveProject, getTimeline, toggleSongFlag, setTimelineSongClips, createTimeline, duplicateTimeline,
  renameTimeline, commitTimelineName, beginBatch, endBatch, addSection, renameSection, commitSectionName, setSectionLength, setSectionOnEnd,
  removeSection, duplicateSection, moveSection, addLane, addDmxFixtureLane, renameLane, commitLaneName, upsertTimeline, setTimelineLoop, setTimelineTouchMode, setTimelineLightsAtStart, setTimelineLightsAtEnd, setSectionArrival, setTimelineArrival,
  addStep, placeStep, setCueFollow, makeOpvPaletteStep, opvPaletteOf, setOpvPalette, moveStepToLane, removeStep, removeSteps, duplicateLane, placeStepsBulk, stepEndBars, makeButtonRefStep,
  overwriteWithMany, unpackPattern, makePattern, addLanes,
  makeTargetValueStep, makeColorMarkerStep, setColorMarkerColor, setColorMarkerTransition, moveColorMarker, undo, redo,
  closeTimelineEditor, timelineEditorTargetId, selectTimelineInEditor, switchTimelineEditor,
  setAllSectionsOnEnd, splitSection, setSectionName, moveSectionEdge, setFirstSectionBar, copySectionSteps, followsSong, setTimelineLength, minTimelineLength, maxTimelineLength, setTimelineTestTrack,
  copyAsLightingTimeline, makeFullTimeline,
} from '../timeline/store.ts';
import { timelineHistory } from '../timeline/store.ts';
import { describeEdit, stepsWord } from '../timeline/editLabel.ts';
import { canCopyAsLighting, isLightingTimeline } from '../timeline/lighting.ts';
import { playheadAt } from '../timeline/playhead.ts';
import { followTransport, transportRun, transportBar, noteTransportLaunch, noteTransportStop, kickTransport, noteTransportJump, noteTransportJumpAt } from '../timeline/transport.ts';
import { applyCurve, colorSegmentStops, sampleColorLane, sampleColorSegment, type Rgb01 } from '../timeline/colorSample.ts';
import { openLease, closeLease, takeLease, leaseFor } from '../timeline/lease.ts';
import { waitWords } from '../timeline/chain.ts';
import { CLIPBOARD_VERSION, getInMemoryClipboard, hasClipboard, readClipboard, writeClipboard, type ClipboardEntry } from '../timeline/selection.ts';
import {
  jumpToSectionAction, jumpToBarAction, jumpToBarInstantAction, homeJumpsInstantly, homeStartsAtBar, homeLoopsRange, setLoopRangeOnBridge, notLiveToastMessage, timelineHomeTarget,
  timelineStepWarnings, laneAudible, timelinePlaysLights, timelinePlaysLightFx, timelinePlaysInstruments, bridgeEndsLightsDark, bridgeBlendsArrival, sectionSlam, playedSteps,
} from '../timeline/bridgeSync.ts';
import {
  scheduleTimelineSync, sweepPendingTimelineDeletes, pushTimelineLiveNow,
} from '../timeline/autoSync.ts';
import { anyBridgeConnectedReactive, bridges, primaryBridge, resolveBridges } from '../opbridge/store.ts';
import { pagesOfActive, hueOf, HUE_VALUES, activeProject, projects } from '../layout/projectStore.ts';
import type { ButtonItem, LayoutItem, Page, PresetChangeItem, SnapshotChangeItem, StreamDeckItem } from '../layout/projectStore.ts';
import { canvasSizeOf } from '../layout/devices.ts';
import { helixSlotLabel } from './PresetChange.tsx';
import { fixtures } from '../fixtures/store.ts';
import { STAGE_COLORS, type StageColor } from '../lighting/palette.ts';
import { LIVE_TARGETS, getLiveTarget, type LiveTarget } from '../liveState/registry.ts';
import { pushToast } from '../ui/toast.ts';
import { plainFireReason } from '../opbridge/dispatch.ts';
import { deleteTimelineFromRig, timelinePlayingNow } from '../timeline/deleteTimeline.ts';
import { launchTimelineOnBridge, stopTimelineOnBridge } from '../timeline/launch.ts';
import { createClockStatus, liveIsStopped } from '../timeline/homeClock.ts';
import { announceHistoryResult } from '../undo/history.ts';
import { canRedoOrdered, canUndoOrdered, redoLatest, undoLatest } from '../undo/editOrder.ts';
import { ulid } from '../utils/ulid.ts';
import { presetState } from '../lighting/presets.ts';
import { ensurePresets } from '../lighting/presetRead.ts';
import { autoValueAt, shapeAt, thinRun } from '../timeline/automation.ts';
import {
  CURVE_CHOICES, curveLabel, GLIDE_OUT_CHOICES, motionDrivesText, motionGlideOut, motionLaneName, motionPlayedPath, motionOff127,
  motionTarget, motionValueText, MOTION_BUSES, MOTION_TARGETS, MOVE_FAMILIES,
} from '../timeline/opMotion.ts';
import { TOUCH_MODES, bridgeTouches } from '../timeline/touch.ts';
import { startPressRecording, stopPressRecording, recordedCount, recordedMoveCount, type RecordedPress, type RecordedMove } from '../timeline/recorder.ts';
import { uiMode, setUiMode } from '../performance/store.ts';
import { enterTestMode } from '../layout/store.ts';
import { TimelinePlaysOnPill } from './TimelinePlaysOnPill.tsx';
import { TimelineSyncPill } from './TimelineSyncPill.tsx';
import {
  TestTrackProps, TestTrackRow, copySelectedSongClips, deleteSelectedSongClips, duplicateSelectedSongClips, nudgeSelectedSongClips,
  pasteSongClipsAt, selectAllSongClips, selectedClips, songTrackMenu, splitSongClipsAt,
} from './TestTrackRows.tsx';
import { SongImportModal } from './SongImport.tsx';
import { gridBpm, sameSectionName, clipsFromMarks, gridOf } from '../timeline/testTrack.ts';
import { beatmaps, loadTrack } from '../timeline/testTrackStore.ts';
import { clockSource } from '../opbridge/clockPoll.ts';
import { SongMarkRow } from './SongMarker.tsx';
import { OpvPalettePicker } from './OpvPalettePicker.tsx';
import { opvLive } from '../opvisual/live.ts';
import { moveSongLine, nameSongPartAt, planFor, splitSongAt, startFollowing } from '../timeline/songFollow.ts';
import { kindOf } from '../timeline/songParts.ts';
import { cellKey, opvListing, watchOpvListing } from '../opvisual/listing.ts';
import { autoPickOpVisualBridge } from '../opvisual/machine.ts';

// ─── constants ───────────────────────────────────────────────────────────────

const HEADER_W = 200;
/** Room a fitted view leaves after the last bar: the Add section + (`.tl2-addsec`, 4 px after the end, 26 px
 *  wide) and a margin. It was 16, which hung the + 14 px off the right edge whenever the whole timeline fitted
 *  (operator, 2026-10-15: "the add section is slightly cut off"; a short timeline opens fitted). */
const FIT_END_ROOM = 44;
/** A lane's colour (2026-10-16, operator: "lane colours"): the schema's `color`, from the calm page hues (their
 *  comment: "calm distinction from control colors"), drawn as a strip by the name and a tint on its presses. */
const LANE_HUES = ['sky', 'violet', 'amber', 'teal', 'rose', 'forest'] as const;
const laneHueVar = (h: string | undefined): string | undefined => (h && (LANE_HUES as readonly string[]).includes(h) ? `var(--page-hue-${h})` : undefined);

const LANE_H_DEFAULT = 44;
/** An automation lane starts taller (2026-10-16): its height IS the value range, and at 44 px one pixel was 4 of the
 *  128 values, too coarse to draw a curve. */
const LANE_H_AUTO = 96;
/** A drum lights lane (2026-10-23) starts a little taller: its blocks carry a title, their cycles and their fills. */
const LANE_H_DRUMS = 56;
const LANE_H_MIN = 32;
/** On a touch screen no lane is shorter than a finger (2026-10-16, the look audit): the DEVICE decides, not the last
 *  hand used, so lanes never jump when a mouse and a finger take turns. */
const LANE_H_TOUCH = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches ? 44 : 0;
const LANE_H_MAX = 240;
const MIN_PX_PER_BAR = 28;
/** A timeline's view: never zoomed, fitted (F), or pixels per bar (D-11). */
type TlView = 'auto' | 'fit' | number;
/** The smallest zoom ever stored; the effective floor is `zoomMin()` (the whole song in view). */
const ZOOM_MIN = 0.05;
/** 32x (was 8x, the pro-grade review 2026-10-13: 16ths stayed cramped on long songs). */
const ZOOM_MAX = 32;
const FOLLOW_KEY = 'opbuttons.timelineV2.follow';
/** The Stream view (2026-11-02, `timeline/colourStream.ts`): lights lanes drawn as the colour they show. */
const STREAM_KEY = 'opbuttons.timelineV2.stream';
const DRAG_THRESHOLD_PX = 4;
const LAYOUT_KEY = 'opbuttons.timelineV2.layout.v1';
const HEIGHTS_KEY = 'opbuttons.timelineV2.laneHeights.v1';
/** Which lane groups are folded, per timeline (`<timelineId>:<group>`). */
const FOLDED_KEY = 'opbuttons.timelineV2.foldedGroups.v1';

/** The grid every click and drag lands on (the review's "grid setting"). */
type Snap = 'bar' | 'beat' | '8th' | '16th' | '8T' | '16T' | 'off';
/** The grid (2026-10-16, operator: "more grid choices"): triplets, and Off (a fine grid, 1/960 of a bar, so positions
 *  stay tidy numbers). Ctrl (Cmd on a Mac) held while dragging ignores the grid too, as on the song row. */
const SNAP_LABEL: Record<Snap, string> = { bar: 'Bar', beat: 'Beat', '8th': '1/8', '16th': '1/16', '8T': '1/8 T', '16T': '1/16 T', off: 'Off' };
/** The finest step: the grid when it is Off, and a drag with Ctrl / Cmd held. */
const FREE_UNIT = 1 / 960;

/** The colour a click on a lights lane puts down: the one ringed in the
 *  browser, White until a colour is used (picked up, placed or chosen for a
 *  keyframe). Operator, 2026-10-10: a turn through five colours read as
 *  random. Remembered per browser; it is a habit, not part of the project. */
const CLICK_COLOUR_KEY = 'opbuttons.timeline.clickColour';
const [clickColour, setClickColourSignal] = createSignal<StageColor>(
  (() => { try { return STAGE_COLORS.find((s) => s.label === localStorage.getItem(CLICK_COLOUR_KEY)) ?? null; } catch { return null; } })()
    ?? STAGE_COLORS.find((s) => s.label === 'White') ?? STAGE_COLORS[1]!,
);
function useClickColour(c: StageColor): void {
  setClickColourSignal(c);
  try { localStorage.setItem(CLICK_COLOUR_KEY, c.label); } catch { /* private mode: not remembered */ }
}

type FireableItem = ButtonItem | PresetChangeItem | SnapshotChangeItem | StreamDeckItem;
function isFireable(it: Page['items'][number]): it is FireableItem {
  return it.kind === 'button' || it.kind === 'presetChange' || it.kind === 'snapshotChange' || it.kind === 'streamdeck';
}
const FIREABLE_ORDER: FireableItem['kind'][] = ['button', 'presetChange', 'snapshotChange', 'streamdeck'];
const FIREABLE_LABEL: Record<FireableItem['kind'], string> = {
  button: 'Buttons', presetChange: 'Guitar FX presets', snapshotChange: 'Helix snapshots', streamdeck: 'Stream Deck',
};

/** OPBridge's timeline engine plays control presses only: light colours and
 *  song settings are dropped at the push until its DMX timeline engine lands
 *  (`bridgeSync.ts::resolveStepToCues`, `unplayableKindLabel`). A Lights lane
 *  says so before anything is put on it, not only after. */
const LIGHTS_WONT = "Light colours don't play from a timeline yet";
/** A drum lights lane on a bridge without `timeline_instrument_routing` (OPBridge before v1.15.211). */
const DRUMS_WONT = 'the OPBridge this timeline plays on needs an update for drum lights';

/** What a lane is FOR, in the words the browser and the blank slate use. */
type LaneRole = 'lights' | 'controls' | 'guitar' | 'markers' | 'opvisual' | 'opvcolour' | 'opvpalette' | 'automation' | 'drums';
function laneRole(l: TimelineLane): LaneRole {
  const k = l.laneKind ?? 'mixed';
  if (k === 'automation') return 'automation';
  if (k === 'instrument') return 'drums';
  if (k === 'dmx-rig' || k === 'dmx-fixture') return 'lights';
  if (k === 'guitar') return 'guitar';
  if (k === 'opvisual') return 'opvisual';
  if (k === 'opvisual-colour') return 'opvcolour';
  if (k === 'opvisual-palette') return 'opvpalette';
  if (k === 'marker') return 'markers';
  return 'controls';
}
/** A lane whose keyframes are colours: a light, or OPVisual's show colour (2026-10-28). Light BEHAVIOUR (groups,
 *  masters, the rig) stays on `laneRole(l) === 'lights'`. */
function editsColour(l: TimelineLane): boolean {
  const r = laneRole(l);
  return r === 'lights' || r === 'opvcolour';
}
/** The canvas kinds a Guitar lane holds (DEC-244): a preset change is a STATE
 *  (the guitar sounds like this from here on), a snapshot a change inside it. */
const GUITAR_KINDS = new Set(['presetChange', 'snapshotChange']);
/** The lanes the editor makes, and their plain names. */
type NewLaneRole = 'lights' | 'controls' | 'guitar' | 'opvisual';
const ROLE_NAME: Record<NewLaneRole, string> = { lights: 'Lights', controls: 'Controls', guitar: 'Guitar', opvisual: 'OPVisual Cell/Column' };
/** An OPVisual step (2026-10-13): a cue that fires one of OPVisual's named actions. Columns are always published
 *  (`opvisual-col-1..10`); a cell (`opvisual-cell-l<layer>-<col>`, OPVisual `controllers.rs`) is published only while
 *  something is bound to it, so a cell step is said to depend on that. */
const OPV_COLUMNS = 10;
const opvColumnId = (col: number) => `opvisual-col-${col}`;
const opvCellId = (layer: number, col: number) => `opvisual-cell-l${layer}-${col}`;
/** Whether an OPVisual cue lets its cell follow: only an explicit `follow: 1`
 *  (absent compiles to 0, `bridgeSync.ts::withOpvFollowDefault`). */
function opvFollows(s: TimelineStep): boolean {
  const a = (s as { action?: { args?: { values?: { follow?: unknown } } } }).action;
  return a?.args?.values?.follow === 1;
}
function opvActionId(s: TimelineStep): string | null {
  if (s.kind !== 'cue') return null;
  const a = s.action as { module?: string; command?: string; args?: { id?: unknown } } | undefined;
  return a && a.module === 'action' && a.command === 'fire' && typeof a.args?.id === 'string' ? a.args.id : null;
}

// ─── automation lanes (2026-10-16, record mode's "fader moves") ─────────────
//
// An automation lane is one control's moves over time: a fader, a knob, or one axis of an XY pad (`lane.control`),
// drawn as a curve through `automationPoint` steps (value 0-127, the MIDI value the control sends). Each point ramps
// to the next (`linear`) or holds and then jumps (`hold`). Before the first point the curve sits at the first value;
// after the last it holds. The PWA compiles it to thinned CC cues (`bridgeSync.ts::automationCues`).

/** A control an automation lane can move, as the "Add a lane" menu lists it. */
type AutoTarget = { itemId: string; axis?: 'x' | 'y'; label: string; hint: string };
/** Top and bottom inset of the curve in its lane, so a point at 0 or 127 can still be grabbed. */
const AP_PAD = 7;
function autoTargetLabel(item: { kind: string }, axis?: 'x' | 'y'): string {
  const it = item as { kind: string; name?: string; ccNumber?: number; xCcNumber?: number; yCcNumber?: number };
  const named = it.name?.trim();
  if (it.kind === 'xypad') {
    const ax = axis === 'y' ? 'Y' : 'X';
    return named ? `${named} ${ax}` : `XY pad ${ax} (CC ${axis === 'y' ? it.yCcNumber : it.xCcNumber})`;
  }
  return named || `${it.kind === 'radial' ? 'Knob' : 'Fader'} (CC ${it.ccNumber})`;
}
function autoTargetsOf(items: Array<{ id: string; kind: string }>): AutoTarget[] {
  const out: AutoTarget[] = [];
  for (const it of items) {
    const c = it as { kind: string; channel?: number; ccNumber?: number; xChannel?: number; xCcNumber?: number; yChannel?: number; yCcNumber?: number };
    if (it.kind === 'fader' || it.kind === 'radial') {
      out.push({ itemId: it.id, label: autoTargetLabel(it), hint: `${it.kind === 'radial' ? 'Knob' : 'Fader'}, CC ${c.ccNumber} ch ${(c.channel ?? 0) + 1}` });
    } else if (it.kind === 'xypad') {
      out.push({ itemId: it.id, axis: 'x', label: autoTargetLabel(it, 'x'), hint: `XY pad across, CC ${c.xCcNumber} ch ${(c.xChannel ?? 0) + 1}` });
      out.push({ itemId: it.id, axis: 'y', label: autoTargetLabel(it, 'y'), hint: `XY pad up and down, CC ${c.yCcNumber} ch ${(c.yChannel ?? 0) + 1}` });
    }
  }
  return out;
}
/** A point's value in words: on an OP-Motion lane in its target's own units ("Tone 30% darker"), on a control's lane
 *  the 7-bit value it sends. */
function autoText(lane: TimelineLane, v: number): string {
  const mt = motionTarget(lane.motion?.target);
  return mt ? `${mt.label} ${motionValueText(mt, v)}` : String(v);
}
/** An automation lane's points in time order. */
function autoPoints(lane: TimelineLane): AutomationPointStep[] {
  return lane.steps.filter((x): x is AutomationPointStep => x.kind === 'automationPoint').sort((a, b) => a.position - b.position);
}
/** What a picked-up or dragged thing becomes when it lands. */
type Payload =
  | { kind: 'item'; itemId: string; label: string; itemKind: string }
  | { kind: 'colour'; colour: StageColor }
  /** A Light FX block (2026-10-19): one of OPBridge's lighting presets, played for a bar from where it lands. */
  | { kind: 'fx'; preset: string; label: string }
  /** A Hue scene (2026-10-23, OPBridge S331): a cue recalling it, on a Controls lane. */
  | { kind: 'hue'; ref: HueSceneRef; label: string }
  | { kind: 'live'; targetId: string; value: number | string; label: string };

function payloadLabel(p: Payload): string {
  return p.kind === 'colour' ? p.colour.label : p.label;
}

/** Which lanes take which payloads - the legacy editor's rule
 *  (`laneAcceptsStep`): colours go on lights lanes only; a control or a song
 *  setting goes on any lane that is not a lights or marker lane. A Guitar lane
 *  takes guitar presets and snapshots only; a Controls lane still takes them
 *  too, so timelines made before DEC-244 keep working. */
function laneAccepts(lane: TimelineLane, p: Payload): boolean {
  const k: TimelineLaneKind = lane.laneKind ?? 'mixed';
  if (p.kind === 'colour') return k === 'dmx-rig' || k === 'dmx-fixture' || k === 'opvisual-colour';
  if (p.kind === 'fx') return k === 'dmx-rig' || k === 'dmx-fixture';
  if (k === 'opvisual-colour' || k === 'opvisual-palette') return false;
  if (k === 'instrument') return false;   // drum lights take blocks from their own menu, not a dragged control
  if (k === 'guitar') return p.kind === 'item' && GUITAR_KINDS.has(p.itemKind);
  if (k === 'dmx-rig' || k === 'dmx-fixture' || k === 'marker' || k === 'automation') return false;
  if (p.kind === 'item') return k === 'mixed' || k === 'midi';
  return k === 'mixed' || k === 'midi' || k === 'osc';
}

/** A change on a lane: what a selection of several holds. */
type StepRef = { laneId: string; stepId: string };
/** One thing, or several lanes, or several changes (operator, 2026-10-02:
 *  "shift and click to highlight a block of multiple lanes, ctrl click to
 *  highlight multiple individual lanes", and the same for changes, and a box
 *  dragged over the lanes). `range` is the stretch of bars a box covered, so
 *  Duplicate repeats the box, not just from the first change to the last. */
/** The operator's five song sections (DEC-262): the names a section menu offers. A section's name is also what
 *  picks the song's music under it (the test track's marks match by name). */
const SONG_SECTION_NAMES = ['INTRO', 'VERSE', 'PRE-CHORUS', 'CHORUS', 'BRIDGE'] as const;

type Selection =
  | { type: 'timeline' }
  | { type: 'section'; id: string }
  | { type: 'track' }
  | { type: 'lane'; id: string }
  | { type: 'step'; laneId: string; stepId: string }
  | { type: 'lanes'; ids: string[] }
  | { type: 'steps'; refs: StepRef[]; range?: { start: number; end: number } };

// ─── small pure helpers ──────────────────────────────────────────────────────

function sigOf(t: Timeline): number {
  return Math.max(1, t.signature?.num ?? 4);
}
function snapUnit(snap: Snap, sig: number): number {
  switch (snap) {
    case 'bar': return 1;
    case 'beat': return 1 / sig;
    case '8th': return 1 / (sig * 2);
    case '16th': return 1 / (sig * 4);
    case '8T': return 1 / (sig * 3);
    case '16T': return 1 / (sig * 6);
    case 'off': return FREE_UNIT;
  }
}
/** "3.2" style: bar.beat, both counted from 1, with a sixteenth when it has one. */
function barBeat(pos: number, sig: number): string {
  const bar = Math.floor(pos + 1e-9);
  const beatsIn = (pos - bar) * sig;
  const beat = Math.floor(beatsIn + 1e-9);
  // Truncated, as a DAW counts (2026-10-16: rounded, a change just before a beat read as the beat itself, and a
  // triplet or an off-grid change could not be told from its neighbour).
  const six = Math.floor((beatsIn - beat) * 4 + 1e-6);
  return six > 0 && six < 4 ? `${bar + 1}.${beat + 1}.${six + 1}` : `${bar + 1}.${beat + 1}`;
}
function barsText(bars: number): string {
  if (Math.abs(bars - Math.round(bars)) < 1e-6) return `${Math.round(bars)} bar${Math.round(bars) === 1 ? '' : 's'}`;
  return `${Math.round(bars * 100) / 100} bars`;
}
function rgbCss(c: { r: number; g: number; b: number }): string {
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return `rgb(${to(c.r)} ${to(c.g)} ${to(c.b)})`;
}
function sameRgb(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }): boolean {
  return Math.abs(a.r - b.r) < 0.01 && Math.abs(a.g - b.g) < 0.01 && Math.abs(a.b - b.b) < 0.01;
}
/** A stage colour is named by WHAT IT IS, so a change recoloured from Red to
 *  Blue never keeps reading "Red"; the stored label only names a colour that
 *  is not one of the sixteen. */
function colourName(c: { r: number; g: number; b: number }, label?: string): string {
  return STAGE_COLORS.find((s) => sameRgb(s.rgb, c))?.label ?? label ?? 'Colour';
}
/** A keyframe's name: its colour, or a split one's two ("Black then Blue"). */
function keyName(k: ColorMarkerStep): string {
  const own = colourName(k.color, k.label);
  return k.arrive ? `${colourName(k.arrive)} then ${own}` : own;
}
function stageColour(name: string): StageColor {
  return STAGE_COLORS.find((s) => s.label === name) ?? STAGE_COLORS[1]!;
}

// ─── colour keyframes (lights lanes) ────────────────────────────────────────
//
// Operator, 2026-10-01: "i want the blocks to act like keyframes ... drop a red
// keyframe and then a blue frame and then figure out the transition between
// the 2. whether it's a smooth curve or a straight cut/flip on the beat." A
// colour change is a POINT on its beat (it always was, `ColorMarkerStep`; v2
// first drew it as a block to the next change). The stretch to the next
// keyframe is a Cut (hold, then switch on the next keyframe's beat) or a Fade,
// stored on the OUTGOING keyframe. Cut and Fade up front; the fade's shape and
// its route round the colour wheel one tap deeper ("More"). A new keyframe is
// a Cut. Before the first keyframe the lane leaves the light alone; after the
// last it holds.

/** A Fade blends round the HSL wheel: the short way ("Direct") or the long
 *  way ("Rainbow"). One space for both, so they read as two directions round
 *  one wheel. Chosen 2026-10-01 from a render of all three spaces on the stage
 *  colours: sRGB dips dark between saturated colours (red to blue through a
 *  dim purple), OKLCH's long way goes muddy; HSL gives the lighting-desk
 *  fades and a clean rainbow once its grey-hue bug was fixed. */
const FADE_SPACE = 'HSL' as const;

type Transition = 'cut' | 'fade';
function transitionOf(s: ColorMarkerStep): Transition {
  return (s.curve ?? 'linear') === 'hold' ? 'cut' : 'fade';
}
/** A fade's shapes, in the words a first-timer can act on. */
const FADE_SHAPES: Array<{ curve: ValuePointCurve; label: string; info: string }> = [
  { curve: 'linear', label: 'Even', info: 'Even: the same speed all the way.' },
  { curve: 'ease-in', label: 'Starts slow', info: 'Starts slow: hangs on the first colour, then hurries into the next.' },
  { curve: 'ease-out', label: 'Ends slow', info: 'Ends slow: leaves quickly, then settles into the next colour.' },
  { curve: 'ease-in-out', label: 'S-curve', info: 'S-curve: slow at both ends, quick in the middle.' },
];
/** White and Off have no place on the colour wheel, so there is no rainbow
 *  to take to or from them. */
function isGrey(c: { r: number; g: number; b: number }): boolean {
  return Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b) < 1e-3;
}
const isOff = (c: { r: number; g: number; b: number }) => sameRgb(c, { r: 0, g: 0, b: 0 });
/** A split keyframe's diamond: the colour it arrives at on its LEFT half, its own on the right. The diamond is a square
 *  turned 45 degrees, so its left half is the square's lower-left triangle. Off is drawn near-black, as on a whole one. */
function splitCss(arrive: { r: number; g: number; b: number }, own: { r: number; g: number; b: number }): string {
  const c = (x: { r: number; g: number; b: number }) => (isOff(x) ? '#0b0b0c' : rgbCss(x));
  return `linear-gradient(to top right, ${c(arrive)} 50%, ${c(own)} 50%)`;
}
function cssGradient(stops: Rgb01[]): string {
  return `linear-gradient(90deg, ${stops.map(rgbCss).join(', ')})`;
}
/** The keyframe after `s` on its lane, or null when `s` is the last, or when
 *  a Hand back comes first (a master's next keyframe then starts a new
 *  takeover: nothing cuts or fades across a Hand back). */
function nextKeyframe(lane: TimelineLane, s: TimelineStep): ColorMarkerStep | null {
  let best: ColorMarkerStep | null = null;
  const played = unrollPatterns(lane.steps);
  for (const x of played) {
    if (!isColorMarkerStep(x) || x.id === s.id || x.position <= s.position + 1e-9) continue;
    if (!best || x.position < best.position) best = x;
  }
  if (best && played.some((x) => isLightReleaseStep(x) && x.position > s.position + 1e-9 && x.position <= best!.position + 1e-9)) return null;
  return best;
}
/** How many times a pattern block plays its cycle ("12 times", "2.5 times"). */
function patternTimes(b: PatternStep): string {
  const n = b.durationBars / b.cycleBars;
  return Math.abs(n - Math.round(n)) < 1e-6 ? `${Math.round(n)} time${Math.round(n) === 1 ? '' : 's'}` : `${n.toFixed(1)} times`;
}
/** Split a keyframe (`ColorMarkerStep.arrive`, 2026-11-02): the colour the fade before it arrives at, its diamond's
 *  left half, while its own colour cuts in on its beat. Null (or its own colour) joins the halves again. One edit. */
function setKeyArrive(timelineId: string, laneId: string, stepId: string, c: ColorMarkerStep['color'] | null) {
  const t = getTimeline(timelineId);
  if (!t) return;
  upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : { ...l, steps: l.steps.map((x) => {
    if (x.id !== stepId || x.kind !== 'colorMarker') return x;
    const { arrive: _a, ...rest } = x;
    void _a;
    return c && !sameRgb(c, x.color) ? { ...rest, arrive: { ...c } } : rest;
  }) })) });
}
/** The colour a stretch ARRIVES at on keyframe `k`: its split's left half, else its colour. */
const arriving = (k: ColorMarkerStep): ColorMarkerStep => (k.arrive ? { ...k, color: k.arrive } : k);

/** An "All lights" lane is the lighting MASTER (2026-10-02): its keyframes
 *  take over every light until a Hand back (`timeline/lightMix.ts`). */
function isMasterLane(l: TimelineLane): boolean {
  return (l.laneKind ?? 'mixed') === 'dmx-rig';
}
/** A ramp from nothing to `c`, in the fade's shape: how a lead-in is drawn. */
function rampCss(c: { r: number; g: number; b: number }, curve: ValuePointCurve, alpha = 1): string {
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  const stops: string[] = [];
  for (let i = 0; i <= 8; i++) stops.push(`rgb(${to(c.r)} ${to(c.g)} ${to(c.b)} / ${(applyCurve(i / 8, curve) * alpha).toFixed(3)})`);
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
/** The lane background a fade back fades the master's ribbon into. */
const WELL_RGB = { r: 29 / 255, g: 30 / 255, b: 32 / 255 };
/** "Cuts to Blue" / "Fades to Blue" / "Holds to the end": what a keyframe does
 *  next, for the cue list and the status line. */
function thenText(lane: TimelineLane, s: ColorMarkerStep, sig: number): string {
  const next = nextKeyframe(lane, s);
  if (next) return `${transitionOf(s) === 'cut' ? 'Cuts' : 'Fades'} to ${colourName(next.color, next.label)}`;
  const hb = handBackAfter(lane, s);
  return hb ? `Until the Hand back at ${barBeat(hb.position, sig)}` : 'Holds to the end';
}
/** The first Hand back after `s` on a master lane, or null. */
function handBackAfter(lane: TimelineLane, s: TimelineStep): LightReleaseStep | null {
  let best: LightReleaseStep | null = null;
  for (const x of lane.steps) if (isLightReleaseStep(x) && x.position > s.position + 1e-9 && (!best || x.position < best.position)) best = x;
  return best;
}
function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const v: unknown = JSON.parse(raw);
    return v && typeof v === 'object' ? { ...fallback, ...(v as object) } : fallback;
  } catch { return fallback; }
}
function writeJson(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode: layout is not remembered */ }
}

/** How a canvas item's outline reads in the miniature: round things round,
 *  pills as pills, everything else a soft rectangle (a triangle or a hexagon
 *  is still recognisable by place and colour at that size). */
function mapShape(it: LayoutItem): 'round' | 'pill' | 'rect' {
  const s = (it as { shape?: string }).shape;
  if (s === 'circle' || s === 'ellipse') return 'round';
  if (s === 'pill') return 'pill';
  return 'rect';
}
/** "xyPad" -> "XY pad": what a control is, for the status line when it has
 *  no name of its own. */
function kindWord(kind: string): string {
  const w = kind.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/^xy\b/, 'XY').replace(/^op visual/, 'OPVisual');
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** Display text for a canvas control, per kind (the legacy palette's wording). */
/** The Hue scene a cue recalls (`{module: hue, command: scene}`, 2026-10-23), or null for any other step. */
function hueCueRef(s: TimelineStep): Pick<HueSceneRef, 'bridge' | 'scene'> | null {
  if (s.kind !== 'cue') return null;
  const a = s.action as { module?: unknown; command?: unknown; args?: { bridge?: unknown; scene?: unknown } } | undefined;
  return a && a.module === 'hue' && a.command === 'scene' && typeof a.args?.bridge === 'string' && typeof a.args.scene === 'string'
    ? { bridge: a.args.bridge, scene: a.args.scene } : null;
}

function itemText(item: FireableItem): { label: string; meta: string } {
  switch (item.kind) {
    case 'button': {
      const a = item.actionType ?? 'cc';
      if (a === 'hueScene') {
        const hs = item.hueScene;
        return { label: item.name ?? (hs?.scene ? hueSceneLabel(hs) : 'Hue scene'), meta: hs?.scene ? `Hue scene, ${hueSceneLabel(hs)}` : 'Hue scene, none chosen' };
      }
      const sends = a === 'noteon' ? `note ${item.note ?? 60}` : a === 'pc' ? `PC ${item.program ?? 0}` : `CC ${item.ccNumber}`;
      return { label: item.name ?? sends, meta: `${sends}, ch ${item.channel + 1}` };
    }
    case 'presetChange': {
      const slot = `${helixSlotLabel(item.program)}`;
      return { label: item.name ?? item.presetName ?? `Preset ${slot}`, meta: `Guitar preset ${slot}` };
    }
    case 'snapshotChange':
      return { label: item.name ?? `Snapshot ${item.snapshot}`, meta: `Helix snapshot ${item.snapshot}` };
    case 'streamdeck':
      return { label: item.name ?? item.label ?? item.actionTitle ?? 'Stream Deck', meta: item.actionTitle ?? 'Stream Deck' };
  }
}

// ─── the editor ─────────────────────────────────────────────────────────────

interface TimelineV2Props {
  /** The app's musical clock (App.tsx's smoothClock), as the legacy editor
   *  and the Launcher use it, so the playhead follows the real tempo. */
  beat: Accessor<number>;
  beatsPerBar: Accessor<number>;
  bpm: Accessor<number>;
  /** A live clock is arriving (App's isSlaved); the editor checks the clock's SOURCE to know whether it is Ableton. */
  slaved?: Accessor<boolean>;
}

export function TimelineV2(props: TimelineV2Props) {
  onMount(() => {
    void sweepPendingTimelineDeletes();
    // Keys work from the first moment, without a click into the editor first.
    setTimeout(() => rootEl?.focus({ preventScroll: true }), 0);
  });

  // ── which timeline ──
  const projectTimelines = createMemo(() => timelinesInActiveProject());
  followSongTempos(projectTimelines);
  const timeline = createMemo<Timeline | null>(() => {
    const id = timelineEditorTargetId();
    if (id) return timelines().find((x) => x.id === id) ?? null;
    return null;
  });
  // "Jump back in" in Navigate (2026-10-05): the timeline open here is the
  // last one touched.
  createEffect(on(() => timeline()?.id, (id) => {
    const t = timeline();
    if (id && t) recordActivity({ kind: 'timeline', id, title: t.name, ...(t.projectId ? { projectId: t.projectId } : {}) });
  }));
  // Lights lanes play where the home bridge says `timeline_colour` (S300).
  // Read here so every "won't play yet" mark on lights follows the bridge
  // rather than being hardcoded (PLAN QUEUED, fixed 2026-10-05).
  const lightsPlay = createMemo(() => { const t = timeline(); return t ? timelinePlaysLights(t) : false; });
  const lightsWontNote = () => (lightsPlay() ? '' : ` ${LIGHTS_WONT}.`);
  // Drum lights play where the home bridge routes live hits (`timeline_instrument_routing`, OPBridge S331).
  const drumsPlay = createMemo(() => { const t = timeline(); return t ? timelinePlaysInstruments(t) : false; });
  // Hue scenes for the browser and the chooser (2026-10-23): read from every connected computer, again on a connect.
  createEffect(on(() => [bridges().length, anyBridgeConnectedReactive(undefined)] as const, () => ensureAllHueScenes()));
  // Opened with no timeline named: open this project's first one, so the
  // editor never starts on an empty picker when there is something to show.
  createEffect(() => {
    if (timelineEditorTargetId()) return;
    const first = projectTimelines()[0];
    if (first) selectTimelineInEditor(first.id);
  });
  // A memo, not a plain accessor: `on(tid, ...)` below must fire when the id
  // CHANGES, and `on()` re-runs on every read it tracks - the timeline object
  // is new after every edit, which reset the selection after each drag.
  const tid = createMemo(() => timeline()?.id ?? '');
  // ── the edit lease (OPBridge S329, `timeline/lease.ts`): one device edits a timeline at a time ──
  createEffect(on(tid, (id, prev) => {
    if (prev && prev !== id) closeLease(prev);
    if (id) { const t = untrack(timeline); void openLease(id, t ? timelineHomeTarget(t) : ''); }
  }));
  onCleanup(() => { const id = untrack(tid); if (id) closeLease(id); });
  const lease = () => leaseFor(tid());
  /** "Keep" chosen: the choice folds to one line; the timeline stays read-only here. */
  const [leaseKept, setLeaseKept] = createSignal(false);
  createEffect(on(() => lease()?.held, () => setLeaseKept(false)));

  // Autosync while editing - the legacy editor's rule, and its reason: `on`
  // scopes tracking to the timeline object alone (a bare effect deep-reads
  // every field through JSON.stringify and loops).
  createEffect(on(timeline, (tl) => { if (tl) scheduleTimelineSync(tl); }, { defer: true }));

  // ── per-device layout ──
  const wide = typeof window !== 'undefined' ? window.innerWidth >= 1100 : true;
  const saved = readJson(LAYOUT_KEY, {
    browser: wide, detail: true, perf: false, browserW: 232, detailH: 220, snap: 'beat' as Snap, zoom: 1, songParts: true,
    views: {} as Record<string, TlView>,
  });
  // The Song parts row folds to a thin strip (2026-10-28, operator: "why is songs parts always there?"), remembered.
  const [songPartsOpen, setSongPartsOpen] = createSignal<boolean>(saved.songParts !== false);
  const [browserOpen, setBrowserOpen] = createSignal<boolean>(saved.browser);
  const [detailOpen, setDetailOpen] = createSignal<boolean>(saved.detail);
  const [perfOpen, setPerfOpen] = createSignal<boolean>(saved.perf);
  // Docked on a desktop (DEC-268) the editor takes Navigate's place: the
  // browser column opens at Navigate's sidebar width, so the column and the
  // wordmark above it stay where they were. 232 is the untouched default; a
  // width the operator dragged to is kept.
  const docked = untrack(navigateDocked);
  const [browserW, setBrowserW] = createSignal<number>(docked && saved.browserW === 232 ? 260 : saved.browserW);
  const [detailH, setDetailH] = createSignal<number>(saved.detailH);
  const [snap, setSnap] = createSignal<Snap>(saved.snap in SNAP_LABEL ? saved.snap : 'beat');
  /** Each timeline's own view (audit 2026-11-03 D-11, operator 2026-11-03: "absolute, per timeline"). It was ONE
   *  zoom factor, relative to "fit", for every timeline: the view rescaled under you whenever the window's width or the
   *  timeline's length changed, and a zoom chosen on one timeline opened every other. Now: 'auto' (never zoomed: the
   *  whole song, at no less than 28 px a bar, as before), 'fit' (F: the whole song, kept fitted), or a number - pixels
   *  per bar, which a resize or a new section leaves alone. */
  const [views, setViews] = createSignal<Record<string, TlView>>(
    saved.views && typeof saved.views === 'object' ? saved.views : {});
  createEffect(() => writeJson(LAYOUT_KEY, {
    browser: browserOpen(), detail: detailOpen(), perf: perfOpen(), browserW: browserW(), detailH: detailH(),
    snap: snap(), songParts: songPartsOpen(), views: views(),
  }));
  const [laneHeights, setLaneHeights] = createSignal<Record<string, number>>(readJson(HEIGHTS_KEY, {}));
  createEffect(() => writeJson(HEIGHTS_KEY, laneHeights()));
  const laneH = (id: string) => Math.max(LANE_H_TOUCH, laneHeights()[id]
    ?? (() => { const k = timeline()?.lanes.find((l) => l.id === id)?.laneKind; return k === 'automation' ? LANE_H_AUTO : k === 'instrument' ? LANE_H_DRUMS : LANE_H_DEFAULT; })());

  // ── lane groups (laneGroups.ts): Lighting, and the operator's light groups in it (DEC-251) ──
  const [foldedGroups, setFoldedGroups] = createSignal<Record<string, boolean>>(readJson(FOLDED_KEY, {}));
  createEffect(() => writeJson(FOLDED_KEY, foldedGroups()));
  const isFolded = (key: string) => !!foldedGroups()[`${tid()}:${key}`];
  function setFolded(key: string, folded: boolean) {
    const k = `${tid()}:${key}`;
    setFoldedGroups((m) => { const n = { ...m }; if (folded) n[k] = true; else delete n[k]; return n; });
  }
  /** Every lane in the order it is drawn (groups gathered). */
  const laneOrder = createMemo<TimelineLane[]>(() => { const t = timeline(); return t ? orderedLanes(t.lanes) : []; });
  /** One stable object per group header, so the `<For>` keeps a header's row
   *  (and a click on it) while lanes change around it. */
  const groupRowCache = new Map<string, { group: string }>();
  const groupRowOf = (key: string) => { let r = groupRowCache.get(key); if (!r) { r = { group: key }; groupRowCache.set(key, r); } return r; };
  /** The rows the arrangement draws, with how far in each sits: a lane is the
   *  lane object itself (the `<For>` keeps its row while it is unchanged), a
   *  group is its header. */
  const rowLayout = createMemo(() => {
    const t = timeline();
    const depth = new Map<string, number>();
    if (!t) return { rows: [] as Array<TimelineLane | { group: string }>, depth };
    const rows = laneRows(t.lanes, isFolded).map((r) => {
      if (r.type === 'lane') { depth.set(r.lane.id, r.depth); return r.lane; }
      depth.set(`group:${r.key}`, r.depth);
      return groupRowOf(r.key);
    });
    return { rows, depth };
  });
  const depthOf = (id: string) => rowLayout().depth.get(id) ?? 0;
  /** Move lanes (or `group:<key>`) one place among their own group. */
  function moveIds(ids: string[], d: -1 | 1) {
    const t = getTimeline(tid());
    if (!t) return;
    const lanes = moveInTree(t.lanes, ids, d);
    if (lanes) upsertTimeline({ ...t, lanes });
  }
  const canMove = (ids: string[], d: -1 | 1) => { const t = timeline(); return !!t && !!moveInTree(t.lanes, ids, d); };
  /** Put lights lanes into a light group (`''` = Lighting itself), in one
   *  edit; `before` drops them just above that lane. */
  function moveToGroup(ids: string[], group: string, before?: string) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: setLightGroup(t.lanes, ids, group, before) });
    if (group) setFolded(groupKeyOf(group.trim()), false);
    setFolded(LIGHTING, false);
  }
  /** A new light group for these lanes, named "Group N" and opened for
   *  renaming at once. */
  function newGroupFor(ids: string[]) {
    const t = getTimeline(tid());
    if (!t) return;
    const name = freeGroupName(t.lanes);
    moveToGroup(ids, name);
    setRenaming(groupKeyOf(name));
  }
  function renameGroup(from: string, to: string) {
    const t = getTimeline(tid());
    const name = to.trim();
    if (!t || !from || !name || name === from) return;
    const merging = lightGroupsOf(t.lanes).includes(name);
    upsertTimeline({ ...t, lanes: renameLightGroup(t.lanes, from, name) });
    if (isFolded(groupKeyOf(from))) setFolded(groupKeyOf(name), true);
    setFolded(groupKeyOf(from), false);
    if (merging) pushToast('ok', `${from} joined ${name}.`, 2500);
  }
  /** A master for a light group: it takes over only that group's lights. */
  function addMasterTo(group: string) {
    const t = getTimeline(tid());
    if (!t) return;
    const lane: TimelineLane = { id: ulid(), name: group ? `${group} master` : 'All lights', laneKind: 'dmx-rig', steps: [], ...(group ? { lightGroup: group } : {}) };
    const inIt = t.lanes.filter((l) => laneRole(l) === 'lights' && lightGroupOf(l) === group);
    const at = inIt.length ? t.lanes.indexOf(inIt[0]!) : t.lanes.length;
    upsertTimeline({ ...t, lanes: [...t.lanes.slice(0, at), lane, ...t.lanes.slice(at)] });
    setFolded(groupKeyOf(group), false);
    setSel({ type: 'lane', id: lane.id });
  }
  /** The "Move to ..." menu entries for lights lanes: Lighting, each other
   *  light group, and a new one. Empty when any lane is not a light. */
  function groupMoveEntries(ids: string[]): MenuEntry[] {
    const t = timeline();
    if (!t) return [];
    const lanes = t.lanes.filter((l) => ids.includes(l.id));
    if (lanes.length === 0 || lanes.some((l) => laneRole(l) !== 'lights')) return [];
    const here = new Set(lanes.map(lightGroupOf));
    const only = here.size === 1 ? [...here][0]! : null;
    const out: MenuEntry[] = [{ sep: true }];
    if (only !== '') out.push({ label: 'Move to Lighting', hint: 'the main group', run: () => moveToGroup(ids, '') });
    for (const g of lightGroupsOf(t.lanes)) if (g !== only) out.push({ label: `Move to ${g}`, run: () => moveToGroup(ids, g) });
    out.push({ label: 'Move to a new group', hint: 'its own master', run: () => newGroupFor(ids) });
    return out;
  }
  /** The light group whose name is being edited in its header. */
  const [renaming, setRenaming] = createSignal<string | null>(null);

  // ── selection, pick-up, status line ──
  const [sel, setSel] = createSignal<Selection>({ type: 'timeline' });
  // Each undo step keeps what was selected when it was made (2026-11-02, the keyframe editing audit: Delete then Ctrl+Z
  // brought the keyframe back unselected): an undo selects what was selected before the edit, a redo what was after.
  timelineHistory.setContext(() => ({ tid: tid(), sel: sel() }));
  onCleanup(() => timelineHistory.setContext(null));
  /** Name the next edit for the undo message (`timelineHistory.label`); unnamed edits are named from what changed. */
  const label = (text: string) => timelineHistory.label(text);
  const word = (steps: readonly TimelineStep[]) => stepsWord(steps, (k) => keyName(k));
  // The test track (2026-10-13): "Play" in the track's panel loops the stretch marked for the section selected last.
  const [lastSectionName, setLastSectionName] = createSignal<string | null>(null);
  createEffect(() => {
    const s = sel(); const t = timeline();
    if (s.type === 'section' && t) setLastSectionName(t.sections.find((x) => x.id === s.id)?.name ?? null);
  });
  const [picked, setPicked] = createSignal<Payload | null>(null);
  const [info, setInfo] = createSignal('');
  const [chooser, setChooser] = createSignal<{ laneId: string; position: number; x: number; y: number; replaceStepId?: string } | null>(null);
  /** The OPVisual chooser: which column or cell an OPVisual lane fires at a bar. */
  const [ovChooser, setOvChooser] = createSignal<{ laneId: string; position: number; x: number; y: number } | null>(null);
  /** The palette chooser on an OPVisual palette lane (2026-10-28). */
  const [palChooser, setPalChooser] = createSignal<{ laneId: string; position: number; x: number; y: number } | null>(null);
  function placePalette(laneId: string, position: number, id: string, name: string) {
    const step = makeOpvPaletteStep(position, id, name);
    addStep(tid(), laneId, step);
    setPalChooser(null);
    setSel({ type: 'step', laneId, stepId: step.id });
  }
  /** Put an OPVisual press at `position` on `laneId`: a cue firing OPVisual's named action `id`. */
  function placeOpv(laneId: string, position: number, id: string, label: string) {
    const step = { id: ulid(), kind: 'cue', position, action: { module: 'action', command: 'fire', args: { id } }, label } as TimelineStep;
    addStep(tid(), laneId, step);
    setOvChooser(null);
    setSel({ type: 'step', laneId, stepId: step.id });
  }
  /** The Cut / Fade chooser, opened from the mark between two keyframes. */
  const [transPop, setTransPop] = createSignal<{ laneId: string; stepId: string; x: number; y: number } | null>(null);
  /** "More" (a fade's shape and route) stays open once opened, in the
   *  chooser and the detail panel alike. Held here, not in the controls: a
   *  `<Show>` can remount its children on an edit and would fold it again. */
  const [fadeMore, setFadeMore] = createSignal(false);
  const [newMenu, setNewMenu] = createSignal(false);
  /** Renaming the timeline in place, in the toolbar (2026-10-16, operator:
   *  "how do i rename a timeline within the timeline page?"). Was only the
   *  Name field of the detail panel with nothing selected. */
  const [renamingTl, setRenamingTl] = createSignal(false);
  let tlNameBefore = '';
  let tlNameCancelled = false;
  function startRenameTimeline() {
    const t = timeline();
    if (!t) return;
    tlNameBefore = t.name;
    tlNameCancelled = false;
    setRenamingTl(true);
  }
  function endRenameTimeline(typed: string) {
    const t = timeline();
    setRenamingTl(false);
    if (!t) return;
    if (tlNameCancelled || !typed.trim()) { if (t.name !== tlNameBefore) renameTimeline(t.id, tlNameBefore); return; }
    if (typed !== t.name) renameTimeline(t.id, typed);
    commitTimelineName(t.id, tlNameBefore);
  }
  const [addLaneMenu, setAddLaneMenu] = createSignal(false);
  /** Where the "Add a lane" menu opens, fixed to the screen on the side with room (operator, 2026-10-14: "the popup
   *  menu rides up and gets cut off": it opened upward inside the lanes' scroll box, which clipped it). */
  const [addLaneAt, setAddLaneAt] = createSignal<{ left: number; top?: number; bottom?: number; maxH: number } | null>(null);
  const openAddLaneMenu = (btn: HTMLElement) => {
    if (addLaneMenu()) { setAddLaneMenu(false); return; }
    const r = btn.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    setAddLaneAt(below >= 260 || below >= above
      ? { left: r.left, top: r.bottom + 4, maxH: Math.max(160, below) }
      : { left: r.left, bottom: window.innerHeight - r.top + 4, maxH: Math.max(160, above) });
    setAddLaneMenu(true);
  };
  /** The song row shows when the timeline has a song, or once "Song" was picked (operator, 2026-10-14: "song lane
   *  shouldn't be in timeline by default ... song should be a pill in the empty timeline picker"). */
  const [songWanted, setSongWanted] = createSignal<string | null>(null);
  const [songPickNow, setSongPickNow] = createSignal(false);
  const addSongRow = () => {
    const t = timeline();
    if (!t) return;
    setAddLaneMenu(false);
    setSongWanted(t.id);
    setSongPickNow(true);
  };
  const songRowShown = () => { const t = timeline(); return !!t && (!!t.testTrack || songWanted() === t.id); };
  /** The tempo the editor shows (operator, 2026-10-14: "when i import a song, the bpm should change to the track bpm.
   *  unless abletonsync or link is running"): Ableton's while it drives the clock, else the song's (its tempo pick
   *  applied), else the app clock's. */
  const songMap = () => { const id = timeline()?.testTrack?.id; return id ? beatmaps()[id] : undefined; };
  createEffect(() => { const id = timeline()?.testTrack?.id; if (id && !(id in beatmaps())) void loadTrack(id); });
  // A live clock from OPBridge's OWN internal clock is not Ableton: only an Ableton source (OP-AbletonSync, Link, the
  // OSC transport) keeps the clock's tempo (the smoke's first version read any live clock as Ableton and showed 120).
  const abletonDrives = () => !!props.slaved?.() && clockSource().startsWith('ableton');
  const shownBpm = () => (abletonDrives() ? props.bpm() : (songMap() ? gridBpm(songMap()!) : props.bpm()));
  /** The sections are the song's parts (2026-10-21, `timeline/songFollow.ts`): their structure is edited on the
   *  song row, so the section strip's own structure edits step aside. */
  const following = () => followsSong(timeline() ?? undefined);
  /** The song has parts but this timeline's sections are still its own (it has lights on them): offer to switch. */
  const canStartFollowing = () => { const t = timeline(); return !!t && !t.testTrack?.partsFollow && !!planFor(t); };
  /** The song row's own entries (2026-10-14, operator: "i need to be able to delete a song lane (right click
   *  delete)", "add more right click menu options"): on its head, on an empty row, and under the clip entries. */
  function songRowEntries(): MenuEntry[] {
    const t = timeline();
    if (!t) return [];
    const song = t.testTrack;
    return [
      ...(song ? [
        { label: 'Mute the song', checked: !!song.mute, ...(song.mute ? { hint: 'It does not play' } : {}), run: () => toggleSongFlag(t.id, 'mute') } as MenuEntry,
        { label: 'Solo the song', checked: !!song.solo, ...(song.solo ? { hint: 'Only it plays' } : {}), run: () => toggleSongFlag(t.id, 'solo') } as MenuEntry,
        { sep: true } as MenuEntry,
      ] : []),
      { label: song ? 'Choose another song...' : 'Choose a song...', run: () => setSongPickNow(true) },
      ...(song && canStartFollowing() ? [{ label: "Use the song's parts as the sections", run: () => startFollowing(t.id) } as MenuEntry] : []),
      ...(song ? [{ label: 'Song settings', run: () => setSel({ type: 'track' }) } as MenuEntry] : []),
      { sep: true },
      { label: song ? 'Remove the song' : 'Remove the song row', danger: true, run: () => { if (song) setTimelineTestTrack(t.id, null); setSongWanted(null); setSel({ type: 'timeline' }); } },
    ];
  }
  // A different timeline opened: nothing of the last one stays selected.
  createEffect(on(tid, () => {
    setSel({ type: 'timeline' }); setChooser(null); setTransPop(null);
    // Nor its transport (2026-11-03 audit D-8): bar 50 of a 64-bar song made Play on a 16-bar one start past its end,
    // and its loop range looped the next. While something plays, the follower owns the playhead, so only the loop
    // range is dropped then.
    setLoopRange(null);
    if (!playing() && !armed()) { setStartBar(0); setPlayBar(0); setScrubBar(null); }
  }, { defer: true }));
  // A chooser closing under the focused button drops focus to <body>, and the
  // editor's keys (Esc, Delete, Space, Ctrl+Z) stopped working until the next
  // click (found by `timeline_v2_colour_keyframes`: a second Esc did nothing).
  // Take focus back when it has fallen out of the editor.
  createEffect(on([transPop, chooser], () => {
    queueMicrotask(() => {
      const a = document.activeElement;
      if (!a || a === document.body) rootEl?.focus({ preventScroll: true });
    });
  }, { defer: true }));

  const selLane = createMemo<TimelineLane | null>(() => {
    const s = sel(); const t = timeline();
    if (!t) return null;
    const id = s.type === 'lane' ? s.id : s.type === 'step' ? s.laneId : null;
    return id ? t.lanes.find((l) => l.id === id) ?? null : null;
  });
  const selStep = createMemo<TimelineStep | null>(() => {
    const s = sel();
    if (s.type !== 'step') return null;
    return selLane()?.steps.find((x) => x.id === s.stepId) ?? null;
  });
  // ── several lanes, several changes ──
  /** The lanes selected (one or several). */
  const selLaneIds = createMemo<ReadonlySet<string>>(() => {
    const s = sel();
    return new Set(s.type === 'lane' ? [s.id] : s.type === 'lanes' ? s.ids : []);
  });
  /** The changes selected, by step id (ids are unique across lanes). */
  const selStepIds = createMemo<ReadonlySet<string>>(() => {
    const s = sel();
    return new Set(s.type === 'step' ? [s.stepId] : s.type === 'steps' ? s.refs.map((r) => r.stepId) : []);
  });
  const isStepSel = (id: string) => selStepIds().has(id);
  /** Several lanes; one is a plain lane selection. */
  function selectLanes(ids: string[]) {
    const order = laneOrder().map((l) => l.id);
    const sorted = order.filter((id) => ids.includes(id));
    if (sorted.length === 0) setSel({ type: 'timeline' });
    else if (sorted.length === 1) setSel({ type: 'lane', id: sorted[0]! });
    else setSel({ type: 'lanes', ids: sorted });
  }
  /** Several changes; one (with no box) is a plain change selection. */
  function selectSteps(refs: StepRef[], range?: { start: number; end: number }) {
    const seen = new Set<string>();
    const uniq = refs.filter((r) => (seen.has(r.stepId) ? false : (seen.add(r.stepId), true)));
    if (uniq.length === 0) setSel({ type: 'timeline' });
    else if (uniq.length === 1 && !range) setSel({ type: 'step', laneId: uniq[0]!.laneId, stepId: uniq[0]!.stepId });
    else setSel(range ? { type: 'steps', refs: uniq, range } : { type: 'steps', refs: uniq });
  }
  /** The steps these refs name (those still there). */
  function refsSteps(refs: readonly StepRef[]): TimelineStep[] {
    const t = timeline();
    return t ? refs.flatMap((r) => { const x = t.lanes.find((l) => l.id === r.laneId)?.steps.find((y) => y.id === r.stepId); return x ? [x] : []; }) : [];
  }
  /** The selected changes as refs, whatever shape the selection has. */
  function selRefs(): StepRef[] {
    const s = sel();
    if (s.type === 'step') return [{ laneId: s.laneId, stepId: s.stepId }];
    if (s.type === 'steps') return s.refs;
    return [];
  }
  /** Where Shift-click measures a block from: the last plain click. */
  let laneAnchor: string | null = null;
  let stepAnchor: StepRef | null = null;
  /** Cmd-click on the Mac (Ctrl-click there is a right-click), Ctrl-click
   *  elsewhere: add or take out one. */
  const addKey = (e: MouseEvent | KeyboardEvent) => e.metaKey || (!isMac && e.ctrlKey);

  // A step that an undo or a synced change removed is not selected any more.
  createEffect(() => {
    const s = sel();
    const t = timeline();
    if (s.type === 'lanes' && t) {
      const ids = s.ids.filter((id) => t.lanes.some((l) => l.id === id));
      if (ids.length !== s.ids.length) selectLanes(ids);
    }
    if (s.type === 'steps' && t) {
      const live = s.refs.filter((r) => t.lanes.some((l) => l.id === r.laneId && l.steps.some((x) => x.id === r.stepId)));
      if (live.length !== s.refs.length) selectSteps(live, live.length > 0 ? s.range : undefined);
    }
    if (s.type === 'step' && !selStep()) setSel(selLane() ? { type: 'lane', id: s.laneId } : { type: 'timeline' });
    if (s.type === 'lane' && !selLane()) setSel({ type: 'timeline' });
    if (s.type === 'section' && !timeline()?.sections.some((x) => x.id === s.id)) setSel({ type: 'timeline' });
    // The Cut / Fade chooser belongs to a stretch: gone with its keyframe, or
    // when there is no next keyframe left to cut or fade to.
    const tp = transPop();
    if (tp) {
      const lane = timeline()?.lanes.find((l) => l.id === tp.laneId);
      const from = lane?.steps.find((x) => x.id === tp.stepId);
      if (!lane || !from || !nextKeyframe(lane, from)) setTransPop(null);
    }
  });

  // ── geometry ──
  const sig = () => (timeline() ? sigOf(timeline()!) : 4);
  const totalBars = () => Math.max(1, timeline() ? timelineTotalBars(timeline()!) : 8);
  const [viewW, setViewW] = createSignal(1000);
  /** Pixels per bar that fit the whole timeline in view. */
  const fitPx = () => (viewW() - HEADER_W - FIT_END_ROOM) / totalBars();
  const view = (): TlView => views()[tid()] ?? 'auto';
  const setView = (v: TlView) => setViews((m) => ({ ...m, [tid()]: v }));
  const ppb = () => {
    const v = view();
    if (typeof v === 'number') return v;
    // The whole song: 'fit' exactly; 'auto' never under 28 px a bar (a long song scrolls).
    return v === 'fit' ? Math.max(1, fitPx()) : Math.max(MIN_PX_PER_BAR, fitPx());
  };
  /** Zoom by `f`, keeping the bar at `atX` (px from the scroll box's left; default its middle) where it is. Out to the
   *  whole song, in to 32 times the 28 px default (the old limits, in pixels). */
  function zoomBy(f: number, atX?: number) {
    const el = scrollEl;
    const p0 = ppb();
    const p1 = Math.max(Math.min(fitPx(), p0), Math.min(Math.max(MIN_PX_PER_BAR, fitPx()) * ZOOM_MAX, p0 * f));
    if (Math.abs(p1 - p0) < 1e-6) return;
    const at = atX ?? (el ? el.clientWidth / 2 : 0);
    const bar = el ? (el.scrollLeft + at - HEADER_W) / p0 : 0;
    setView(p1);
    if (el && bar >= 0) el.scrollLeft = Math.max(0, HEADER_W + bar * p1 - at);
  }
  /** The whole song in view (F), kept fitted until the next zoom. */
  function fitView() { setView('fit'); if (scrollEl) scrollEl.scrollLeft = 0; }
  const contentW = () => Math.round(totalBars() * ppb());
  const unit = () => snapUnit(snap(), sig());
  /** The grid step, or the finest one while Ctrl / Cmd is held during a drag (`free`). */
  const unitOf = (free?: boolean) => (free ? FREE_UNIT : unit());
  const snapRound = (pos: number, free?: boolean) => Math.max(0, Math.round(pos / unitOf(free)) * unitOf(free));
  const snapFloor = (pos: number) => Math.max(0, Math.floor(pos / unit() + 1e-9) * unit());
  /** An arrow key's step: the grid's, or a sixteenth when the grid is Off (1/960 of a bar would not visibly move). */
  const nudgeUnit = () => (snap() === 'off' ? 1 / (sig() * 4) : unit());
  const freeKey = (e: PointerEvent) => e.ctrlKey || e.metaKey;
  let scrollEl: HTMLDivElement | undefined;
  let rootEl: HTMLDivElement | undefined;
  /** The arrangement (`.tl2-arr`): the box the lane popovers are positioned in. */
  let arrEl: HTMLElement | undefined;
  /** Where a lane popover goes (2026-10-28). Its opener records `x` / `y` from the editor's top-left, but the popover
   *  is placed inside the arrangement, which starts after the browser column: measured 237 px off, so a chooser opened
   *  near the right edge ran off the screen. Converted here, and kept inside the arrangement. */
  function popAt(x: number, y: number, w: number, h: number, laneId?: string): { left: string; top: string } {
    const root = rootEl?.getBoundingClientRect();
    const arr = arrEl?.getBoundingClientRect();
    const dx = root && arr ? arr.left - root.left : 0;
    const dy = root && arr ? arr.top - root.top : 0;
    const W = arrEl?.clientWidth ?? 800;
    const H = arrEl?.clientHeight ?? 600;
    const left = `${Math.max(8, Math.min(x - dx, W - w - 8))}px`;
    // Never over the lane it was opened from (operator, 2026-10-28: "the palette modal that popup should not overlap the
    // highlighted track"): under that lane's row, or above it when there is no room below.
    const row = laneId && arr ? arrEl?.querySelector(`.tl2-lane[data-lane-id="${CSS.escape(laneId)}"]`)?.getBoundingClientRect() : undefined;
    if (row && arr) {
      const below = row.bottom - arr.top + 4, above = row.top - arr.top - h - 4;
      if (below + h <= H - 8) return { left, top: `${below}px` };
      if (above >= 8) return { left, top: `${above}px` };
    }
    return { left, top: `${Math.max(8, Math.min(y - dy, H - h - 8))}px` };
  }
  onMount(() => {
    if (!scrollEl) return;
    const ro = new ResizeObserver(() => { if (scrollEl) setViewW(scrollEl.clientWidth); });
    ro.observe(scrollEl);
    setViewW(scrollEl.clientWidth);
    onCleanup(() => ro.disconnect());
  });
  // Two fingers on the lanes pinch-zoom about their middle (2026-10-16, the look audit: only the song marker strip
  // could, and on an iPad the keyframes along a bar were too close together to tap at the zoom the editor opened at).
  // One finger still scrolls natively. Touch events, not pointer events: the browser cancels a pointer once it starts
  // panning, which would end the pinch. A second finger cancels a drag the first one started; Safari's own page zoom
  // is kept off the lanes.
  onMount(() => {
    const el = scrollEl;
    if (!el) return;
    let pinch: { d: number } | null = null;
    const gap = (t: TouchList) => Math.max(8, Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY));
    const midX = (t: TouchList) => (t[0]!.clientX + t[1]!.clientX) / 2 - el.getBoundingClientRect().left;
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      pinch = { d: gap(e.touches) };
      abortDrag?.();
    };
    const move = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const d = gap(e.touches);
      zoomBy(d / pinch.d, midX(e.touches));
      pinch.d = d;
    };
    const end = (e: TouchEvent) => { if (e.touches.length < 2) pinch = null; };
    const noPageZoom = (e: Event) => e.preventDefault();
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    el.addEventListener('gesturestart', noPageZoom);
    onCleanup(() => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
      el.removeEventListener('gesturestart', noPageZoom);
    });
  });
  /** Fractional bar under a viewport x, measured off a lane's content cell. */
  function barAtX(contentEl: Element, clientX: number): number {
    const r = contentEl.getBoundingClientRect();
    return Math.max(0, Math.min(totalBars() - 1e-6, (clientX - r.left) / ppb()));
  }
  function laneAtPoint(x: number, y: number): { lane: TimelineLane; cell: Element } | null {
    const el = document.elementFromPoint(x, y);
    const cell = el?.closest('[data-tl2-cell]');
    const id = cell?.getAttribute('data-tl2-cell');
    const lane = id ? timeline()?.lanes.find((l) => l.id === id) : undefined;
    return lane && cell ? { lane, cell } : null;
  }

  // ── what will not play ──
  const warnings = createMemo<ReadonlyMap<string, string>>(() => {
    const t = timeline();
    if (!t) return new Map();
    bridges(); // re-judge when bridges change
    return timelineStepWarnings(t);
  });
  function laneWontPlay(l: TimelineLane): string | null {
    // An automation lane's control is judged once, on its first point (`automationCues`).
    if (laneRole(l) === 'automation') { const first = autoPoints(l)[0]; return first ? warnings().get(first.id) ?? null : null; }
    const steps = l.steps.filter((s) => s.kind !== 'marker');
    if (steps.length === 0) return null;
    const reasons = steps.map((s) => warnings().get(s.id));
    if (reasons.some((r) => !r)) return null;
    return [...new Set(reasons)].join('; ');
  }

  // ── canvas controls (the browser's "Controls" group) ──
  const controls = createMemo(() => {
    const all: FireableItem[] = [];
    for (const p of pagesOfActive()) for (const it of p.items) if (isFireable(it)) all.push(it);
    return FIREABLE_ORDER
      .map((kind) => ({ kind, label: FIREABLE_LABEL[kind], items: all.filter((i) => i.kind === kind) }))
      .filter((g) => g.items.length > 0);
  });
  const controlById = (id: string): FireableItem | undefined => {
    for (const p of pagesOfActive()) for (const it of p.items) if (it.id === id && isFireable(it)) return it;
    return undefined;
  };
  /** Any item on this project's canvas by id (an automation lane's control is a fader, a knob or an XY pad, which
   *  `controlById` leaves out: they are not pressed). */
  const canvasItemById = (id: string): { id: string; kind: string } | undefined => {
    for (const p of pagesOfActive()) for (const it of p.items) if (it.id === id) return it;
    return undefined;
  };
  /** The controls an automation lane can move, in canvas order. */
  // OPVisual's knobs are offered once there is an OPVisual to turn them on (an item aimed at it, or one machine to aim at).
  // OPVisual's lanes go to every connected computer (2026-10-28), so they are offered whenever a computer is paired,
  // not only when one is set for OPVisual (two Windows PCs leave that unset).
  const autoTargets = createMemo(() => autoTargetsOf(pagesOfActive().flatMap((p) => p.items)));
  const [search, setSearch] = createSignal('');
  const matches = (text: string) => {
    const q = search().trim().toLowerCase();
    return !q || text.toLowerCase().includes(q);
  };

  // ── edits ──
  function newTimeline(tier: TimelineTier) {
    const t = createTimeline(tier, tier === 'song' ? 'New song' : 'New action');
    selectTimelineInEditor(t.id);
    setNewMenu(false);
    setSel({ type: 'timeline' });
  }
  /** An OPVisual colour or palette lane (2026-10-28): named after itself, selected. */
  function addOpvShowLane(kind: 'opvisual-colour' | 'opvisual-palette'): string | null {
    const t = timeline();
    if (!t) return null;
    const lane = addLane(t.id, kind);
    if (!lane) return null;
    setSel({ type: 'lane', id: lane.id });
    setAddLaneMenu(false);
    return lane.id;
  }
  function addLaneOf(role: NewLaneRole, fixtureId?: string): string | null {
    const t = timeline();
    if (!t) return null;
    const lane = role === 'lights'
      ? (fixtureId ? addDmxFixtureLane(t.id, fixtureId) : addLane(t.id, 'dmx-rig'))
      : addLane(t.id, role === 'guitar' ? 'guitar' : role === 'opvisual' ? 'opvisual' : 'mixed');
    if (!lane) return null;
    // Plain names, not the store's kind defaults ("DMX Rig", "Lane").
    if (!fixtureId) {
      const base = ROLE_NAME[role];
      const n = t.lanes.filter((l) => laneRole(l) === role && !l.fixtureRef).length;
      renameLane(t.id, lane.id, n === 0 ? base : `${base} ${n + 1}`);
    }
    setSel({ type: 'lane', id: lane.id });
    setAddLaneMenu(false);
    return lane.id;
  }
  /** An automation lane for one control (or one axis of an XY pad), named after it. */
  /** A lane for one of OPVisual's controls or visualiser knobs (`timeline/opvControls.ts`), named after it. */
  function addOpvLane(name: string, fields: Pick<TimelineLane, 'vizKnob' | 'opvControl'>): string | null {
    const t = getTimeline(tid());
    if (!t) return null;
    const lane: TimelineLane = { id: ulid(), name, laneKind: 'automation', steps: [], ...fields };
    upsertTimeline({ ...t, lanes: [...t.lanes, lane] });
    setSel({ type: 'lane', id: lane.id });
    setAddLaneMenu(false);
    return lane.id;
  }
  function addAutomationLane(itemId: string, axis?: 'x' | 'y'): string | null {
    const t = getTimeline(tid());
    const it = canvasItemById(itemId);
    if (!t || !it) return null;
    const ax = it.kind === 'xypad' ? (axis ?? 'x') : undefined;
    const lane: TimelineLane = { id: ulid(), name: autoTargetLabel(it, ax), laneKind: 'automation', steps: [], control: { itemId, ...(ax ? { axis: ax } : {}) } };
    upsertTimeline({ ...t, lanes: [...t.lanes, lane] });
    setSel({ type: 'lane', id: lane.id });
    setAddLaneMenu(false);
    return lane.id;
  }
  /** The bars a new drum lights block covers: the section `pos` is in (the whole timeline after the lead-in when
   *  `pos` is not given), so "repeat until the section ends" needs no setting. */
  function drumSpan(pos?: number): { position: number; bars: number } {
    const t = timeline();
    const total = t ? timelineTotalBars(t) : 8;
    const lead = t ? timelineLeadIn(t) : 0;
    if (pos === undefined) return { position: lead, bars: Math.max(1, total - lead) };
    const sec = sectionStarts().find((x) => pos >= x.start - 1e-9 && pos < x.start + x.bars - 1e-9);
    return sec ? { position: sec.start, bars: sec.bars } : { position: Math.floor(pos), bars: Math.max(1, Math.min(8, total - Math.floor(pos))) };
  }
  /** A drum lights preset (2026-10-23): its lane(s), each with its block across the timeline (or the section at `pos`),
   *  in ONE edit; the first block is selected, so its panel is open to change the lights at once. */
  function addDrumPreset(preset: DrumPreset, pos?: number) {
    const t = getTimeline(tid());
    if (!t) return;
    const span = drumSpan(pos);
    const taken = new Set(t.lanes.map((l) => l.name));
    const lanes: TimelineLane[] = preset.lanes.map((pl) => {
      let name = pl.name;
      for (let n = 2; taken.has(name); n++) name = `${pl.name} ${n}`;
      taken.add(name);
      return { id: ulid(), name, laneKind: 'instrument', steps: [pl.block(ulid(), span.position, span.bars)] };
    });
    addLanes(t.id, lanes);
    setAddLaneMenu(false);
    const first = lanes[0]!;
    setSel({ type: 'step', laneId: first.id, stepId: first.steps[0]!.id });
    setDetailOpen(true);
    pushToast('ok', `Added ${preset.name}. Change its lights and colours below, and press Try it to see it.`, 4000);
  }
  /** A block onto an existing drum lights lane at `pos` (a double-click or the lane's menu): like the lane's nearest
   *  block, else the lane's preset, covering the section there. */
  function addDrumBlockAt(lane: TimelineLane, pos: number, preset?: DrumPreset) {
    const span = drumSpan(pos);
    const near = lane.steps.filter(isHitBlockStep).sort((a, b) => Math.abs(a.position - pos) - Math.abs(b.position - pos))[0];
    const block: HitBlockStep | null = preset ? preset.lanes[0]!.block(ulid(), span.position, span.bars)
      : near ? { ...structuredClone(near), id: ulid(), position: span.position, durationBars: span.bars }
        : drumPresets(fixtures())[0]!.lanes[0]!.block(ulid(), span.position, span.bars);
    if (!block) return;
    if (addStep(tid(), lane.id, block, { overwrite: true })) { setSel({ type: 'step', laneId: lane.id, stepId: block.id }); setDetailOpen(true); }
  }
  /** The presets as menu rows. `pos` given: the section there; a single-lane preset goes onto `onto` when given. */
  function drumPresetEntries(pos?: number, onto?: TimelineLane): MenuEntry[] {
    return drumPresets(fixtures())
      .filter((p) => !onto || p.lanes.length === 1)
      .map((p) => ({ label: onto ? `Add ${p.name} here` : `Drum lights: ${p.name}`, hint: p.blurb, run: () => (onto ? addDrumBlockAt(onto, pos ?? 0, p) : addDrumPreset(p, pos)) }));
  }
  /** Open the presets as a menu at a point (the "Drum lights" button in the Add a lane menus). */
  function openDrumPresets(x: number, y: number) {
    setAddLaneMenu(false);
    const rr = rootEl?.getBoundingClientRect();
    const entries = drumPresetEntries();
    const W = rr?.width ?? 1000; const H = rr?.height ?? 700;
    const h = 44 + entries.length * 30;
    setCtxMenu({ title: 'Add drum lights', entries, x: Math.max(8, Math.min(x - (rr?.left ?? 0), W - 300)), y: Math.max(8, Math.min(y - (rr?.top ?? 0), H - h - 8)) });
  }
  /** An OP-Motion lane (2026-10-23, OPM4LBuilder's ask): one target, on Main to start with (the whole mix, never
   *  doubled up across the submixes); its buses, target and glide-out are in the lane's panel, which opens. */
  function addMotionLane(target: string): string | null {
    const t = getTimeline(tid());
    if (!t || !motionTarget(target)) return null;
    const buses = ['master'];
    const lane: TimelineLane = { id: ulid(), name: motionLaneName(target, buses), laneKind: 'automation', steps: [], motion: { buses, target } };
    upsertTimeline({ ...t, lanes: [...t.lanes, lane] });
    setSel({ type: 'lane', id: lane.id });
    setAddLaneMenu(false);
    setDetailOpen(true);
    return lane.id;
  }
  /** OP-Motion's targets as menu rows: the effects, the Moves by family, the crossfader. */
  function motionEntries(): MenuEntry[] {
    return MOTION_TARGETS.map((x) => ({
      label: `OP-Motion: ${x.label}`,
      hint: x.family === 'Effect' ? 'effect' : x.family === 'Crossfader' ? 'Main only' : `${x.family.toLowerCase()} move`,
      run: () => void addMotionLane(x.id),
    }));
  }
  /** Open OP-Motion's targets as a menu at a point (the "OP-Motion" button in the Add a lane panels). */
  function openMotionTargets(x: number, y: number) {
    setAddLaneMenu(false);
    const rr = rootEl?.getBoundingClientRect();
    const entries = motionEntries();
    const W = rr?.width ?? 1000; const H = rr?.height ?? 700;
    const h = 44 + entries.length * 30;
    setCtxMenu({ title: 'Add an OP-Motion lane', entries, x: Math.max(8, Math.min(x - (rr?.left ?? 0), W - 300)), y: Math.max(8, Math.min(y - (rr?.top ?? 0), H - h - 8)) });
  }
  /** Change one automation point (where it is, its value, or how it travels to the next) in one edit. */
  function setAutoPoint(laneId: string, stepId: string, patch: Partial<Pick<AutomationPointStep, 'position' | 'value' | 'curve'>>) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : {
      ...l, steps: l.steps.map((x) => (x.id === stepId && x.kind === 'automationPoint' ? { ...x, ...patch } : x)),
    })) });
  }
  function addAutoPoint(laneId: string, position: number, value: number) {
    // An OP-Motion lane's new point eases in and out (answer 19's default shape); a control's ramps straight.
    const motion = !!timeline()?.lanes.find((l) => l.id === laneId)?.motion;
    const step: AutomationPointStep = { id: ulid(), kind: 'automationPoint', position: Math.max(0, position), value: Math.max(0, Math.min(127, Math.round(value))), ...(motion ? { curve: 's' as const } : {}) };
    if (addStep(tid(), laneId, step)) setSel({ type: 'step', laneId, stepId: step.id });
  }
  /** "Add every light in your rig" (operator, 2026-10-02): the "All lights"
   *  master (when there is none) and a lane for each of the rig's lights that
   *  has none, in patch order, kept together under the lights lanes already
   *  there, in ONE edit. Safe to press again: only what is missing is added.
   *  The rig is OPBridge's (read on every connect, `fixtures/bridgeSync.ts`). */
  function addEveryLight() {
    const t = getTimeline(tid());
    if (!t) return;
    const rig = fixtures();
    if (rig.length === 0) { pushToast('warn', 'No lights in your rig yet: patch them on OPBridge first.'); return; }
    const adding: TimelineLane[] = [];
    const madeMaster = !t.lanes.some((l) => isMasterLane(l) && lightGroupOf(l) === '');
    if (madeMaster) adding.push({ id: ulid(), name: 'All lights', laneKind: 'dmx-rig', steps: [] });
    for (const f of rig) {
      if (t.lanes.some((l) => l.laneKind === 'dmx-fixture' && l.fixtureRef?.fixtureId === f.id)) continue;
      adding.push({ id: ulid(), name: f.name || f.id, laneKind: 'dmx-fixture', steps: [], fixtureRef: { fixtureId: f.id } });
    }
    if (adding.length === 0) { pushToast('ok', 'Every light in your rig already has a lane.', 2000); return; }
    let at = -1;
    t.lanes.forEach((l, i) => { if (laneRole(l) === 'lights') at = i; });
    const lanes = at < 0 ? [...t.lanes, ...adding] : [...t.lanes.slice(0, at + 1), ...adding, ...t.lanes.slice(at + 1)];
    upsertTimeline({ ...t, lanes });
    const lights = adding.length - (madeMaster ? 1 : 0);
    pushToast('ok', `Added ${lights > 0 ? `${lights} light${lights === 1 ? '' : 's'}` : ''}${lights > 0 && madeMaster ? ' and ' : ''}${madeMaster ? 'the All lights master' : ''}.`, 2500);
    selectLanes(adding.map((l) => l.id));
  }
  /** The lane a dropped thing makes when it lands below the lanes (Ableton's
   *  "drop below the last track makes a track"): a colour makes a Lights lane,
   *  a guitar preset or snapshot a Guitar lane (DEC-244), anything else a
   *  Controls lane. So a blank timeline takes a drag at once. */
  function laneRoleFor(p: Payload): NewLaneRole {
    if (p.kind === 'colour') return 'lights';
    if (p.kind === 'item' && GUITAR_KINDS.has(p.itemKind)) return 'guitar';
    return 'controls';
  }
  function placeOnNewLane(p: Payload, position: number): boolean {
    // One undo step: the lane and what was dropped on it. Two left an empty lane behind the first Ctrl+Z
    // (2026-11-03 audit D-9).
    beginBatch();
    try {
      const id = addLaneOf(laneRoleFor(p));
      return id ? place(p, id, position) : false;
    } finally { endBatch(); }
  }
  /** The empty space below the lanes: a drop (or a click with something
   *  picked up) there adds a lane for it. */
  function newLaneCell(): JSX.HTMLAttributes<HTMLDivElement> & Record<string, unknown> {
    const role = () => {
      const p = browserDrag()?.payload ?? picked();
      return p ? ROLE_NAME[laneRoleFor(p)] : undefined;
    };
    return {
      'data-tl2-newlane': '',
      get 'data-drop'() { return browserDrag()?.newLane ? 'new' : picked() ? 'pick' : undefined; },
      get 'data-newrole'() { return role(); },
      get 'data-info'() {
        const p = picked();
        return p ? `Click to add a ${role()} lane with ${payloadLabel(p)} here.` : 'Drop a control or a colour here to add a lane for it.';
      },
      // A box can start below the lanes too and reach up into them.
      onPointerDown: (e: PointerEvent) => onCellPointerDown(e),
      onClick: (e: MouseEvent) => {
        if (suppressClick) { suppressClick = false; return; }
        const p = picked();
        if (!p || (e.target as Element).closest('.tl2-start')) return;
        if (placeOnNewLane(p, snapFloor(barAtX(e.currentTarget as Element, e.clientX))) && !e.shiftKey) setPicked(null);
      },
    };
  }
  function removeLaneHere(laneId: string) {
    const t = getTimeline(tid());
    if (!t) return;
    // The legacy store refuses to remove the last lane; v2 has a blank slate
    // to go back to, so it may.
    upsertTimeline({ ...t, lanes: t.lanes.filter((l) => l.id !== laneId) });
    setSel({ type: 'timeline' });
  }
  function stepFromPayload(p: Payload, position: number): TimelineStep {
    if (p.kind === 'colour') return makeColorMarkerStep(position, p.colour.rgb, { label: p.colour.label, curve: 'hold' });
    if (p.kind === 'item') return makeButtonRefStep(p.itemId, position, p.label);
    if (p.kind === 'fx') return { id: ulid(), kind: 'animation', position, durationBars: 1, preset: p.preset } as TimelineStep;
    if (p.kind === 'hue') return { id: ulid(), kind: 'cue', position, action: hueSceneAction(p.ref), label: p.label } as TimelineStep;
    return makeTargetValueStep(p.targetId, p.value, position, p.label);
  }
  /** Put it on a lane; false when the lane refused it (and said why). */
  function place(p: Payload, laneId: string, position: number): boolean {
    const t = timeline();
    const lane = t?.lanes.find((l) => l.id === laneId);
    if (!t || !lane) return false;
    if (!laneAccepts(lane, p)) {
      pushToast('warn', p.kind === 'colour' || p.kind === 'fx'
        ? `${p.kind === 'fx' ? 'Light FX' : 'Colours'} go on a Lights lane.`
        : laneRole(lane) === 'guitar'
          ? 'A Guitar lane takes guitar presets and snapshots only.'
          : `${payloadLabel(p)} goes on a Controls lane, not ${lane.name}.`);
      return false;
    }
    const step = stepFromPayload(p, position);
    if (p.kind === 'colour') useClickColour(p.colour);
    if (step.kind === 'colorMarker') {
      // A colour change replaces one already on that beat.
      const existing = lane.steps.find((s) => s.kind === 'colorMarker' && Math.abs(s.position - position) < 1e-6);
      if (existing) {
        setColorMarkerColor(t.id, lane.id, existing.id, (step as ColorMarkerStep).color);
        setSel({ type: 'step', laneId: lane.id, stepId: existing.id });
        return true;
      }
    }
    addStep(t.id, lane.id, step, { overwrite: true });
    setSel({ type: 'step', laneId: lane.id, stepId: step.id });
    return true;
  }
  // ── edits the right-click menus (and the chooser) run ──
  /** The chooser's pick: put it on its bar, or swap the press it was opened
   *  for (right-click > Swap for another control). */
  function chooseFromChooser(p: Payload) {
    const c = chooser();
    if (!c) return;
    setChooser(null);
    if (c.replaceStepId) replaceControl(c.laneId, c.replaceStepId, p);
    else place(p, c.laneId, c.position);
  }
  /** Point a press at a different control, in one edit; its beat and length
   *  stay. */
  function replaceControl(laneId: string, stepId: string, p: Payload) {
    const t = getTimeline(tid());
    const lane = t?.lanes.find((l) => l.id === laneId);
    if (!t || !lane || p.kind !== 'item') return;
    if (!laneAccepts(lane, p)) { pushToast('warn', `${p.label} can't go on ${lane.name}.`); return; }
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : {
      ...l, steps: l.steps.map((s) => (s.id === stepId && s.kind === 'buttonRef' ? { ...s, buttonId: p.itemId, labelHint: p.label } : s)),
    })) });
    setSel({ type: 'step', laneId, stepId });
  }
  /** Touch wordings for hints written in other components, whole string to whole string (`inHandWords`). */
const TOUCH_HINTS: Record<string, string> = {
  'Click a clip to select it (Shift adds); drag on empty song to select a box. Drag to move, an edge to trim, Alt-drag to slip the audio. Bars; Shift = beats; Ctrl = no snap; Esc cancels. Ctrl+E split at the playhead, Ctrl+D duplicate, Ctrl+C / V copy and paste, arrows nudge, Del delete.':
    'Tap a clip to select it. Drag it to move it, an edge to trim it; long-press to split, duplicate, copy or delete.',
};

/** A press plays once (instant) or is held for a length. */
  function setStepHeld(laneId: string, stepId: string, bars: number | null) {
    const t = getTimeline(tid());
    if (!t) return;
    if (bars !== null) { placeStep(t.id, laneId, stepId, { durationBars: bars }); return; }
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : {
      ...l, steps: l.steps.map((x) => {
        if (x.id !== stepId) return x;
        const { durationBars: _d, ...rest } = x as TimelineStep & { durationBars?: number };
        void _d;
        return rest as TimelineStep;
      }),
    })) });
  }
  function moveLaneBy(laneId: string, d: -1 | 1) {
    moveIds([laneId], d);
  }
  /** Lanes that are not lights, dragged by their names to sit just before `before` (a lane id; null = after every
   *  lane), in one edit (2026-10-16, operator: "reorder any lane"; only light lanes could be dragged). The Lighting
   *  block draws where its first light lane is, so `before` a light lane means above the block. */
  function moveLanesTo(ids: string[], before: string | null) {
    const t = getTimeline(tid());
    if (!t) return;
    const moving = t.lanes.filter((l) => ids.includes(l.id));
    const rest = t.lanes.filter((l) => !ids.includes(l.id));
    const at = before === null ? rest.length : rest.findIndex((l) => l.id === before);
    if (at < 0 || moving.length === 0) return;
    const next = [...rest.slice(0, at), ...moving, ...rest.slice(at)];
    if (next.every((l, i) => l === t.lanes[i])) return;
    upsertTimeline({ ...t, lanes: next });
  }
  // ── lanes, one or several, each edit ONE undo step ──
  /** Mute or solo lanes: on for all of them, unless all of them already are
   *  (then off), as a DAW's toggle on a group does. Pushed at once with
   *  `?live=true`, so a timeline OPBridge is playing changes now (a muted
   *  light goes off); a bridge too old for that keeps the copy it started
   *  with, and then it says when it will apply. */
  /** A master takes over every light, or only its own group (DEC-329). */
  function toggleAllLights(id: string) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => {
      if (l.id !== id) return l;
      const { allLights, ...rest } = l;
      return allLights ? rest : { ...rest, allLights: true as const };
    }) });
  }
  function toggleLaneFlag(ids: string[], key: 'mute' | 'solo') {
    const t = getTimeline(tid());
    if (!t || ids.length === 0) return;
    const on = !ids.every((id) => t.lanes.find((l) => l.id === id)?.[key]);
    const next = { ...t, lanes: t.lanes.map((l) => {
      if (!ids.includes(l.id)) return l;
      const { [key]: _old, ...rest } = l;
      void _old;
      return on ? { ...rest, [key]: true } : rest;
    }) };
    upsertTimeline(next);
    if (!live()) return;
    const wasPlaying = playing() || armed();
    void pushTimelineLiveNow(next).then((r) => {
      if (r.kind === 'next-play' && wasPlaying) pushToast('ok', 'Changed. A playing timeline picks it up the next time it plays.', 2500);
    });
  }
  function duplicateLanes(ids: string[]) {
    const t = getTimeline(tid());
    if (!t) return;
    const copies: string[] = [];
    const lanes: TimelineLane[] = [];
    for (const l of t.lanes) {
      lanes.push(l);
      if (!ids.includes(l.id)) continue;
      const c: TimelineLane = { ...l, id: ulid(), name: `${l.name} copy`, steps: l.steps.map((s) => ({ ...s, id: ulid() })) };
      lanes.push(c);
      copies.push(c.id);
    }
    upsertTimeline({ ...t, lanes });
    selectLanes(copies);
  }
  function removeLanes(ids: string[]) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.filter((l) => !ids.includes(l.id)) });
    setSel({ type: 'timeline' });
    if (ids.length > 1) pushToast('ok', `Deleted ${ids.length} lanes. ${keyHint('Z')} brings them back.`, 2500);
  }
  function clearLanes(ids: string[]) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (ids.includes(l.id) ? { ...l, steps: [] } : l)) });
  }
  /** Move a group of lanes up or down one place, keeping their order, within
   *  the lane group they share (not at all when they span groups). */
  function moveLanesBy(ids: string[], d: -1 | 1) {
    moveIds(ids, d);
  }
  /** Every change on these lanes, selected. */
  function selectChangesOf(ids: string[]) {
    const t = timeline();
    if (!t) return;
    selectSteps(t.lanes.filter((l) => ids.includes(l.id)).flatMap((l) => l.steps.map((s) => ({ laneId: l.id, stepId: s.id }))));
  }

  // ── changes, several at once ──
  /** Move changes by the same number of bars, each staying on its lane, in
   *  one edit; what they land on is cut (DEC-243's overwrite, as a drag). */
  function moveStepsBy(refs: StepRef[], d: number) {
    const t = getTimeline(tid());
    if (!t || Math.abs(d) < 1e-9) return;
    const ids = new Set(refs.map((r) => r.stepId));
    // The GROUP stops at bar 1 and at the end (2026-10-16): clamped one by one, a selection touching bar 1 squashed
    // onto one beat, and one past the end slid out of sight.
    const moving = t.lanes.flatMap((l) => l.steps.filter((s) => ids.has(s.id)));
    if (moving.length === 0) return;
    const first = Math.min(...moving.map((s) => s.position));
    const last = Math.max(...moving.map((s) => stepEndBars(s, sigOf(t))));
    d = Math.max(d, -first);
    d = Math.min(d, timelineTotalBars(t) - last);
    if (Math.abs(d) < 1e-9) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => {
      const moving = l.steps.filter((s) => ids.has(s.id));
      if (moving.length === 0) return l;
      const rest = l.steps.filter((s) => !ids.has(s.id));
      return { ...l, steps: overwriteWithMany(rest, moving.map((s) => ({ ...s, position: s.position + d })), sigOf(t)) };
    }) });
    const s = sel();
    if (s.type === 'steps' && s.range) setSel({ ...s, range: { start: s.range.start + d, end: s.range.end + d } });
  }
  function removeSelectedSteps(): boolean {
    const refs = selRefs();
    if (refs.length === 0) return false;
    removeSteps(tid(), refs);
    setSel({ type: 'timeline' });
    return true;
  }
  /** The selected changes again, straight after them (after the box when a
   *  box chose them), in one edit, and the copies selected. */
  function duplicateSelectedSteps(): boolean {
    const t = timeline();
    const refs = selRefs();
    if (!t || refs.length < 2 && sel().type !== 'steps') return false;
    const s = sel();
    const found = refs.map((r) => ({ r, st: t.lanes.find((l) => l.id === r.laneId)?.steps.find((x) => x.id === r.stepId) }))
      .filter((x): x is { r: StepRef; st: TimelineStep } => !!x.st);
    if (found.length === 0) return false;
    const start = s.type === 'steps' && s.range ? s.range.start : Math.min(...found.map((x) => x.st.position));
    const end = s.type === 'steps' && s.range ? s.range.end : Math.max(...found.map((x) => stepEndBars(x.st, sigOf(t))));
    const span = Math.max(unit(), Math.ceil((end - start) / unit() - 1e-9) * unit());
    const all = found.map((x) => ({ laneId: x.r.laneId, step: { ...x.st, id: ulid(), position: x.st.position + span } as TimelineStep }));
    // Nothing lands past the end (audit 2026-11-03 D-10): a copy there was
    // stored but drawn nowhere, and the arrows then pulled it back in.
    const adds = all.filter((a) => a.step.position < totalBars() - 1e-9);
    if (adds.length < all.length) {
      pushToast('warn', adds.length === 0
        ? `No room after them: the timeline ends at bar ${totalBars()}. Make it longer first.`
        : `${all.length - adds.length} of the copies would land past the end (bar ${totalBars()}), so they were left out.`);
    }
    if (adds.length === 0) return true;
    label(`duplicate ${word(adds.map((a) => a.step))}`);
    placeStepsBulk(t.id, adds);
    selectSteps(adds.map((a) => ({ laneId: a.laneId, stepId: a.step.id })), { start: start + span, end: start + 2 * span });
    return true;
  }
  /** Every change in the timeline (Ctrl+A). */
  function selectAllSteps() {
    const t = timeline();
    if (!t) return;
    selectSteps(t.lanes.flatMap((l) => l.steps.map((s) => ({ laneId: l.id, stepId: s.id }))));
  }

  function clearLane(laneId: string) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id === laneId ? { ...l, steps: [] } : l)) });
    setSel({ type: 'lane', id: laneId });
  }
  /** A Controls lane's guitar preset and snapshot presses move to the Guitar
   *  lane (made under it when there is none), in ONE edit: how a timeline made
   *  before DEC-244 gets its Guitar lane in one click. */
  function moveGuitarToGuitarLane(fromLaneId: string) {
    const t = getTimeline(tid());
    const from = t?.lanes.find((l) => l.id === fromLaneId);
    if (!t || !from) return;
    const moving = from.steps.filter(isGuitarStep);
    if (moving.length === 0) return;
    const existing = t.lanes.find((l) => l.laneKind === 'guitar');
    const target: TimelineLane = existing ?? { id: ulid(), name: 'Guitar', laneKind: 'guitar', steps: [] };
    const lanes: TimelineLane[] = [];
    for (const l of t.lanes) {
      if (l.id === from.id) {
        lanes.push({ ...l, steps: l.steps.filter((s) => !moving.includes(s)) });
        if (!existing) lanes.push({ ...target, steps: moving });
      } else if (existing && l.id === existing.id) lanes.push({ ...l, steps: [...l.steps, ...moving] });
      else lanes.push(l);
    }
    upsertTimeline({ ...t, lanes });
    setSel({ type: 'lane', id: target.id });
    pushToast('ok', `Moved ${moving.length} guitar change${moving.length === 1 ? '' : 's'} to ${target.name}.`, 2500);
  }
  /** Copy (or cut) presses and keyframes to the clipboard the Legacy timeline
   *  uses too (offsets from the leftmost), so either editor pastes them. */
  async function copySteps(laneId: string, stepIds: string[], cut: boolean) {
    await copyRefs(stepIds.map((stepId) => ({ laneId, stepId })), cut);
  }
  /** Copy changes from one lane or several; each remembers its lane, so a
   *  copy taken across lanes pastes back across the same lanes. */
  async function copyRefs(refs: StepRef[], cut: boolean) {
    const t = timeline();
    if (!t) return;
    const found: Array<{ lane: TimelineLane; s: TimelineStep }> = [];
    for (const r of refs) {
      const lane = t.lanes.find((l) => l.id === r.laneId);
      const s = lane?.steps.find((x) => x.id === r.stepId);
      if (lane && s) found.push({ lane, s });
    }
    if (found.length === 0) return;
    // Measured from the BOX when a box chose them (2026-10-16: from the first change, so a box whose first hit was on
    // its second bar pasted a bar early), as Duplicate already does.
    const box = (() => { const s = sel(); return s.type === 'steps' && s.range ? s.range : null; })();
    const first = Math.min(...found.map((f) => f.s.position));
    const left = box && box.start <= first + 1e-9 ? box.start : first;
    const right = box && box.start <= first + 1e-9 ? box.end : Math.max(...found.map((f) => f.s.position));
    const entries: ClipboardEntry[] = found.map(({ lane, s }) => {
      const body: Record<string, unknown> = { ...s };
      delete body['id'];
      delete body['position'];
      return { offsetFromLeftmostBars: s.position - left, sourceLaneId: lane.id, sourceLaneKind: lane.laneKind ?? 'mixed', step: body };
    });
    await writeClipboard({ version: CLIPBOARD_VERSION, spanBars: right - left, entries });
    if (cut) { label(`cut ${word(found.map((f) => f.s))}`); removeSteps(t.id, found.map((f) => ({ laneId: f.lane.id, stepId: f.s.id }))); setSel({ type: 'timeline' }); }
    pushToast('ok', `${cut ? 'Cut' : 'Copied'} ${found.length === 1 ? stepTitle(found[0]!.s) : `${found.length} changes`}.`, 1500);
  }
  /** The row Ctrl+V pastes on: the selected lane, else the (top) lane of the selected changes, else the lane last
   *  clicked. */
  function pasteRow(): string | null {
    const s = sel();
    if (s.type === 'lane') return s.id;
    if (s.type === 'lanes') return s.ids[0] ?? null;
    if (s.type === 'step') return s.laneId;
    if (s.type === 'steps' && s.refs.length > 0) {
      const order = laneOrder().map((l) => l.id);
      return [...s.refs].sort((a, b) => order.indexOf(a.laneId) - order.indexOf(b.laneId))[0]!.laneId;
    }
    return lastPoint?.laneId ?? null;
  }
  /** Paste at a lane and bar, in ONE edit that cuts what it lands on
   *  (DEC-238): onto that lane when it takes the step, else back onto its
   *  own lane, else onto the first lane that takes it. `advance`: the playhead
   *  then moves past what was pasted (whole bars, at least one), so Ctrl+V
   *  again lays the next copy after it, as a DAW's insert point does. */
  async function pasteAt(laneId: string, position: number, advance = false) {
    // This session's own copy first: reading the system clipboard asks for
    // permission in Chrome and needs a tap on a "Paste" bubble on the iPad,
    // which is no way to paste from a menu. The system clipboard is only the
    // way in for a copy made in another tab.
    const payload = getInMemoryClipboard() ?? await readClipboard();
    const t = timeline();
    if (!payload || payload.entries.length === 0 || !t) { pushToast('warn', 'Nothing to paste yet: copy a change first.'); return; }
    const target = t.lanes.find((l) => l.id === laneId);
    // A copy taken across several lanes goes back onto those lanes (moved in
    // time to where it is pasted), so lights and guitar stay lined up.
    const sourceLanes = [...new Set(payload.entries.map((e) => e.sourceLaneId))];
    const acrossLanes = sourceLanes.length > 1;
    // A copy across lanes from ANOTHER timeline (its lanes are not here): the k-th source lane goes to the k-th lane
    // from where it is pasted, as a DAW pastes tracks (2026-10-16: it all landed on one lane).
    const foreign = acrossLanes && !sourceLanes.some((id) => t.lanes.some((l) => l.id === id));
    const targetIx = Math.max(0, t.lanes.findIndex((l) => l.id === laneId));
    // Only the paste point snaps; the copy keeps its own rhythm (2026-10-16: each change snapped on its own, so a
    // 1/16 pattern pasted at Grid = Bar stacked on one bar line).
    const anchor = Math.max(0, snapRound(position));
    const adds: Array<{ laneId: string; step: TimelineStep }> = [];
    let skipped = 0;
    let pastEnd = 0;
    for (const e of payload.entries) {
      const step = { ...e.step, id: ulid(), position: Math.max(0, anchor + e.offsetFromLeftmostBars) } as TimelineStep;
      const byOrder = foreign ? t.lanes[targetIx + sourceLanes.indexOf(e.sourceLaneId)] : undefined;
      const own = t.lanes.find((l) => l.id === e.sourceLaneId && laneTakesStep(l, step))
        ?? (byOrder && laneTakesStep(byOrder, step) ? byOrder : undefined);
      const dest = (acrossLanes ? own : undefined)
        ?? (target && laneTakesStep(target, step) ? target : undefined)
        ?? own
        ?? t.lanes.find((l) => laneTakesStep(l, step));
      if (!dest) { skipped += 1; continue; }
      // Past the end it would be stored but drawn nowhere (D-10).
      if (step.position >= totalBars() - 1e-9) { pastEnd += 1; continue; }
      adds.push({ laneId: dest.id, step });
    }
    if (pastEnd > 0) pushToast('warn', `${pastEnd} change${pastEnd === 1 ? '' : 's'} would land past the end (bar ${totalBars()}), so ${pastEnd === 1 ? 'it was' : 'they were'} left out.`);
    if (adds.length > 0) {
      label(`paste ${word(adds.map((a) => a.step))}`);
      placeStepsBulk(t.id, adds);
      selectSteps(adds.map((a) => ({ laneId: a.laneId, stepId: a.step.id })));
      if (advance && !playing() && !armed()) seekTo(anchor + Math.max(1, Math.ceil(payload.spanBars - 1e-9)));
    }
    if (skipped > 0) pushToast('warn', `${skipped} change${skipped === 1 ? '' : 's'} had no lane that takes ${skipped === 1 ? 'it' : 'them'}.`);
  }

  // ── right-click menus (DEC-245) ──
  //
  // Operator, 2026-10-02: "timeline needs a right click menu on most of the
  // interface". Every surface that holds something gets one: a press, a
  // keyframe, a guitar change, an empty stretch of lane, a lane's name, a
  // section, the ruler, the space below the lanes. A long press opens the same
  // menu on the iPad (DEC-227: every right-click is a long-press). The menus
  // only gather what the editor already does, so nothing in them is new
  // behaviour to learn.
  type MenuEntry =
    | { label: string; hint?: string; danger?: boolean; checked?: boolean; disabled?: boolean; run: () => void }
    /** A folder: tapping it swaps the menu for `open()` in the same place (a tap, so it works on the iPad). */
    | { label: string; hint?: string; open: () => Menu }
    | { sep: true }
    | { swatches: string; current?: { r: number; g: number; b: number }; pick: (c: StageColor) => void }
    | { hues: string; current?: string; pick: (h: string | undefined) => void };
  type Menu = { title: string; entries: MenuEntry[] };
  /** Colour lanes (`undefined` = none), in one edit. */
  function setLaneHue(ids: string[], hue: string | undefined) {
    const t = getTimeline(tid());
    if (!t || ids.length === 0) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => {
      if (!ids.includes(l.id)) return l;
      const { color: _c, ...rest } = l;
      void _c;
      return hue ? { ...rest, color: hue } : rest;
    }) });
  }
  const [ctxMenu, setCtxMenu] = createSignal<(Menu & { x: number; y: number }) | null>(null);
  /** An arrow-key nudge in progress: its moves are one undo step until the key comes up. */
  let nudgeBatch = false;
  const endNudge = (ev?: KeyboardEvent) => {
    if (!nudgeBatch || (ev && ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight')) return;
    nudgeBatch = false;
    endBatch();
  };
  const endNudgeOnBlur = () => endNudge();
  window.addEventListener('keyup', endNudge);
  window.addEventListener('blur', endNudgeOnBlur);
  onCleanup(() => { window.removeEventListener('keyup', endNudge); window.removeEventListener('blur', endNudgeOnBlur); endNudge(); });
  /** Where the last menu opened, for a menu that opens a second one in its place ("Copy lights from..."). */
  let lastMenuAt = { x: 0, y: 0 };
  createEffect(() => { const m = ctxMenu(); if (m) lastMenuAt = { x: m.x, y: m.y }; });
  // A menu closing under the clicked row drops focus to <body>, and the
  // editor's keys stop (found by `timeline_v2_guitar_lane_and_menus`: Ctrl+Y
  // after a menu edit did nothing). Same rule as the choosers above.
  createEffect(on(ctxMenu, () => {
    queueMicrotask(() => {
      const a = document.activeElement;
      if (!a || a === document.body) rootEl?.focus({ preventScroll: true });
    });
  }, { defer: true }));
  /** A long press opens the menu under the finger, and lifting that finger
   *  must not press the menu's first row. */
  let menuGuardUntil = 0;
  /** Where Ctrl+V lands when nothing is selected: the last bar clicked or
   *  right-clicked on a lane. */
  let lastPoint: { laneId: string; position: number } | null = null;
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const keyHint = (k: string) => (isMac ? `⌘${k}` : `Ctrl+${k}`);

  function laneFromEl(el: Element): TimelineLane | null {
    const id = el.closest('[data-tl2-cell]')?.getAttribute('data-tl2-cell') ?? el.closest('[data-lane-id]')?.getAttribute('data-lane-id');
    return id ? timeline()?.lanes.find((l) => l.id === id) ?? null : null;
  }
  /** The menu for several selected changes (right-click any of them). */
  function stepsMenu(): Menu {
    const refs = selRefs();
    const n = refs.length;
    // What one change's menu offers, for all of them at once, as ONE undo step (2026-10-16: several changes could only
    // be copied, cut, duplicated or deleted): their colour when they are all keyframes, Instant or Held for those that
    // can be held, and Paste after the last of them.
    const t = timeline();
    const found = refs.flatMap((r) => {
      const lane = t?.lanes.find((l) => l.id === r.laneId);
      const s = lane?.steps.find((x) => x.id === r.stepId);
      return lane && s ? [{ lane, s }] : [];
    });
    const held = (s: TimelineStep) => !!(s as { durationBars?: number }).durationBars;
    const holdable = found.filter(({ lane, s }) => (s.kind === 'buttonRef' && laneRole(lane) !== 'guitar') || s.kind === 'animation');
    const keyframes = found.filter(({ s }) => s.kind === 'colorMarker');
    const together = (fn: () => void) => { beginBatch(); try { fn(); } finally { endBatch(); } };
    const some = (label: string) => (holdable.length === n ? label : `${label} (${holdable.length} of them)`);
    const entries: MenuEntry[] = [];
    if (keyframes.length > 1 && keyframes.length === found.length && new Set(keyframes.map(({ lane }) => lane.id)).size === 1) {
      const laneId = keyframes[0]!.lane.id;
      entries.push({ label: 'Make a pattern', hint: 'drag its end to repeat it', run: () => {
        const id = makePattern(tid(), laneId, keyframes.map(({ s }) => s.id));
        if (id) setSel({ type: 'step', laneId, stepId: id });
      } });
    }
    if (keyframes.length > 0 && keyframes.length === found.length) {
      entries.push({ swatches: 'Colour', pick: (c: StageColor) => { useClickColour(c); together(() => { for (const { lane, s } of keyframes) setColorMarkerColor(tid(), lane.id, s.id, c.rgb); }); } });
    }
    const points = found.filter(({ s }) => s.kind === 'automationPoint');
    if (points.length > 0) {
      const of = (label: string) => (points.length === n ? label : `${label} (${points.length} of them)`);
      const curveOf = (s: TimelineStep) => (s as AutomationPointStep).curve ?? 'linear';
      for (const c of CURVE_CHOICES) {
        entries.push({ label: of(c.label), hint: c.hint, checked: points.every(({ s }) => curveOf(s) === c.id), run: () => together(() => { for (const { lane, s } of points) setAutoPoint(lane.id, s.id, { curve: c.id }); }) });
      }
    }
    if (holdable.length > 0) {
      entries.push({ label: some('Instant'), checked: holdable.every(({ s }) => !held(s)), run: () => together(() => { for (const { lane, s } of holdable) if (held(s)) setStepHeld(lane.id, s.id, null); }) });
      entries.push({ label: some('Held'), hint: 'from 1 bar', checked: holdable.every(({ s }) => held(s)), run: () => together(() => { for (const { lane, s } of holdable) if (!held(s)) setStepHeld(lane.id, s.id, 1); }) });
    }
    if (entries.length > 0) entries.push({ sep: true });
    entries.push(
      { label: 'Copy', hint: keyHint('C'), run: () => void copyRefs(refs, false) },
      { label: 'Cut', hint: keyHint('X'), run: () => void copyRefs(refs, true) },
      { label: 'Duplicate', hint: keyHint('D'), run: () => void duplicateSelectedSteps() },
    );
    if (hasClipboard() && found.length > 0) {
      const last = found.reduce((a, b) => (stepEndBars(b.s, sig()) > stepEndBars(a.s, sig()) ? b : a));
      entries.push({ label: 'Paste after them', hint: keyHint('V'), run: () => void pasteAt(last.lane.id, stepEndBars(last.s, sig())) });
    }
    entries.push({ sep: true }, { label: `Delete ${n} changes`, hint: 'Del', danger: true, run: () => void removeSelectedSteps() });
    return { title: `${n} changes`, entries };
  }
  function stepMenu(lane: TimelineLane, s: TimelineStep, x: number, y: number): Menu {
    if (sel().type === 'steps' && isStepSel(s.id)) return stepsMenu();
    // Remember what was selected, for "Add to the selection" (a touch screen
    // has no Shift or Ctrl).
    const before = selRefs();
    setSel({ type: 'step', laneId: lane.id, stepId: s.id });
    const entries: MenuEntry[] = [];
    if (s.kind === 'colorMarker') {
      entries.push({ swatches: 'Colour', current: s.color, pick: (c: StageColor) => { useClickColour(c); setColorMarkerColor(tid(), lane.id, s.id, c.rgb); } });
      entries.push({ swatches: s.arrive ? 'The fade arrives at (left half)' : 'Split it: the fade before it arrives at', ...(s.arrive ? { current: s.arrive } : {}), pick: (c: StageColor) => setKeyArrive(tid(), lane.id, s.id, c.rgb) });
      if (s.arrive) entries.push({ label: 'Join the halves', hint: 'one colour', run: () => setKeyArrive(tid(), lane.id, s.id, null) });
      const next = nextKeyframe(lane, s);
      if (next) {
        const fade = transitionOf(s) === 'fade';
        const to = colourName(next.color, next.label);
        entries.push({ label: `Cut to ${to}`, checked: !fade, run: () => setColorMarkerTransition(tid(), lane.id, s.id, { curve: 'hold' }) });
        entries.push({ label: `Fade to ${to}`, checked: fade, run: () => {
          if (!fade) setColorMarkerTransition(tid(), lane.id, s.id, s.space === 'HSL' || s.space === 'OKLCH' ? { curve: 'linear' } : { curve: 'linear', space: FADE_SPACE });
        } });
      }
      entries.push({ sep: true });
    }
    if (s.kind === 'pattern') {
      entries.push({ label: 'Unpack into keyframes', run: () => { const n = unpackPattern(tid(), lane.id, s.id); setSel({ type: 'timeline' }); pushToast('ok', `Unpacked into ${n} keyframes.`); } });
      entries.push({ sep: true });
    }
    if (s.kind === 'automationPoint') {
      // How it travels to the next point: OP-Motion's five shapes, or a hold (2026-10-23; a control's lane samples them).
      for (const c of CURVE_CHOICES) entries.push({ label: c.label, hint: c.hint, checked: (s.curve ?? 'linear') === c.id, run: () => setAutoPoint(lane.id, s.id, { curve: c.id }) });
      entries.push({ sep: true });
    }
    if (s.kind === 'buttonRef') {
      entries.push({ label: 'Swap for another control...', run: () => {
        const rr = rootEl?.getBoundingClientRect();
        setChooser({ laneId: lane.id, position: s.position, x: x - (rr?.left ?? 0), y: y - (rr?.top ?? 0), replaceStepId: s.id });
      } });
    }
    // A guitar change has no length: it switches the sound, and the next one
    // switches it again.
    if ((s.kind === 'buttonRef' && laneRole(lane) !== 'guitar') || s.kind === 'animation') {
      const dur = (s as { durationBars?: number }).durationBars;
      entries.push({ label: 'Instant', checked: !dur, run: () => setStepHeld(lane.id, s.id, null) });
      entries.push({ label: dur ? `Held for ${barsText(dur)}` : 'Held for 1 bar', checked: !!dur, run: () => setStepHeld(lane.id, s.id, dur ?? 1) });
    }
    const lead = leadInfo(lane, s);
    if (lead) {
      entries.push({
        label: lead.kind === 'in' ? 'Fade in from the lights' : 'Fade back to the lights',
        checked: lead.bars > 0,
        disabled: lead.max <= 1e-9,
        ...(lead.bars > 0 ? { hint: barsText(lead.bars) } : {}),
        run: () => setLeadIn(lane.id, s.id, lead.bars > 0 ? 0 : Math.min(1, lead.max)),
      });
    }
    if (entries.length > 0 && !('sep' in entries[entries.length - 1]!)) entries.push({ sep: true });
    entries.push({ label: 'Copy', hint: keyHint('C'), run: () => void copySteps(lane.id, [s.id], false) });
    entries.push({ label: 'Cut', hint: keyHint('X'), run: () => void copySteps(lane.id, [s.id], true) });
    entries.push({ label: 'Duplicate', hint: keyHint('D'), run: () => { setSel({ type: 'step', laneId: lane.id, stepId: s.id }); duplicateSelectedStep(); } });
    if (hasClipboard()) entries.push({ label: 'Paste after it', hint: keyHint('V'), run: () => void pasteAt(lane.id, stepEndBars(s, sig())) });
    entries.push({ sep: true });
    if (before.length > 0 && !before.some((r) => r.stepId === s.id)) {
      entries.push({ label: 'Add to the selection', hint: isMac ? '⌘-click' : 'Ctrl-click', run: () => selectSteps([...before, { laneId: lane.id, stepId: s.id }]) });
    }
    entries.push({ label: 'Select everything on this lane', run: () => selectChangesOf([lane.id]) });
    entries.push({ sep: true });
    entries.push({ label: 'Delete', hint: 'Del', danger: true, run: () => { removeStep(tid(), lane.id, s.id); setSel({ type: 'lane', id: lane.id }); } });
    return { title: `${stepTitle(s)} at ${barBeat(s.position, sig())}`, entries };
  }
  /** The menu for several selected lanes (right-click any of their names). */
  function lanesMenu(ids: string[]): Menu {
    const t = timeline()!;
    const n = ids.length;
    const lanes = t.lanes.filter((l) => ids.includes(l.id));
    return { title: `${n} lanes`, entries: [
      { label: `Mute ${n} lanes`, checked: lanes.every((l) => l.mute), run: () => toggleLaneFlag(ids, 'mute') },
      { label: `Solo ${n} lanes`, checked: lanes.every((l) => l.solo), run: () => toggleLaneFlag(ids, 'solo') },
      { hues: 'Colour', ...(lanes.every((l) => l.color && l.color === lanes[0]?.color) && lanes[0]?.color ? { current: lanes[0].color } : {}), pick: (h: string | undefined) => setLaneHue(ids, h) },
      { sep: true },
      { label: 'Select all their changes', run: () => selectChangesOf(ids) },
      { label: `Duplicate ${n} lanes`, run: () => duplicateLanes(ids) },
      { label: 'Move up', disabled: !canMove(ids, -1), run: () => moveLanesBy(ids, -1) },
      { label: 'Move down', disabled: !canMove(ids, 1), run: () => moveLanesBy(ids, 1) },
      { sep: true },
      { label: `Clear ${n} lanes`, danger: true, disabled: lanes.every((l) => l.steps.length === 0), run: () => clearLanes(ids) },
      { label: `Delete ${n} lanes`, danger: true, run: () => removeLanes(ids) },
      ...groupMoveEntries(ids),
    ] };
  }
  function laneMenuEntries(lane: TimelineLane, before: ReadonlySet<string> = new Set()): MenuEntry[] {
    const t = timeline()!;
    const others = [...before].filter((id) => id !== lane.id);
    const muteWord = lane.laneKind === 'dmx-fixture' ? 'Mute (light off)' : isMasterLane(lane) ? 'Mute (no takeovers)' : 'Mute';
    const entries: MenuEntry[] = [
      { label: muteWord, checked: !!lane.mute, ...(lane.mute ? { hint: 'It does not play' } : {}), run: () => toggleLaneFlag([lane.id], 'mute') },
      { label: 'Solo', checked: !!lane.solo, ...(lane.solo ? { hint: 'Only soloed lanes play' } : {}), run: () => toggleLaneFlag([lane.id], 'solo') },
      ...(isMasterLane(lane) ? [{ label: 'Take over every light', checked: !!lane.allLights, hint: 'Every group, above their masters', run: () => toggleAllLights(lane.id) }] : []),
      { hues: 'Colour', ...(lane.color ? { current: lane.color } : {}), pick: (h: string | undefined) => setLaneHue([lane.id], h) },
      { sep: true },
      // In place, the same as double-clicking its name (2026-10-16; this
      // opened the detail panel's Name field before).
      { label: 'Rename the lane', run: () => { setSel({ type: 'lane', id: lane.id }); setRenaming(`lane:${lane.id}`); } },
      { label: 'Duplicate the lane', run: () => { const c = duplicateLane(t.id, lane.id); if (c) setSel({ type: 'lane', id: c.id }); } },
      { label: 'Move up', disabled: !canMove([lane.id], -1), run: () => moveLaneBy(lane.id, -1) },
      { label: 'Move down', disabled: !canMove([lane.id], 1), run: () => moveLaneBy(lane.id, 1) },
    ];
    entries.push(...groupMoveEntries([lane.id]));
    const guitar = laneRole(lane) === 'controls' ? lane.steps.filter(isGuitarStep).length : 0;
    if (guitar > 0) {
      entries.push({ sep: true }, { label: `Move ${guitar} guitar change${guitar === 1 ? '' : 's'} to a Guitar lane`, run: () => moveGuitarToGuitarLane(lane.id) });
    }
    entries.push({ sep: true });
    if (others.length > 0) entries.push({ label: 'Add to the selection', hint: isMac ? '⌘-click' : 'Ctrl-click', run: () => selectLanes([...others, lane.id]) });
    entries.push({ label: 'Select all its changes', disabled: lane.steps.length === 0, run: () => selectChangesOf([lane.id]) });
    entries.push(
      { sep: true },
      { label: 'Clear the lane', danger: true, disabled: lane.steps.length === 0, run: () => clearLane(lane.id) },
      { label: 'Delete the lane', danger: true, run: () => removeLaneHere(lane.id) },
    );
    return entries;
  }
  function cellMenu(lane: TimelineLane, pos: number, x: number, y: number): Menu {
    lastPoint = { laneId: lane.id, position: pos };
    setSel({ type: 'lane', id: lane.id });
    const at = barBeat(pos, sig());
    const entries: MenuEntry[] = [];
    if (editsColour(lane)) {
      entries.push({ swatches: `Add a colour at ${at}`, pick: (c: StageColor) => place({ kind: 'colour', colour: c }, lane.id, pos) });
      if (isMasterLane(lane)) entries.push({ label: `Hand back to the lights at ${at}`, hint: 'Each light plays its own lane again', run: () => addHandBack(lane.id, pos) });
      if (laneRole(lane) === 'opvcolour') entries.push({ label: `Back to each clip's own look at ${at}`, hint: 'OPVisual lets the colour go', run: () => addHandBack(lane.id, pos) });
    } else if (laneRole(lane) === 'opvpalette') {
      entries.push({ label: `Change the palette at ${at}...`, run: () => {
        const rr = rootEl?.getBoundingClientRect();
        setPalChooser({ laneId: lane.id, position: pos, x: x - (rr?.left ?? 0), y: y - (rr?.top ?? 0) });
      } });
    } else if (laneRole(lane) === 'drums') {
      if (lane.steps.some(isHitBlockStep)) entries.push({ label: 'Add a block like the one before', hint: 'the section here', run: () => addDrumBlockAt(lane, pos) });
      entries.push(...drumPresetEntries(pos, lane));
    } else if (laneRole(lane) === 'automation') {
      entries.push({ label: `Add a point at ${at}`, hint: 'on the curve', run: () => addAutoPoint(lane.id, pos, autoValueAt(autoPoints(lane), pos) ?? 64) });
    } else if (laneRole(lane) !== 'markers') {
      entries.push({ label: `Choose what happens at ${at}...`, run: () => {
        const rr = rootEl?.getBoundingClientRect();
        setChooser({ laneId: lane.id, position: pos, x: x - (rr?.left ?? 0), y: y - (rr?.top ?? 0) });
      } });
      entries.push({ label: 'Point at it on your canvas', run: () => startCanvasPick(lane.id, pos) });
    }
    if (hasClipboard()) entries.push({ label: `Paste at ${at}`, hint: keyHint('V'), run: () => void pasteAt(lane.id, pos) });
    entries.push({ sep: true }, ...laneMenuEntries(lane));
    return { title: `${lane.name}, ${at}`, entries };
  }
  function sectionMenu(sectionId: string, x?: number, el?: Element): Menu | null {
    const t = timeline();
    const s = t?.sections.find((x) => x.id === sectionId);
    if (!t || !s) return null;
    setSel({ type: 'section', id: s.id });
    // The playhead goes to the nearest bar first (operator, 2026-10-13), and Split splits there.
    const strip = el?.closest('.tl2-c');
    const clicked = strip && x !== undefined ? Math.round(barAtX(strip, x)) : null;
    if (clicked !== null) seekTo(clicked);
    const startOf = sectionStarts().find((v) => v.id === s.id)?.start ?? 0;
    const splitAt = clicked !== null ? clicked - startOf : -1;
    const song = t.testTrack;
    // Following its song (2026-10-21): the section is a part of the song, so splitting and naming happen on the song
    // (a timeline bar is the song's bar), and adding, moving or deleting is done on the Song parts row.
    const f = following();
    const entries: MenuEntry[] = [
      { label: 'Split here', ...(splitAt >= 1 && splitAt < s.lengthBars ? { hint: `at bar ${clicked! + 1}` } : {}), disabled: !(splitAt >= 1 && splitAt < s.lengthBars),
        run: () => (f ? splitSongAt(t, clicked!) : splitSectionHere(s.id, splitAt)) },
      { sep: true },
      ...SONG_SECTION_NAMES.map((name, i): MenuEntry => ({
        label: `Call it ${name}`,
        checked: f ? !!s.name && kindOf(s.name) === name : s.name.toUpperCase() === name,
        ...(i === 0 && song && !f ? { hint: "picks the song's music" } : {}),
        run: () => (f ? nameSongPartAt(t, startOf, name) : setSectionName(t.id, s.id, name)),
      })),
      ...(f ? [{ label: 'Not a part', disabled: !s.name, run: () => nameSongPartAt(t, startOf, null) } as MenuEntry] : []),
      { sep: true },
      // "Copy lights from" (2026-10-14): start this scene as a copy of another, then change what differs.
      { label: 'Copy lights from...', disabled: t.sections.length < 2, run: () => setCtxMenu({
        title: `Copy into ${s.name}`, ...lastMenuAt,
        entries: t.sections.filter((o) => o.id !== s.id).map((o): MenuEntry => ({
          label: o.name, hint: `${o.lengthBars} bars`,
          run: () => {
            const n = copySectionSteps(t.id, o.id, s.id);
            pushToast(n ? 'ok' : 'warn', n ? `${s.name} now has ${o.name}'s ${n} lights and cues. Undo puts it back.` : `${o.name} has nothing to copy yet.`, 2500);
          },
        })),
      }) },
      ...(f ? [] : [
        // In place, the same as double-clicking it (2026-10-16).
        { label: 'Rename the section', run: () => { setSel({ type: 'section', id: s.id }); setRenaming(`sec:${s.id}`); } } as MenuEntry,
        // Duplicate and move, with what is in them (2026-10-16: lanes could, sections could not).
        { label: 'Duplicate the section', run: () => duplicateSectionHere(s.id) } as MenuEntry,
      ]),
      { label: 'Loop its bars', hint: keyHint('L'), checked: !!loopRange()?.on && loopRange()!.from === sectionStarts().find((x) => x.id === s.id)?.start, run: () => { setSel({ type: 'section', id: s.id }); loopSelection(); } },
      ...(f ? [] : [
        { label: 'Move earlier', disabled: t.sections[0]?.id === s.id, run: () => moveSection(t.id, s.id, -1) } as MenuEntry,
        { label: 'Move later', disabled: t.sections.at(-1)?.id === s.id, run: () => moveSection(t.id, s.id, 1) } as MenuEntry,
        { label: 'One bar longer', run: () => setSectionLength(t.id, s.id, s.lengthBars + 1) } as MenuEntry,
        { label: 'One bar shorter', disabled: s.lengthBars <= 1, run: () => setSectionLength(t.id, s.id, s.lengthBars - 1) } as MenuEntry,
      ]),
      { sep: true },
    ];
    if (t.tier === 'song') {
      const end = s.onEnd ?? 'auto-advance';
      entries.push(
        { label: 'Loop until I move on', checked: end === 'loop', run: () => setSectionOnEnd(t.id, s.id, 'loop') },
        { label: 'Then the next section', checked: end === 'auto-advance', run: () => setSectionOnEnd(t.id, s.id, 'auto-advance') },
        { label: 'Then stop', checked: end === 'stop', run: () => setSectionOnEnd(t.id, s.id, 'stop') },
        { sep: true },
      );
    }
    if (!f) entries.push({ label: 'Add a section', run: () => { const n = addSection(t.id); if (n) setSel({ type: 'section', id: n.id }); } });
    if (t.tier === 'song' && playing()) entries.push({ label: `Jump to ${s.name || 'here'}`, hint: 'at the next bar', run: () => jumpTo(s.id) });
    if (f) entries.push({ sep: true }, { label: 'Add, move or delete parts on the Song parts row', disabled: true, run: () => {} });
    else entries.push({ sep: true }, { label: 'Delete the section', danger: true, disabled: t.sections.length <= 1, run: () => removeSection(t.id, s.id) });
    return { title: s.name || 'Not a part yet', entries };
  }
  /** The sections strip where there is no section (after the last one): add one, named. */
  function addSectionMenu(): Menu | null {
    const t = timeline();
    if (!t || following()) return null;
    const add = (name?: string) => { const n = addSection(t.id, name); if (n) setSel({ type: 'section', id: n.id }); };
    return { title: 'Add a section', entries: [
      ...SONG_SECTION_NAMES.map((name): MenuEntry => ({ label: `Add ${name}`, run: () => add(name) })),
      { sep: true },
      { label: 'Add a section', hint: 'unnamed', run: () => add() },
    ] };
  }
  function rulerMenu(): Menu | null {
    const t = timeline();
    if (!t) return null;
    return { title: t.name, entries: [
      { label: playing() || armed() ? 'Stop' : 'Play', hint: 'Space', run: () => togglePlay() },
      { label: 'Loop', checked: timelineLoops(t), run: () => setTimelineLoop(t.id, !timelineLoops(t)) },
      ...(loopRange() ? [
        { label: `Loop bars ${loopRange()!.from + 1} to ${loopRange()!.to}`, checked: loopRange()!.on, run: () => setLoopRange({ ...loopRange()!, on: !loopRange()!.on }) } as MenuEntry,
        { label: 'Clear the bar loop', run: () => setLoopRange(null) } as MenuEntry,
      ] : []),
      { sep: true },
      { label: 'Add a section', run: () => { const n = addSection(t.id); if (n) setSel({ type: 'section', id: n.id }); } },
      { sep: true },
      { label: 'Rename this timeline', run: () => startRenameTimeline() },
      { label: 'Duplicate this timeline', run: () => duplicateTimelineNow() },
      ...(canCopyAsLighting(t) ? [{ label: 'Copy as lighting timeline', hint: 'its lights only', run: () => copyAsLightingNow() } as MenuEntry] : []),
      ...(isLightingTimeline(t) ? [{ label: 'Make it a full timeline', hint: 'every kind of lane', run: () => makeFullNow() } as MenuEntry] : []),
      { label: 'Delete this timeline', danger: true, run: () => deleteTimelineNow() },
    ] };
  }
  /** OPVisual's folder in every Add a lane menu (2026-11-02). Knobs are named per visualiser and layers open their own
   *  folder, so the first screen stays short. */
  function opvMenu(): Menu {
    const back = (to: () => Menu): MenuEntry => ({ label: 'Back', open: to });
    const knobs = (): Menu => ({ title: 'OPVisual knobs', entries: [back(opvMenu), { sep: true }, ...VIZ_KNOB_NAMES.map((v): MenuEntry => ({
      label: v.name,
      open: () => ({ title: v.name, entries: [back(knobs), { sep: true }, ...v.knobs.map((k, i): MenuEntry => ({
        label: k, run: () => void addOpvLane(`${v.name}: ${k}`, { vizKnob: { macro: i + 1 } }),
      }))] }),
    }))] });
    const layers = (): Menu => ({ title: 'OPVisual layers', entries: [back(opvMenu), { sep: true }, ...OPV_LAYERS.map((n): MenuEntry => ({
      label: `Layer ${n}`,
      open: () => ({ title: `Layer ${n}`, entries: [back(layers), { sep: true }, ...OPV_LAYER_CONTROLS.map((d): MenuEntry => ({
        label: d.label, run: () => void addOpvLane(`OPVisual L${n} ${d.label.toLowerCase()}`, { opvControl: { verb: d.verb, layer: n } }),
      }))] }),
    }))] });
    // Effect on a cell (2026-11-02, OPVisual's ask): layer, then column (its clip's name when OPVisual's listing has it,
    // the same listing the Cell/Column chooser reads), then the effect or its amount.
    const clipIn = (lane: number, col: number) => opvListing(autoPickOpVisualBridge() ?? undefined)?.cells[cellKey(col, lane)]?.label;
    const cellFx = (): Menu => ({ title: 'Effect on a cell', entries: [back(opvMenu), { sep: true }, ...OPV_LAYERS.map((n): MenuEntry => ({
      label: `Layer ${n}`,
      open: () => ({ title: `Layer ${n}: which cell?`, entries: [back(cellFx), { sep: true }, ...OPV_COLUMN_LIST.map((col): MenuEntry => {
        const cell = opvCellLabel({ lane: n, col });
        const clip = clipIn(n, col);
        return {
          label: `Cell ${cell}`,
          ...(clip ? { hint: clip } : {}),
          open: () => ({ title: `Cell ${cell}${clip ? `: ${clip}` : ''}`, entries: [back(cellFx), { sep: true }, ...OPV_CELL_FX.map((d): MenuEntry => ({
            label: d.label,
            hint: d.mode === 'value' ? 'how strong' : 'on / off',
            run: () => void addOpvLane(`OPVisual ${cell} ${d.label.toLowerCase()}`, { opvControl: { verb: d.verb, param: d.param!, cell: { lane: n, col } } }),
          }))] }),
        };
      })] }),
    }))] });
    return { title: 'OPVisual', entries: [
      { label: 'Cell/Column', hint: 'fires clips', run: () => void addLaneOf('opvisual') },
      ...(bridges().length > 0 ? [
        { label: 'Colour', hint: 'recolours its clips', run: () => void addOpvShowLane('opvisual-colour') } as MenuEntry,
        { label: 'Palette', hint: 'its visualisers', run: () => void addOpvShowLane('opvisual-palette') } as MenuEntry,
        { label: 'Visualiser knobs', open: knobs } as MenuEntry,
        { label: 'Layers', hint: 'opacity, speed, on/off, clear', open: layers } as MenuEntry,
        { label: 'Effect on a cell', hint: 'Flow, Echo... and how strong', open: cellFx } as MenuEntry,
        { sep: true } as MenuEntry,
        ...OPV_PICTURE_CONTROLS.map((d): MenuEntry => ({
          label: d.label, run: () => void addOpvLane(`OPVisual ${d.label.toLowerCase()}`, { opvControl: { verb: d.verb, ...(d.param ? { param: d.param } : {}) } }),
        })),
      ] : []),
    ] };
  }
  /** Open OPVisual's folder at a point (the "OPVisual" button in the Add a lane panels). */
  function openOpvMenu(x: number, y: number) {
    setAddLaneMenu(false);
    const rr = rootEl?.getBoundingClientRect();
    placeMenu(opvMenu(), x - (rr?.left ?? 0), y - (rr?.top ?? 0));
  }
  /** Show a menu at a point inside the editor, kept on screen. */
  function placeMenu(m: Menu, x: number, y: number) {
    const rr = rootEl?.getBoundingClientRect();
    const W = rr?.width ?? 1000; const H = rr?.height ?? 700;
    const h = 44 + m.entries.reduce((n, e) => n + ('sep' in e ? 9 : 30), 0);
    setCtxMenu({ ...m, x: Math.max(8, Math.min(x, W - 268)), y: Math.max(8, Math.min(y, H - h - 8)) });
  }
  function newLaneMenu(): Menu {
    // A lighting timeline (DEC-323) takes light lanes only; "Make it a full timeline" in its menu opens the rest.
    if (lightingOnly()) return { title: 'Add a light lane', entries: [
      ...(fixtures().length > 0 ? [{ label: 'Add every light in your rig', hint: `${fixtures().length}`, run: () => addEveryLight() }, { sep: true as const }] : []),
      { label: 'Lights lane', hint: 'every light', run: () => void addLaneOf('lights') },
      ...fixtures().map((f) => ({ label: f.name, hint: 'one light', run: () => void addLaneOf('lights', f.id) })),
      { sep: true as const },
      ...drumPresetEntries(),
    ] };
    return { title: 'Add a lane', entries: [
      ...(fixtures().length > 0 ? [{ label: 'Add every light in your rig', hint: `${fixtures().length}`, run: () => addEveryLight() }, { sep: true as const }] : []),
      { label: 'Controls lane', run: () => void addLaneOf('controls') },
      { label: 'Guitar lane', run: () => void addLaneOf('guitar') },
      { label: 'Lights lane', run: () => void addLaneOf('lights') },
      { label: 'OPVisual', hint: 'cells, knobs, more', open: opvMenu },
      { sep: true as const },
      ...drumPresetEntries(),
      ...(!songRowShown() ? [{ label: 'Song', hint: 'an audio file under the timeline', run: () => addSongRow() }] : []),
      ...(autoTargets().length > 0 ? [{ sep: true as const }] : []),
      ...autoTargets().slice(0, 8).map((a) => ({ label: `Automation: ${a.label}`, hint: 'its moves', run: () => void addAutomationLane(a.itemId, a.axis) })),
      { sep: true as const },
      ...motionEntries(),
    ] };
  }
  /** The menu for whatever is under the pointer, or null for no menu (a
   *  transition mark opens its own chooser instead). */
  function menuFor(t: Element, x: number, y: number): Menu | null {
    if (!timeline()) return null;
    const mark = t.closest('.tl2-kfh');
    const stepEl = t.closest('[data-step-id]');
    if (stepEl) {
      const lane = laneFromEl(stepEl);
      const s = lane?.steps.find((v) => v.id === stepEl.getAttribute('data-step-id'));
      if (!lane || !s) return null;
      if (mark && s.kind === 'colorMarker') { openTransition(lane, s, mark); return null; }
      return stepMenu(lane, s, x, y);
    }
    const grp = t.closest('.tl2-grp');
    if (grp) return groupMenu(grp.getAttribute('data-group') ?? '');
    if (t.closest('.tl2-lane > .tl2-h')) {
      const lane = laneFromEl(t);
      if (!lane) return null;
      const before = selLaneIds();
      if (sel().type === 'lanes' && before.has(lane.id)) return lanesMenu([...before]);
      setSel({ type: 'lane', id: lane.id });
      return { title: lane.name, entries: laneMenuEntries(lane, before) };
    }
    // The song row (2026-10-13, trim and split): split, repeat, delete or add a clip there.
    if (t.closest('.tl2-track > .tl2-h')) { if (timeline()?.testTrack) setSel({ type: 'track' }); return { title: timeline()?.testTrack?.name ?? 'Song', entries: songRowEntries() }; }
    const trk = t.closest('.tl2-track > .tl2-c');
    if (trk) {
      if (!timeline()?.testTrack) return { title: 'Song', entries: songRowEntries() };
      setSel({ type: 'track' });
      const m = songTrackMenu(timeline()!, sectionStarts(), barAtX(trk, x), playBar());
      return { title: m?.title ?? timeline()!.testTrack!.name, entries: [...(m?.entries ?? []), { sep: true }, ...songRowEntries()] };
    }
    const cell = t.closest('[data-tl2-cell]');
    if (cell) {
      const lane = laneFromEl(cell);
      return lane ? cellMenu(lane, snapFloor(barAtX(cell, x)), x, y) : null;
    }
    const sec = t.closest('[data-sec-id]');
    if (sec) return sectionMenu(sec.getAttribute('data-sec-id')!, x, sec);
    if (t.closest('.tl2-secs > .tl2-c')) return addSectionMenu();
    if (t.closest('.tl2-ruler')) return rulerMenu();
    if (t.closest('[data-tl2-newlane]')) return newLaneMenu();
    return null;
  }
  function openMenuAt(t: Element, x: number, y: number): boolean {
    setChooser(null);
    setTransPop(null);
    const m = menuFor(t, x, y);
    if (!m) { setCtxMenu(null); return false; }
    const rr = rootEl?.getBoundingClientRect();
    const W = rr?.width ?? 1000; const H = rr?.height ?? 700;
    const h = 44 + m.entries.reduce((n, e) => n + ('sep' in e ? 9 : 'swatches' in e ? 80 : 'hues' in e ? 52 : 30), 0);
    setCtxMenu({ ...m, x: Math.max(8, Math.min(x - (rr?.left ?? 0), W - 268)), y: Math.max(8, Math.min(y - (rr?.top ?? 0), H - h - 8)) });
    return true;
  }
  function onContextMenu(ev: MouseEvent) {
    const t = ev.target as Element;
    // Text fields keep the browser's own menu (paste into a name).
    if (t.closest('input, textarea, select')) return;
    ev.preventDefault();
    if (t.closest('.tl2-ctx')) return;
    openMenuAt(t, ev.clientX, ev.clientY);
  }
  // A long press (a finger or a pen, held still) opens the same menu.
  let longPress: { timer: number; done: () => void } | null = null;
  function cancelLongPress() {
    if (!longPress) return;
    clearTimeout(longPress.timer);
    longPress.done();
    longPress = null;
  }
  function startLongPress(e: PointerEvent) {
    cancelLongPress();
    const target = e.target as Element;
    if (target.closest('input, textarea, select, .tl2-ctx, .tl2-pop, .tl2-tpop')) return;
    const x0 = e.clientX; const y0 = e.clientY;
    const move = (m: PointerEvent) => { if (Math.hypot(m.clientX - x0, m.clientY - y0) > 8) cancelLongPress(); };
    const end = () => cancelLongPress();
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    longPress = {
      timer: window.setTimeout(() => {
        cancelLongPress();
        if (!openMenuAt(target, x0 + 14, y0 + 14)) return;
        menuGuardUntil = performance.now() + 450;
        suppressClick = true;
        // A held press is not a drag: let go of the block under the finger.
        setClipDrag(null);
        setDragPreview(null);
      }, 550),
      done: () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
      },
    };
  }
  onCleanup(cancelLongPress);

  /** A lane's name clicked: select it; Shift selects the block of lanes from
   *  the last one clicked; Cmd (Mac) or Ctrl adds or takes out one. */
  /** A light lane's name dragged (mouse or pen) onto another light group, or
   *  between two of its lanes: the lane (or the selected light lanes it is one
   *  of) moves there, in one edit. A finger uses the menu's "Move to ...", so
   *  a finger on a name still scrolls the lanes. */
  const [laneDrag, setLaneDrag] = createSignal<{ ids: string[]; target: { key: string; group: string; before?: string } | null } | null>(null);
  let headDragged = false;
  function onLaneHeadPointerDown(ev: PointerEvent, lane: TimelineLane) {
    if (ev.button !== 0 || ev.pointerType === 'touch') return;
    if (laneRole(lane) !== 'lights') { startPlainLaneDrag(ev, lane); return; }
    if ((ev.target as Element).closest('button, input')) return;
    const t = timeline();
    if (!t) return;
    const sel = selLaneIds();
    const all = sel.has(lane.id) && [...sel].every((id) => { const l = t.lanes.find((x) => x.id === id); return !!l && laneRole(l) === 'lights'; });
    const ids = all ? [...sel] : [lane.id];
    const x0 = ev.clientX; const y0 = ev.clientY;
    let on = false;
    const targetAt = (x: number, y: number) => {
      const el = document.elementFromPoint(x, y);
      const grp = el?.closest('.tl2-grp');
      if (grp) { const key = grp.getAttribute('data-group') ?? ''; return { key, group: groupOfKey(key) }; }
      const row = el?.closest<HTMLElement>('.tl2-lane[data-lane-id]');
      const over = row ? timeline()?.lanes.find((l) => l.id === row.dataset['laneId']) : undefined;
      if (!row || !over || laneRole(over) !== 'lights') return null;
      const group = lightGroupOf(over);
      const r = row.getBoundingClientRect();
      // The upper half drops above this lane, the lower half above the next
      // one in the same group (or at the group's end).
      let before: string | undefined = over.id;
      if (y >= r.top + r.height / 2) {
        const order = laneOrder().filter((l) => laneRole(l) === 'lights' && lightGroupOf(l) === group && !ids.includes(l.id));
        before = order[order.findIndex((l) => l.id === over.id) + 1]?.id;
      }
      if (before && ids.includes(before)) before = undefined;
      return { key: groupKeyOf(group), group, ...(before ? { before } : {}) };
    };
    // Esc, a second finger, Back and a pointercancel all CANCEL it (audit 2026-11-03 D-4): it used to commit on a
    // cancel and ignore the others. `abortDrag` is the editor's one cancel hook (the block drags already use it).
    const pid = ev.pointerId;
    let cancelled = false;
    const abort = () => { cancelled = true; up(); };
    abortDrag = abort;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      if (!on && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      on = true;
      const target = targetAt(e.clientX, e.clientY);
      setLaneDrag({ ids, target });
      const what = ids.length === 1 ? (t.lanes.find((l) => l.id === ids[0])?.name ?? 'the light') : `${ids.length} lights`;
      setInfo(target ? `Drop to move ${what} to ${groupName(target.key)}` : `Drag ${what} onto a light group`);
    };
    const up = (ue?: Event) => {
      if (ue instanceof PointerEvent && ue.pointerId !== pid) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (abortDrag === abort) abortDrag = null;
      const d = laneDrag();
      setLaneDrag(null);
      if (!on) return;
      if (cancelled || ue?.type === 'pointercancel') { setInfo(''); return; }
      // Swallow the click this release makes on the name, and only that one.
      headDragged = true;
      window.setTimeout(() => { headDragged = false; }, 0);
      if (d?.target) moveToGroup(d.ids, d.target.group, d.target.before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** Any other lane's name dragged up or down (mouse or pen; a finger uses the menu's Move up / Move down): drops
   *  above the lane under the pointer (its upper half) or below it (its lower half), or above the Lighting block. */
  function startPlainLaneDrag(ev: PointerEvent, lane: TimelineLane) {
    if ((ev.target as Element).closest('button, input')) return;
    const t = timeline();
    if (!t) return;
    const sel = selLaneIds();
    const plain = (id: string) => { const l = t.lanes.find((x) => x.id === id); return !!l && laneRole(l) !== 'lights'; };
    const ids = sel.has(lane.id) && [...sel].every(plain) ? [...sel] : [lane.id];
    const x0 = ev.clientX; const y0 = ev.clientY;
    let on = false;
    let before: string | null | undefined;
    const firstLight = () => timeline()?.lanes.find((l) => laneRole(l) === 'lights' && !ids.includes(l.id))?.id;
    const targetAt = (x: number, y: number): string | null | undefined => {
      const el = document.elementFromPoint(x, y);
      if (el?.closest('.tl2-grp')) return firstLight();
      const row = el?.closest<HTMLElement>('.tl2-lane[data-lane-id]');
      const lanes = timeline()?.lanes ?? [];
      const over = row ? lanes.find((l) => l.id === row.dataset['laneId']) : undefined;
      if (!row || !over) return undefined;
      if (laneRole(over) === 'lights') return firstLight();
      const r = row.getBoundingClientRect();
      if (y < r.top + r.height / 2) return ids.includes(over.id) ? undefined : over.id;
      const rest = lanes.filter((l) => !ids.includes(l.id));
      const i = rest.findIndex((l) => l.id === over.id);
      return i < 0 ? undefined : (rest[i + 1]?.id ?? null);
    };
    // Esc, a second finger, Back and a pointercancel all CANCEL it (audit 2026-11-03 D-4): it used to commit on a
    // cancel and ignore the others. `abortDrag` is the editor's one cancel hook (the block drags already use it).
    const pid = ev.pointerId;
    let cancelled = false;
    const abort = () => { cancelled = true; up(); };
    abortDrag = abort;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      if (!on && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      on = true;
      before = targetAt(e.clientX, e.clientY);
      setLaneDrag({ ids, target: before === undefined ? null : { key: '', group: '', ...(before ? { before } : {}) } });
      const what = ids.length === 1 ? (t.lanes.find((l) => l.id === ids[0])?.name ?? 'the lane') : `${ids.length} lanes`;
      setInfo(before === undefined ? `Drag ${what} up or down to move it` : `Drop to move ${what} here`);
    };
    const up = (ue?: Event) => {
      if (ue instanceof PointerEvent && ue.pointerId !== pid) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (abortDrag === abort) abortDrag = null;
      setLaneDrag(null);
      if (!on) return;
      headDragged = true;
      window.setTimeout(() => { headDragged = false; }, 0);
      if (cancelled || ue?.type === 'pointercancel') { setInfo(''); return; }
      if (before !== undefined) moveLanesTo(ids, before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  function onLaneHeadClick(ev: MouseEvent, lane: TimelineLane) {
    if (headDragged) { headDragged = false; return; }
    const t = timeline();
    if (!t) return;
    if (ev.shiftKey) {
      const from = laneAnchor && t.lanes.some((l) => l.id === laneAnchor) ? laneAnchor : (selLane()?.id ?? lane.id);
      const order = laneOrder();
      const a = order.findIndex((l) => l.id === from);
      const b = order.findIndex((l) => l.id === lane.id);
      selectLanes(order.slice(Math.min(a, b), Math.max(a, b) + 1).map((l) => l.id));
      return;
    }
    if (addKey(ev)) {
      const cur = new Set(selLaneIds());
      if (cur.has(lane.id)) cur.delete(lane.id); else cur.add(lane.id);
      laneAnchor = lane.id;
      selectLanes([...cur]);
      return;
    }
    laneAnchor = lane.id;
    setSel({ type: 'lane', id: lane.id });
  }
  function onCellClick(ev: MouseEvent, lane: TimelineLane, cell: Element) {
    const pos = snapFloor(barAtX(cell, ev.clientX));
    lastPoint = { laneId: lane.id, position: pos };
    // A pick-up is put down once it lands (operator, 2026-10-02: "add the
    // thing and then defocus once it's placed"); Shift keeps it held to place
    // it again. A lane that refuses it keeps it held, to try another lane.
    const p = picked();
    if (p) { if (place(p, lane.id, pos) && !ev.shiftKey) setPicked(null); return; }
    // A plain click on blank lane space moves the playhead to the nearest bar; a DOUBLE-click or Ctrl/Cmd+click adds
    // there (operator, 2026-10-13: "maybe ctrl click in blank space would add a keyframe", "or double click is fine
    // too"). A touch tap still adds: the iPad has no Ctrl, and its playhead moves from the ruler. Adapts DEC-265's
    // gesture; the ringed colour is unchanged.
    // The click also highlights that row and lets go of the changes (2026-11-02, operator: "when the mouse is clicked
    // somewhere in the timeline the playhead moves there and the row is highlighted. so user would know where things
    // would be pasted"); a keyframe stayed selected, and Ctrl+V then pasted beside it, not where the click was.
    // While the show PLAYS, a lane click only highlights the row and sets the paste point; the ruler and the playhead
    // still move playback. Seeking here jumped the live song on the first click of every double-click meant to add a
    // keyframe, and on any click meant to pick a row (2026-11-03 audit D-1; adapts the 2026-11-02 click-to-move rule,
    // which stands while stopped).
    if (!(ev.detail >= 2 || addKey(ev) || lastPointer === 'touch')) {
      if (!playing() && !armed()) seekTo(barAtX(cell, ev.clientX));
      setSel({ type: 'lane', id: lane.id }); laneAnchor = lane.id;
      return;
    }
    const role = laneRole(lane);
    if (role === 'lights' || role === 'opvcolour') {
      place({ kind: 'colour', colour: clickColour() }, lane.id, pos);
      return;
    }
    if (role === 'opvpalette') {
      setSel({ type: 'lane', id: lane.id });
      const rr = rootEl?.getBoundingClientRect();
      setPalChooser({ laneId: lane.id, position: pos, x: ev.clientX - (rr?.left ?? 0), y: ev.clientY - (rr?.top ?? 0) });
      return;
    }
    if (role === 'automation') {
      const r = cell.getBoundingClientRect();
      addAutoPoint(lane.id, Math.min(totalBars(), snapRound(barAtX(cell, ev.clientX))), autoValueFromY(ev.clientY - r.top, r.height));
      return;
    }
    if (role === 'drums') {
      addDrumBlockAt(lane, pos);
      return;
    }
    if (role === 'opvisual') {
      setSel({ type: 'lane', id: lane.id });
      const rr = rootEl?.getBoundingClientRect();
      setOvChooser({ laneId: lane.id, position: pos, x: ev.clientX - (rr?.left ?? 0), y: ev.clientY - (rr?.top ?? 0) });
      return;
    }
    if (role === 'controls' || role === 'guitar') {
      setSel({ type: 'lane', id: lane.id });
      const rr = rootEl?.getBoundingClientRect();
      setChooser({ laneId: lane.id, position: pos, x: ev.clientX - (rr?.left ?? 0), y: ev.clientY - (rr?.top ?? 0) });
      return;
    }
    setSel({ type: 'lane', id: lane.id });
  }
  function deleteSelectedStep(): boolean {
    const s = sel();
    if (s.type !== 'step') return false;
    removeStep(tid(), s.laneId, s.stepId);
    setSel({ type: 'lane', id: s.laneId });
    return true;
  }
  function duplicateSelectedStep(): boolean {
    const s = sel(); const st = selStep(); const t = timeline();
    if (s.type !== 'step' || !st || !t) return false;
    const end = stepEndBars(st, sigOf(t));
    // Not past the end (D-10): it would be stored and drawn nowhere.
    if (end >= totalBars() - 1e-9) { pushToast('warn', `No room after it: the timeline ends at bar ${totalBars()}. Make it longer first.`); return true; }
    const copy = { ...st, id: ulid(), position: end } as TimelineStep;
    label(`duplicate ${word([st])}`);
    placeStepsBulk(t.id, [{ laneId: s.laneId, step: copy }]);
    setSel({ type: 'step', laneId: s.laneId, stepId: copy.id });
    return true;
  }
  function duplicateTimelineNow() {
    const c = duplicateTimeline(tid());
    if (c) { selectTimelineInEditor(c.id); pushToast('ok', `Duplicated as "${c.name}"`, 2000); }
  }
  /** A LIGHTING timeline (DEC-323): its lanes are lights only, so the editor offers light lanes only. */
  const lightingOnly = () => isLightingTimeline(timeline());
  function copyAsLightingNow() {
    const c = copyAsLightingTimeline(tid());
    if (c) { selectTimelineInEditor(c.id); pushToast('ok', `Made "${c.name}": this timeline's lights only`, 2500); }
  }
  function makeFullNow() {
    makeFullTimeline(tid());
    pushToast('ok', 'A full timeline now: every kind of lane can be added', 2500);
  }
  /** Delete asks in the editor, not with the browser's own dialog (2026-10-16, the look audit: a system box over the
   *  editor, and no word of the pads that launch it, which Navigate's delete names). */
  const [confirmDelete, setConfirmDelete] = createSignal<{ id: string; name: string; pads: string[] } | null>(null);
  function deleteTimelineNow() {
    const t = timeline();
    if (!t) return;
    const pads = projects().flatMap((p) => p.pages.flatMap((pg) => pg.items
      .filter((it) => it.kind === 'timelineLauncher' && (it as { timelineId?: string }).timelineId === t.id)
      .map(() => `${p.name} / ${pg.name}`)));
    setConfirmDelete({ id: t.id, name: t.name, pads });
  }
  function deleteTimelineConfirmed() {
    const c = confirmDelete();
    setConfirmDelete(null);
    if (!c) return;
    const { id } = c;
    const next = projectTimelines().find((x) => x.id !== id)?.id ?? null;
    // Removed from OPBridge too; an offline machine is named and retried
    // when it is back (timeline/deleteTimeline.ts, every Delete's one path).
    deleteTimelineFromRig(id);
    selectTimelineInEditor(next);
  }

  // ── transport: the real launch, and the playhead that follows the clock ──
  const live = () => anyBridgeConnectedReactive(undefined);
  const [playing, setPlaying] = createSignal(false);
  const [armed, setArmed] = createSignal(false);
  const [playBar, setPlayBar] = createSignal(0);
  /** A SONG timeline with a computer connected follows OPBridge's own position (timeline/transport.ts, 2026-10-16):
   *  Play, Stop, armed and the playhead all come from what the engine reports for this timeline's home machine,
   *  whoever started it (this editor, a pad, the iPad, a foot pedal). Before, the editor re-derived OPBridge's start
   *  rule from the clock and came in a bar late when a song's clock started just before its bar line ("the audio
   *  starts and then a bar into the playhead starts", operator 2026-10-16). Action timelines (OPBridge's state does
   *  not report them) and rehearsal with no computer keep the local clock below. */
  const homeTarget = createMemo(() => { const t = timeline(); return t ? timelineHomeTarget(t) : ''; });
  /** Light FX blocks (2026-10-19): OPBridge's lighting presets, read from the bridge the timeline plays on. */
  const fxPlay = createMemo(() => { const t = timeline(); return t ? timelinePlaysLightFx(t) : false; });
  const fxState = createMemo(() => { const h = homeTarget(); return h ? presetState(h) : null; });
  createEffect(() => { const h = homeTarget(); if (h) ensurePresets(h); });
  const followsBridge = () => live() && timeline()?.tier === 'song';
  createEffect(on(homeTarget, (h) => { const release = followTransport(h); onCleanup(release); }));
  const bridgeRun = createMemo(() => { const r = transportRun(homeTarget()); return r && r.timelineId === tid() ? r : null; });
  /** This editor started the run that is playing (closing the editor stops only that, never a run started elsewhere). */
  let launchedHere = false;
  const [rafTick, setRafTick] = createSignal(0);
  /** The frame's own timestamp (2026-10-20): the playhead is placed for the moment the frame is SHOWN, not for when
   *  this code happens to run in it - on a heavy timeline that moment wandered a few ms frame to frame, and the
   *  playhead's speed with it. */
  let frameAt = 0;
  createEffect(() => {
    if (!followsBridge() || !bridgeRun()) return;
    let raf = 0;
    const loop = (ts: number) => { frameAt = ts; setRafTick((n) => n + 1); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    onCleanup(() => cancelAnimationFrame(raf));
  });
  createEffect(() => {
    if (!followsBridge()) return;
    rafTick();
    const t = timeline();
    const r = bridgeRun();
    if (!t || !r) {
      if (playing() || armed()) { setPlaying(false); setArmed(false); setPlayBar(startBar()); launchedHere = false; }
      return;
    }
    const at = untrack(() => transportBar(homeTarget(), t, frameAt > 0 ? frameAt : performance.now()));
    setArmed(r.state === 'queued');
    setPlaying(r.state !== 'queued');
    if (at) setPlayBar(Math.max(0, at.pos));
  });
  let launchBeat = 0;
  /** Where the playhead stood at `launchBeat`: bar 0 at a launch, a section's start after a jump. The
   *  position is walked forward from here through each section's end rule (`playheadAt`, DEC-227), so a
   *  looping section replays and a jump re-anchors instead of being faked as elapsed time. */
  let anchorBar = 0;
  /** A section jump waiting for its bar: OPBridge jumps on the next bar line
   *  (`jump_to_section` queues a pending jump), so the playhead here moves on
   *  the same beat rather than running on from where it was. */
  let localJump: { atBeat: number; toBar: number } | null = null;
  let armTarget = 0;
  /** When the last launch was pressed (to tell its own late song clock from a clock change mid-song). */
  let launchedAtMs = 0;
  /** When the playhead started moving (armed -> playing). */
  let playStartedMs = 0;
  /** Follow: while playing, the view pages along so the playhead stays in sight (the pro-grade review 2026-10-13: it
   *  ran off the screen). Remembered per browser. */
  const [follow, setFollow] = createSignal<boolean>((() => { try { return localStorage.getItem(FOLLOW_KEY) !== '0'; } catch { return true; } })());
  createEffect(() => { const f = follow(); try { localStorage.setItem(FOLLOW_KEY, f ? '1' : '0'); } catch { /* a convenience */ } });
  /** Stream: every lights lane drawn as the colour its light shows from start to end, black where it is off, in place
   *  of its keyframes and blocks (operator, 2026-11-02). A view only: what plays is unchanged. Remembered per browser. */
  const [stream, setStream] = createSignal<boolean>((() => { try { return localStorage.getItem(STREAM_KEY) === '1'; } catch { return false; } })());
  createEffect(() => { const v = stream(); try { localStorage.setItem(STREAM_KEY, v ? '1' : '0'); } catch { /* a convenience */ } });
  const streamsLane = (lane: TimelineLane) => stream() && laneRole(lane) === 'lights';
  /** Where Play starts (operator, 2026-10-13: a click moves the playhead to the nearest bar). 0 = the top. */
  const [startBar, setStartBar] = createSignal(0);
  /** Where THIS launch started: the start bar when the bridge starts there (or on screen only), else the top. */
  let startAt = 0;
  /** The clock's beat at the previous run, to see it run BACKWARD. */
  let lastBeat = Number.NEGATIVE_INFINITY;
  function nextBarBeat(b: number): number {
    const bp = Math.max(1, props.beatsPerBar());
    return (Math.floor(b / bp) + 1) * bp;
  }
  createEffect(() => {
    const b = props.beat();
    const prev = lastBeat;
    lastBeat = b;
    if (untrack(followsBridge)) return;
    if (!playing() && !armed()) return;
    // The clock ran BACKWARD: another source took it. A timeline that brings its song does exactly this on Play
    // (OPBridge S318: the clock becomes the song's own beat count, from 0, where the internal clock was ~9500), so
    // waiting for the old count's next bar froze the playhead (operator, 2026-10-13: "the playhead doesnt move").
    // Re-anchor the way OPBridge's engine does. Armed: it anchors a start on the NEXT bar line once the clock plays
    // (`quantized_anchor`), unless the clock is exactly on one. Measured 2026-10-13 on Danger Zone: the song's clock
    // starts part-way into a count-in bar (beat 1.84 at 79 ms) and the timeline and the song's bar 1 begin together
    // at beat 4; anchoring on the bar line we were inside (beat 0) put this playhead a bar AHEAD of the music
    // (operator: "i think the playhead is a bar in front of audio"). Playing: the engine keeps its position and
    // re-anchors on the new beat at once (`beat_jumped_back`), so do the same.
    if (b < prev - 1) {
      const bp = Math.max(1, props.beatsPerBar());
      const barLine = Math.floor(b / bp) * bp;
      localJump = null;
      if (armed()) armTarget = b - barLine < 0.01 ? barLine : barLine + bp;
      else if (Date.now() - playStartedMs < 4000 && Date.now() - launchedAtMs < 15000) {
        // The launch's OWN song clock, arriving after the old clock already passed the bar line we armed on (a slow
        // machine: measured 2026-10-13, 0.6 to 1.7 bars ahead while another build loaded the CPU; reproduced with the
        // fake's songClockDelayMs). OPBridge starts the timeline on the song's clock, so wait again for ITS next bar.
        // Wall-clock, not beats: the beat bookkeeping is exactly what a clock change makes untrustworthy.
        setPlaying(false); setArmed(true); setPlayBar(startAt);
        armTarget = b - barLine < 0.01 ? barLine : barLine + bp;
      } else { anchorBar = Math.max(0, playBar()); launchBeat = b; }
    }
    // A clock that LEAPS forward while armed changed source (a stale song count giving way to the internal clock, after
    // a quick Stop then Play): that is not time passing, so wait for the new clock's own next bar line.
    if (armed() && b > prev + 1 && b > armTarget + Math.max(1, props.beatsPerBar())) armTarget = nextBarBeat(b);
    if (armed() && b >= armTarget) {
      setArmed(false);
      playStartedMs = Date.now();
      launchBeat = armTarget;
      anchorBar = startAt;
      setPlaying(true);
      setPlayBar(startAt);
      return;
    }
    if (!playing()) return;
    const t = timeline();
    if (!t) return;
    if (localJump && b >= localJump.atBeat) {
      launchBeat = localJump.atBeat;
      anchorBar = localJump.toBar;
      localJump = null;
    }
    const at = playheadAt(t, anchorBar, (b - launchBeat) / Math.max(1, props.beatsPerBar()), timelineLoops(t));
    if (at.ended) { setPlaying(false); setPlayBar(0); return; }
    setPlayBar(Math.max(0, at.pos));
  });
  /** The launch lives in timeline/launch.ts now (2026-10-05), shared with
   *  Navigate's Timelines pane so the two can never drift apart. */
  const launchOnBridge = launchTimelineOnBridge;
  /** Live is open and stopped on the computer this timeline is pressed on (2026-10-28): only then does Launch (wait for
   *  Live's first bar line) differ from Play (at once, at Live's last tempo, onto Live's bar lines when it plays). */
  const homeClockNow = createClockStatus(() => homeTarget() || null);
  const liveWaits = createMemo(() => { const s = homeClockNow(); return live() && timeline()?.tier === 'song' && !!s && liveIsStopped(s); });
  function play(launch = false) {
    const t = timeline();
    if (!t) return;
    if (t.tier === 'genre') { pushToast('warn', "OPBridge can't play Genre timelines yet."); return; }
    const from = t.tier === 'song' ? startBar() : 0;
    if (live()) {
      // From the playhead where the bridge can start there (asked of OPBridge 2026-10-13); else from the top, said.
      const canAt = homeStartsAtBar(timelineHomeTarget(t));
      if (from > 0 && !canAt) pushToast('warn', "Play starts from the beginning: this OPBridge can't start mid-timeline yet.");
      startAt = from > 0 && canAt ? from : 0;
      launchOnBridge(t, { ...(startAt > 0 ? { fromBar: startAt } : {}), ...(launch ? { launch: true } : {}), editor: true });
      if (followsBridge()) {
        // OPBridge's reports drive it from here (the effect above); shown queued at once.
        noteTransportLaunch(homeTarget(), t.id, startAt);
        launchedHere = true;
        return;
      }
    } else {
      startAt = from;
      // Rehearsal: no computer to play on, so it plays on screen only, and
      // says so once rather than pretending.
      const s = notLiveToastMessage(t.name);
      pushToast(s.kind, s.message);
    }
    armTarget = nextBarBeat(props.beat());
    launchedAtMs = Date.now();
    setPlaying(false);
    setArmed(true);
  }
  /** Stop: back to where Play started. Pressed again while stopped (a double-click, or two taps on the iPad), back to
   *  the start, the way Live's Stop does (operator, 2026-11-04: "double click stop should put playhead to start"). */
  function stop() {
    const was = playing() || armed();
    setPlaying(false); setArmed(false);
    if (!was) { setStartBar(0); setScrubBar(null); }
    setPlayBar(startBar());
    launchedHere = false;
    const t = timeline();
    if (!was || !t || !live() || t.tier !== 'song') return;
    stopTimelineOnBridge(t);
    noteTransportStop(homeTarget());
  }
  /** Play / Stop from what is REALLY running: a song started from a pad or a pedal shows playing here, so this stops
   *  it rather than starting it again from the top (2026-10-16). */
  function togglePlay() { if (playing() || armed() || (followsBridge() && bridgeRun())) stop(); else play(); }
  /** Jump a PLAYING song to a section, on the next bar line: on OPBridge when
   *  it plays there, and the playhead here with it (on screen only when no
   *  computer is connected). A stopped song has nothing to jump: OPBridge
   *  answers "no active song timeline", so it is not offered then. */
  /** Keys and gestures, behind "?" (2026-10-16, the look audit; the lab's sheet, DEC-232 rule 4): every key and
   *  modifier action had no place it was written down, and the iPad had no way to learn the long-press. */
  const [helpOpen, setHelpOpen] = createSignal(false);
  {
    const outside = (e: PointerEvent) => {
      if (!helpOpen()) return;
      if ((e.target as Element | null)?.closest('.tl2-helpop, .tl2-help')) return;
      setHelpOpen(false);
    };
    window.addEventListener('pointerdown', outside, true);
    onCleanup(() => window.removeEventListener('pointerdown', outside, true));
  }
  // ── record mode (operator, 2026-10-16; `.notepads/timeline-pro-grade.md` RECORD MODE) ──
  /** Lanes armed to record into (this editor's, not saved with the timeline). */
  const [recArmed, setRecArmed] = createSignal<ReadonlySet<string>>(new Set());
  const toggleArm = (id: string) => setRecArmed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  /** Lanes that record: the ones that take canvas presses, and automation lanes (a control's moves). */
  const recordable = (lane: TimelineLane) => laneRole(lane) === 'controls' || laneRole(lane) === 'guitar' || (laneRole(lane) === 'automation' && !lane.motion);
  /** The take running now: what to put back when it ends, and the bars it covered. */
  const [recTake, setRecTake] = createSignal<{ mode: 'edit' | 'test'; low: number; high: number } | null>(null);
  /** Replace: a take wipes the armed lanes' changes in the bars it covers (else it adds). */
  const [recReplace, setRecReplace] = createSignal(false);
  createEffect(() => {
    const r = recTake();
    if (!r || !playing()) return;
    const b = playBar();
    if (b < r.low || b > r.high) setRecTake({ ...r, low: Math.min(r.low, b), high: Math.max(r.high, b) });
  });
  function startRecording() {
    const t = timeline();
    if (!t) return;
    // Nothing armed records into whatever fits (2026-10-16): someone who only wants their fader moves has no lane to
    // arm until the take makes one. Arming a lane narrows a take to it.
    const from = playBar();
    setRecTake({ mode: uiMode(), low: from, high: from });
    enterTestMode();   // a press on the canvas fires (and is recorded), not picked up to edit; no selection left over
    startPressRecording(() => (playing() ? playBar() : null));
    if (!playing() && !armed()) play();
  }
  function stopRecording() {
    const r = recTake();
    if (!r) return;
    const { presses, moves } = stopPressRecording();
    setRecTake(null);
    setUiMode(r.mode);
    stop();
    commitTake(presses, moves, r.low, r.high);
    queueMicrotask(() => rootEl?.focus({ preventScroll: true }));
  }
  /** The take as changes, in ONE undo step (2026-10-16).
   *
   *  Presses: onto the lane that already has that control, else the first lane that takes it; with lanes armed, only
   *  armed lanes are considered, and with none armed a press nothing takes gets a new lane of its own. Each lands on
   *  the grid, held for as long as it was held when that is at least a grid step.
   *
   *  Moves: each control's run of values becomes points on its automation lane, thinned to the fewest that stay
   *  within one MIDI step of what was sent, not snapped. The lane is the one bound to that control (armed, when any
   *  are); a control with no lane gets one, but only for an axis that actually moved. A move replaces the curve in
   *  the stretch it covered, as a DAW's touch mode does.
   *
   *  Replace wipes the armed lanes, and every lane the take wrote to, in the bars it covered. */
  function commitTake(presses: RecordedPress[], moves: RecordedMove[], low: number, high: number) {
    const t = getTimeline(tid());
    if (!t) return;
    const armedLanes = t.lanes.filter((l) => recArmed().has(l.id) && recordable(l));
    const anyArmed = armedLanes.length > 0;
    const pool = anyArmed ? armedLanes : t.lanes.filter(recordable);
    const total = totalBars();
    const newLanes: TimelineLane[] = [];
    const add = new Map<string, TimelineStep[]>();
    let placed = 0;
    for (const p of presses) {
      const step = makeButtonRefStep(p.itemId, 0, p.label);
      const has = pool.find((l) => l.steps.some((s) => s.kind === 'buttonRef' && (s as { buttonId?: string }).buttonId === p.itemId) && laneTakesStep(l, step));
      let lane = has ?? [...pool, ...newLanes].find((l) => laneAccepts(l, { kind: 'item', itemId: p.itemId, label: p.label, itemKind: p.itemKind }));
      if (!lane && !anyArmed) {
        const guitar = GUITAR_KINDS.has(p.itemKind);
        const base = guitar ? 'Guitar' : 'Controls';
        const n = t.lanes.filter((l) => laneRole(l) === (guitar ? 'guitar' : 'controls')).length;
        lane = { id: ulid(), name: n === 0 ? base : `${base} ${n + 1}`, laneKind: guitar ? 'guitar' : 'mixed', steps: [] };
        newLanes.push(lane);
      }
      if (!lane) continue;
      const position = Math.min(total - unit(), snapRound(p.downBar));
      const held = p.upBar !== null && laneRole(lane) !== 'guitar' ? snapRound(p.upBar - p.downBar) : 0;
      const s: TimelineStep = { ...step, position, ...(held >= unit() - 1e-9 ? { durationBars: held } : {}) } as TimelineStep;
      add.set(lane.id, [...(add.get(lane.id) ?? []), s]);
      placed++;
    }
    // Moves, one run per control (or XY axis), in time order; a value sent twice on one bar keeps the later.
    const runs = new Map<string, RecordedMove[]>();
    for (const m of moves) {
      const k = `${m.itemId}|${m.axis ?? ''}`;
      runs.set(k, [...(runs.get(k) ?? []), m]);
    }
    const touched = new Map<string, { from: number; to: number; points: AutomationPointStep[] }>();
    let movedControls = 0;
    for (const run of runs.values()) {
      const first = run[0]!;
      const it = canvasItemById(first.itemId);
      if (!it) continue;
      const axis = it.kind === 'xypad' ? (first.axis ?? 'x') : undefined;
      const bound = t.lanes.filter((l) => laneRole(l) === 'automation' && l.control?.itemId === first.itemId && (l.control?.axis ?? undefined) === axis);
      let lane = anyArmed ? bound.find((l) => recArmed().has(l.id)) : bound[0];
      if (!lane && bound.length > 0) continue;   // it has a lane, left unarmed: not this take
      const q = (b: number) => Math.round(Math.max(0, Math.min(total, b)) / FREE_UNIT) * FREE_UNIT;
      const dedup: Array<{ bar: number; value: number }> = [];
      for (const m of [...run].sort((a, b) => a.bar - b.bar)) {
        const bar = q(m.bar);
        if (dedup.length && Math.abs(dedup[dedup.length - 1]!.bar - bar) < 1e-9) dedup[dedup.length - 1]!.value = m.value;
        else dedup.push({ bar, value: m.value });
      }
      if (!lane) {
        if (dedup.every((d) => d.value === dedup[0]!.value) && dedup.length > 1) continue;   // an axis that never moved
        lane = { id: ulid(), name: autoTargetLabel(it, axis), laneKind: 'automation', steps: [], control: { itemId: first.itemId, ...(axis ? { axis } : {}) } };
        newLanes.push(lane);
      }
      const points = thinRun(dedup).map((d): AutomationPointStep => ({ id: ulid(), kind: 'automationPoint', position: d.bar, value: d.value }));
      touched.set(lane.id, { from: dedup[0]!.bar, to: dedup[dedup.length - 1]!.bar, points });
      movedControls++;
    }
    const lo = Math.floor(low + 1e-6); const hi = Math.ceil(high - 1e-6);
    if (placed === 0 && movedControls === 0 && !(recReplace() && anyArmed)) {
      pushToast('warn', presses.length || moves.length
        ? 'Nothing was recorded: no armed lane takes those controls.'
        : 'Nothing was recorded: no control on your canvas was pressed or moved.', 3000);
      return;
    }
    const wrote = new Set([...add.keys(), ...touched.keys()]);
    const wipe = (l: TimelineLane) => recReplace() && (armedLanes.some((a) => a.id === l.id) || wrote.has(l.id));
    const rewrite = (l: TimelineLane): TimelineLane => {
      let steps = wipe(l) ? l.steps.filter((x) => x.position < lo - 1e-6 || x.position >= hi - 1e-6) : l.steps;
      const tch = touched.get(l.id);
      if (tch) steps = [...steps.filter((x) => x.position < tch.from - 1e-9 || x.position > tch.to + 1e-9), ...tch.points];
      return steps === l.steps ? l : { ...l, steps };
    };
    beginBatch();
    try {
      upsertTimeline({ ...t, lanes: [...t.lanes, ...newLanes].map(rewrite) });
      // ADDED, never overwriting (2026-10-16): overwrite cuts whatever a new block covers on its lane, so two controls
      // pressed in the same grid step (a kick and a snare on a Drums lane) kept only the last. Replace wipes first.
      for (const [laneId, steps] of add) for (const s of steps) addStep(t.id, laneId, s);
    } finally {
      endBatch();
    }
    const allLanes = [...t.lanes, ...newLanes];
    const names = allLanes.filter((l) => wrote.has(l.id)).map((l) => l.name).join(', ');
    const what = [
      placed ? `${placed} press${placed === 1 ? '' : 'es'}` : '',
      movedControls ? `the moves of ${movedControls} control${movedControls === 1 ? '' : 's'}` : '',
    ].filter(Boolean).join(' and ');
    pushToast('ok', `Recorded ${what || 'nothing'}${names ? ` on ${names}` : ''}${recReplace() ? `, replacing bars ${lo + 1} to ${hi}` : ''}.`, 3000);
  }
  /** Loop a range of bars while rehearsing (operator, 2026-10-16: "loop a range"): `from` to `to` (bars from the
   *  start; `to` is the bar line it loops at). Drawn by dragging on the ruler, or Ctrl/Cmd+L on what is selected;
   *  the band on the ruler turns it on and off. It loops by jumping: entering the range's last bar, the playhead is
   *  sent back to `from`, and a jump lands on the next bar line, which is exactly `to` (on OPBridge with
   *  `jumpToBar`, on screen when rehearsing). Kept for this editor; it is a rehearsal aid, not part of the timeline. */
  const [loopRange, setLoopRange] = createSignal<{ from: number; to: number; on: boolean } | null>(null);
  let loopSent = false;
  let lastLoopBar = 0;
  /** OPBridge loops the range itself (S324, `timeline_loop_range`): set on it while the song plays there, cleared when
   *  the loop is turned off, set again after every new start (a start clears it there). While it holds, the editor
   *  sends no jumps; if OPBridge cannot take it (an older one, or fetch cannot reach it), the editor jumps as before. */
  const [nativeLoop, setNativeLoop] = createSignal(false);
  let nativeSent: string | null = null;   // what OPBridge was last told for this run: "a:b", or "off"
  let lastRunState: string | null = null;
  createEffect(() => {
    const t = timeline();
    const r = loopRange();
    const pl = playing();
    const st = bridgeRun()?.state ?? null;
    const home = t ? timelineHomeTarget(t) : '';
    const restarted = st === 'queued' && lastRunState !== 'queued';
    lastRunState = st;
    if (!pl || !t || t.tier !== 'song' || !live() || !homeLoopsRange(home)) { nativeSent = null; setNativeLoop(false); return; }
    if (restarted) nativeSent = null;
    const want = r?.on ? `${r.from}:${r.to}` : 'off';
    if (want === nativeSent || (want === 'off' && nativeSent === null)) return;
    nativeSent = want;
    const range = r?.on ? { fromBar: r.from, toBar: r.to } : null;
    void setLoopRangeOnBridge(range, home).then((ok) => {
      if (nativeSent !== want) return;   // superseded
      if (!ok) { nativeSent = null; setNativeLoop(false); return; }
      setNativeLoop(range !== null);
    });
  });
  createEffect(() => {
    const r = loopRange();
    const b = playBar();
    const pl = playing();
    if (b < lastLoopBar - 0.25) loopSent = false;   // a jump landed, or it started again
    lastLoopBar = b;
    if (nativeLoop()) { loopSent = false; return; }   // OPBridge loops it
    if (!r?.on || !pl) { loopSent = false; return; }
    if (b < r.to - 1 - 1e-6) loopSent = false;
    if (!loopSent && b >= r.to - 1 - 1e-6 && b < r.to) { loopSent = true; untrack(() => seekTo(r.from)); }
  });
  /** Ctrl/Cmd+L: loop what is selected (its changes' bars, or a section), or turn the loop off and on again. */
  function loopSelection() {
    const t = timeline();
    if (!t) return;
    const s = sel();
    let span: { from: number; to: number } | null = null;
    if (s.type === 'section') {
      const sec = sectionStarts().find((x) => x.id === s.id);
      if (sec) span = { from: sec.start, to: sec.start + sec.bars };
    } else {
      const found = selRefs().flatMap((r) => {
        const st = t.lanes.find((l) => l.id === r.laneId)?.steps.find((x) => x.id === r.stepId);
        return st ? [st] : [];
      });
      if (found.length > 0) {
        span = {
          from: Math.floor(Math.min(...found.map((x) => x.position)) + 1e-6),
          to: Math.ceil(Math.max(...found.map((x) => stepEndBars(x, sig()))) - 1e-6),
        };
        if (span.to <= span.from) span.to = span.from + 1;
      }
    }
    const r = loopRange();
    if (span) setLoopRange({ ...span, on: !(r && r.on && r.from === span.from && r.to === span.to) });
    else if (r) setLoopRange({ ...r, on: !r.on });
    else pushToast('warn', 'Select some changes or a section first, or drag on the ruler, to loop it.', 2500);
  }
  /** Dragging the playhead's triangle on the ruler scrubs (2026-10-19, operator: "drag the playhead to scrub"): it
   *  follows the pointer to the nearest bar and moves there on release, one jump (or, stopped, where Play starts). A
   *  drag on the rest of the ruler still draws a loop. */
  const [scrubBar, setScrubBar] = createSignal<number | null>(null);
  /** The bar the playhead is drawn at: the scrub while one is held, else where the timeline is. */
  const shownBar = () => scrubBar() ?? playBar();
  function onPlayheadPointerDown(ev: PointerEvent) {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    const cell = (ev.currentTarget as Element).parentElement!;
    const at = (x: number) => Math.max(0, Math.min(Math.max(0, totalBars() - 1), Math.round(barAtX(cell, x))));
    setScrubBar(at(ev.clientX));
    const move = (e: PointerEvent) => { setScrubBar(at(e.clientX)); setInfo(`Bar ${at(e.clientX) + 1}: let go to move there`); };
    const up = (e: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      const b = e.type === 'pointercancel' ? null : at(e.clientX);
      setScrubBar(null);
      rulerDragged = true; window.setTimeout(() => { rulerDragged = false; }, 0);
      if (b !== null) seekTo(b);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** A drag on the ruler draws a loop over whole bars (a plain click still moves the playhead). */
  let rulerDragged = false;
  function onRulerPointerDown(ev: PointerEvent) {
    if (ev.button !== 0 || ev.pointerType === 'touch') return;
    const cell = ev.currentTarget as Element;
    const x0 = ev.clientX;
    const b0 = barAtX(cell, x0);
    let on = false;
    // The loop is drawn as it is dragged, so a cancel puts the one it replaced back (D-4).
    const was = loopRange();
    const pid = ev.pointerId;
    let cancelled = false;
    const abort = () => { cancelled = true; up(); };
    abortDrag = abort;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      if (!on && Math.abs(e.clientX - x0) < DRAG_THRESHOLD_PX) return;
      on = true;
      const b1 = barAtX(cell, e.clientX);
      const from = Math.max(0, Math.floor(Math.min(b0, b1)));
      const to = Math.min(totalBars(), Math.max(from + 1, Math.ceil(Math.max(b0, b1))));
      setLoopRange({ from, to, on: true });
      setInfo(`Loop bars ${from + 1} to ${to}`);
    };
    const up = (ue?: Event) => {
      if (ue instanceof PointerEvent && ue.pointerId !== pid) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (abortDrag === abort) abortDrag = null;
      if (on) { rulerDragged = true; window.setTimeout(() => { rulerDragged = false; }, 0); }
      if (on && (cancelled || ue?.type === 'pointercancel')) { setLoopRange(was); setInfo(''); }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** Split a section so its second half CARRIES ON through the song (operator, 2026-10-16). With no clips of its own a
   *  section plays its named song part from that part's start, so the half after a split restarted the music. The song
   *  is first written out as clips from the sections as they are, which play exactly the same music, so the split
   *  changes no sound. One undo step. A bridge without `timeline_song_clips` still plays by the marks (the restart). */
  function splitSectionHere(sectionId: string, at: number) {
    const t = timeline();
    if (!t) return;
    const m = songMap();
    beginBatch();
    try {
      if (t.testTrack && !t.testTrack.clips && m) {
        setTimelineSongClips(t.id, clipsFromMarks(m, sectionStarts().map((x) => ({ name: x.name, start: x.start, bars: x.bars })), ulid), gridOf(m));
      }
      const n = splitSection(t.id, sectionId, at);
      if (n) setSel({ type: 'section', id: n.id });
    } finally {
      endBatch();
    }
  }
  /** Duplicate a section with what is in it and select the copy; the menu, Ctrl+D and the panel all do this, and say
   *  the same thing when there is no room (2026-10-16). */
  function duplicateSectionHere(sectionId: string): void {
    const t = timeline();
    const s = t?.sections.find((x) => x.id === sectionId);
    if (!t || !s) return;
    const c = duplicateSection(t.id, s.id);
    if (c) setSel({ type: 'section', id: c.id });
    else pushToast('warn', `No room for another ${s.name}: the timeline would pass its longest.`, 2500);
  }
  /** A jump pressed on a pad here, until the playhead lands on that section's start (or 8 s pass). */
  const [jumpPressed, setJumpPressed] = createSignal<{ id: string; at: number } | null>(null);
  createEffect(() => {
    const j = jumpPressed();
    if (!j) return;
    const s = sectionStarts().find((x) => x.id === j.id);
    const b = playBar();
    const now = performance.now();
    const landed = !!s && now - j.at > 120 && b >= s.start - 1e-6 && b < s.start + 0.25;
    if (!s || !playing() || landed || now - j.at > 8000) setJumpPressed(null);
  });
  /** The section the playhead is in while it plays, and the one a jump is waiting to land on (pressed here, or on any
   *  device when OPBridge says, S323): the pads light and blink from these (2026-10-16: they showed neither). */
  const playingSectionId = createMemo(() => {
    if (!playing()) return null;
    const b = playBar();
    return sectionStarts().find((s) => b >= s.start - 1e-6 && b < s.start + s.bars)?.id ?? null;
  });
  const queuedSectionId = createMemo(() => {
    const pressed = jumpPressed()?.id;
    if (pressed) return pressed;
    const bar = bridgeRun()?.pendingJumpBar;
    return typeof bar === 'number' ? sectionStarts().find((s) => Math.abs(s.start - bar) < 1e-6)?.id ?? null : null;
  });

  function jumpTo(sectionId: string) {
    const t = timeline();
    if (!t) return;
    if (t.tier !== 'song') { pushToast('warn', 'Sections jump in a song timeline.'); return; }
    const start = sectionStarts().find((x) => x.id === sectionId)?.start;
    if (start === undefined) return;
    if (!playing()) { pushToast('warn', 'Press Play first: a jump moves a song that is playing.', 2500); return; }
    setJumpPressed({ id: sectionId, at: performance.now() });
    const follow = () => { localJump = { atBeat: nextBarBeat(props.beat()), toBar: start }; };
    if (!live()) { follow(); return; }
    void jumpToSectionAction(sectionId, timelineHomeTarget(t)).then((r) => {
      const failed = r.filter((x) => x.kind !== 'ok');
      if (failed.length > 0) { setJumpPressed(null); pushToast('error', `The jump did not happen: ${plainFireReason(failed[0]!)}.`); }
      else if (followsBridge()) kickTransport();
      else follow();
    });
  }
  createEffect(() => {
    const at = playBar() * ppb();
    if (!playing() || !follow() || !scrollEl) return;
    const left = scrollEl.scrollLeft;
    const lanesW = scrollEl.clientWidth - HEADER_W;
    // Page when it reaches 40px from the right edge or leaves on the left (a loop back, a jump).
    if (at < left || at > left + lanesW - 40) scrollEl.scrollLeft = Math.max(0, at - 40);
  });
  /** The previous / next section's start from the playhead (`[` `]`): back goes to the start of the section the
   *  playhead is in unless it is already there. */
  function stepSection(dir: 1 | -1) {
    const ss = sectionStarts();
    if (ss.length === 0) return;
    const at = playBar();
    const ix = Math.max(0, ss.findIndex((x) => at >= x.start - 1e-6 && at < x.start + x.bars - 1e-6));
    if (dir > 0) { const n = ss[ix + 1]; if (n) seekTo(n.start); return; }
    const cur = ss[ix]!;
    seekTo(at > cur.start + 0.05 ? cur.start : (ss[ix - 1] ?? cur).start);
  }
  /** Move the playhead to the nearest bar (a click on the ruler, the sections strip or blank lane space; operator,
   *  2026-10-13; or a playhead drag let go, 2026-10-19). Stopped: Play starts there. Playing: a jump on the next bar
   *  line, to that bar where the bridge can (`timeline_start_at_bar`), else to the start of the section holding it; the
   *  playhead moves on that line, not at the next reading (`noteTransportJump`). INSTANT where the home jumps instantly
   *  (operator, 2026-10-19: "instant to closest bar line is good"; OPBridge S329 `timeline_jump_instant`): the run and
   *  its song land a few tens of milliseconds after the click, keeping the clock's bar grid, and the playhead moves
   *  there on the beat OPBridge names. */
  function seekTo(bar: number) {
    const t = timeline();
    if (!t) return;
    const b = Math.max(0, Math.min(Math.max(0, totalBars() - 1), Math.round(bar)));
    if (!playing() && !armed()) { setStartBar(b); setPlayBar(b); return; }
    if (t.tier !== 'song' || !playing()) return;
    if (!live()) { localJump = { atBeat: nextBarBeat(props.beat()), toBar: b }; return; }
    const home = timelineHomeTarget(t);
    if (followsBridge() && homeJumpsInstantly(home)) {
      void jumpToBarInstantAction(b, home).then(({ results, answer }) => {
        const failed = results.filter((x) => x.kind !== 'ok');
        if (failed.length > 0) { pushToast('error', `The jump did not happen: ${plainFireReason(failed[0]!)}.`); return; }
        if (answer?.quantize === 'instant' && typeof answer.landingBar === 'number' && typeof answer.atBeat === 'number') noteTransportJumpAt(home, answer.landingBar, answer.atBeat);
        else noteTransportJump(home, b);
      });
      return;
    }
    if (homeStartsAtBar(home)) {
      void jumpToBarAction(b, home).then((r) => {
        const failed = r.filter((x) => x.kind !== 'ok');
        if (failed.length > 0) pushToast('error', `The jump did not happen: ${plainFireReason(failed[0]!)}.`);
        else if (followsBridge()) noteTransportJump(home, b);
        else localJump = { atBeat: nextBarBeat(props.beat()), toBar: b };
      });
      return;
    }
    const sec = sectionStarts().find((x) => b >= x.start && b < x.start + x.bars);
    if (sec) jumpTo(sec.id);
  }
  /** Drag a section's right edge (bar-quantized): the line between it and the next moves (`moveSectionEdge`). */
  const [edgeDrag, setEdgeDrag] = createSignal<{ id: string; end: number } | null>(null);
  let edgeJustDragged = false;
  function startEdgeDrag(e: PointerEvent, id: string) {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const strip = (e.currentTarget as Element).parentElement!;
    const start = sectionStarts().find((x) => x.id === id)?.start ?? 0;
    // Measured here, not with barAtX: that clamps to the current length, and the LAST edge must be able to go past
    // it (the drag that lengthens the timeline did nothing, found by the smoke 2026-10-13).
    // ...but never past the tier's length cap (1024 bars for a song or action): Duplicate refused there and the length
    // field stopped there, and only this drag went on (2026-11-03 audit D-15).
    const cap = (() => { const tl = timeline(); return tl ? maxTimelineLength(tl) : Number.MAX_SAFE_INTEGER; })();
    const at = (x: number) => Math.min(cap, Math.max(start + 1, Math.round((x - strip.getBoundingClientRect().left) / ppb())));
    setEdgeDrag({ id, end: at(e.clientX) });
    let cancelled = false;
    abortDrag = () => { cancelled = true; up(); };
    const move = (m: PointerEvent) => setEdgeDrag({ id, end: at(m.clientX) });
    const up = (ue?: Event) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      abortDrag = null;
      const d = edgeDrag();
      setEdgeDrag(null);
      edgeJustDragged = true;
      if (cancelled || ue?.type === 'pointercancel') return;
      if (!d) return;
      // Following its song (2026-10-21): the edge is a line between two of the song's parts, moved on the song.
      if (following()) {
        const s0 = sectionStarts().find((x) => x.id === d.id);
        if (!s0) return;
        if (timeline()!.sections.at(-1)?.id === d.id) { pushToast('warn', 'The song ends here.', 2000); return; }
        moveSongLine(timeline()!, s0.start + s0.bars, d.end);
        return;
      }
      moveSectionEdge(tid(), d.id, d.end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** Drag the FIRST section's left edge (2026-10-19, operator: "i need to be able to move the left side of the intro
   *  section"): the bars before it become the lead-in (`setFirstSectionBar`); its end stays put. */
  const leadIn = () => (timeline() ? timelineLeadIn(timeline()!) : 0);
  function startFirstSectionDrag(e: PointerEvent) {
    if (e.button !== 0) return;
    const first = sectionStarts()[0];
    if (!first) return;
    e.preventDefault(); e.stopPropagation();
    const strip = (e.currentTarget as Element).parentElement!;
    const end = first.start + first.bars;
    const at = (x: number) => Math.max(0, Math.min(end - 1, Math.round((x - strip.getBoundingClientRect().left) / ppb())));
    setEdgeDrag({ id: '', end: at(e.clientX) });
    let cancelled = false;
    abortDrag = () => { cancelled = true; up(); };
    const move = (m: PointerEvent) => { const b = at(m.clientX); setEdgeDrag({ id: '', end: b }); setInfo(b > 0 ? `${first.name} starts at bar ${b + 1}: the ${barsText(b)} before it play the song alone` : `${first.name} starts the timeline`); };
    const up = (ue?: Event) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      abortDrag = null;
      const d = edgeDrag();
      setEdgeDrag(null);
      edgeJustDragged = true;
      if (cancelled || ue?.type === 'pointercancel') return;
      if (!d) return;
      if (following()) { moveSongLine(timeline()!, leadIn(), d.end); return; }
      setFirstSectionBar(tid(), d.end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  // Closing the editor stops what IT started, never a run started from a pad, the iPad or a pedal (2026-10-16).
  // Whatever closes the editor mid-take keeps the take: it is committed, the
  // recorder's listeners stop and the mode it changed is put back (2026-11-03
  // audit D-2: an unmount left all three behind and the presses were lost).
  onCleanup(() => { if (recTake()) stopRecording(); });
  onCleanup(() => { if ((playing() || armed()) && (!followsBridge() || launchedHere)) stop(); });

  const lcdPos = () => {
    const pos = playBar();
    const bar = Math.floor(pos);
    const beats = (pos - bar) * sig();
    const beat = Math.floor(beats);
    const six = Math.floor((beats - beat) * 4);
    return `${bar + 1}. ${beat + 1}. ${six + 1}`;
  };
  /** What a lane is doing at the playhead (the review: "SEE what the rig
   *  would do even with no rig attached"). */
  function laneNow(l: TimelineLane): string | null {
    if (!playing()) return null;
    const t = timeline();
    if (t && !laneAudible(t, l)) return null;
    const pos = playBar();
    if (laneRole(l) === 'automation') { const v = autoValueAt(autoPoints(l), pos); return v === null ? null : autoText(l, v); }
    let best: TimelineStep | null = null;
    for (const s of l.steps) if (s.position <= pos + 1e-6 && (!best || s.position >= best.position)) best = s;
    if (!best) return null;
    if (best.kind === 'colorMarker' && transitionOf(best) === 'fade') {
      const next = nextKeyframe(l, best);
      if (next) return `Fading to ${colourName(next.color, next.label)}`;
    }
    return stepTitle(best);
  }
  function stepTitle(s: TimelineStep): string {
    switch (s.kind) {
      case 'colorMarker': return colourName(s.color, s.label);
      case 'lightRelease': return 'Hand back';
      case 'buttonRef': {
        const it = controlById(s.buttonId);
        return it ? itemText(it).label : (s.labelHint ?? 'Missing control');
      }
      case 'targetValue': return s.labelHint ?? `${getLiveTarget(s.targetId)?.label ?? s.targetId}: ${s.value}`;
      case 'animation': return s.preset;
      case 'marker': return s.text;
      case 'sequencer': return 'Sequencer';
      case 'macro': return 'Macro';
      case 'cue': {
        if (opvPaletteOf(s) !== null) return `Palette: ${s.label ?? opvPaletteOf(s)}`;
        if (hueCueRef(s)) return s.label ?? 'Hue scene';
        // What it fires, not "Cue" (2026-11-02: a lane of "Cue" read as broken and hid which column each one was).
        const opv = /^opvisual-(?:col-(\d+)|cell-l(\d+)-(\d+))$/.exec(opvActionId(s) ?? '');
        if (opv) return opv[1] ? `Column ${opv[1]}` : `Layer ${opv[2]}, column ${opv[3]}`;
        return 'Cue';
      }
      case 'automationPoint': { const l = timeline()?.lanes.find((x) => x.steps.some((y) => y.id === s.id)); return l?.motion ? autoText(l, s.value) : `Value ${s.value}`; }
      case 'pattern': return `${s.label ?? 'Pattern'}: ${barsText(s.cycleBars)}, ${patternTimes(s)}`;
      case 'wait': return `Wait ${waitWords(s)}`;
      case 'hitBlock': return `${s.label ?? sourceLabel(s.source)}: every ${barsText(s.cycleBars)}${hitBlockFill(s) ? ', fill' : ''}`;
      case 'hit': return `${sourceLabel(s.source)} hit${s.velocity !== undefined && s.velocity < 127 ? `, velocity ${s.velocity}` : ''} (plays when OP-MidiFeed is not)`;
      case 'hitRoute': return `${sourceLabel(s.source)} lights ${timeline()?.instrumentLooks?.find((l) => l.id === s.lookId)?.name ?? (s.lookId.startsWith('~') ? 'nothing' : s.lookId)}`;
    }
  }

  // ── your canvas, in miniature ──
  //
  // Operator, 2026-10-01: "it'd be good to have a visual reference of the
  // performance canvas when selecting items in the left column. maybe drag and
  // droppable. i dunno i need a system that is super easy to use." A control is
  // remembered by WHERE it sits and its colour, not by its name or its CC, so
  // the browser shows the canvas: each page's controls at their places, in
  // their colours. Drawn from the project as plain shapes, never the live
  // controls, so nothing in the map can send anything. Drag a control onto a
  // lane, or click it and then click a bar; the "What happens at 3.1?" chooser
  // shows the same map. It links both ways: pointing at a control lights its
  // presses on the lanes, pointing at a press lights its control, selecting a
  // press turns the map to its page; a number says how often this timeline
  // presses a control; while the timeline plays, each control lights as it is
  // pressed. A light-colour button is a colour: onto a Lights lane it is a
  // keyframe of that colour.
  const frame = () => {
    const p = activeProject();
    return p ? canvasSizeOf(p) : { w: 1180, h: 820 };
  };
  /** The part of the canvas the map shows: every page's controls plus a
   *  margin, the same view for every page so turning pages does not jump, and
   *  never under 40% of the screen each way so a lone button is not drawn as a
   *  giant. A canvas that fills the screen is shown whole. True proportions
   *  alone drew three buttons in a corner at 28px, labels cut (seen in a
   *  render, 2026-10-01). */
  const mapView = createMemo(() => {
    const { w: W, h: H } = frame();
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    for (const p of pagesOfActive()) {
      for (const it of p.items) {
        x0 = Math.min(x0, it.x); y0 = Math.min(y0, it.y);
        x1 = Math.max(x1, it.x + it.w); y1 = Math.max(y1, it.y + it.h);
      }
    }
    if (!Number.isFinite(x0)) return { x: 0, y: 0, w: W, h: H };
    const m = W * 0.03;
    const w = Math.min(W, Math.max(x1 - x0 + 2 * m, W * 0.4));
    const h = Math.min(H, Math.max(y1 - y0 + 2 * m, H * 0.4));
    const x = Math.max(0, Math.min(W - w, (x0 + x1) / 2 - w / 2));
    const y = Math.max(0, Math.min(H - h, (y0 + y1) / 2 - h / 2));
    return { x, y, w, h };
  });
  const [mapPageId, setMapPageId] = createSignal<string | null>(null);
  const mapPage = createMemo<Page | null>(() => {
    const pages = pagesOfActive();
    return pages.find((p) => p.id === mapPageId())
      ?? pages.find((p) => p.id === activeProject()?.activePageId)
      ?? pages[0] ?? null;
  });
  const [hoverItemId, setHoverItemId] = createSignal<string | null>(null);
  /** How many presses of each control this timeline holds. */
  const usesById = createMemo(() => {
    const m = new Map<string, number>();
    for (const l of timeline()?.lanes ?? []) {
      for (const s of l.steps) if (s.kind === 'buttonRef') m.set(s.buttonId, (m.get(s.buttonId) ?? 0) + 1);
    }
    return m;
  });
  /** The controls being pressed at the playhead (a press lights for its
   *  length, an instant one for a beat). */
  const liveIds = createMemo(() => {
    const out = new Set<string>();
    if (!playing()) return out;
    const pos = playBar();
    const t = timeline();
    for (const l of (t?.lanes ?? []).filter((x) => laneAudible(t!, x))) {
      for (const s of l.steps) {
        if (s.kind === 'buttonRef' && pos >= s.position - 1e-6 && pos < s.position + stepDuration(s)) out.add(s.buttonId);
      }
    }
    return out;
  });
  // A memo, so an edit elsewhere (a new selected-step object, the same id)
  // does not turn the map back after the operator turned it.
  const selItemId = createMemo(() => {
    const s = selStep();
    return s && s.kind === 'buttonRef' ? s.buttonId : null;
  });
  createEffect(on(selItemId, (id) => {
    if (!id) return;
    const pg = pagesOfActive().find((p) => p.items.some((it) => it.id === id));
    if (pg) setMapPageId(pg.id);
  }, { defer: true }));
  /** What a canvas item becomes on a lane, or null when a timeline cannot
   *  use it yet. */
  function mapPayload(it: LayoutItem): Payload | null {
    if (isFireable(it)) return { kind: 'item', itemId: it.id, label: itemText(it).label, itemKind: it.kind };
    if (it.kind === 'lightColor') {
      const c = STAGE_COLORS.find((s) => s.label === it.color_label);
      return c ? { kind: 'colour', colour: c } : null;
    }
    return null;
  }
  function mapLabel(it: LayoutItem): string {
    if (isFireable(it)) return itemText(it).label;
    const named = it as { name?: string; text?: string; label?: string };
    if (it.kind === 'lightColor') return named.name ?? it.color_label;
    return named.name ?? named.label ?? (it.kind === 'label' ? named.text ?? '' : '');
  }
  function mapColour(it: LayoutItem): string | undefined {
    if (it.kind === 'lightColor') {
      const c = STAGE_COLORS.find((s) => s.label === it.color_label);
      return c && c.label !== 'Off' ? rgbCss(c.rgb) : undefined;
    }
    const h = hueOf(it as Parameters<typeof hueOf>[0]);
    return h !== 'none' ? HUE_VALUES[h] : undefined;
  }
  const isPickedPayload = (p: Payload | null) => {
    const k = picked();
    if (!k || !p) return false;
    if (k.kind === 'item' && p.kind === 'item') return k.itemId === p.itemId;
    if (k.kind === 'colour' && p.kind === 'colour') return k.colour.label === p.colour.label;
    return false;
  };
  // ── point at it on your REAL canvas ──
  //
  // Operator, 2026-10-01, "yes" to: the timeline steps aside, the actual
  // canvas shows full size, a tap on a control puts it on the bar. The editor
  // is hidden, not closed (nothing of it is lost). A veil over the whole app
  // takes every tap, so nothing on the canvas can fire; rings mark what can be
  // picked, measured off the canvas's own items. Only the page the canvas is
  // on: switching pages changes the project's active page, which is stored
  // and syncs to every device (an iPad mid-gig would follow), so other pages
  // are the map's job. From the chooser the pick lands on its bar; from the
  // browser it is picked up, to click onto a bar.
  const [canvasPick, setCanvasPick] = createSignal<{ laneId: string | null; position: number | null } | null>(null);
  function startCanvasPick(laneId: string | null, position: number | null) {
    setChooser(null);
    setCanvasPick({ laneId, position });
  }
  function finishCanvasPick(p: Payload) {
    const cp = canvasPick();
    setCanvasPick(null);
    if (!cp) return;
    if (cp.laneId !== null && cp.position !== null) place(p, cp.laneId, cp.position);
    else setPicked(p);
    queueMicrotask(() => rootEl?.focus({ preventScroll: true }));
  }
  function cancelCanvasPick() {
    setCanvasPick(null);
    queueMicrotask(() => rootEl?.focus({ preventScroll: true }));
  }
  function canvasPickVeil(): JSX.Element {
    type Spot = { it: LayoutItem; p: Payload | null; ok: boolean; r: DOMRect };
    const [spots, setSpots] = createSignal<Spot[]>([]);
    const [hover, setHover] = createSignal<string | null>(null);
    const lane = () => {
      const id = canvasPick()?.laneId;
      return id ? timeline()?.lanes.find((l) => l.id === id) ?? null : null;
    };
    const measure = () => {
      const pg = pagesOfActive().find((p) => p.id === activeProject()?.activePageId);
      const out: Spot[] = [];
      for (const el of document.querySelectorAll<HTMLElement>('.scaled-canvas-inner [data-item-id]')) {
        const it = pg?.items.find((x) => x.id === el.dataset['itemId']);
        if (!it) continue;
        const p = mapPayload(it);
        const l = lane();
        out.push({ it, p, ok: !!p && (!l || laneAccepts(l, p)), r: el.getBoundingClientRect() });
      }
      setSpots(out);
    };
    const hit = (x: number, y: number): Spot | null => {
      let found: Spot | null = null;
      for (const s of spots()) if (x >= s.r.left && x <= s.r.right && y >= s.r.top && y <= s.r.bottom) found = s;
      return found;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation();
      cancelCanvasPick();
    };
    onMount(() => {
      measure();
      window.addEventListener('resize', measure);
      window.addEventListener('keydown', onKey, { capture: true });
    });
    onCleanup(() => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('keydown', onKey, { capture: true });
    });
    const where = () => {
      const cp = canvasPick();
      const l = lane();
      return cp && l && cp.position !== null ? ` to put at ${barBeat(cp.position, sig())} on ${l.name}` : ' to pick it up';
    };
    return (
      <Portal>
        <div
          class="tl2-aim"
          data-hovering={hover() && spots().find((s) => s.it.id === hover())?.ok ? 'true' : undefined}
          onPointerMove={(e) => setHover(hit(e.clientX, e.clientY)?.it.id ?? null)}
          onClick={(e) => {
            const s = hit(e.clientX, e.clientY);
            if (s && s.ok && s.p) finishCanvasPick(s.p);
          }}
        >
          <For each={spots()}>
            {(s) => (
              <div
                class="tl2-aim-it"
                data-ok={s.ok ? 'true' : undefined}
                data-hover={hover() === s.it.id ? 'true' : undefined}
                data-pick-id={s.it.id}
                style={{ left: `${s.r.left}px`, top: `${s.r.top}px`, width: `${s.r.width}px`, height: `${s.r.height}px` }}
              >
                <Show when={s.ok && hover() === s.it.id}><span>{mapLabel(s.it)}</span></Show>
              </div>
            )}
          </For>
          <div class="tl2-aim-bar" onClick={(e) => e.stopPropagation()}>
            <b>Tap a control{where()}</b>
            <span>This page of your canvas. Nothing you tap here is sent.</span>
            <button type="button" onClick={() => cancelCanvasPick()}>Cancel</button>
          </div>
        </div>
      </Portal>
    );
  }

  /** The map, for the browser (drag, or click to pick up) or for the chooser
   *  (a click puts it at the chooser's bar). */
  function canvasMap(mode: 'browser' | 'chooser'): JSX.Element {
    const lane = () => (mode === 'chooser' ? timeline()?.lanes.find((l) => l.id === chooser()?.laneId) ?? null : null);
    return (
      <div class="tl2-map" data-mode={mode}>
        <Show when={pagesOfActive().length > 1}>
          <div class="tl2-map-tabs" role="tablist" aria-label="Canvas pages">
            <For each={pagesOfActive()}>
              {(pg) => (
                <button
                  type="button"
                  role="tab"
                  class="tl2-map-tab"
                  aria-selected={mapPage()?.id === pg.id}
                  style={{ '--pg': `var(--page-hue-${pg.color})` } as JSX.CSSProperties}
                  data-info={`${pg.name}: show this page of your canvas.`}
                  onClick={() => setMapPageId(pg.id)}
                >{pg.name}</button>
              )}
            </For>
          </div>
        </Show>
        <div class="tl2-map-frame" style={{ 'aspect-ratio': `${mapView().w} / ${mapView().h}` }}>
          <For each={mapPage()?.items ?? []}>
            {(it) => {
              const p = mapPayload(it);
              const can = () => !!p && (mode === 'browser' || (!!lane() && laneAccepts(lane()!, p)));
              const label = mapLabel(it);
              const uses = () => usesById().get(it.id) ?? 0;
              const info = () => {
                if (!p) return `${label ? `${label}: a` : 'A'} ${kindWord(it.kind).toLowerCase()} can't go on a timeline yet.`;
                if (p.kind === 'colour') return can() ? `${label}: drag onto a Lights lane for a ${p.colour.label} keyframe, or click it and then click a bar.` : `${label} is a light colour: it goes on a Lights lane.`;
                const meta = isFireable(it) ? itemText(it).meta : '';
                const n = uses();
                const used = n > 0 ? ` Pressed ${n} time${n === 1 ? '' : 's'} in this timeline.` : '';
                return mode === 'chooser'
                  ? `${label} (${meta}): click to put it at ${barBeat(chooser()?.position ?? 0, sig())}.${used}`
                  : `${label} (${meta}): drag onto a Controls lane, or click it and then click a bar.${used}`;
              };
              const v = () => mapView();
              return (
                <div
                  class="tl2-map-it"
                  role={can() ? 'button' : undefined}
                  aria-label={label || kindWord(it.kind)}
                  data-kind={it.kind}
                  data-can={can() ? 'true' : undefined}
                  data-shape={mapShape(it)}
                  data-picked={mode === 'browser' && isPickedPayload(p) ? 'true' : undefined}
                  data-sel={selItemId() === it.id ? 'true' : undefined}
                  data-hover={hoverItemId() === it.id ? 'true' : undefined}
                  data-live={liveIds().has(it.id) ? 'true' : undefined}
                  data-dim={search().trim() && !matches(`${label} ${isFireable(it) ? itemText(it).meta : ''}`) ? 'true' : undefined}
                  style={{
                    left: `${((it.x - v().x) / v().w) * 100}%`, top: `${((it.y - v().y) / v().h) * 100}%`,
                    width: `${(it.w / v().w) * 100}%`, height: `${(it.h / v().h) * 100}%`,
                    '--c': mapColour(it) ?? '#8b9099',
                  } as JSX.CSSProperties}
                  data-info={info()}
                  onPointerEnter={() => setHoverItemId(it.id)}
                  onPointerLeave={() => setHoverItemId((h) => (h === it.id ? null : h))}
                  onPointerDown={(e) => { if (p && can() && mode === 'browser') onBrowserPointerDown(e, p); }}
                  onClick={() => {
                    const c = chooser();
                    if (mode !== 'chooser' || !p || !can() || !c) return;
                    chooseFromChooser(p);
                  }}
                >
                  <span>{label}</span>
                  <Show when={uses() > 0}><b class="tl2-map-n">{uses()}</b></Show>
                </div>
              );
            }}
          </For>
          <Show when={(mapPage()?.items.length ?? 0) === 0}>
            <span class="tl2-map-empty">Nothing on this page yet</span>
          </Show>
        </div>
      </div>
    );
  }

  // ── pointer drags: clips, browser items, lane edges, panel dividers ──
  type ClipDrag = { laneId: string; stepId: string; mode: 'move' | 'resize'; x0: number; y0: number; pos0: number; dur0: number; moved: boolean };
  const [clipDrag, setClipDrag] = createSignal<ClipDrag | null>(null);
  /** `copy`: an Alt (Option) drag, which leaves the original and drops a copy (2026-11-02, operator's pick). */
  const [dragPreview, setDragPreview] = createSignal<{ stepId: string; laneId: string; position: number; durationBars: number; copy?: boolean } | null>(null);
  const [browserDrag, setBrowserDrag] = createSignal<{ payload: Payload; x: number; y: number; laneId: string | null; position: number; ok: boolean; newLane: NewLaneRole | undefined } | null>(null);
  /** Set when a drag ends, so the click the browser fires after it is not
   *  read as a click on the lane underneath. */
  let suppressClick = false;
  // ...and only that click: a dragged block re-renders when it lands, so its
  // trailing click can land nowhere and leave the flag set, which ate the NEXT
  // click on a lane (found by the v2 smoke). A new press always clears it.
  onMount(() => {
    const clear = () => { suppressClick = false; };
    rootEl?.addEventListener('pointerdown', clear, { capture: true });
    onCleanup(() => rootEl?.removeEventListener('pointerdown', clear, { capture: true }));
  });
  // The long press starts in the CAPTURE phase: a press, a keyframe and a
  // guitar flag stop their pointerdown from bubbling (their drags start
  // there), so a bubbling listener never saw a long press on exactly the
  // things that most need a menu (found by `timeline_v2_guitar_lane_and_menus`).
  onMount(() => {
    const down = (e: PointerEvent) => { if (e.pointerType !== 'mouse') startLongPress(e); };
    rootEl?.addEventListener('pointerdown', down, { capture: true });
    onCleanup(() => rootEl?.removeEventListener('pointerdown', down, { capture: true }));
  });

  function stepDuration(s: TimelineStep): number {
    const d = (s as { durationBars?: number }).durationBars;
    return typeof d === 'number' && d > 0 ? d : 1 / sig();
  }
  function onClipPointerDown(ev: PointerEvent, lane: TimelineLane, s: TimelineStep, mode: 'move' | 'resize') {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    rootEl?.focus({ preventScroll: true });
    setChooser(null);
    const ref: StepRef = { laneId: lane.id, stepId: s.id };
    // Cmd/Ctrl-CLICK adds or takes out one change; a Cmd/Ctrl-DRAG moves it off the grid (2026-11-02, the keyframe
    // editing audit: the press with the key down was always taken as a click, so a drag free of the grid could only be
    // had by pressing the key after the drag began). Decided on the first move past the threshold, or the release.
    if (addKey(ev)) {
      suppressClick = true;
      const x0 = ev.clientX; const y0 = ev.clientY;
      const off = () => {
        window.removeEventListener('pointermove', pend);
        window.removeEventListener('pointerup', done);
        window.removeEventListener('pointercancel', done);
      };
      const pend = (e: PointerEvent) => {
        if (Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
        off();
        if (mode === 'move' && sel().type === 'steps' && isStepSel(s.id)) startGroupDrag(ev, ref, s);
        else startStepDrag(ev, lane, s, mode);
      };
      const done = (ue: Event) => {
        off();
        if (ue.type === 'pointercancel') return;
        const cur = selRefs();
        selectSteps(cur.some((r) => r.stepId === s.id) ? cur.filter((r) => r.stepId !== s.id) : [...cur, ref]);
        stepAnchor = ref;
      };
      window.addEventListener('pointermove', pend);
      window.addEventListener('pointerup', done);
      window.addEventListener('pointercancel', done);
      return;
    }
    // Shift-click selects the block from the last change clicked to this one, across the lanes between; it does not
    // drag. (The click that follows must not reach the lane underneath.)
    if (ev.shiftKey && mode === 'move') {
      selectStepBlock(ref);
      suppressClick = true;
      return;
    }
    // Grabbing one of several selected changes moves them all.
    if (mode === 'move' && sel().type === 'steps' && isStepSel(s.id)) { startGroupDrag(ev, ref, s); return; }
    startStepDrag(ev, lane, s, mode);
  }
  /** One change dragged: moved (to another lane that takes it, too), or its length when `resize`; with Alt (Option)
   *  held at any moment of the drag, a COPY is dropped and the original stays (Logic's, Premiere's gesture). */
  function startStepDrag(ev: PointerEvent, lane: TimelineLane, s: TimelineStep, mode: 'move' | 'resize') {
    const ref: StepRef = { laneId: lane.id, stepId: s.id };
    stepAnchor = ref;
    setSel({ type: 'step', laneId: lane.id, stepId: s.id });
    setClipDrag({ laneId: lane.id, stepId: s.id, mode, x0: ev.clientX, y0: ev.clientY, pos0: s.position, dur0: stepDuration(s), moved: false });
    let cancelled = false;
    let copy = mode === 'move' && ev.altKey;
    abortDrag = () => { cancelled = true; up(); };
    const move = (e: PointerEvent) => {
      const d = clipDrag();
      if (!d) return;
      if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_THRESHOLD_PX) return;
      if (!d.moved) setClipDrag({ ...d, moved: true });
      const dBars = (e.clientX - d.x0) / ppb();
      if (d.mode === 'resize') {
        const u = unitOf(freeKey(e));
        // One rule for every drag (audit 2026-11-03 D-14): a block ENDS by the
        // timeline's end. The resize had no limit, the move limited only its
        // start, and the group limited its last start.
        const room = Math.max(u, totalBars() - d.pos0);
        const dur = Math.min(room, Math.max(u, Math.round((d.dur0 + dBars) / u) * u));
        setDragPreview({ stepId: d.stepId, laneId: d.laneId, position: d.pos0, durationBars: dur });
        setInfo(`Length ${barsText(dur)}`);
        return;
      }
      copy = e.altKey;
      // Its END stays inside (D-14); a block longer than the timeline starts at 0.
      const position = Math.max(0, Math.min(totalBars() - Math.max(unit(), d.dur0), snapRound(d.pos0 + dBars, freeKey(e))));
      // Across lanes: only to a lane that takes this kind of step.
      let laneId = d.laneId;
      const hit = laneAtPoint(e.clientX, e.clientY);
      const src = timeline()?.lanes.find((l) => l.id === d.laneId);
      const step = src?.steps.find((x) => x.id === d.stepId);
      if (hit && step && hit.lane.id !== d.laneId && laneTakesStep(hit.lane, step)) laneId = hit.lane.id;
      setDragPreview({ stepId: d.stepId, laneId, position, durationBars: d.dur0, ...(copy ? { copy: true } : {}) });
      setInfo(`${copy ? 'Copy' : 'Move'} to ${barBeat(position, sig())}${copy ? '' : ' (hold Alt to copy)'}`);
    };
    const up = (ue?: Event) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      abortDrag = null;
      const d = clipDrag(); const p = dragPreview();
      setClipDrag(null); setDragPreview(null);
      if (cancelled || ue?.type === 'pointercancel') { if (d?.moved) suppressClick = true; setInfo(''); return; }
      if (!d || !d.moved || !p) return;
      suppressClick = true;
      const st = timeline()?.lanes.find((l) => l.id === d.laneId)?.steps.find((x) => x.id === d.stepId);
      const keyframe = st?.kind === 'colorMarker';
      if (d.mode === 'resize') placeStep(tid(), d.laneId, d.stepId, { durationBars: p.durationBars });
      else if ((copy || (ue as PointerEvent | undefined)?.altKey === true) && st) {
        // Dropped where it was: nothing to copy.
        if (p.laneId === d.laneId && Math.abs(p.position - d.pos0) < 1e-9) return;
        const c = { ...st, id: ulid(), position: p.position } as TimelineStep;
        label(`copy ${word([st])}`);
        placeStepsBulk(tid(), [{ laneId: p.laneId, step: c }]);
        setSel({ type: 'step', laneId: p.laneId, stepId: c.id });
      } else if (keyframe) {
        // One edit, and a keyframe already on that beat is replaced.
        if (p.laneId !== d.laneId || Math.abs(p.position - d.pos0) > 1e-9) moveColorMarker(tid(), d.laneId, p.laneId, d.stepId, p.position);
        if (p.laneId !== d.laneId) setSel({ type: 'step', laneId: p.laneId, stepId: d.stepId });
      } else if (p.laneId !== d.laneId) {
        moveStepToLane(tid(), d.laneId, p.laneId, d.stepId, p.position);
        setSel({ type: 'step', laneId: p.laneId, stepId: d.stepId });
      } else if (Math.abs(p.position - d.pos0) > 1e-9) placeStep(tid(), d.laneId, d.stepId, { position: p.position });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** Shift-click: the block from the anchor change to this one - the lanes
   *  between them, and the bars from the earlier to the later. */
  function selectStepBlock(to: StepRef) {
    const t = timeline();
    if (!t) return;
    const find = (r: StepRef | null) => (r ? t.lanes.find((l) => l.id === r.laneId)?.steps.find((x) => x.id === r.stepId) : undefined);
    const cur = selRefs();
    const from = (find(stepAnchor) ? stepAnchor : cur.length === 1 ? cur[0]! : null) ?? to;
    const a = find(from); const b = find(to);
    if (!a || !b) return;
    const order = laneOrder();
    const ia = order.findIndex((l) => l.id === from.laneId);
    const ib = order.findIndex((l) => l.id === to.laneId);
    const start = Math.min(a.position, b.position);
    const end = Math.max(a.position, b.position);
    const refs: StepRef[] = [];
    for (const l of order.slice(Math.min(ia, ib), Math.max(ia, ib) + 1)) {
      for (const x of l.steps) if (x.position >= start - 1e-9 && x.position <= end + 1e-9) refs.push({ laneId: l.id, stepId: x.id });
    }
    selectSteps(refs);
  }
  /** Several selected changes, dragged together: the same number of bars
   *  each, on their own lanes, snapped by the one grabbed. A press without a
   *  drag selects just that one. */
  const [groupShift, setGroupShift] = createSignal<number | null>(null);
  /** The group drag is an Alt (Option) copy: the originals stay, the copies are drawn as ghosts. */
  const [groupCopy, setGroupCopy] = createSignal(false);
  const shiftOf = (id: string) => { const g = groupShift(); return g !== null && !groupCopy() && isStepSel(id) ? g : 0; };
  function startGroupDrag(ev: PointerEvent, ref: StepRef, grabbed: TimelineStep) {
    const t = timeline();
    if (!t) return;
    const refs = selRefs();
    const ids = new Set(refs.map((r) => r.stepId));
    const chosen = t.lanes.flatMap((l) => l.steps).filter((x) => ids.has(x.id));
    const pos = chosen.map((x) => x.position);
    const lo = Math.min(...pos);
    // The latest END, so the whole group stays inside the timeline (D-14).
    const hiEnd = Math.max(...chosen.map((x) => Math.max(x.position + unit(), x.position + stepDuration(x))));
    const x0 = ev.clientX; const y0 = ev.clientY;
    let moved = false; let d = 0;
    let cancelled = false;
    let copy = ev.altKey;
    abortDrag = () => { cancelled = true; up(); };
    const move = (e: PointerEvent) => {
      if (!moved && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      moved = true;
      copy = e.altKey;
      d = snapRound(grabbed.position + (e.clientX - x0) / ppb(), freeKey(e)) - grabbed.position;
      d = Math.max(-lo, Math.min(totalBars() - hiEnd, d));
      setGroupCopy(copy);
      setGroupShift(d);
      setInfo(`${copy ? 'Copy' : 'Move'} ${refs.length} changes ${d >= 0 ? 'later' : 'earlier'} by ${barsText(Math.abs(d))}${copy ? '' : ' (hold Alt to copy)'}`);
    };
    const up = (ue?: Event) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      abortDrag = null;
      setGroupShift(null);
      setGroupCopy(false);
      // A long press opened the group's menu: keep the group.
      if (ctxMenu()) return;
      if (cancelled || (moved && ue?.type === 'pointercancel')) { suppressClick = moved; setInfo(''); return; }
      if (!moved) { stepAnchor = ref; setSel({ type: 'step', laneId: ref.laneId, stepId: ref.stepId }); return; }
      suppressClick = true;
      if (copy || (ue as PointerEvent | undefined)?.altKey === true) {
        if (Math.abs(d) < 1e-9) return;
        const t2 = timeline();
        if (!t2) return;
        const adds = refs.flatMap((r) => {
          const x = t2.lanes.find((l) => l.id === r.laneId)?.steps.find((y) => y.id === r.stepId);
          return x ? [{ laneId: r.laneId, step: { ...x, id: ulid(), position: x.position + d } as TimelineStep }] : [];
        });
        label(`copy ${word(adds.map((a) => a.step))}`);
        placeStepsBulk(t2.id, adds);
        selectSteps(adds.map((a) => ({ laneId: a.laneId, stepId: a.step.id })));
        return;
      }
      moveStepsBy(refs, d);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** The drag in progress, to cancel (2026-10-16): Esc puts it back, as in a DAW, and so does a pointer the system
   *  takes away (a cancelled touch on the iPad), which used to COMMIT the half-finished move. */
  let abortDrag: (() => void) | null = null;
  /** The kind of pointer that pressed a lane last ('mouse' | 'pen' | 'touch'): a touch tap adds, a click seeks. */
  let lastPointer = 'mouse';
  /** Hints in the words of the hand in use (2026-10-16, the look audit: the iPad was told to right-click,
   *  double-click and Ctrl + scroll). A finger's touch switches them to touch words, a mouse or pen back; it starts
   *  from the device. `tip` picks a hint's two wordings; `inHandWords` turns a mouse hint from anywhere in the editor
   *  (a row of another component included) into touch words. */
  const [touchUi, setTouchUi] = createSignal(typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
  const tip = (mouse: string, touch: string): string => (touchUi() ? touch : mouse);
  const inHandWords = (text: string): string => {
    if (!touchUi()) return text;
    const whole = TOUCH_HINTS[text];
    if (whole) return whole;
    // A long-press is the right-click on a touch screen (DEC-227).
    return text.replace(/\bRight-click/g, 'Long-press').replace(/\bright-click/g, 'long-press')
      .replace(/click it and then click a bar/g, 'tap it and then tap a bar').replace(/then click a bar/g, 'then tap a bar')
      .replace(/clicks on a Lights lane/g, 'taps on a Lights lane');
  };
  /** A box dragged over the lanes with the mouse (or a pen) selects every
   *  change in it, live as it grows; Shift or Cmd/Ctrl adds to what was
   *  selected. A finger scrolls instead (the iPad selects from the menus). */
  const [box, setBox] = createSignal<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  function onCellPointerDown(ev: PointerEvent) {
    lastPointer = ev.pointerType;
    if (ev.button !== 0 || ev.pointerType === 'touch' || picked()) return;
    if ((ev.target as Element).closest('.tl2-clip, .tl2-kf, .tl2-kfh, .tl2-gflag, .tl2-gsnap, .tl2-hb, .tl2-leadh, .tl2-start, .tl2-ap')) return;
    const x0 = ev.clientX; const y0 = ev.clientY;
    const before = ev.shiftKey || addKey(ev) ? selRefs() : null;
    let on = false;
    const pick = (x1: number, y1: number) => {
      const t = timeline();
      const cell = rootEl?.querySelector('[data-tl2-cell]');
      if (!t || !cell) return;
      const left = cell.getBoundingClientRect().left;
      const tot = totalBars();
      const a = Math.max(0, Math.min(tot, (Math.min(x0, x1) - left) / ppb()));
      const b = Math.max(0, Math.min(tot, (Math.max(x0, x1) - left) / ppb()));
      const start = snapFloor(a);
      const end = Math.min(tot, Math.max(start + unit(), Math.ceil(b / unit() - 1e-9) * unit()));
      const top = Math.min(y0, y1); const bottom = Math.max(y0, y1);
      const refs: StepRef[] = [...(before ?? [])];
      // A folded group's row stands for every lane inside it.
      for (const row of rootEl?.querySelectorAll<HTMLElement>('.tl2-lane[data-lane-id], .tl2-grp[data-folded]') ?? []) {
        const r = row.getBoundingClientRect();
        if (r.bottom <= top || r.top >= bottom) continue;
        const ids = row.dataset['laneId'] ? [row.dataset['laneId']] : (row.dataset['groupLanes'] ?? '').split(' ').filter(Boolean);
        for (const id of ids) {
          const lane = t.lanes.find((l) => l.id === id);
          if (!lane) continue;
          for (const x of lane.steps) {
            const inside = x.position >= start - 1e-9 && x.position < end - 1e-9;
            const overlaps = x.kind !== 'colorMarker' && x.position < end - 1e-9 && x.position + stepDuration(x) > start + 1e-9;
            if (inside || overlaps) refs.push({ laneId: lane.id, stepId: x.id });
          }
        }
      }
      selectSteps(refs, before ? undefined : { start, end });
      setInfo(`${refs.length} change${refs.length === 1 ? '' : 's'} from ${barBeat(start, sig())} to ${barBeat(end, sig())}`);
    };
    const move = (e: PointerEvent) => {
      if (!on && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      on = true;
      setBox({ x0, y0, x1: e.clientX, y1: e.clientY });
      pick(e.clientX, e.clientY);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      const was = box();
      setBox(null);
      if (!on || !was) return;
      suppressClick = true;
      // An empty box selects nothing: a stretch with no changes has nothing
      // to copy or move.
      if (selRefs().length === 0) setSel({ type: 'timeline' });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  function laneTakesStep(lane: TimelineLane, s: TimelineStep): boolean {
    if (s.kind === 'hitBlock' || s.kind === 'hitRoute' || s.kind === 'hit') return laneRole(lane) === 'drums';
    if (laneRole(lane) === 'drums') return false;
    if (s.kind === 'automationPoint') return laneRole(lane) === 'automation';
    if (laneRole(lane) === 'automation') return false;
    if (s.kind === 'lightRelease') return isMasterLane(lane) || laneRole(lane) === 'opvcolour';
    if (s.kind === 'colorMarker') return editsColour(lane);
    if (s.kind === 'pattern') return editsColour(lane);
    if (laneRole(lane) === 'opvpalette') return opvPaletteOf(s) !== null;
    if (laneRole(lane) === 'opvcolour') return false;
    if (s.kind === 'animation') return laneRole(lane) === 'lights';
    if (laneRole(lane) === 'guitar') return isGuitarStep(s);
    return laneRole(lane) === 'controls';
  }
  /** A press of a guitar preset or snapshot (DEC-244). */
  function isGuitarStep(s: TimelineStep): boolean {
    if (s.kind !== 'buttonRef') return false;
    const it = controlById(s.buttonId);
    return !!it && GUITAR_KINDS.has(it.kind);
  }

  function onBrowserPointerDown(ev: PointerEvent, payload: Payload) {
    if (ev.button !== 0) return;
    const x0 = ev.clientX; const y0 = ev.clientY;
    const pid = ev.pointerId;
    let dragging = false;
    let cancelled = false;
    const abort = () => { cancelled = true; up(new PointerEvent('pointercancel', { pointerId: pid })); };
    abortDrag = abort;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid || cancelled) return;
      if (!dragging && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      dragging = true;
      const hit = laneAtPoint(e.clientX, e.clientY);
      // Below the lanes (or on a blank timeline): a new lane for it.
      const spot = hit ? null : document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-tl2-newlane]') ?? null;
      const newLane = spot ? laneRoleFor(payload) : undefined;
      const cell = hit?.cell ?? spot;
      const ok = hit ? laneAccepts(hit.lane, payload) : !!newLane;
      const position = cell ? Math.min(totalBars() - unit(), snapRound(barAtX(cell, e.clientX))) : 0;
      setBrowserDrag({ payload, x: e.clientX, y: e.clientY, laneId: hit?.lane.id ?? null, position, ok, newLane });
      setInfo(hit
        ? (ok ? `Drop ${payloadLabel(payload)} at ${barBeat(position, sig())}` : `${hit.lane.name} doesn't take this`)
        : newLane
          ? `Drop to add a ${ROLE_NAME[newLane]} lane with ${payloadLabel(payload)} at ${barBeat(position, sig())}`
          : `Drag ${payloadLabel(payload)} onto a lane`);
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (abortDrag === abort) abortDrag = null;
      const d = browserDrag();
      setBrowserDrag(null);
      // A cancel (the list scrolled under a flick, a second finger, the system
      // took the touch) is neither a click nor a drop (audit D-4 / D-5): it
      // used to pick the item up, or place it where the finger was.
      if (e.type === 'pointercancel') { setInfo(''); return; }
      if (!dragging) {
        // A click: pick it up (or put it back down).
        const cur = picked();
        setPicked(cur && JSON.stringify(cur) === JSON.stringify(payload) ? null : payload);
        if (payload.kind === 'colour') useClickColour(payload.colour);
        return;
      }
      suppressClick = true;
      if (d && d.laneId && d.ok) place(payload, d.laneId, d.position);
      else if (d && d.newLane) placeOnNewLane(payload, d.position);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  function startResize(ev: PointerEvent, apply: (dx: number, dy: number) => void) {
    ev.preventDefault();
    ev.stopPropagation();
    const x0 = ev.clientX; const y0 = ev.clientY;
    const el = ev.currentTarget as HTMLElement;
    el.setAttribute('data-on', '');
    const move = (e: PointerEvent) => apply(e.clientX - x0, e.clientY - y0);
    const up = () => {
      el.removeAttribute('data-on');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  // ── undo / redo (2026-10-13): one order across the timeline and its song (`undo/editOrder.ts`), from the keys,
  // the toolbar, or a key pressed while focus sits outside the editor (after a panel click it drops to <body>, where
  // the app's own Ctrl+Z would undo whatever store was touched last instead of this timeline).
  function undoNow() { undoRedo('undo'); }
  function redoNow() { undoRedo('redo'); }
  /** Undo or redo, and say WHICH timeline it changed (2026-10-16, the editing audit: the toast said "Undid: Timeline",
   *  and a Ctrl+Z here could quietly undo an edit to another timeline, off screen). When it was another timeline,
   *  the editor shows it, so what changed is in front of you. A timeline's history is the whole library's, so the
   *  timeline is found by what changed: untouched timelines keep their objects through an undo. */
  function undoRedo(kind: 'undo' | 'redo') {
    const before = new Map(timelines().map((t) => [t.id, t] as const));
    const r = kind === 'undo' ? undoLatest() : redoLatest();
    if (!r.ok) return;
    if (r.name !== 'timeline') { announceHistoryResult(kind, true, r.name); return; }
    const after = timelines();
    const changed = [
      ...after.filter((t) => before.get(t.id) !== t),
      ...[...before.values()].filter((t) => !after.some((a) => a.id === t.id)),
    ];
    const verb = kind === 'undo' ? 'Undid' : 'Redid';
    const moved = timelineHistory.lastMoved();
    if (changed.length !== 1) { if (moved?.label) pushToast('ok', `${verb}: ${moved.label}.`, 2200); else announceHistoryResult(kind, true, r.name); return; }
    const t = changed[0]!;
    const now = after.find((a) => a.id === t.id);
    const stillThere = !!now;
    const elsewhere = t.id !== tid();
    if (elsewhere && stillThere) selectTimelineInEditor(t.id);
    // What was selected when the edit was made (before it, for an undo; after it, for a redo).
    const ctx = moved?.context as { tid?: string; sel?: Selection } | undefined;
    if (stillThere && ctx?.sel && ctx.tid === t.id) setSel(ctx.sel);
    // The edit's own name, else what changed between the two versions (the edit runs prev -> now on a redo, now -> prev
    // on an undo).
    const prev = before.get(t.id);
    const what = moved?.label ?? (prev && now ? describeEdit(kind === 'undo' ? now : prev, kind === 'undo' ? prev : now, (k) => keyName(k)) : `a change to "${t.name}"`);
    pushToast('ok', `${verb}: ${what}${elsewhere && stillThere ? ` in "${t.name}", now shown here` : ''}${stillThere ? '' : ` ("${t.name}" is deleted again)`}.`, 2200);
  }
  {
    const outside = (ev: KeyboardEvent) => {
      if (!(ev.ctrlKey || ev.metaKey)) return;
      const k = ev.key.toLowerCase();
      if (k !== 'z' && k !== 'y') return;
      const t = ev.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (rootEl && t && rootEl.contains(t)) return; // the editor's own handler has it
      if (t && t !== document.body && t.closest('[role="dialog"], .modal, .sheet')) return; // another surface's keys
      ev.preventDefault(); ev.stopPropagation();
      if (k === 'y' || ev.shiftKey) redoNow(); else undoNow();
    };
    onMount(() => window.addEventListener('keydown', outside, { capture: true }));
    onCleanup(() => window.removeEventListener('keydown', outside, { capture: true }));
  }

  // ── keys ──
  // Keys keep working when focus falls to the page (2026-10-16): an edit that re-draws the focused element (Ctrl+D on a
  // section rebuilds the section strip) dropped focus to <body>, outside this editor, and Delete, Ctrl+D and the arrows
  // then did nothing until the next click. A key with nothing focused comes here; when it is used here, the rest of the
  // app does not see it. On the DOCUMENT's capture phase, never the window's: every popover (the song picker, menus,
  // hints) takes its keys on the window's capture phase, which always runs first, so an open one still gets its Esc
  // (on the window this ran before them, by registration order, and an Esc meant for the song picker closed the editor).
  const onStrayKey = (ev: KeyboardEvent) => {
    if (ev.defaultPrevented) return;
    const a = document.activeElement;
    if (a && a !== document.body && a !== document.documentElement) return;
    onKey(ev);
    if (ev.defaultPrevented) ev.stopImmediatePropagation();
  };
  document.addEventListener('keydown', onStrayKey, true);
  onCleanup(() => document.removeEventListener('keydown', onStrayKey, true));
  /** The browser's back (2026-11-03, navigate/backButton.ts): the editor's own menus and sheets first, one per press,
   *  as Esc closes them; with none open, back closes the editor (backButton's next step). A selection is not a layer. */
  onCleanup(onBack(() => {
    if (abortDrag) { abortDrag(); return true; }
    // A take in progress ends (and is kept) before anything closes: back used to
    // close the editor mid-take and every press was lost (2026-11-03 audit D-2).
    if (recTake()) { stopRecording(); return true; }
    if (renamingTl()) { tlNameCancelled = true; (document.activeElement as HTMLElement | null)?.blur(); setRenamingTl(false); return true; }
    if (canvasPick()) { cancelCanvasPick(); return true; }
    if (ctxMenu()) { setCtxMenu(null); return true; }
    if (confirmDelete()) { setConfirmDelete(null); return true; }
    if (helpOpen()) { setHelpOpen(false); return true; }
    if (chooser() || ovChooser() || palChooser() || transPop() || newMenu() || addLaneMenu()) {
      setChooser(null); setOvChooser(null); setPalChooser(null); setTransPop(null); setNewMenu(false); setAddLaneMenu(false);
      return true;
    }
    if (picked()) { setPicked(null); return true; }
    return false;
  }));
  function onKey(ev: KeyboardEvent) {
    const target = ev.target as HTMLElement | null;
    const typing = !!target?.closest('input, textarea, select, [contenteditable="true"]');
    const mod = ev.ctrlKey || ev.metaKey;
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      if (abortDrag) { ev.preventDefault(); abortDrag(); return; }
      if (typing) { target?.blur(); return; }
      if (ctxMenu()) { setCtxMenu(null); return; }
      if (confirmDelete()) { setConfirmDelete(null); return; }
      if (helpOpen()) { setHelpOpen(false); return; }
      if (chooser() || ovChooser() || palChooser() || transPop() || newMenu() || addLaneMenu()) { setChooser(null); setOvChooser(null); setPalChooser(null); setTransPop(null); setNewMenu(false); setAddLaneMenu(false); return; }
      if (picked()) { setPicked(null); return; }
      if (sel().type !== 'timeline') { setSel({ type: 'timeline' }); return; }
      // Esc never closes the editor (operator, 2026-10-16): only its close button does. A second Esc meant to let go
      // of something closed the whole timeline.
      return;
    }
    if (typing) return;
    if (ev.code === 'Space') { ev.preventDefault(); ev.stopPropagation(); togglePlay(); return; }
    // (the nudge's undo batch is closed by `endNudge` on keyup / blur)
    // Transport and view keys (the pro-grade review 2026-10-13): Home = the start, [ ] = previous / next section,
    // + - = zoom, F = fit the song; Left / Right nudge the selected changes a grid step (Shift: a bar), or move the
    // playhead a bar when nothing is selected.
    if (!mod && !ev.altKey) {
      const k = ev.key;
      if (k === '?') { ev.preventDefault(); ev.stopPropagation(); setHelpOpen(!helpOpen()); return; }
      if (k === 'Home') { ev.preventDefault(); ev.stopPropagation(); seekTo(0); return; }
      if (k === 'End') { ev.preventDefault(); ev.stopPropagation(); seekTo(totalBars() - 1); return; }
      // 1-9: that section, as its pad (2026-10-16, the editing audit; the pads mirror the five foot switches, DEC-281):
      // a jump at the next bar line while a song plays, else the playhead to its start.
      if (/^[1-9]$/.test(k)) {
        const sec = sectionStarts()[Number(k) - 1];
        if (!sec) return;
        ev.preventDefault(); ev.stopPropagation();
        if (playing() && timeline()?.tier === 'song') jumpTo(sec.id); else seekTo(sec.start);
        return;
      }
      if (k === '[' || k === ']') { ev.preventDefault(); ev.stopPropagation(); stepSection(k === ']' ? 1 : -1); return; }
      if (k === '+' || k === '=') { ev.preventDefault(); ev.stopPropagation(); zoomBy(1.4); return; }
      if (k === '-' || k === '_') { ev.preventDefault(); ev.stopPropagation(); zoomBy(1 / 1.4); return; }
      if (k === 'f' || k === 'F') { ev.preventDefault(); ev.stopPropagation(); fitView(); return; }
      if (k === 'ArrowLeft' || k === 'ArrowRight') {
        ev.preventDefault(); ev.stopPropagation();
        const dir = k === 'ArrowRight' ? 1 : -1;
        const refs = selRefs();
        // A held arrow is ONE undo step, closed when the key comes up (2026-10-16: every repeat was its own, and the
        // 100-step history lost everything older after about three seconds).
        if ((refs.length > 0 || (sel().type === 'track' && selectedClips().length)) && !nudgeBatch) { beginBatch(); nudgeBatch = true; }
        if (refs.length > 0) { label(`nudge ${word(refsSteps(refs))}`); moveStepsBy(refs, dir * (ev.shiftKey ? 1 : nudgeUnit())); return; }
        // Selected song clips: the same as changes, a grid step, or a bar with Shift (was a bar, and a beat with Shift).
        if (sel().type === 'track' && selectedClips().length && timeline() && nudgeSelectedSongClips(timeline()!, sectionStarts(), dir * Math.max(1, props.beatsPerBar()) * (ev.shiftKey ? 1 : unit()))) return;
        seekTo(Math.round(playBar()) + dir);
        return;
      }
    }
    // Delete removes the selected change or changes. Never lanes: a click on
    // a lane selects it on the way to something else, and one key would then
    // take the lane (lanes are deleted from their menu).
    if ((ev.key === 'Delete' || ev.key === 'Backspace') && sel().type === 'track' && timeline() && deleteSelectedSongClips(timeline()!, sectionStarts())) { ev.preventDefault(); ev.stopPropagation(); return; }
    // A selected SECTION answers Delete and Ctrl/Cmd+D as its menu does (2026-10-16: the keys did nothing on one).
    {
      const s = sel(); const t = timeline();
      if (s.type === 'section' && t) {
        if ((ev.key === 'Delete' || ev.key === 'Backspace') && t.sections.length > 1) { ev.preventDefault(); ev.stopPropagation(); removeSection(t.id, s.id); setSel({ type: 'timeline' }); return; }
        if (mod && ev.key.toLowerCase() === 'd') {
          ev.preventDefault(); ev.stopPropagation();
          duplicateSectionHere(s.id);
          return;
        }
      }
      if (s.type === 'lane' && mod && ev.key.toLowerCase() === 'd') { ev.preventDefault(); ev.stopPropagation(); duplicateLanes([s.id]); return; }
    }
    // Song clips, as in Live: Ctrl/Cmd+E splits at the playhead (the selected clips, or the one under it).
    if (mod && ev.key.toLowerCase() === 'e' && timeline()?.testTrack) {
      ev.preventDefault(); ev.stopPropagation();
      if (!splitSongClipsAt(timeline()!, sectionStarts(), playBar())) pushToast('warn', 'No song clip under the playhead to split');
      return;
    }
    if (mod && ev.key.toLowerCase() === 'd' && sel().type === 'track' && selectedClips().length && timeline()) {
      ev.preventDefault(); ev.stopPropagation();
      duplicateSelectedSongClips(timeline()!, sectionStarts());
      return;
    }
    if ((ev.key === 'Delete' || ev.key === 'Backspace') && (sel().type === 'steps' ? removeSelectedSteps() : deleteSelectedStep())) { ev.preventDefault(); ev.stopPropagation(); return; }
    if (mod && ev.key.toLowerCase() === 'l') { ev.preventDefault(); ev.stopPropagation(); loopSelection(); return; }
    if (mod && ev.key.toLowerCase() === 'a') { ev.preventDefault(); ev.stopPropagation(); if (sel().type === 'track' && timeline() && selectAllSongClips(timeline()!, sectionStarts())) return; selectAllSteps(); return; }
    // Song clips: copy the selected ones, paste at the playhead (the song row selected).
    if (mod && ev.key.toLowerCase() === 'c' && sel().type === 'track' && selectedClips().length && timeline()) { ev.preventDefault(); ev.stopPropagation(); copySelectedSongClips(timeline()!, sectionStarts()); return; }
    if (mod && ev.key.toLowerCase() === 'v' && sel().type === 'track' && timeline()) { ev.preventDefault(); ev.stopPropagation(); if (!pasteSongClipsAt(timeline()!, sectionStarts(), playBar())) pushToast('warn', 'Nothing copied to paste: select song clips and press Ctrl+C first'); return; }
    if (mod && ev.key.toLowerCase() === 'z') {
      ev.preventDefault(); ev.stopPropagation();
      if (ev.shiftKey) redoNow(); else undoNow();
      return;
    }
    if (mod && ev.key.toLowerCase() === 'y') { ev.preventDefault(); ev.stopPropagation(); redoNow(); return; }
    if (mod && ev.key.toLowerCase() === 'd') {
      ev.preventDefault(); ev.stopPropagation();
      if (sel().type === 'steps') duplicateSelectedSteps(); else duplicateSelectedStep();
      return;
    }
    if (mod && (ev.key.toLowerCase() === 'c' || ev.key.toLowerCase() === 'x')) {
      const refs = selRefs();
      if (refs.length === 0) return;
      ev.preventDefault(); ev.stopPropagation();
      void copyRefs(refs, ev.key.toLowerCase() === 'x');
      return;
    }
    if (mod && ev.key.toLowerCase() === 'v') {
      ev.preventDefault(); ev.stopPropagation();
      // At the PLAYHEAD, on the highlighted row (operator, 2026-11-02): it pasted after the selection, so a click on
      // bar 12 and Ctrl+V put the copy back beside the keyframe still selected at bar 5.
      const row = pasteRow();
      if (row) void pasteAt(row, playBar(), true);
      else pushToast('warn', 'Click a lane where it should go first, then paste.', 2000);
      return;
    }
  }
  // Operator, 2026-10-02: "alt + mouse scroll to zoom in and out. ctrl +
  // scroll on a lane to make it taller". Plain scroll still scrolls and Shift
  // still scrolls sideways. A Ctrl + scroll off the lanes is eaten too, or the
  // browser would zoom the whole page.
  // A trackpad PINCH arrives as a wheel event with ctrlKey set and no Control
  // key down (Chrome and Safari on a Mac both do this), so it read as
  // "Ctrl + scroll" and resized the lanes (audit 2026-11-03 D-7). The real key
  // is tracked, so a pinch zooms, as it does in every other timeline.
  let ctrlKeyDown = false;
  const trackCtrl = (e: KeyboardEvent) => { if (e.key === 'Control') ctrlKeyDown = e.type === 'keydown'; };
  const dropCtrl = () => { ctrlKeyDown = false; };
  window.addEventListener('keydown', trackCtrl, true);
  window.addEventListener('keyup', trackCtrl, true);
  window.addEventListener('blur', dropCtrl);
  onCleanup(() => {
    window.removeEventListener('keydown', trackCtrl, true);
    window.removeEventListener('keyup', trackCtrl, true);
    window.removeEventListener('blur', dropCtrl);
  });

  function onWheel(ev: WheelEvent) {
    if (ev.ctrlKey && !ctrlKeyDown && !ev.metaKey && !ev.altKey) {
      ev.preventDefault();
      // Pinch apart = zoom in. A pinch's deltas are small and continuous.
      zoomBy(Math.exp(-ev.deltaY * 0.01), scrollEl ? ev.clientX - scrollEl.getBoundingClientRect().left : 0);
      return;
    }
    if (ev.altKey) {
      ev.preventDefault();
      // The bar under the pointer stays under it.
      zoomBy(ev.deltaY < 0 ? 1.2 : 1 / 1.2, scrollEl ? ev.clientX - scrollEl.getBoundingClientRect().left : 0);
      return;
    }
    if (ev.ctrlKey || ev.metaKey) {
      ev.preventDefault();
      const id = (ev.target as Element | null)?.closest('.tl2-lane')?.getAttribute('data-lane-id');
      if (!id) return;
      // A mouse notch (deltaY 100) is 20px; a trackpad's small deltas glide.
      const d = Math.max(-40, Math.min(40, -ev.deltaY * 0.2));
      // Over one of several selected lanes, all of them.
      const ids = selLaneIds().has(id) ? [...selLaneIds()] : [id];
      setLaneHeights((m) => {
        const next = { ...m };
        for (const k of ids) next[k] = Math.round(Math.max(LANE_H_MIN, Math.min(LANE_H_MAX, (m[k] ?? LANE_H_DEFAULT) + d)));
        return next;
      });
    }
  }
  function onPointerOver(ev: PointerEvent) {
    setTouchUi(ev.pointerType === 'touch');
    if (clipDrag() || browserDrag()) return;
    const el = (ev.target as Element | null)?.closest('[data-info]');
    setInfo(inHandWords(el?.getAttribute('data-info') ?? ''));
  }
  const defaultInfo = () => {
    const s = sel();
    if (s.type === 'steps') return tip(`${s.refs.length} changes selected: drag one to move them all; ${keyHint('D')} repeats them after; Delete removes them; Esc lets go.`,
      `${s.refs.length} changes selected: drag one to move them all; long-press one to repeat or delete them.`);
    if (s.type === 'lanes') return tip(`${s.ids.length} lanes selected: right-click a name to mute, solo, duplicate or delete them; Ctrl + scroll sizes them all.`,
      `${s.ids.length} lanes selected: long-press a name to mute, solo, duplicate or delete them.`);
    const p = picked();

    if (p) return tip(`Click a bar in a lane to put ${payloadLabel(p)} there (Shift-click to place it more than once). Esc to put it down.`,
      `Tap a bar in a lane to put ${payloadLabel(p)} there.`);
    return tip('Point at anything to see what it does.', 'Touch anything to see what it does.');
  };

  // ── render helpers ──
  const sectionStarts = createMemo(() => {
    const out: Array<{ id: string; name: string; start: number; bars: number }> = [];
    // The sections start after the lead-in (`firstSectionBar`): the bars before the first one play the song alone.
    let at = timeline() ? timelineLeadIn(timeline()!) : 0;
    for (const s of timeline()?.sections ?? []) { out.push({ id: s.id, name: s.name, start: at, bars: Math.max(1, s.lengthBars) }); at += Math.max(1, s.lengthBars); }
    return out;
  });
  const barLabelEvery = () => {
    const p = ppb();
    return p >= 36 ? 1 : p >= 18 ? 2 : p >= 9 ? 4 : 8;
  };
  const gridStyle = (): JSX.CSSProperties => ({
    '--tl2-ppb': `${ppb()}px`,
    '--tl2-beat': `${ppb() / sig()}px`,
    width: `${contentW()}px`,
  } as JSX.CSSProperties);

  /** The blocks a lane draws: everything with a length, or a beat. Colour
   *  keyframes are points, drawn by `keyframesOf`/`segmentsOf` instead. */
  function clipsOf(lane: TimelineLane): Array<{ step: TimelineStep; start: number; end: number }> {
    const guitar = laneRole(lane) === 'guitar';
    return lane.steps
      // Designed drum hits draw as ticks (`hitLayer`), never as clips: a performance carries hundreds.
      .filter((s) => s.kind !== 'colorMarker' && s.kind !== 'lightRelease' && s.kind !== 'automationPoint' && s.kind !== 'hit' && !(guitar && s.kind === 'buttonRef')
        // A palette draws as a state, like a guitar preset (`opvPaletteLayer`).
        && opvPaletteOf(s) === null)
      .sort((a, b) => a.position - b.position)
      .map((s) => ({ step: s, start: s.position, end: s.position + stepDuration(s) }));
  }

  /** A drum lane's DESIGNED hits (DEC-324): one tick per hit up from the lane's bottom edge, its height by velocity (up
   *  to 30% of the lane), as ONE path (hundreds of hits, one element). Over the drum blocks, bright and dark-edged (the
   *  first build, 35% white, could not be seen on the blocks in a screenshot while a count of its ticks passed,
   *  2026-10-27), taking no pointer, so the blocks and gestures work as before. They play while OP-MidiFeed is not sending that drum. */
  function hitLayer(lane: TimelineLane) {
    const hits = () => lane.steps.filter(isHitStep);
    const width = () => Math.max(1, timelineTotalBars(timeline()!) * ppb());
    const d = () => hits().map((h) => `M${Math.round(h.position * ppb() * 10) / 10} 100V${100 - Math.round(8 + 22 * ((h.velocity ?? 127) / 127))}`).join('');
    const counts = () => {
      const by = new Map<string, number>();
      for (const h of hits()) { const k = sourceLabel(h.source).toLowerCase(); by.set(k, (by.get(k) ?? 0) + 1); }
      return [...by].map(([k, n]) => `${n} ${k}`).join(', ');
    };
    return (
      <svg class="tl2-hits" data-testid="tl2-hits" viewBox={`0 0 ${width()} 100`} preserveAspectRatio="none" style={{ width: `${width()}px` }}
        aria-label={`Designed hits: ${counts()}. They play while OP-MidiFeed is not connected; your live playing replaces them drum by drum.`}>
        <path d={d()} vector-effect="non-scaling-stroke" />
      </svg>
    );
  }

  /** `edge`: a pattern block's first or last written-out key (2026-10-20), there only so the fades into and out of
   *  the block draw true; never drawn as a keyframe, and the stretch from a first edge is the block's own. */
  type Keyframe = { step: ColorMarkerStep; position: number; ghost?: boolean; away?: boolean; edge?: 'first' | 'last' };
  /** A lights lane's colour keyframes in time order, with a drag in progress
   *  applied, so the fades redraw as a keyframe moves and the lane shows what
   *  the drop will leave: the dragged one at its new beat (or as a ghost on
   *  the lane it is being dragged to, and faded out here), and a keyframe on
   *  that beat gone, as the drop replaces it (`moveColorMarker`). */
  function keyframesOf(lane: TimelineLane): Keyframe[] {
    const p = dragPreview();
    const here = p && p.laneId === lane.id ? p : null;
    const out: Keyframe[] = [];
    const pats = lane.steps.filter(isPatternStep);
    const inBlock = (pos: number) => pats.some((b) => pos >= b.position - 1e-9 && pos < b.position + b.durationBars - 1e-9);
    if (pats.length) {
      const played = unrollPatterns(lane.steps); // cached per lane, so the edges keep their objects
      for (const b of pats) {
        const ks = played.filter((x): x is ColorMarkerStep => isColorMarkerStep(x) && patternOf(x.id) === b.id);
        if (ks.length === 0) continue;
        if (ks.length > 1) out.push({ step: ks[0]!, position: ks[0]!.position, edge: 'first' });
        out.push({ step: ks[ks.length - 1]!, position: ks[ks.length - 1]!.position, edge: 'last' });
      }
    }
    for (const s of lane.steps) {
      if (!isColorMarkerStep(s)) continue;
      if (inBlock(s.position) && !(p && p.stepId === s.id)) continue;
      if (p && p.stepId === s.id) {
        // An Alt (copy) drag: the original stays where it is, the copy follows the pointer.
        if (p.copy) {
          out.push({ step: s, position: s.position });
          if (here && Math.abs(here.position - s.position) > 1e-9) out.push({ step: s, position: here.position, ghost: true });
          continue;
        }
        out.push(here ? { step: s, position: here.position } : { step: s, position: s.position, away: true });
        continue;
      }
      if (here && Math.abs(s.position - here.position) < 1e-6) continue;
      const g = groupShift();
      if (g !== null && groupCopy() && isStepSel(s.id)) {
        out.push({ step: s, position: s.position });
        if (Math.abs(g) > 1e-9) out.push({ step: s, position: s.position + g, ghost: true });
        continue;
      }
      out.push({ step: s, position: s.position + shiftOf(s.id) });
    }
    if (here && !lane.steps.some((x) => x.id === here.stepId)) {
      const moving = timeline()?.lanes.flatMap((l) => l.steps).find((x) => x.id === here.stepId);
      if (moving && isColorMarkerStep(moving)) out.push({ step: moving, position: here.position, ghost: true });
    }
    return out.sort((a, b) => a.position - b.position);
  }
  type Segment = { from: Keyframe; to: Keyframe | null; start: number; end: number };
  /** The stretch from each keyframe to the next (the last one holds to the
   *  end). Nothing before the first: the lane leaves the light alone. */
  function segmentsOf(kfs: Keyframe[], stops: number[] = []): Segment[] {
    const live = kfs.filter((k) => !k.away);
    return live.map((k, i) => {
      const to = live[i + 1] ?? null;
      // A master's stretch ends at its Hand back.
      const stop = stops.filter((p) => p > k.position + 1e-9).sort((a, b) => a - b)[0];
      if (stop !== undefined && (!to || stop <= to.position + 1e-9)) return { from: k, to: null, start: k.position, end: stop };
      return { from: k, to, start: k.position, end: to ? to.position : totalBars() };
    });
  }
  // ── the lighting master (2026-10-02, `timeline/lightMix.ts`) ──
  /** While a lead-in or fade-back handle is dragged: its new length. */
  const [leadPreview, setLeadPreview] = createSignal<{ stepId: string; bars: number } | null>(null);
  /** A lane as it will be once the drag in progress lands, so ramps and the
   *  lights' overlays follow a drag instead of jumping on the drop. */
  function previewLane(lane: TimelineLane): TimelineLane {
    const p = dragPreview(); const g = groupShift(); const lp = leadPreview();
    if (!p && g === null && !lp) return lane;
    const steps: TimelineStep[] = [];
    for (const s of lane.steps) {
      if (p && p.stepId === s.id && p.laneId !== lane.id) continue;
      let x: TimelineStep = p && p.stepId === s.id ? { ...s, position: p.position } : { ...s, position: s.position + shiftOf(s.id) };
      if (lp && lp.stepId === s.id && (x.kind === 'colorMarker' || x.kind === 'lightRelease')) x = { ...x, leadInBars: lp.bars };
      steps.push(x);
    }
    if (p && p.laneId === lane.id && !lane.steps.some((x) => x.id === p.stepId)) {
      const moving = timeline()?.lanes.flatMap((l) => l.steps).find((x) => x.id === p.stepId);
      if (moving) steps.push({ ...moving, position: p.position });
    }
    return { ...lane, steps };
  }
  function releasesOf(lane: TimelineLane): LightReleaseStep[] {
    return previewLane(lane).steps.filter(isLightReleaseStep);
  }
  /** Set (or clear, with 0) the fade into a takeover or back out of it. */
  function setLeadIn(laneId: string, stepId: string, bars: number, curve?: ValuePointCurve) {
    const t = getTimeline(tid());
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : { ...l, steps: l.steps.map((x) => {
      if (x.id !== stepId || (x.kind !== 'colorMarker' && x.kind !== 'lightRelease')) return x;
      const { leadInBars: _b, leadInCurve: _c, ...rest } = x;
      void _b; void _c;
      const shape = curve ?? x.leadInCurve;
      return (bars > 0 ? { ...rest, leadInBars: bars, ...(shape && shape !== 'linear' ? { leadInCurve: shape } : {}) } : rest) as TimelineStep;
    }) })) });
  }
  function addHandBack(laneId: string, position: number) {
    const t = timeline();
    if (!t) return;
    const step = { id: ulid(), kind: 'lightRelease', position } as TimelineStep;
    addStep(t.id, laneId, step, { overwrite: true });
    setSel({ type: 'step', laneId, stepId: step.id });
  }
  /** What a takeover-starting keyframe or a Hand back can fade over: the
   *  room before the takeover (back to the one before it), or the takeover
   *  itself. Null for anything else. */
  function leadInfo(lane: TimelineLane, s: TimelineStep): { kind: 'in' | 'back'; bars: number; curve: ValuePointCurve; max: number } | null {
    if (laneRole(lane) === 'opvcolour' && (s.kind === 'colorMarker' || s.kind === 'lightRelease')) {
      // OPVisual's colour (2026-10-28): fade in to a keyframe, or back to each clip's own look, over the room before it.
      const before = lane.steps.filter((x) => (x.kind === 'colorMarker' || x.kind === 'lightRelease') && x.position < s.position - 1e-9);
      const room = s.position - (before.length ? Math.max(...before.map((x) => x.position)) : 0);
      return { kind: s.kind === 'colorMarker' ? 'in' : 'back', bars: s.leadInBars ?? 0, curve: 'linear', max: room };
    }
    if (!isMasterLane(lane)) return null;
    const tks = takeoversOf(lane, totalBars());
    if (s.kind === 'colorMarker') {
      const i = tks.findIndex((tk) => tk.keys[0]!.id === s.id);
      if (i < 0) return null;
      return { kind: 'in', bars: s.leadInBars ?? 0, curve: s.leadInCurve ?? 'linear', max: tks[i]!.start - (i > 0 ? tks[i - 1]!.end : 0) };
    }
    if (s.kind === 'lightRelease') {
      const tk = tks.find((x) => x.handBack?.id === s.id);
      return { kind: 'back', bars: s.leadInBars ?? 0, curve: s.leadInCurve ?? 'linear', max: tk ? tk.end - tk.start : 0 };
    }
    return null;
  }
  /** Drag a lead-in's handle: its length is from the handle to the beat it
   *  arrives on, snapped, and never longer than the room it has. */
  function startLeadDrag(ev: PointerEvent, laneId: string, stepId: string, anchor: number, max: number) {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    const cell = (ev.currentTarget as Element).closest('[data-tl2-cell]');
    if (!cell) return;
    let bars = -1;
    let cancelled = false;
    abortDrag = () => { cancelled = true; up(); };
    const move = (e: PointerEvent) => {
      bars = Math.max(0, Math.min(max, snapRound(anchor - barAtX(cell, e.clientX), freeKey(e))));
      setLeadPreview({ stepId, bars });
      setInfo(bars > 0 ? `Fade over ${barsText(bars)}, arriving at ${barBeat(anchor, sig())}` : 'A cut on the beat');
    };
    const up = (ue?: Event) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      abortDrag = null;
      setLeadPreview(null);
      suppressClick = true;
      if (cancelled || ue?.type === 'pointercancel') { setInfo(''); return; }
      if (bars >= 0) setLeadIn(laneId, stepId, bars);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** A master lane's extras: each takeover's lead-in ramp and fade-back, the
   *  handles to drag them, and its Hand back markers. */
  function masterLayer(lane: TimelineLane): JSX.Element {
    const tks = createMemo(() => takeoversOf(previewLane(lane), totalBars()));
    const real = (id: string) => lane.steps.find((x) => x.id === id);
    const room = (i: number) => { const all = tks(); return all[i]!.start - (i > 0 ? all[i - 1]!.end : 0); };
    return (
      <>
        <For each={tks()}>
          {(tk, i) => {
            const first = tk.keys[0]!;
            const name = colourName(first.color, first.label);
            return (
              <>
                <Show when={tk.leadIn > 0}>
                  <div class="tl2-ramp" style={{ left: `${(tk.start - tk.leadIn) * ppb()}px`, width: `${tk.leadIn * ppb()}px`, background: rampCss(first.color, tk.leadInCurve) }} />
                  <button
                    type="button"
                    class="tl2-leadh"
                    data-lead-for={first.id}
                    aria-label={`Fade into ${name}: drag to change`}
                    style={{ left: `${(tk.start - tk.leadIn) * ppb()}px` }}
                    data-info={`Every light fades into ${name} from ${barBeat(tk.start - tk.leadIn, sig())}, arriving at ${barBeat(tk.start, sig())}. Drag to make it longer or shorter.`}
                    onPointerDown={(e) => startLeadDrag(e, lane.id, first.id, tk.start, room(i()))}
                  />
                </Show>
                <Show when={tk.release > 0 && tk.handBack}>
                  <div class="tl2-ramp" data-back="true" style={{ left: `${(tk.end - tk.release) * ppb()}px`, width: `${tk.release * ppb()}px`, background: rampCss(WELL_RGB, tk.releaseCurve) }} />
                  <button
                    type="button"
                    class="tl2-leadh"
                    data-lead-for={tk.handBack!.id}
                    aria-label="Fade back to the lights: drag to change"
                    style={{ left: `${(tk.end - tk.release) * ppb()}px` }}
                    data-info={`Every light fades back to its own lane from ${barBeat(tk.end - tk.release, sig())}, arriving at ${barBeat(tk.end, sig())}. Drag to make it longer or shorter.`}
                    onPointerDown={(e) => startLeadDrag(e, lane.id, tk.handBack!.id, tk.end, tk.end - tk.start)}
                  />
                </Show>
              </>
            );
          }}
        </For>
        <For each={releasesOf(lane)}>
          {(r) => (
            <button
              type="button"
              class="tl2-hb"
              data-step-id={r.id}
              aria-selected={isStepSel(r.id)}
              aria-label={`Hand back at ${barBeat(r.position, sig())}`}
              style={{ left: `${r.position * ppb()}px` }}
              data-info={`Hand back at ${barBeat(r.position, sig())}: every light goes back to its own lane. Drag to move it; right-click to fade back or delete it.`}
              onPointerDown={(e) => { const s = real(r.id); if (s) onClipPointerDown(e, lane, s, 'move'); }}
            ><span>Hand back</span></button>
          )}
        </For>
      </>
    );
  }
  /** An OPVisual colour lane's "own look" marks (2026-10-28): where every clip goes back to its own look. */
  function opvReleaseLayer(lane: TimelineLane): JSX.Element {
    const real = (id: string) => lane.steps.find((x) => x.id === id);
    // OPVisual fades a keyframe in over its lead-in, and a release back to each clip's own look (2026-10-28): drawn as a
    // ramp over the ribbon, so the lane never shows a cut where the projector fades (the lights study found it).
    const ramps = createMemo(() => {
      const lv = previewLane(lane);
      return lv.steps
        .filter((x): x is ColorMarkerStep | LightReleaseStep => (x.kind === 'colorMarker' || x.kind === 'lightRelease') && (x.leadInBars ?? 0) > 0)
        .map((x) => {
          const lead = x.leadInBars!;
          const from = sampleColorLane(lv, Math.max(0, x.position - lead - 1e-6)) ?? WELL_RGB;
          const to = x.kind === 'colorMarker' ? x.color : WELL_RGB;
          return { id: x.id, start: Math.max(0, x.position - lead), end: x.position, from, to };
        });
    });
    const css = (c: { r: number; g: number; b: number; w?: number }) => {
      const w = c.w ?? 0, f = (v: number) => Math.max(0, Math.min(255, Math.round(Math.min(1, v + w) * 255)));
      return `rgb(${f(c.r)} ${f(c.g)} ${f(c.b)})`;
    };
    return (
      <>
      <For each={ramps()}>
        {(r) => <div class="tl2-ramp" data-back="true" data-opv-fade={r.id} style={{ left: `${r.start * ppb()}px`, width: `${Math.max(1, (r.end - r.start) * ppb())}px`, background: `linear-gradient(90deg, ${css(r.from)}, ${css(r.to)})` }} />}
      </For>
      <For each={releasesOf(lane)}>
        {(r) => (
          <button
            type="button"
            class="tl2-hb"
            data-step-id={r.id}
            data-opv-release="true"
            aria-selected={isStepSel(r.id)}
            aria-label={`Each clip's own look at ${barBeat(r.position, sig())}`}
            style={{ left: `${r.position * ppb()}px` }}
            data-info={`At ${barBeat(r.position, sig())} every OPVisual clip goes back to its own look${(r.leadInBars ?? 0) > 0 ? `, fading over ${barsText(r.leadInBars!)}` : ', fading over a bar'}. Drag to move it; right-click to delete it.`}
            onPointerDown={(e) => { const s = real(r.id); if (s) onClipPointerDown(e, lane, s, 'move'); }}
          ><span>Own look</span></button>
        )}
      </For>
      </>
    );
  }
  /** On a single light's lane: what the masters do to it, drawn over its own
   *  ribbon (hatched, so it reads as "taken over"), fading in and out with
   *  the same weights the rig will use. */
  function takeoverOverlay(lane: TimelineLane): JSX.Element {
    const bands = createMemo(() => {
      const t = timeline();
      if (!t || lane.laneKind !== 'dmx-fixture') return [];
      const out: Array<{ left: number; width: number; bg: string; mask: string }> = [];
      // The lowest master first, so the highest is drawn last and wins.
      const keptOut = fixtures().find((f) => f.id === lane.fixtureRef?.fixtureId)?.passthrough?.['keepOutOfMasters'] === true;
      const masters = mastersReaching(t.lanes.filter((l) => isMasterLane(l) && laneAudible(t, l)), lane, keptOut).reverse();
      for (const m of masters) {
        for (const tk of takeoversOf(previewLane(m), totalBars())) {
          const s0 = tk.start - tk.leadIn;
          const span = tk.end - s0;
          if (span <= 1e-9) continue;
          const n = Math.max(2, Math.min(64, Math.round((span * ppb()) / 8)));
          const cols: string[] = [];
          for (let i = 0; i < n; i++) cols.push(rgbCss(takeoverColour(tk, s0 + span * Math.min(1 - 1e-6, i / (n - 1)))));
          const pct = (p: number) => `${(((p - s0) / span) * 100).toFixed(2)}%`;
          const a = (p: number) => `rgb(0 0 0 / ${takeoverWeight(tk, Math.min(p, tk.end - 1e-6)).toFixed(3)})`;
          const mask: string[] = [];
          if (tk.leadIn > 0) for (let i = 0; i <= 8; i++) { const p = s0 + (tk.leadIn * i) / 8; mask.push(`${a(p)} ${pct(p)}`); }
          else mask.push(`rgb(0 0 0 / 1) 0%`);
          if (tk.release > 0) for (let i = 0; i <= 8; i++) { const p = tk.end - tk.release + (tk.release * i) / 8; mask.push(`${i === 8 ? 'rgb(0 0 0 / 0)' : a(p)} ${pct(p)}`); }
          else mask.push('rgb(0 0 0 / 1) 100%');
          out.push({
            left: s0, width: span,
            bg: `repeating-linear-gradient(135deg, #0000 0 7px, #0000003d 7px 9px), linear-gradient(90deg, ${cols.join(', ')})`,
            mask: `linear-gradient(90deg, ${mask.join(', ')})`,
          });
        }
      }
      return out;
    });
    return (
      <For each={bands()}>
        {(b) => (
          <div
            class="tl2-over"
            style={{ left: `${b.left * ppb()}px`, width: `${b.width * ppb()}px`, background: b.bg, '-webkit-mask-image': b.mask, 'mask-image': b.mask } as JSX.CSSProperties}
          />
        )}
      </For>
    );
  }
  /** A light's lane that does not play (muted, or another lane soloed): its
   *  light is OFF, except where a master takes it over. */
  const lightIsOff = (lane: TimelineLane) => lane.laneKind === 'dmx-fixture' && !!timeline() && !laneAudible(timeline()!, lane);
  /** How a stretch is painted: the colour it holds, or its fade sampled from
   *  the same maths that will play it (`colorSample.ts`), so a shape or a
   *  rainbow on the lane is the one the lights get. */
  /** A pattern block's look (2026-10-20): ONE cycle's colours as a gradient, repeated along the block, with a hairline
   *  where each repeat starts - one element where the same hits were a keyframe, a fade and a label each. */
  function patternPaint(b: PatternStep): JSX.CSSProperties {
    const w = b.cycleBars * ppb();
    const ks = [...b.keys].filter((k) => k.at >= 0 && k.at < b.cycleBars).sort((x, y) => x.at - y.at);
    if (ks.length === 0 || !(w > 0)) return {};
    const asKey = (k: PatternStep['keys'][number], at: number): ColorMarkerStep => ({ id: '', kind: 'colorMarker', position: at, color: k.color, ...(k.curve ? { curve: k.curve } : {}), ...(k.space ? { space: k.space } : {}), ...(k.path ? { path: k.path } : {}) });
    const px = (bars: number) => `${(bars * ppb()).toFixed(2)}px`;
    const stops: string[] = [];
    const last = ks[ks.length - 1]!;
    // Before the cycle's first key: the last key's stretch, wrapped round from the cycle before.
    if (ks[0]!.at > 1e-9) {
      const span = b.cycleBars - last.at + ks[0]!.at;
      stops.push(`${rgbCss(sampleColorSegment(asKey(last, last.at - b.cycleBars), asKey(ks[0]!, ks[0]!.at), (b.cycleBars - last.at) / span))} 0px`);
    }
    ks.forEach((k, i) => {
      const nx = ks[i + 1] ?? { ...ks[0]!, at: ks[0]!.at + b.cycleBars };
      const to = Math.min(nx.at, b.cycleBars);
      if (to <= k.at) return;
      if (!k.curve || k.curve === 'hold') { stops.push(`${rgbCss(k.color)} ${px(k.at)} ${px(to)}`); return; }
      const n = Math.max(2, Math.min(24, Math.round(((nx.at - k.at) * ppb()) / 6)));
      const cs = colorSegmentStops(asKey(k, k.at), asKey(nx, nx.at), n);
      cs.forEach((c, j) => { const at = k.at + ((nx.at - k.at) * j) / (n - 1); if (at <= b.cycleBars + 1e-9) stops.push(`${rgbCss(c)} ${px(at)}`); });
    });
    return {
      'background-image': `linear-gradient(90deg, #0000008c 0 1px, transparent 1px), linear-gradient(90deg, ${stops.join(', ')})`,
      'background-size': `${w.toFixed(2)}px 100%, ${w.toFixed(2)}px 100%`,
      'background-repeat': 'repeat-x',
    };
  }
  /** A drum lights block's look (2026-10-23): each cycle drawn as its look's colours (dimmed: they are what a hit
   *  flashes, not what the light holds) and its fill bright at the cycle's end, with a hairline where each cycle starts. */
  function drumPaint(b: HitBlockStep): JSX.CSSProperties {
    const w = b.cycleBars * ppb();
    if (!(w > 0)) return {};
    const fill = hitBlockFill(b);
    const fx = fill ? Math.max(0, b.cycleBars - fill.bars) * ppb() : w;
    const tone = (c: { r: number; g: number; b: number }, k: number) => cssColour({ r: c.r * k, g: c.g * k, b: c.b * k });
    const band = (cs: Array<{ r: number; g: number; b: number }>, from: number, to: number, k: number) => {
      const list = cs.length ? cs : [{ r: 1, g: 1, b: 1 }];
      return list.map((c, i) => `${tone(c, k)} ${(from + ((to - from) * i) / list.length).toFixed(2)}px ${(from + ((to - from) * (i + 1)) / list.length).toFixed(2)}px`).join(', ');
    };
    const stops = [fx > 0 ? band(b.look.colors, 0, fx, 0.42) : '', fill ? band(fill.look.colors, fx, w, 0.88) : ''].filter(Boolean).join(', ');
    return {
      'background-image': `linear-gradient(90deg, #000000a8 0 1px, transparent 1px), linear-gradient(90deg, ${stops})`,
      'background-size': `${w.toFixed(2)}px 100%, ${w.toFixed(2)}px 100%`,
      'background-repeat': 'repeat-x',
    };
  }
  function segmentPaint(sg: Segment): { background?: string; off: boolean } {
    // A stretch fades to the colour it ARRIVES at: a split keyframe's left half.
    const a = sg.from.step; const b = sg.to ? arriving(sg.to.step) : undefined;
    if (!b || transitionOf(a) === 'cut') return isOff(a.color) ? { off: true } : { background: rgbCss(a.color), off: false };
    const n = Math.max(2, Math.min(48, Math.round(((sg.end - sg.start) * ppb()) / 6)));
    return { background: cssGradient(colorSegmentStops(a, b, n)), off: false };
  }
  /** Double-click a keyframe (2026-11-02, operator's pick): its colour, and the colour the fade before it arrives at
   *  (a SPLIT: the diamond's left half). On a split keyframe the half double-clicked comes first. */
  function openKeyColour(lane: TimelineLane, k: ColorMarkerStep, ev: MouseEvent) {
    const r = (ev.currentTarget as Element).getBoundingClientRect();
    const leftHalf = !!k.arrive && ev.clientX < r.left + r.width / 2;
    setSel({ type: 'step', laneId: lane.id, stepId: k.id });
    const own: MenuEntry = { swatches: k.arrive ? 'From its beat (right half)' : 'Colour', current: k.color, pick: (c: StageColor) => { useClickColour(c); setColorMarkerColor(tid(), lane.id, k.id, c.rgb); } };
    const arrive: MenuEntry = { swatches: k.arrive ? 'The fade arrives at (left half)' : 'Split it: the fade before it arrives at', ...(k.arrive ? { current: k.arrive } : {}), pick: (c: StageColor) => setKeyArrive(tid(), lane.id, k.id, c.rgb) };
    const entries: MenuEntry[] = leftHalf ? [arrive, own] : [own, arrive];
    if (k.arrive) entries.push({ sep: true }, { label: 'Join the halves', hint: 'one colour', run: () => setKeyArrive(tid(), lane.id, k.id, null) });
    const rr = rootEl?.getBoundingClientRect();
    const W = rr?.width ?? 1000; const H = rr?.height ?? 700;
    setCtxMenu({ title: `${keyName(k)} at ${barBeat(k.position, sig())}`, entries, x: Math.max(8, Math.min(r.left - (rr?.left ?? 0), W - 300)), y: Math.max(8, Math.min(r.bottom + 6 - (rr?.top ?? 0), H - 220)) });
  }
  /** Open the Cut / Fade chooser under the mark between two keyframes. The
   *  outgoing keyframe is selected too: the transition is stored on it, and
   *  the detail panel shows the same choice. */
  function openTransition(lane: TimelineLane, from: ColorMarkerStep, mark: Element) {
    const r = mark.getBoundingClientRect();
    const rr = rootEl?.getBoundingClientRect();
    setChooser(null);
    setSel({ type: 'step', laneId: lane.id, stepId: from.id });
    const cur = transPop();
    if (cur && cur.stepId === from.id) { setTransPop(null); return; }
    setTransPop({ laneId: lane.id, stepId: from.id, x: r.left + r.width / 2 - (rr?.left ?? 0), y: r.bottom + 6 - (rr?.top ?? 0) });
  }
  const draggingKeyframe = () => {
    const p = dragPreview();
    return !!p && !!timeline()?.lanes.some((l) => l.steps.some((x) => x.id === p.stepId && x.kind === 'colorMarker'));
  };

  /** A lane group's header row (laneGroups.ts): the fold, the name, how many
   *  lanes, and an M / S chip when every lane in it is muted / soloed. A click
   *  selects every lane in it; folded, its track shows each light's colours
   *  as a thin strip, so a folded Lighting group still says what the rig does. */
  function groupHeader(key: string): JSX.Element {
    const members = createMemo(() => { const t = timeline(); return t ? lanesInGroup(t.lanes, key) : []; });
    const ids = () => members().map((l) => l.id);
    const folded = () => isFolded(key);
    const n = () => members().length;
    const every = (f: (l: TimelineLane) => boolean) => n() > 0 && members().every(f);
    const name = groupName(key);
    return (
      <div
        class="tl2-row tl2-grp"
        data-group={key}
        data-group-lanes={ids().join(' ')}
        data-folded={folded() ? 'true' : undefined}
        data-depth={depthOf(`group:${key}`) || undefined}
        aria-selected={every((l) => selLaneIds().has(l.id))}
        data-silent={timeline() && every((l) => !laneAudible(timeline()!, l)) ? 'true' : undefined}
        data-droptarget={laneDrag()?.target?.key === key ? 'true' : undefined}
      >
        <div
          class="tl2-h"
          style={{ '--tl2-depth': depthOf(`group:${key}`) } as JSX.CSSProperties}
          data-info={`${name}: ${n()} lane${n() === 1 ? '' : 's'}. Click to select them all; the arrow ${folded() ? 'opens' : 'folds'} the group; right-click to mute, solo or move it.`}
          onClick={(e) => onGroupHeadClick(e, key)}
        >
          <button
            type="button"
            class="tl2-fold"
            aria-expanded={!folded()}
            aria-label={`${folded() ? 'Open' : 'Fold'} ${name}`}
            onClick={(e) => { e.stopPropagation(); setFolded(key, !folded()); }}
          >{folded() ? ICON.right : ICON.down}</button>
          <Show when={key === LIGHTING}>{ICON.lights}</Show>
          <div class="tl2-nm">
            <Show
              when={renaming() === key}
              fallback={<b onDblClick={(e) => { if (groupOfKey(key)) { e.stopPropagation(); setRenaming(key); } }}>{name}</b>}
            >
              <input
                class="tl2-grpname"
                value={name}
                aria-label="Group name"
                ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') { e.currentTarget.value = name; e.currentTarget.blur(); }
                }}
                onBlur={(e) => { setRenaming(null); renameGroup(groupOfKey(key), e.currentTarget.value); }}
              />
            </Show>
            <span>{n()} lane{n() === 1 ? '' : 's'}</span>
          </div>
          {/* Stream (2026-11-02): the Lighting header holds the toggle, beside the lanes it redraws. In the toolbar it
              wrapped the toolbar at 1400 px (timeline_length_in_display), which has no width to spare. */}
          <Show when={key === LIGHTING}>
            <button
              type="button"
              class="tl2-stream-btn"
              aria-pressed={stream()}
              title="Stream: the colour each light shows"
              aria-label="Stream: the colour each light shows"
              data-info="Stream: each light drawn as the colour it shows from start to end, black where it is off. Turn it off to see and edit the keyframes."
              onClick={(e) => { e.stopPropagation(); setStream((v) => !v); }}
            >{ICON.stream}</button>
          </Show>
          <Show when={every((l) => !!l.mute) || every((l) => !!l.solo)}>
            <span class="tl2-ms">
              <Show when={every((l) => !!l.mute)}>
                <button type="button" data-k="m" title="All muted: click to unmute" aria-label={`Unmute ${name}`} onClick={(e) => { e.stopPropagation(); toggleLaneFlag(ids(), 'mute'); }}>M</button>
              </Show>
              <Show when={every((l) => !!l.solo)}>
                <button type="button" data-k="s" title="All soloed: click to unsolo" aria-label={`Unsolo ${name}`} onClick={(e) => { e.stopPropagation(); toggleLaneFlag(ids(), 'solo'); }}>S</button>
              </Show>
            </span>
          </Show>
        </div>
        <div class="tl2-c tl2-grpc" style={gridStyle()}>
          <Show when={folded()}>
            <div class="tl2-mini" data-stream-view={stream() ? 'true' : undefined}>
              <For each={members().filter((l) => laneRole(l) === 'lights')}>
                {(l) => {
                  const segs = createMemo(() => segmentsOf(keyframesOf(l), isMasterLane(l) ? releasesOf(l).map((r) => r.position) : []));
                  return (
                    <div class="tl2-ministrip" data-lane-strip={l.id} data-off={lightIsOff(l) && !stream() ? 'true' : undefined}>
                      <Show when={stream()}>
                        {(() => {
                          const tiles = createMemo(() => streamTiles(streamRunsOf(l), ppb()));
                          return <For each={tiles()}>{(tl) => <i style={{ left: `${tl.left}px`, width: `${tl.width}px`, background: tl.background }} />}</For>;
                        })()}
                      </Show>
                      <For each={lightIsOff(l) || stream() ? [] : segs().filter((sg) => sg.from.edge !== 'first')}>
                        {(sg) => {
                          const paint = segmentPaint(sg);
                          return paint.off ? null : <i style={{ left: `${sg.start * ppb()}px`, width: `${Math.max(0, (sg.end - sg.start) * ppb())}px`, background: paint.background }} />;
                        }}
                      </For>
                      <For each={lightIsOff(l) || stream() ? [] : l.steps.filter(isPatternStep)}>
                        {(b) => <i style={{ left: `${b.position * ppb()}px`, width: `${b.durationBars * ppb()}px`, ...patternPaint(b) }} />}
                      </For>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>
        </div>
      </div>
    );
  }
  function onGroupHeadClick(ev: MouseEvent, key: string) {
    const t = timeline();
    if (!t) return;
    const ids = lanesInGroup(t.lanes, key).map((l) => l.id);
    if (ids.length === 0) return;
    laneAnchor = ids[0]!;
    selectLanes(ev.shiftKey || addKey(ev) ? [...selLaneIds(), ...ids] : ids);
  }
  /** Right-click (or long-press) a group's header or its folded track. */
  function groupMenu(key: string): Menu | null {
    const t = timeline();
    if (!t) return null;
    const lanes = lanesInGroup(t.lanes, key);
    const ids = lanes.map((l) => l.id);
    const n = ids.length;
    if (n === 0) return null;
    selectLanes(ids);
    const word = `${n} lane${n === 1 ? '' : 's'}`;
    const gid = `group:${key}`;
    const g = groupOfKey(key);
    const own = key === LIGHTING ? lanes.filter((l) => lightGroupOf(l) === '') : lanes;
    const extra: MenuEntry[] = [];
    if (!own.some(isMasterLane)) extra.push({ label: 'Add a master', hint: `takes over ${g || 'these'} lights`, run: () => addMasterTo(g) });
    if (g) {
      extra.push({ label: 'Rename the group', run: () => setRenaming(key) });
      extra.push({ label: 'Back to Lighting', hint: 'ungroup', run: () => moveToGroup(ids, '') });
    }
    return { title: groupName(key), entries: [
      { label: isFolded(key) ? 'Open the group' : 'Fold the group', run: () => setFolded(key, !isFolded(key)) },
      ...extra,
      { sep: true },
      { label: `Mute ${word}`, checked: lanes.every((l) => l.mute), ...(key.startsWith(LIGHTING) ? { hint: 'Lights off' } : {}), run: () => toggleLaneFlag(ids, 'mute') },
      { label: `Solo ${word}`, checked: lanes.every((l) => l.solo), run: () => toggleLaneFlag(ids, 'solo') },
      { sep: true },
      { label: 'Select all their changes', disabled: lanes.every((l) => l.steps.length === 0), run: () => selectChangesOf(ids) },
      { label: 'Move the group up', disabled: !canMove([gid], -1), run: () => moveIds([gid], -1) },
      { label: 'Move the group down', disabled: !canMove([gid], 1), run: () => moveIds([gid], 1) },
      { sep: true },
      { label: `Clear ${word}`, danger: true, disabled: lanes.every((l) => l.steps.length === 0), run: () => clearLanes(ids) },
      { label: `Delete ${word}`, danger: true, run: () => removeLanes(ids) },
    ] };
  }

  /** The Stream view's runs for one lights lane (`timeline/colourStream.ts`), with every lane's steps AS THEY PLAY
   *  (`playedSteps`: the lead-in applied, pattern blocks written out; a timeline that starts dark puts each light's Off
   *  on bar 0), so the stream is what the rig is sent, not a redrawing of the keyframes. Sampled every 4 px. */
  function streamRunsOf(lane: TimelineLane): StreamRun[] {
    const t = timeline();
    if (!t) return [];
    const total = totalBars();
    const step = 4 / Math.max(1e-6, ppb());
    const dark = timelineStartsDark(t);
    const played = (l: TimelineLane): TimelineLane => ({
      ...l, steps: dark && l.laneKind === 'dmx-fixture' ? darkStartSteps(l.id, playedSteps(t, l)) : playedSteps(t, l),
    });
    if (isMasterLane(lane)) return masterStream(played(lane), laneAudible(t, lane), total, step);
    const keptOut = fixtures().find((f) => f.id === lane.fixtureRef?.fixtureId)?.passthrough?.['keepOutOfMasters'] === true;
    const masters = mastersReaching(t.lanes.filter(isMasterLane), lane, keptOut).map((m) => ({ lane: played(m), audible: laneAudible(t, m) }));
    return lightStream({ lane: played(lane), audible: laneAudible(t, lane) }, masters, total, step);
  }
  /** A lights lane in the Stream view: one strip, the colour its light shows at every moment (black = off), with a
   *  dotted line where nothing drives it. Takes no pointer: clicks reach the lane as in the keyframe view. */
  function streamLayer(lane: TimelineLane): JSX.Element {
    const tiles = createMemo(() => streamTiles(streamRunsOf(lane), ppb()));
    return (
      <div class="tl2-stream" data-stream={lane.id} data-master={isMasterLane(lane) ? 'true' : undefined} style={{ width: `${totalBars() * ppb()}px` }}>
        <For each={tiles()}>
          {(tl) => <i style={{ left: `${tl.left}px`, width: `${tl.width}px`, background: tl.background }} />}
        </For>
      </div>
    );
  }

  /** A lights lane's keyframes: the stretches between them (painted with
   *  what they hold or how they fade), a mark on each stretch that has a next
   *  keyframe (click it for Cut or Fade), and a diamond per keyframe (drag to
   *  move, click to select, Delete removes). A click anywhere else on the lane
   *  adds a keyframe (`onCellClick`). */
  function keyframeLayer(lane: TimelineLane): JSX.Element {
    // Unchanged keyframes and segments keep their OBJECT, and a list that did not change is not a change: `For`
    // matches rows by object, so new objects on every drag move rebuilt every keyframe of every lights lane (~1500
    // on Danger Zone; the pro-grade review 2026-10-13). Now a drag touches the lane it is on.
    const sameList = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((x, i) => x === b[i]);
    let prevK = new Map<string, Keyframe>();
    const kfs = createMemo(() => {
      const next = new Map<string, Keyframe>();
      const out = keyframesOf(lane).map((k) => {
        const key = `${k.step.id}|${k.ghost ? 'g' : ''}`;
        const old = prevK.get(key);
        const keep = old && old.step === k.step && old.position === k.position && !!old.away === !!k.away && !!old.ghost === !!k.ghost ? old : k;
        next.set(key, keep);
        return keep;
      });
      prevK = next;
      return out;
    }, [] as Keyframe[], { equals: sameList });
    let prevS = new Map<string, Segment>();
    const segs = createMemo(() => {
      const next = new Map<string, Segment>();
      const out = segmentsOf(kfs(), isMasterLane(lane) || laneRole(lane) === 'opvcolour' ? releasesOf(lane).map((r) => r.position) : []).map((sg) => {
        const key = `${sg.from.step.id}|${sg.to?.step.id ?? ''}|${sg.start}|${sg.end}`;
        const old = prevS.get(key);
        const keep = old && old.from === sg.from && old.to === sg.to ? old : sg;
        next.set(key, keep);
        return keep;
      });
      prevS = next;
      return out;
    }, [] as Segment[], { equals: sameList });
    /** Each keyframe's next live neighbour, worked out once per change (it was a scan per keyframe). */
    const nextOf = createMemo(() => {
      const m = new Map<Keyframe, Keyframe | undefined>();
      const live = kfs().filter((x) => !x.away);
      kfs().forEach((k) => { const ix = live.indexOf(k); m.set(k, ix >= 0 ? live[ix + 1] : live.find((x) => x.position > k.position)); });
      return m;
    });
    const selected = (id: string) => isStepSel(id);
    return (
      <>
        <Show when={lightIsOff(lane)}>
          <div class="tl2-kfseg" data-off="true" data-muted="true" style={{ left: '0px', width: `${totalBars() * ppb()}px` }} />
        </Show>
        <For each={lightIsOff(lane) ? [] : segs().filter((sg) => sg.from.edge !== 'first')}>
          {(sg) => {
            const paint = segmentPaint(sg);
            return (
              <div
                class="tl2-kfseg"
                data-off={paint.off ? 'true' : undefined}
                data-open={transPop()?.stepId === sg.from.step.id && sg.to ? 'true' : undefined}
                style={{ left: `${sg.start * ppb()}px`, width: `${Math.max(0, (sg.end - sg.start) * ppb())}px`, background: paint.background }}
              />
            );
          }}
        </For>
        <For each={segs().filter((sg) => sg.to && (sg.end - sg.start) * ppb() >= 34 && !sg.from.ghost && !sg.to.ghost && !sg.from.edge && !sg.to.edge)}>
          {(sg) => {
            const from = sg.from.step; const to = sg.to!.step;
            const cut = transitionOf(from) === 'cut';
            const words = `${colourName(from.color, from.label)} ${cut ? 'cuts' : 'fades'} to ${to.arrive ? colourName(to.arrive) : colourName(to.color, to.label)} at ${barBeat(sg.end, sig())}${to.arrive ? `, then ${colourName(to.color, to.label)}` : ''}`;
            return (
              <button
                type="button"
                class="tl2-kfh"
                data-step-id={from.id}
                data-t={cut ? 'cut' : 'fade'}
                aria-expanded={transPop()?.stepId === from.id}
                aria-label={`${words}. Choose Cut or Fade.`}
                title={cut ? 'Cut' : 'Fade'}
                style={{ left: `${((sg.start + sg.end) / 2) * ppb()}px` }}
                data-info={`${words}. Click to choose Cut or Fade.`}
                onClick={(e) => openTransition(lane, from, e.currentTarget)}
              >{cut ? ICON.cut : ICON.fade}</button>
            );
          }}
        </For>
        <For each={kfs().filter((k) => !k.edge)}>
          {(k) => {
            const name = keyName(k.step);
            const room = () => { const next = nextOf().get(k); return ((next ? next.position : totalBars()) - k.position) * ppb() - 12; };
            return (
              <>
                <Show when={!k.ghost && !k.away && room() >= 26}>
                  <span class="tl2-kfn" style={{ left: `${Math.max(7, k.position * ppb()) + 9}px`, 'max-width': `${room()}px` }}>{name}</span>
                </Show>
                <button
                  type="button"
                  class="tl2-kf"
                  data-step-id={k.step.id}
                  data-ghost={k.ghost ? 'true' : undefined}
                  data-away={k.away ? 'true' : undefined}
                  aria-selected={!k.ghost && selected(k.step.id)}
                  aria-label={`${name} keyframe at ${barBeat(k.position, sig())}`}
                  data-split={k.step.arrive ? 'true' : undefined}
                  onDblClick={(e) => { if (!k.ghost && !k.away) openKeyColour(lane, k.step, e); }}
                  // Never half under the lane's left edge: a keyframe on 1.1
                  // sits just inside it, whole and grabbable.
                  style={{ left: `${Math.max(7, k.position * ppb())}px` }}
                  data-info={`${name} at ${barBeat(k.position, sig())}, ${thenText(lane, k.step, sig()).toLowerCase()}. Drag to move it, Alt-drag to copy it; double-click for its colour (and to split it); Delete removes it.`}
                  onPointerDown={(e) => { if (!k.ghost) onClipPointerDown(e, lane, k.step, 'move'); }}
                >
                  <i data-off={isOff(k.step.color) && !k.step.arrive ? 'true' : undefined} style={{ background: k.step.arrive ? splitCss(k.step.arrive, k.step.color) : isOff(k.step.color) ? undefined : rgbCss(k.step.color) }} />
                </button>
              </>
            );
          }}
        </For>
        {isMasterLane(lane) ? masterLayer(lane) : takeoverOverlay(lane)}
      </>
    );
  }

  // ── a Guitar lane (DEC-244) ──
  //
  // Operator, 2026-10-02: "is there a better way than a block for guitar
  // presets?" - and chose "a Guitar lane". A preset change is a STATE, not an
  // action: from its beat the guitar sounds like "The Pretender" until the next
  // change. So each preset change is a flag on its beat and the stretch to the
  // next one carries the preset's whole name (a one-beat block cut it to "The
  // Pretend"). A snapshot is a change inside a preset: a tick with its name.
  type GuitarMark = { step: ButtonRefStep; position: number; snapshot: boolean; ghost?: boolean; away?: boolean };
  /** An OPVisual palette lane (2026-10-28): each palette a band from its change to the next, in OPVisual's own swatches
   *  and name, with a flag to drag. A palette lasts until the next change, as a guitar preset does. */
  function opvPaletteLayer(lane: TimelineLane): JSX.Element {
    const marks = createMemo(() => previewLane(lane).steps.filter((x) => opvPaletteOf(x) !== null).sort((a, b) => a.position - b.position));
    // OPVisual's palettes come with its state: keep reading it while the lane is drawn (the socket alone may be elsewhere).
    const target = autoPickOpVisualBridge() ?? undefined;
    onCleanup(watchOpvListing(target));
    const swatchOf = (id: string) => opvLive(target)?.palettes.find((x) => x.id === id)?.swatch ?? [];
    const real = (id: string) => lane.steps.find((x) => x.id === id);
    return (
      <For each={marks()}>
        {(m, i) => {
          const id = () => opvPaletteOf(m) ?? '';
          const name = () => (m as { label?: string }).label ?? id();
          const end = () => marks()[i() + 1]?.position ?? totalBars();
          const c = () => swatchOf(id())[2] ?? '#8b9099';
          return (
            <>
              <div class="tl2-gband tl2-pband" style={{ left: `${m.position * ppb()}px`, width: `${Math.max(0, (end() - m.position) * ppb())}px`, '--c': c() } as JSX.CSSProperties}>
                <span class="tl2-psw" aria-hidden="true"><For each={swatchOf(id())}>{(x) => <i style={{ background: x }} />}</For></span>
                <b>{name()}</b>
              </div>
              <button
                type="button"
                class="tl2-gflag"
                data-step-id={m.id}
                data-opv-palette={id()}
                aria-selected={isStepSel(m.id)}
                aria-label={`Palette ${name()} at ${barBeat(m.position, sig())}`}
                style={{ left: `${m.position * ppb()}px`, '--c': c() } as JSX.CSSProperties}
                data-info={`OPVisual's visualisers draw with ${name()} from ${barBeat(m.position, sig())} until the next palette change. Drag to move it; Delete removes it; click it to change the palette.`}
                onPointerDown={(e) => { const s = real(m.id); if (s) onClipPointerDown(e, lane, s, 'move'); }}
              />
            </>
          );
        }}
      </For>
    );
  }
  function guitarMarksOf(lane: TimelineLane): GuitarMark[] {
    const p = dragPreview();
    const here = p && p.laneId === lane.id ? p : null;
    const isSnap = (s: ButtonRefStep) => controlById(s.buttonId)?.kind === 'snapshotChange';
    const out: GuitarMark[] = [];
    for (const s of lane.steps) {
      if (s.kind !== 'buttonRef') continue;
      if (p && p.stepId === s.id) {
        out.push(here ? { step: s, position: here.position, snapshot: isSnap(s) } : { step: s, position: s.position, snapshot: isSnap(s), away: true });
        continue;
      }
      out.push({ step: s, position: s.position + shiftOf(s.id), snapshot: isSnap(s) });
    }
    if (here && !lane.steps.some((x) => x.id === here.stepId)) {
      const moving = timeline()?.lanes.flatMap((l) => l.steps).find((x) => x.id === here.stepId);
      if (moving && moving.kind === 'buttonRef') out.push({ step: moving, position: here.position, snapshot: isSnap(moving), ghost: true });
    }
    return out.sort((a, b) => a.position - b.position);
  }
  /** A guitar press's name and its slot ("01A"), from the control it fires. */
  function guitarText(s: ButtonRefStep): { name: string; slot: string; missing: boolean } {
    const it = controlById(s.buttonId);
    if (!it) return { name: s.labelHint ?? 'Missing preset', slot: '', missing: true };
    const t = itemText(it);
    const slot = it.kind === 'presetChange' ? helixSlotLabel(it.program) : it.kind === 'snapshotChange' ? `Snapshot ${it.snapshot}` : t.meta;
    return { name: t.label, slot, missing: false };
  }
  function guitarLayer(lane: TimelineLane): JSX.Element {
    const marks = createMemo(() => guitarMarksOf(lane));
    const bands = createMemo(() => {
      const presets = marks().filter((m) => !m.snapshot && !m.away);
      return presets.map((m, i) => ({ m, start: m.position, end: presets[i + 1]?.position ?? totalBars() }));
    });
    const selected = (id: string) => isStepSel(id);
    const hue = (s: ButtonRefStep) => {
      const it = controlById(s.buttonId);
      const h = it ? hueOf(it) : 'none';
      return h !== 'none' ? HUE_VALUES[h] : '#8b9099';
    };
    return (
      <>
        <For each={bands()}>
          {(b) => {
            const txt = guitarText(b.m.step);
            return (
              <div
                class="tl2-gband"
                data-ghost={b.m.ghost ? 'true' : undefined}
                data-missing={txt.missing ? 'true' : undefined}
                style={{ left: `${b.start * ppb()}px`, width: `${Math.max(0, (b.end - b.start) * ppb())}px`, '--c': hue(b.m.step) } as JSX.CSSProperties}
              >
                <b>{txt.name}</b><em>{txt.slot}</em>
              </div>
            );
          }}
        </For>
        <For each={marks()}>
          {(m) => {
            const txt = guitarText(m.step);
            const words = `${txt.name}${txt.slot ? ` (${txt.slot})` : ''} at ${barBeat(m.position, sig())}`;
            return (
              <button
                type="button"
                class={m.snapshot ? 'tl2-gsnap' : 'tl2-gflag'}
                data-step-id={m.step.id}
                data-ghost={m.ghost ? 'true' : undefined}
                data-away={m.away ? 'true' : undefined}
                data-wont={warnings().get(m.step.id) ? 'true' : undefined}
                data-glow={hoverItemId() === m.step.buttonId ? 'true' : undefined}
                aria-selected={!m.ghost && selected(m.step.id)}
                aria-label={words}
                title={warnings().get(m.step.id) ? `Won't play: ${warnings().get(m.step.id)}` : undefined}
                style={{ left: `${m.position * ppb()}px`, '--c': hue(m.step) } as JSX.CSSProperties}
                data-info={`${words}${m.snapshot ? '' : ': the guitar sounds like this until the next preset'}. Drag to move it; Delete removes it; right-click for more.`}
                onPointerEnter={() => setHoverItemId(m.step.buttonId)}
                onPointerLeave={() => setHoverItemId((h) => (h === m.step.buttonId ? null : h))}
                onPointerDown={(e) => { if (!m.ghost) onClipPointerDown(e, lane, m.step, 'move'); }}
              >
                <Show when={m.snapshot}><span>{txt.name}</span></Show>
              </button>
            );
          }}
        </For>
      </>
    );
  }

  /** A value from a height in an automation lane (top = 127), inside the curve's inset. */
  function autoValueFromY(y: number, h: number): number {
    const span = Math.max(1, h - 2 * AP_PAD);
    return Math.max(0, Math.min(127, Math.round((1 - (y - AP_PAD) / span) * 127)));
  }
  /** A point being dragged: drawn here until it is let go, then written in one edit. */
  const [apDrag, setApDrag] = createSignal<{ stepId: string; position: number; value: number } | null>(null);
  function onPointPointerDown(ev: PointerEvent, lane: TimelineLane, pt: AutomationPointStep) {
    lastPointer = ev.pointerType;
    if (ev.button !== 0) return;
    const before = selRefs();
    // Shift or Ctrl/Cmd and let go without moving adds it to (or takes it out of) the selection, as on every other
    // change; Shift and DRAG moves only its value. So the choice is made on letting go.
    const toggling = ev.shiftKey || addKey(ev);
    if (!toggling) setSel({ type: 'step', laneId: lane.id, stepId: pt.id });
    const cell = (ev.currentTarget as Element).closest('[data-tl2-cell]');
    const span = Math.max(1, (cell?.getBoundingClientRect().height ?? laneH(lane.id)) - 2 * AP_PAD);
    const x0 = ev.clientX; const y0 = ev.clientY;
    let on = false;
    // Esc, a second finger, Back and a pointercancel all CANCEL it (audit 2026-11-03 D-4): it used to commit on a
    // cancel and ignore the others. `abortDrag` is the editor's one cancel hook (the block drags already use it).
    const pid = ev.pointerId;
    let cancelled = false;
    const abort = () => { cancelled = true; up(); };
    abortDrag = abort;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      if (!on && Math.hypot(e.clientX - x0, e.clientY - y0) < DRAG_THRESHOLD_PX) return;
      on = true;
      cancelLongPress();
      // Shift keeps it on its beat: only the value moves.
      const position = e.shiftKey ? pt.position : Math.max(0, Math.min(totalBars(), snapRound(pt.position + (e.clientX - x0) / ppb(), freeKey(e))));
      const value = Math.max(0, Math.min(127, Math.round(pt.value - ((e.clientY - y0) / span) * 127)));
      setApDrag({ stepId: pt.id, position, value });
      setInfo(`${lane.name}: ${autoText(lane, value)} at ${barBeat(position, sig())}`);
    };
    const up = (ue?: Event) => {
      if (ue instanceof PointerEvent && ue.pointerId !== pid) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (abortDrag === abort) abortDrag = null;
      const d = apDrag();
      setApDrag(null);
      if (cancelled || ue?.type === 'pointercancel') { if (on) suppressClick = true; return; }
      if (!on && toggling) { selectSteps(isStepSel(pt.id) ? before.filter((r) => r.stepId !== pt.id) : [...before, { laneId: lane.id, stepId: pt.id }]); return; }
      if (!on || !d) return;
      suppressClick = true;
      setSel({ type: 'step', laneId: lane.id, stepId: pt.id });
      setAutoPoint(lane.id, pt.id, { position: d.position, value: d.value });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  /** An automation lane's curve: a line through its points (a ramp, or a hold then a jump), shaded under, and a
   *  handle on each point. Drag a handle to move it (Shift: only its value); double-click one to delete it. */
  function automationLayer(lane: TimelineLane): JSX.Element {
    const pts = createMemo(() => {
      const d = apDrag();
      const all = autoPoints(lane).map((x) => (d && d.stepId === x.id ? { ...x, position: d.position, value: d.value } : x));
      return all.sort((a, b) => a.position - b.position);
    });
    const W = () => Math.max(1, totalBars() * ppb());
    const mt = () => motionTarget(lane.motion?.target);
    /** The line as it plays, in bars and 0-127. A control's lane: through its points, each stretch in its shape, flat
     *  before the first and after the last. An OP-Motion lane: what the device plays (`motionPlayedPath`), so a
     *  section's start, a cut at a section's end and the glide-out are drawn where they happen; nothing before the
     *  first point, where the effect is off. */
    const path = createMemo((): Array<{ bar: number; v: number }> => {
      const p = pts();
      if (p.length === 0) return [];
      if (lane.motion) {
        const t = timeline();
        return t ? motionPlayedPath(t, { ...lane, steps: p }, totalBars()) : [];
      }
      const out: Array<{ bar: number; v: number }> = [{ bar: 0, v: p[0]!.value }];
      p.forEach((x, i) => {
        const prev = p[i - 1];
        if (prev && (prev.curve ?? 'linear') === 'hold') out.push({ bar: x.position, v: prev.value });
        else if (prev && (prev.curve ?? 'linear') !== 'linear' && x.position > prev.position) {
          const n = Math.max(6, Math.ceil((x.position - prev.position) * 24));
          for (let k = 1; k < n; k++) out.push({ bar: prev.position + ((x.position - prev.position) * k) / n, v: prev.value + (x.value - prev.value) * shapeAt(prev.curve, k / n) });
        }
        out.push({ bar: x.position, v: x.value });
      });
      out.push({ bar: totalBars(), v: p[p.length - 1]!.value });
      return out;
    });
    const X = (bar: number) => (bar * ppb()).toFixed(1);
    const Y = (v: number) => (127 - v).toFixed(2);
    const line = () => path().map((q, i) => `${i ? 'L' : 'M'}${X(q.bar)} ${Y(q.v)}`).join(' ');
    // Shaded down to where the target is off: the middle of Tone, the bottom of the rest.
    const base = () => Y(mt() ? motionOff127(mt()!) : 0);
    const fill = () => {
      const q = path();
      return q.length === 0 ? '' : `${line()} L${X(q[q.length - 1]!.bar)} ${base()} L${X(q[0]!.bar)} ${base()} Z`;
    };
    return (
      <>
        <Show when={pts().length > 0}>
          <svg class="tl2-auto" viewBox={`0 0 ${W().toFixed(1)} 127`} preserveAspectRatio="none" style={{ width: `${W()}px`, top: `${AP_PAD}px`, height: `calc(100% - ${2 * AP_PAD}px)` }} aria-hidden="true">
            <Show when={mt()?.bipolar}>
              <path class="tl2-automid" d={`M0 ${Y(64)} L${W().toFixed(1)} ${Y(64)}`} vector-effect="non-scaling-stroke" />
            </Show>
            <path class="tl2-autofill" d={fill()} />
            <path class="tl2-autoline" d={line()} vector-effect="non-scaling-stroke" />
          </svg>
        </Show>
        <For each={pts()}>
          {(x) => (
            <button
              type="button"
              class="tl2-ap"
              data-step-id={x.id}
              data-hold={x.curve === 'hold' ? 'true' : undefined}
              data-wont={warnings().get(x.id) ? 'true' : undefined}
              aria-selected={isStepSel(x.id)}
              aria-label={`${autoText(lane, x.value)} at ${barBeat(x.position, sig())}`}
              style={{ left: `${x.position * ppb()}px`, top: `calc(${AP_PAD}px + ${(1 - x.value / 127).toFixed(4)} * (100% - ${2 * AP_PAD}px))` }}
              data-info={tip(`${lane.name}: ${autoText(lane, x.value)} at ${barBeat(x.position, sig())}, ${x.curve === 'hold' ? 'held, then a jump to the next point' : `${curveLabel(x.curve).toLowerCase()} to the next point`}. Drag to move it (Shift: only its value); double-click or Delete removes it; right-click for its shape.`,
                `${lane.name}: ${autoText(lane, x.value)} at ${barBeat(x.position, sig())}. Drag to move it; long-press for its shape, or to delete it.`)}
              onPointerDown={(e) => onPointPointerDown(e, lane, autoPoints(lane).find((y) => y.id === x.id) ?? x)}
              onDblClick={(e) => { e.stopPropagation(); removeStep(tid(), lane.id, x.id); setSel({ type: 'lane', id: lane.id }); }}
            />
          )}
        </For>
      </>
    );
  }

  function clipBody(s: TimelineStep): JSX.Element {
    if (opvActionId(s)) {
      const id = opvActionId(s)!;
      const label = (s as { label?: string }).label;
      return <span>{label && label !== stepTitle(s) ? label : ''}</span>;
    }
    if (s.kind === 'buttonRef') {
      const it = controlById(s.buttonId);
      return <span>{it ? itemText(it).meta : 'Not on this project\'s canvas'}</span>;
    }
    if (s.kind === 'targetValue') return <span>Song setting</span>;
    if (s.kind === 'pattern') return <span />;
    const hue = hueCueRef(s);
    if (hue) {
      const sw = hueSwatchCss(findHueScene(hue)?.scene.swatch);
      return <span class="tl2-hue-cb"><i class="tl2-hue-sw" style={{ background: sw ?? '#2a2b2e' }} />Hue scene</span>;
    }
    if (s.kind === 'hitBlock') {
      const lights = (l: HitBlockStep['look']) => (l.allLights ? 'every light' : `${l.lights.length} light${l.lights.length === 1 ? '' : 's'}`);
      const f = hitBlockFill(s);
      return <span>{lights(s.look)}{f ? `, fill on ${lights(f.look)}` : ''}</span>;
    }
    return <span>{barsText(stepDuration(s))}</span>;
  }

  const roleIcon = (l: TimelineLane) => {
    const r = laneRole(l);
    return r === 'lights' || r === 'opvcolour' || r === 'opvpalette' ? ICON.lights : r === 'guitar' ? ICON.guitar : r === 'markers' ? ICON.marker : r === 'automation' ? ICON.auto : r === 'drums' ? ICON.drum : ICON.controls;
  };
  const laneDrives = (l: TimelineLane): string => {
    if (l.laneKind === 'dmx-fixture') return 'One light';
    if (l.laneKind === 'dmx-rig') {
      if (l.allLights) return 'Every light, in every group';
      const t = timeline();
      if (!t || lightGroupsOf(t.lanes).length === 0) return 'All lights';
      const n = t.lanes.filter((x) => x.laneKind === 'dmx-fixture' && masterReaches(l, x)).length;
      return `Master of ${n} light${n === 1 ? '' : 's'}`;
    }
    if (laneRole(l) === 'guitar') return 'Guitar presets and snapshots';
    if (laneRole(l) === 'drums') {
      const names = [...new Set(l.steps.flatMap((x) => (isHitBlockStep(x) || isHitRouteStep(x) ? [sourceLabel(x.source)] : [])))];
      return names.length ? `${names.join(' and ')} light${names.length === 1 ? 's' : ''} the rig` : 'Drums light the rig';
    }
    if (laneRole(l) === 'automation') {
      if (l.motion) return `OP-Motion: ${motionDrivesText(l)}`;
      if (l.vizKnob) return `Turns ${vizKnobNames(l.vizKnob.macro)}${l.vizKnob.layer ? ` on L${l.vizKnob.layer}` : ' on the newest visualiser on air'}`;
      if (l.opvControl) return opvControlDef(l.opvControl)?.does ?? 'An OPVisual control this version does not know';
      const it = l.control ? canvasItemById(l.control.itemId) : undefined;
      return it ? `Moves ${autoTargetLabel(it, l.control?.axis)}` : 'Its control is no longer on your canvas';
    }
    if (laneRole(l) === 'markers') return 'Notes, not sent';
    if (laneRole(l) === 'opvcolour') return "OPVisual's show colour";
    if (laneRole(l) === 'opvpalette') return "OPVisual's show palette";
    if (laneRole(l) === 'opvisual') return "Fires OPVisual's columns and cells, on every PC running it";
    return 'Controls on your canvas';
  };

  // ── the page ──
  return (
    <Portal>
      <div
        ref={rootEl}
        class="tl2"
        role="dialog"
        data-back-surface
        aria-modal="true"
        aria-label="Timeline"
        tabindex={-1}
        data-docked={docked ? 'true' : undefined}
        style={{ '--tl2-browser': `${browserW()}px` } as JSX.CSSProperties}
        data-picking={picked() ? 'true' : undefined}
        data-aside={canvasPick() || recTake() ? 'true' : undefined}
        onKeyDown={onKey}
        onPointerOver={onPointerOver}
        onContextMenu={onContextMenu}
        onPointerDown={(e) => {
          const t = e.target as HTMLElement;
          if (!t.closest('.tl2-ctx')) setCtxMenu(null);
          if (!t.closest('input, textarea, select')) rootEl?.focus({ preventScroll: true });
          if (!t.closest('.tl2-pop, .tl2-menu')) { if (!t.closest('[data-tl2-cell]')) setChooser(null); }
          if (!t.closest('.tl2-tpop, .tl2-kfh')) setTransPop(null);
          if (!t.closest('.tl2-newwrap')) setNewMenu(false);
          if (!t.closest('.tl2-addwrap')) setAddLaneMenu(false);
        }}
      >
        {/* ───────── toolbar ───────── */}
        <header class="tl2-tb">
          {/* Docked (DEC-268): the wordmark in Navigate's place, the way back
              to Navigate's Timelines. */}
          <Show when={docked}>
            <button type="button" class="tl2-brand" title="Back to Navigate" aria-label="Back to Navigate"
              onClick={() => { closeTimelineEditor(); openNavigateAt('timelines'); }}>
              <img src="/logo/opbuttons-logo.png" alt="OPButtons" />
            </button>
          </Show>
          <div class="tl2-grp tl2-newwrap">
            <button type="button" class="tl2-txt" data-info="New: start a timeline from a blank slate." onClick={() => setNewMenu(!newMenu())}>
              {ICON.add}New
            </button>
            <Show when={newMenu()}>
              <div class="tl2-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => newTimeline('song')}>
                  <b>Song timeline</b><span>Sections you move through, launched from a pad.</span>
                </button>
                <button type="button" role="menuitem" onClick={() => newTimeline('action')}>
                  <b>Action timeline</b><span>A short run of changes fired from one button.</span>
                </button>
              </div>
            </Show>
          </div>
          <Show when={renamingTl() && timeline()}>
            <input
              class="tl2-pick-name"
              value={timeline()!.name}
              aria-label="Timeline name"
              data-info="Type the new name. Enter keeps it, Esc puts the old one back."
              ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); tlNameCancelled = true; e.currentTarget.blur(); }
              }}
              onBlur={(e) => endRenameTimeline(e.currentTarget.value)}
            />
          </Show>
          <label class="tl2-pick" hidden={renamingTl()} data-info="The timeline you are editing. This project's timelines are listed. The pencil beside it renames it.">
            <select
              value={tid()}
              onChange={(e) => { selectTimelineInEditor(e.currentTarget.value || null); queueMicrotask(() => rootEl?.focus({ preventScroll: true })); }}
              aria-label="Timeline"
              title={timeline()?.name ?? ''}
            >
              <Show when={!timeline()}><option value="">No timeline</option></Show>
              <For each={(['song', 'action', 'genre'] as TimelineTier[]).filter((tier) => projectTimelines().some((x) => x.tier === tier))}>
                {(tier) => (
                  <optgroup label={tier === 'song' ? 'Songs' : tier === 'action' ? 'Actions' : 'Genres'}>
                    <For each={projectTimelines().filter((x) => x.tier === tier)}>
                      {(x) => <option value={x.id}>{x.name}{tempoText(x) ? ` \u00b7 ${tempoText(x)} BPM` : ''}</option>}
                    </For>
                  </optgroup>
                )}
              </For>
              <Show when={timeline() && !projectTimelines().some((x) => x.id === tid())}>
                <option value={tid()}>{timeline()!.name} (another project)</option>
              </Show>
            </select>
          </label>
          <Show when={timeline()}>
            <div class="tl2-grp">
              <button
                type="button"
                class="tl2-rentl"
                title="Rename this timeline"
                aria-label="Rename this timeline"
                aria-pressed={renamingTl() ? 'true' : 'false'}
                data-info={`Rename "${timeline()!.name}" right here.`}
                onClick={() => (renamingTl() ? undefined : startRenameTimeline())}
              >{ICON.pencil}</button>
              <button
                type="button"
                class="tl2-deltl"
                title="Delete this timeline"
                aria-label="Delete this timeline"
                data-info={`Delete "${timeline()!.name}": asks first. Removed from every computer it is on.`}
                onClick={deleteTimelineNow}
              >{ICON.trash}</button>
            </div>
          </Show>
          <div class="tl2-grp">
            <button type="button" class="tl2-undo" disabled={!canUndoOrdered()} title="Undo (Ctrl+Z)" aria-label="Undo" data-info="Undo the last change: to the timeline or to its song (marks, tempo, clips)." onClick={undoNow}>{ICON.undo}</button>
            <button type="button" class="tl2-redo" disabled={!canRedoOrdered()} title="Redo (Ctrl+Y)" aria-label="Redo" data-info="Redo what Undo took back." onClick={redoNow}>{ICON.redo}</button>
          </div>
          <div class="tl2-grp">
            <button
              type="button"
              class="tl2-tx tl2-play"
              aria-pressed={playing() || armed()}
              data-armed={armed() ? 'true' : undefined}
              disabled={!timeline()}
              title="Play (Space)"
              data-info={!live() ? 'Play: no computer connected, so it plays on screen only. Space.'
                : liveWaits() ? "Play: starts now at Live's last tempo, and lines up with Live's bars when you press play in Live. Space."
                : 'Play: starts this timeline on OPBridge: on the next bar while Live plays, at its own tempo when Live is not open. Space.'}
              onClick={() => togglePlay()}
            >{ICON.play}</button>
            <Show when={liveWaits() && !(playing() || armed())}>
              <button
                type="button"
                class="tl2-tx tl2-launch"
                data-testid="tl2-launch"
                disabled={!timeline()}
                title="Launch: wait for Live"
                data-info="Launch: waits for Live, and starts on Live's first bar line once you press play in Live."
                onClick={() => play(true)}
              >Launch</button>
            </Show>
            <button type="button" class="tl2-stop" disabled={!timeline()} title="Stop" data-info="Stop: the timeline stops and lets go, back to where it started. Press it again to go back to the start." onClick={stop}>{ICON.stop}</button>
            <button type="button" class="tl2-tx tl2-rec" aria-pressed={!!recTake()} disabled={!timeline()} title="Record"
              data-info="Record: arm a lane (the round button by its name), press Record, and play your canvas; your presses land on the armed lanes."
              onClick={() => (recTake() ? stopRecording() : startRecording())}><i class="tl2-recdot" aria-hidden="true" /></button>
            <button
              type="button"
              class="tl2-tx tl2-loop"
              aria-pressed={!!timeline() && timelineLoops(timeline()!)}
              disabled={!timeline()}
              title="Loop"
              data-info="Loop: when it reaches the end it starts again."
              onClick={() => { const t = timeline(); if (t) setTimelineLoop(t.id, !timelineLoops(t)); }}
            >{ICON.loop}</button>
            <button
              type="button"
              class="tl2-tx tl2-follow"
              aria-pressed={follow()}
              title="Follow the playhead"
              data-info="Follow: while it plays, the view moves along with the playhead."
              onClick={() => setFollow((v) => !v)}
            >Follow</button>
            <Show when={timeline()?.tier === 'song'}>
              {(() => {
                const all = () => timeline()!.sections.every((x) => x.onEnd === 'loop');
                const some = () => timeline()!.sections.some((x) => x.onEnd === 'loop');
                return (
                  <button
                    type="button"
                    class="tl2-tx tl2-loopsec"
                    aria-pressed={all()}
                    data-mixed={some() && !all() ? 'true' : undefined}
                    title="Loop sections"
                    data-info={some() && !all()
                      ? 'Loop sections: some sections loop and some run on. Press to make every section loop until you move on.'
                      : 'Loop sections: on, every section plays again and again until you move on; off, the timeline runs straight through.'}
                    onClick={() => { const t = timeline(); if (t) setAllSectionsOnEnd(t.id, all() ? 'auto-advance' : 'loop'); }}
                  >Loop sections</button>
                );
              })()}
            </Show>
          </div>
          <div class="tl2-lcd" data-info="Where the playhead is (bar. beat. sixteenth) and the tempo.">
            <div><b class="tl2-pos" data-play={playing() ? 'true' : undefined}>{lcdPos()}</b></div>
            <Show when={timeline()}>
              {(t) => <LengthControl timeline={t()} />}
            </Show>
            <div class="tl2-bpm" title={abletonDrives() ? "Ableton's tempo" : songMap() ? "The song's tempo" : undefined}><b>{(shownBpm() || 0).toFixed(2)}</b><small>BPM</small></div>
            <div class="tl2-snap" data-info="Grid: where clicks and drags land.">
              <small>Grid</small>
              <select value={snap()} onChange={(e) => { setSnap(e.currentTarget.value as Snap); queueMicrotask(() => rootEl?.focus({ preventScroll: true })); }} aria-label="Grid">
                <For each={Object.keys(SNAP_LABEL) as Snap[]}>{(k) => <option value={k}>{SNAP_LABEL[k]}</option>}</For>
              </select>
            </div>
          </div>
          <span class="tl2-sp" />
          <div class="tl2-grp" data-info="Zoom: Alt + scroll also zooms. Ctrl + scroll on a lane makes it taller or shorter.">
            <button type="button" title="Zoom out (-)" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.4)}>{ICON.minus}</button>
            <button type="button" title="Zoom in (+)" aria-label="Zoom in" onClick={() => zoomBy(1.4)}>{ICON.add}</button>
            <button type="button" class="tl2-txt tl2-fit" title="Fit the whole song (F)" aria-label="Fit the whole song" onClick={() => fitView()}>Fit</button>
          </div>
          <div class="tl2-grp">
            <button type="button" class="tl2-vw" aria-pressed={browserOpen()} title="Browser" data-info="Browser: show or fold it." onClick={() => setBrowserOpen(!browserOpen())}>{ICON.browser}</button>
            <button type="button" class="tl2-vw" aria-pressed={detailOpen()} title="Detail" data-info="Detail: show or fold it." onClick={() => setDetailOpen(!detailOpen())}>{ICON.detail}</button>
            <button type="button" class="tl2-vw" aria-pressed={perfOpen()} title="Performance strip" data-info="Performance strip: the section pads." onClick={() => setPerfOpen(!perfOpen())}>{ICON.perf}</button>
          </div>
          <div class="tl2-grp">
            <button
              type="button"
              class="tl2-txt tl2-legacy"
              data-info="Legacy timeline: the first timeline editor, kept. Same timelines."
              onClick={() => switchTimelineEditor('legacy')}
            >Legacy</button>
          </div>
          <div class="tl2-grp">
            <button
              type="button" class="tl2-txt tl2-help" title="Keys and gestures (?)" aria-label="Keys and gestures"
              aria-expanded={helpOpen()} data-info="Keys and gestures: everything you can do here, and how."
              onClick={() => setHelpOpen(!helpOpen())}
            >?</button>
          </div>
          <Show when={confirmDelete()}>
            {(c) => (
              <div class="tl2-confirm-scrim" onPointerDown={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
                <div class="tl2-confirm" role="alertdialog" aria-label="Delete timeline">
                  <b>Delete the timeline "{c().name}"?</b>
                  {/* Said first when it is playing: deleting it stops it on every computer (2026-11-03 audit C-6). */}
                  <Show when={timeline() && timeline()!.id === c().id && timelinePlayingNow(timeline()!)}>
                    <span class="tl2-confirm-playing">It is playing now: deleting it stops it on every computer.</span>
                  </Show>
                  <span>{c().pads.length > 0
                    ? `${c().pads.length === 1 ? 'A pad launches it' : `${c().pads.length} pads launch it`} (${c().pads.join(', ')}); ${c().pads.length === 1 ? 'it does' : 'they do'} nothing once it is gone. `
                    : ''}{keyHint('Z')} brings it back.</span>
                  <div class="tl2-actions">
                    <button type="button" class="tl2-btn tl2-confirm-keep" onClick={() => setConfirmDelete(null)}>Keep it</button>
                    <button type="button" class="tl2-btn tl2-danger tl2-confirm-delete" ref={(el) => queueMicrotask(() => el.focus())} onClick={deleteTimelineConfirmed}>Delete</button>
                  </div>
                </div>
              </div>
            )}
          </Show>
          <Show when={helpOpen()}>
            <div class="tl2-helpop" role="dialog" aria-label="Keys and gestures" data-hand={touchUi() ? 'touch' : 'mouse'}>
              <Show
                when={touchUi()}
                fallback={
                  <>
                    <b>Space</b><span>Play or stop</span>
                    <b>Click a bar</b><span>Move the playhead there and pick that row: a paste goes there (the ruler moves the playhead too)</span>
                    <b>Double-click a bar</b><span>Add a change there</span>
                    <b>Drag</b><span>Move a change; its right edge makes it longer</span>
                    <b>Alt-drag</b><span>Copy it there instead (the original stays)</span>
                    <b>Double-click a keyframe</b><span>Its colour; or split it: the colour the fade before it arrives at, then its own from its beat</span>
                    <b>Right-click</b><span>Everything else: rename, duplicate, mute, delete</span>
                    <b>Shift-click, {isMac ? 'Cmd' : 'Ctrl'}-click</b><span>Select a block, or one more</span>
                    <i />
                    <b>Arrows</b><span>Nudge what is selected a grid step (Shift: a bar); nothing selected: the playhead</span>
                    <b>Home, End, [ ]</b><span>The start, the end, the previous or next section</span>
                    <b>1 to 9</b><span>That section: a jump at the next bar while playing, else the playhead there</span>
                    <b>Record</b><span>Press Record, then play your canvas: presses and fader moves land on the lanes; arm lanes (the round button by a name) to record into those only</span>
                    <b>Automation</b><span>A fader's moves as a curve: double-click to add a point, drag one (Shift: only its value), double-click it to remove it</span>
                    <b>Drag on the ruler, {keyHint('L')}</b><span>Loop those bars (or what is selected); click the band to turn it off</span>
                    <b>{keyHint('C')} {keyHint('X')} {keyHint('V')}</b><span>Copy, cut, paste at the playhead on the picked row (paste again: after it)</span>
                    <b>{keyHint('D')}</b><span>Repeat it after itself</span>
                    <b>Delete</b><span>Remove it</span>
                    <b>{keyHint('Z')} {keyHint('Y')}</b><span>Undo, redo</span>
                    <b>Esc</b><span>Cancel a drag, close a menu, let go</span>
                    <i />
                    <b>+ - F</b><span>Zoom in, out, fit the whole song</span>
                    <b>{isMac ? 'Cmd' : 'Ctrl'}-drag</b><span>Ignore the grid (held from the start, or pressed while dragging)</span>
                    <b>Alt + scroll</b><span>Zoom where the pointer is</span>
                    <b>{isMac ? 'Cmd' : 'Ctrl'} + scroll</b><span>Make a lane taller or shorter</span>
                  </>
                }
              >
                <b>Tap a bar</b><span>Add a change there</span>
                <b>Tap the ruler</b><span>Move the playhead there</span>
                <b>Drag</b><span>Move a change; its right edge makes it longer</span>
                <b>Long-press</b><span>Everything else: rename, duplicate, mute, delete</span>
                <b>One finger</b><span>Scroll</span>
                <b>Two fingers</b><span>Pinch to zoom</span>
                <i />
                <b>Section pads</b><span>Jump at the next bar: lit is playing, an amber rim is waiting</span>
                <b>Record</b><span>Press Record, then play your canvas: presses and fader moves land on the lanes</span>
                <b>Automation</b><span>Tap to add a point; drag one to move it; long-press it to delete it</span>
              </Show>
            </div>
          </Show>
          <button type="button" class="tl2-close" title="Close" aria-label="Close timeline" onClick={closeTimelineEditor}>{ICON.close}</button>
          {/* The edit lease (S329): another device is editing this timeline, so changes here are paused. */}
          <Show when={lease()?.supported && !lease()!.held}>
            <div class="tl2-lease" role="status" data-testid="tl2-lease" data-gone={lease()!.holderGone ? 'true' : undefined}>
              <span>
                {lease()!.lost ? `${lease()!.lost!.byName} took over editing this timeline. ` : ''}
                {lease()!.holderGone ? `${lease()!.holderName ?? 'The other device'} has stopped editing it.` : `${lease()!.holderName ?? 'Another device'} is editing this timeline.`}
                {' '}Changes here are paused.
              </span>
              <Show when={lease()!.holderGone} fallback={
                <Show when={!leaseKept()} fallback={<button type="button" data-testid="tl2-lease-take" onClick={() => void takeLease(tid(), 'takeover')}>Take over</button>}>
                  <button type="button" data-testid="tl2-lease-take" title="Edit it here from the latest copy; the other device stops editing" onClick={() => void takeLease(tid(), 'takeover')}>Take over</button>
                  <button type="button" data-testid="tl2-lease-discard" title="Edit it here; the other device's open session is dropped" onClick={() => void takeLease(tid(), 'discard')}>Discard theirs</button>
                  <button type="button" data-testid="tl2-lease-keep" title="Leave it with them; look without editing" onClick={() => setLeaseKept(true)}>Keep</button>
                </Show>
              }>
                <button type="button" data-testid="tl2-lease-reopen" onClick={() => { const t = timeline(); void openLease(tid(), t ? timelineHomeTarget(t) : ''); }}>Reopen</button>
              </Show>
            </div>
          </Show>
        </header>

        {/* ───────── browser | arrangement over detail ───────── */}
        <div class="tl2-main" style={{ '--tl2-browser': `${browserW()}px` } as JSX.CSSProperties} data-browser={browserOpen() ? 'open' : 'folded'}>
          <Show
            when={browserOpen()}
            fallback={
              <button type="button" class="tl2-bstrip" title="Show the browser" data-info="Browser (folded): click to open it." onClick={() => setBrowserOpen(true)}>
                {ICON.right}<span>Browser</span>
              </button>
            }
          >
            <aside class="tl2-browser">
              <div class="tl2-ptitle">
                <b>Browser</b><span class="tl2-sp" />
                <button type="button" class="tl2-fold" title="Fold the browser" data-info="Fold the browser to a strip." onClick={() => setBrowserOpen(false)}>{ICON.left}</button>
              </div>
              <label class="tl2-search">
                {ICON.search}
                <input type="search" placeholder="Search" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} aria-label="Search the browser" />
              </label>
              <div class="tl2-blist">
                <Show when={timeline()}>
                  <BrowserGroup title="Add a lane">
                    <Show when={fixtures().length > 0 && matches('lights rig every all fixtures master')}>
                      <button
                        type="button"
                        class="tl2-allbtn tl2-everylight"
                        data-info={`Add every light in your rig: the All lights master, and a lane for each of your ${fixtures().length} lights that has none. Safe to press again.`}
                        onClick={addEveryLight}
                      >{ICON.lights}Add every light in your rig ({fixtures().length})</button>
                    </Show>
                    <Show when={!lightingOnly() && !songRowShown() && matches('song audio track music mp3 file')}>
                      <button type="button" class="tl2-bitem" data-testid="tl2-browser-song" data-info="Add the song row: an audio file under the timeline, to build the show against and play with it." onClick={addSongRow}>
                        <span class="tl2-song-i" aria-hidden="true">♪</span><span>Song</span><em>Audio library</em>
                      </button>
                    </Show>
                    <Show when={!lightingOnly() && matches('controls buttons canvas presets snapshots')}>
                      <button type="button" class="tl2-bitem" data-info="Add a Controls lane: presses of the buttons and presets on your canvas." onClick={() => addLaneOf('controls')}>
                        {ICON.controls}<span>Controls</span><em>Your canvas</em>
                      </button>
                    </Show>
                    <Show when={!lightingOnly() && matches('guitar presets snapshots helix')}>
                      <button type="button" class="tl2-bitem" data-info="Add a Guitar lane: the guitar's preset and snapshot changes, each one's name running until the next." onClick={() => addLaneOf('guitar')}>
                        {ICON.guitar}<span>Guitar</span><em>Presets, snapshots</em>
                      </button>
                    </Show>
                    <Show when={matches('lights all fixtures rig')}>
                      <button type="button" class="tl2-bitem" data-info={`Add a Lights lane: colour changes for every light at once.${lightsWontNote()}`} onClick={() => addLaneOf('lights')}>
                        {ICON.lights}<span>Lights</span><em>{lightsPlay() ? 'Every light' : "Won't play yet"}</em>
                      </button>
                    </Show>
                    <Show when={!lightingOnly() && matches('opvisual cell column colour color visual clips palette visualiser visualizer knob layer opacity blackout shake brightness hue bloom')}>
                      <button type="button" class="tl2-bitem" data-testid="tl2-browser-opv" data-info="OPVisual: pick what a new lane controls: cells and columns, colour, palette, visualiser knobs, layers and the whole picture."
                        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); openOpvMenu(r.right, r.top); }}>
                        {ICON.lights}<span>OPVisual</span><em>Cells, knobs, more</em>
                      </button>
                    </Show>
                    <Show when={matches('drums drum lights kick snare hat hihat fill chase instrument live')}>
                      <button type="button" class="tl2-bitem" data-testid="tl2-browser-drums" data-info="Add drum lights: your kick, snare and hi-hat light the rig as you play. Pick a ready-made one."
                        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); openDrumPresets(r.right, r.top); }}>
                        {ICON.drum}<span>Drum lights</span><em>{drumsPlay() ? 'Kick, snare, hats' : "Won't play yet"}</em>
                      </button>
                    </Show>
                    <For each={fixtures().filter((f) => matches(`${f.name} light fixture`))}>
                      {(f) => (
                        <button type="button" class="tl2-bitem" data-info={`Add a lane for ${f.name} on its own.${lightsWontNote()}`} onClick={() => addLaneOf('lights', f.id)}>
                          {ICON.lights}<span>{f.name}</span><em>{lightsPlay() ? 'One light' : "Won't play yet"}</em>
                        </button>
                      )}
                    </For>
                    <For each={lightingOnly() ? [] : MOTION_TARGETS.filter((x) => matches(`OP-Motion motion ${x.label} ${x.family} ${x.hint}`))}>
                      {(x) => (
                        <button type="button" class="tl2-bitem" data-testid="tl2-browser-motion" data-motion-target={x.id}
                          data-info={`Add an OP-Motion lane for ${x.label}: ${x.hint}. On Main to start with; pick the submixes in its panel.`}
                          onClick={() => void addMotionLane(x.id)}>
                          {ICON.auto}<span>{x.label}</span><em>OP-Motion{x.family === 'Effect' || x.family === 'Crossfader' ? '' : `, ${x.family.toLowerCase()}`}</em>
                        </button>
                      )}
                    </For>
                    <For each={lightingOnly() ? [] : autoTargets().filter((a) => matches(`${a.label} ${a.hint} automation curve moves`))}>
                      {(a) => (
                        <button type="button" class="tl2-bitem" data-testid="tl2-browser-auto" data-info={`Add an Automation lane for ${a.label}: its moves over time, as a curve. Press Record and move it, or double-click the lane to add points.`} onClick={() => void addAutomationLane(a.itemId, a.axis)}>
                          {ICON.auto}<span>{a.label}</span><em>Automation</em>
                        </button>
                      )}
                    </For>
                  </BrowserGroup>
                  <Show when={!lightingOnly()}>
                  <BrowserGroup title="Your canvas">
                    {canvasMap('browser')}
                    <p class="tl2-bnote tl2-map-hint">{tip('Drag a control onto a lane, or click it and then click a bar.', 'Drag a control onto a lane, or tap it and then tap a bar.')}</p>
                    <button
                      type="button"
                      class="tl2-aimbtn tl2-aimbtn-b"
                      data-info="Your canvas at full size: tap a control to pick it up, then click a bar. Nothing is sent."
                      onClick={() => startCanvasPick(null, null)}
                    >{ICON.target}Point at it on your canvas</button>
                  </BrowserGroup>
                  <For each={controls()}>
                    {(g) => (
                      <Show when={g.items.some((it) => matches(`${itemText(it).label} ${itemText(it).meta}`))}>
                        <BrowserGroup title={g.label}>
                          <For each={g.items.filter((it) => matches(`${itemText(it).label} ${itemText(it).meta}`))}>
                            {(it) => {
                              const txt = itemText(it);
                              const hue = hueOf(it);
                              const payload: Payload = { kind: 'item', itemId: it.id, label: txt.label, itemKind: it.kind };
                              return (
                                <button
                                  type="button"
                                  class="tl2-bitem"
                                  data-picked={picked()?.kind === 'item' && (picked() as { itemId: string }).itemId === it.id ? 'true' : undefined}
                                  data-info={`${txt.label} (${txt.meta}). Drag onto a Controls lane, or click it and then click a bar.`}
                                  onPointerDown={(e) => onBrowserPointerDown(e, payload)}
                                >
                                  <i class="tl2-dot" style={{ background: hue !== 'none' ? HUE_VALUES[hue] : undefined }} />
                                  <span>{txt.label}</span><em>{txt.meta}</em>
                                </button>
                              );
                            }}
                          </For>
                        </BrowserGroup>
                      </Show>
                    )}
                  </For>
                  <Show when={controls().length === 0 && matches('controls buttons')}>
                    <p class="tl2-bnote">No buttons or presets on this project's canvas yet. Add one from Navigate, Add a control.</p>
                  </Show>
                  </Show>
                  <Show when={STAGE_COLORS.some((c) => matches(`${c.label} colour color light`))}>
                    {/* Said only when it is true: the bridge plays lights where it says `timeline_colour` (2026-10-16: the
                        browser said "won't play yet" on every light and colour even when they played). */}
                    <BrowserGroup title={lightsPlay() ? 'Colours' : "Colours (won't play yet)"}>
                      <div class="tl2-swatches">
                        <For each={STAGE_COLORS.filter((c) => matches(`${c.label} colour color light`))}>
                          {(c) => (
                            <button
                              type="button"
                              class="tl2-swatch"
                              style={{ background: c.label === 'Off' ? undefined : rgbCss(c.rgb) }}
                              data-off={c.label === 'Off' ? 'true' : undefined}
                              data-picked={picked()?.kind === 'colour' && (picked() as { colour: StageColor }).colour.label === c.label ? 'true' : undefined}
                              data-current={clickColour().label === c.label ? 'true' : undefined}
                              title={c.label}
                              aria-label={c.label}
                              data-info={clickColour().label === c.label
                                ? `${c.label}: what a click on a Lights lane adds. Click another colour to change it.`
                                : `${c.label}: drag onto a Lights lane, or click it and then click a bar; clicks on a Lights lane add it from then on.`}
                              onPointerDown={(e) => onBrowserPointerDown(e, { kind: 'colour', colour: c })}
                            />
                          )}
                        </For>
                      </div>
                    </BrowserGroup>
                  </Show>
                  <Show when={matches('light fx effect preset lighting block flow strobe chase')}>
                    {/* Light FX (2026-10-19, OPBridge S295): a preset played as a block on a Lights lane, from OPBridge's own
                        list, so a block always names a preset that bridge has. */}
                    <BrowserGroup title={fxPlay() ? 'Light FX' : "Light FX (won't play yet)"}>
                      <Show when={(fxState()?.presets.length ?? 0) > 0} fallback={
                        <p class="tl2-bnote" data-testid="tl2-fx-note">{!homeTarget() ? 'Pair the computer the lights are on to choose an effect.'
                          : fxState()?.status === 'unknown' ? 'Reading the effects from OPBridge...'
                            : fxState()?.detail ?? 'OPBridge did not list any effects.'}</p>
                      }>
                        <For each={fxState()!.presets.filter((x) => matches(`${x.name} ${x.id} ${x.tags.join(' ')} light fx effect`) || matches('light fx effect'))}>
                          {(x) => (
                            <button
                              type="button" class="tl2-bitem" data-testid="tl2-fx" data-preset={x.id}
                              data-picked={picked()?.kind === 'fx' && (picked() as { preset: string }).preset === x.id ? 'true' : undefined}
                              data-info={`${x.name}: ${x.description || 'a lighting effect'}. Drag onto a Lights lane, or click it and then click a bar: it plays for a bar, drag its end to make it longer.`}
                              onPointerDown={(e) => onBrowserPointerDown(e, { kind: 'fx', preset: x.id, label: x.name })}
                            >
                              {ICON.sections}<span>{x.name}</span><em>{x.scope === 'single' ? 'one light' : 'the rig'}</em>
                            </button>
                          )}
                        </For>
                      </Show>
                    </BrowserGroup>
                  </Show>
                  <Show when={hueSceneEntries().length > 0 && hueSceneEntries().some((e) => matches(`${e.scene.name} ${e.group.name} hue scene lights room`))}>
                    {/* Hue scenes (2026-10-23, OPBridge S331): a cue that recalls one, on a Controls lane; drag it on, or
                        click it and then a bar. Recalled on the computer holding the Hue bridge, wherever this plays. */}
                    <BrowserGroup title="Hue scenes">
                      <For each={hueSceneEntries().filter((e) => matches(`${e.scene.name} ${e.group.name} hue scene lights room`))}>
                        {(e) => {
                          const ref = hueRefOf(e);
                          return (
                            <button
                              type="button" class="tl2-bitem" data-testid="tl2-hue" data-scene={e.scene.id}
                              data-picked={picked()?.kind === 'hue' && (picked() as { ref: HueSceneRef }).ref.scene === e.scene.id ? 'true' : undefined}
                              data-info={`${hueSceneLabel(ref)}: the Hue app's scene. Drag onto a Controls lane, or click it and then click a bar: the room changes to it there.`}
                              onPointerDown={(ev) => onBrowserPointerDown(ev, { kind: 'hue', ref, label: hueSceneLabel(ref) })}
                            >
                              <i class="tl2-hue-sw" style={{ background: hueSwatchCss(e.scene.swatch) ?? '#2a2b2e' }} /><span>{e.scene.name}</span><em>{e.group.name}</em>
                            </button>
                          );
                        }}
                      </For>
                    </BrowserGroup>
                  </Show>
                  <Show when={!lightingOnly() && LIVE_TARGETS.some((x) => matches(`${x.label} song setting transpose capo key`))}>
                    <BrowserGroup title="Song settings (won't play yet)">
                      <For each={LIVE_TARGETS.filter((x) => matches(`${x.label} song setting transpose capo key`))}>
                        {(target) => <LiveTargetRow target={target} onPointerDown={onBrowserPointerDown} picked={picked()} />}
                      </For>
                    </BrowserGroup>
                  </Show>
                </Show>
              </div>
            </aside>
            <div
              class="tl2-split-v"
              data-info="Drag to resize the browser."
              onPointerDown={(e) => { const w0 = browserW(); startResize(e, (dx) => setBrowserW(Math.max(170, Math.min(440, w0 + dx)))); }}
            />
          </Show>

          <div class="tl2-right" style={{ '--tl2-detail': `${detailH()}px` } as JSX.CSSProperties} data-detail={detailOpen() ? 'open' : 'folded'}>
            {/* ── arrangement ── */}
            <section class="tl2-arr" ref={(el) => (arrEl = el)}>
              <div class="tl2-scroll" ref={scrollEl} onWheel={onWheel}>
                <Show
                  when={timeline()}
                  fallback={
                    <div class="tl2-start tl2-start-none">
                      <h2>No timelines in this project yet</h2>
                      <p>A timeline plays changes in time: light colours, guitar presets, button presses. Make one to start.</p>
                      <div class="tl2-qa">
                        <button type="button" onClick={() => newTimeline('song')}>{ICON.add}New song timeline</button>
                        <button type="button" onClick={() => newTimeline('action')}>{ICON.add}New action timeline</button>
                      </div>
                    </div>
                  }
                >
                  <div class="tl2-canvas" style={{ width: `${HEADER_W + contentW()}px` }}>
                    {/* sections + ruler, stuck to the top */}
                    <div class="tl2-top">
                      <div class="tl2-row tl2-secs">
                        <div class="tl2-h" data-info={tip('Sections: click one to edit it; drag an edge to resize; right-click for more; + adds one.', 'Sections: tap one to edit it; drag an edge to resize; long-press to rename, duplicate, move or delete; + adds one.')}>
                          {ICON.sections}<span>Sections</span>
                        </div>
                        <div class="tl2-c" style={gridStyle()} onClick={(e) => { if (edgeJustDragged) { edgeJustDragged = false; return; } if (e.target === e.currentTarget) seekTo(barAtX(e.currentTarget, e.clientX)); }}>
                          <For each={sectionStarts()}>
                            {(s) => (
                              // Double-click renames in place (the novice's "call
                              // Section 1 Chorus", timeline-desk-review 7). A box,
                              // not the button, holds the field: an input inside a
                              // button is invalid and Space would press the button.
                              <Show
                                when={renaming() === `sec:${s.id}`}
                                fallback={
                                  <button
                                    type="button"
                                    class="tl2-sec"
                                    data-sec-id={s.id}
                                    style={{ left: `${s.start * ppb()}px`, width: `${s.bars * ppb()}px` }}
                                    aria-selected={sel().type === 'section' && (sel() as { id: string }).id === s.id}
                                    data-info={tip(`${s.name}: ${barsText(s.bars)}. Click to edit it and move the playhead there; double-click to rename it; right-click for more.`,
                                      `${s.name}: ${barsText(s.bars)}. Tap to edit it and move the playhead there; long-press to rename it and more.`)}
                                    onClick={(e) => { setSel({ type: 'section', id: s.id }); seekTo(barAtX(e.currentTarget.parentElement!, e.clientX)); }}
                                    onDblClick={(e) => { e.stopPropagation(); setSel({ type: 'section', id: s.id }); if (!following()) setRenaming(`sec:${s.id}`); }}
                                  >
                                    <span>{s.name}</span><em>{s.bars}</em>
                                  </button>
                                }
                              >
                                <div class="tl2-sec" data-sec-id={s.id} data-renaming="true" style={{ left: `${s.start * ppb()}px`, width: `${s.bars * ppb()}px` }}>
                                  <input
                                    class="tl2-grpname"
                                    value={s.name}
                                    aria-label="Section name"
                                    ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                      if (e.key === 'Escape') { e.currentTarget.value = s.name; e.currentTarget.blur(); }
                                    }}
                                    onBlur={(e) => {
                                      setRenaming(null);
                                      const typed = e.currentTarget.value;
                                      if (typed === s.name) return;
                                      renameSection(tid(), s.id, typed);
                                      commitSectionName(tid(), s.id, s.name);
                                    }}
                                  />
                                </div>
                              </Show>
                            )}
                          </For>
                          {/* The lead-in (bars before the first section, where the song plays alone) and the first section's LEFT
                              edge: drag it right to start the first section later, back to bar 1 to remove the lead-in. */}
                          <Show when={leadIn() > 0}>
                            <div class="tl2-leadin" style={{ left: '0px', width: `${leadIn() * ppb()}px` }}
                              data-info={`Lead-in: ${barsText(leadIn())} where the song plays and nothing else does. Drag the start of ${sectionStarts()[0]?.name ?? 'the first section'} to change it.`}>
                              <span>Lead-in</span>
                            </div>
                          </Show>
                          <Show when={sectionStarts()[0]}>
                            {(f) => (
                              <i
                                class="tl2-secedge" data-start-of={f().id}
                                style={{ left: `${f().start * ppb()}px` }}
                                title={`Drag to change where ${f().name} starts (the bars before it play the song alone)`}
                                onPointerDown={(e) => startFirstSectionDrag(e)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </Show>
                          {/* each section's right edge: drag it (bar-quantized) to move the line to the next section */}
                          <For each={sectionStarts()}>
                            {(s) => (
                              <i
                                class="tl2-secedge"
                                data-edge-of={s.id}
                                data-end={s.start + s.bars >= totalBars() ? 'true' : undefined}
                                style={{ left: `${(s.start + s.bars) * ppb()}px` }}
                                title={`Drag to change where ${s.name} ends`}
                                onPointerDown={(e) => startEdgeDrag(e, s.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </For>
                          <Show when={edgeDrag()}>
                            {(d) => <i class="tl2-secedge-guide" style={{ left: `${d().end * ppb()}px` }} />}
                          </Show>
                          {/* Room past the end (2026-10-14, "i cant seem to change the length"): the last edge sat at the
                              very end of the scroll, often off screen, with nowhere to drag it. */}
                          <i class="tl2-tail" style={{ left: `${totalBars() * ppb()}px`, width: `${8 * ppb()}px` }} title="Drag the end of the last section into here to make the timeline longer" />
                          <Show when={!following()}>
                            <button
                              type="button"
                              class="tl2-addsec"
                              style={{ left: `${contentW() + 4}px` }}
                              title="Add a section"
                              data-info="Add a section: a verse, a chorus, a drop."
                              onClick={() => { const s = addSection(tid()); if (s) setSel({ type: 'section', id: s.id }); }}
                            >{ICON.add}</button>
                          </Show>
                        </div>
                      </div>
                      <div class="tl2-row tl2-ruler">
                        {/* The timeline's name: a click shows its own properties (2026-10-16: nothing but Esc reached them). */}
                        <div class="tl2-h" style={{ cursor: 'pointer' }} data-info="The timeline: click for its properties (length, loop, when a grabbed fader comes back)."
                          onClick={() => setSel({ type: 'timeline' })}><b>{timeline()!.name}</b><span>{barsText(totalBars())}</span>
                          {/* Where it was made (`origin`, immutable; both apps edit it): the maker app's wordmark. */}
                          <img class="tl2-origin" data-origin={timelineOrigin(timeline()!)} src={`/logo/${timelineOrigin(timeline()!)}-logo.png`}
                            alt={`Made in ${timelineOrigin(timeline()!) === 'opbridge' ? 'OPBridge' : 'OPButtons'}`} title={`Made in ${timelineOrigin(timeline()!) === 'opbridge' ? 'OPBridge' : 'OPButtons'}`} /></div>
                        <div class="tl2-c" style={gridStyle()} data-info="Click to move the playhead to the nearest bar; drag to loop those bars."
                          onPointerDown={onRulerPointerDown}
                          onClick={(e) => { if (rulerDragged) { rulerDragged = false; return; } seekTo(barAtX(e.currentTarget, e.clientX)); }}>
                          <Show when={loopRange()}>
                            {(r) => (
                              <div
                                class="tl2-loopband" data-on={r().on ? 'true' : undefined}
                                style={{ left: `${r().from * ppb()}px`, width: `${(r().to - r().from) * ppb()}px` }}
                                title={`Loop bars ${r().from + 1} to ${r().to}: click to turn it ${r().on ? 'off' : 'on'}, right-click to clear`}
                                data-info={`Loop bars ${r().from + 1} to ${r().to}: click to turn it ${r().on ? 'off' : 'on'}; right-click to clear it.`}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); setLoopRange({ ...r(), on: !r().on }); }}
                                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setLoopRange(null); }}
                              />
                            )}
                          </Show>
                          <For each={Array.from({ length: totalBars() }, (_, i) => i)}>
                            {(b) => (
                              <>
                                <i class="tl2-tk" data-sec={sectionStarts().some((s) => s.start === b) ? 'true' : undefined} style={{ left: `${b * ppb()}px` }} />
                                <Show when={b % barLabelEvery() === 0}>
                                  <b class="tl2-bn" style={{ left: `${b * ppb()}px` }}>{b + 1}</b>
                                </Show>
                              </>
                            )}
                          </For>
                          {/* always shown, as in a DAW: stopped, it sits where Play will start (bar 1 included) */}
                          <Show when={!!timeline()}>
                            <i class="tl2-rph" style={{ left: `${shownBar() * ppb()}px` }} data-cursor={!playing() && !armed() ? 'true' : undefined}
                              data-scrub={scrubBar() !== null ? 'true' : undefined} data-info="Drag the playhead to move it; it moves there when you let go."
                              onPointerDown={onPlayheadPointerDown} />
                          </Show>
                        </div>
                      </div>
                      {/* the test track (2026-10-13): song timelines, or any timeline already pointed at a song */}
                      <Show when={songRowShown()}>
                        <TestTrackRow
                          timeline={timeline()!}
                          openPickerNow={songPickNow()}
                          onPickerOpened={() => setSongPickNow(false)}
                          {...(!timeline()!.testTrack ? { onRemoveRow: () => setSongWanted(null) } : {})}
                          sections={sectionStarts()}
                          ppb={ppb()}
                          gridStyle={gridStyle()}
                          selected={sel().type === 'track'}
                          onSelect={() => setSel({ type: 'track' })}
                          timelineBar={playing() ? playBar() : null}
                          onSeek={(bar) => seekTo(bar)}
                        />
                        {/* The song's parts (2026-10-21): here while the timeline has a song, and in a timeline that
                            follows it they are its sections. Hide folds it to a strip (2026-10-28), the offer with it. */}
                        <Show when={!!timeline()!.testTrack && !songPartsOpen()}>
                          <div class="tl2-row tl2-song-folded" data-testid="tl2-song-folded">
                            <button type="button" class="tl2-song-unfold" data-testid="tl2-song-show" onClick={() => setSongPartsOpen(true)}
                              title="Show the song's parts" data-info="The song's parts (INTRO, VERSE, CHORUS...), where you cut the song up. Click to show them.">
                              <b>Song parts</b><span>Show</span>
                            </button>
                          </div>
                        </Show>
                        <Show when={!!timeline()!.testTrack && songPartsOpen()}>
                          <SongMarkRow
                            trackId={timeline()!.testTrack!.id}
                            {...(timeline()!.testTrack!.pc ? { pc: timeline()!.testTrack!.pc } : {})}
                            {...(timeline()!.testTrack!.output !== undefined ? { output: timeline()!.testTrack!.output } : {})}
                            width={Math.max(240, Math.min(contentW(), viewW() - HEADER_W - 16))}
                            onHide={() => setSongPartsOpen(false)}
                          />
                          <Show when={canStartFollowing()}>
                            <div class="tl2-follow-ask" data-testid="tl2-follow-ask">
                              <span>The song has parts, but this timeline's sections are still its own.</span>
                              <button type="button" class="tl2-btn" data-testid="tl2-follow" onClick={() => startFollowing(timeline()!.id)}>Use the song's parts as the sections</button>
                            </div>
                          </Show>
                        </Show>
                      </Show>
                    </div>

                    {/* lanes */}
                    <For each={rowLayout().rows}>
                      {(row) => {
                        if (!('steps' in row)) return groupHeader(row.group);
                        const lane = row;
                        const wont = () => laneWontPlay(lane);
                        // The Stream view draws a lights lane's pattern blocks as the colours they play (`streamLayer`).
                        const all = () => (streamsLane(lane) ? clipsOf(lane).filter((c) => !isPatternStep(c.step)) : clipsOf(lane));
                        return (
                          <div
                            class="tl2-row tl2-lane"
                            data-lane-id={lane.id}
                            data-hue={laneHueVar(lane.color) ? lane.color : undefined}
                            style={{ height: `${laneH(lane.id)}px`, ...(laneHueVar(lane.color) ? { '--tl2-lane-hue': laneHueVar(lane.color)! } : {}) } as JSX.CSSProperties}
                            aria-selected={selLaneIds().has(lane.id)}
                            data-silent={!laneAudible(timeline()!, lane) ? (lane.mute ? 'muted' : 'solo') : undefined}
                            data-role={laneRole(lane)}
                            data-depth={depthOf(lane.id) || undefined}
                            data-droptarget={laneDrag() && laneRole(lane) === 'lights' && laneDrag()!.target?.key === groupKeyOf(lightGroupOf(lane)) ? 'true' : undefined}
                            data-dropbefore={laneDrag()?.target?.before === lane.id ? 'true' : undefined}
                            data-dragging={laneDrag()?.ids.includes(lane.id) ? 'true' : undefined}
                            data-drop={browserDrag()?.laneId === lane.id ? (browserDrag()!.ok ? 'ok' : 'no') : picked() ? (laneAccepts(lane, picked()!) ? 'ok' : 'no') : undefined}
                          >
                            <div
                              class="tl2-h"
                              data-info={tip(`${lane.name}: ${laneDrives(lane)}. Click to select, Shift-click for a block of lanes, ${isMac ? 'Cmd' : 'Ctrl'}-click to add one; double-click its name to rename it; right-click to mute or solo. Drag the bottom edge, or Ctrl + scroll, to make it taller.`,
                                `${lane.name}: ${laneDrives(lane)}. Tap to select; long-press to rename, mute, solo, duplicate or delete it. Drag the bottom edge to make it taller.`)}
                              style={{ '--tl2-depth': depthOf(lane.id) } as JSX.CSSProperties}
                              onPointerDown={(e) => onLaneHeadPointerDown(e, lane)}
                              onClick={(e) => onLaneHeadClick(e, lane)}
                            >
                              {roleIcon(lane)}
                              <Show when={recordable(lane)}>
                                <button type="button" class="tl2-arm" aria-pressed={recArmed().has(lane.id)}
                                  title={recArmed().has(lane.id) ? 'Armed: Record puts your presses here. Click to disarm.' : 'Arm for recording'}
                                  aria-label={`${recArmed().has(lane.id) ? 'Disarm' : 'Arm'} ${lane.name} for recording`}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); toggleArm(lane.id); }} />
                              </Show>
                              <div class="tl2-nm">
                                {/* Double-click renames it in place, as a section does
                                    (2026-10-16: lanes renamed only in the detail panel). */}
                                <Show
                                  when={renaming() === `lane:${lane.id}`}
                                  fallback={<b onDblClick={(e) => { e.stopPropagation(); setSel({ type: 'lane', id: lane.id }); setRenaming(`lane:${lane.id}`); }}>{lane.name}</b>}
                                >
                                  <input
                                    class="tl2-grpname tl2-lanename"
                                    value={lane.name}
                                    aria-label="Lane name"
                                    ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                      if (e.key === 'Escape') { e.currentTarget.value = lane.name; e.currentTarget.blur(); }
                                    }}
                                    onBlur={(e) => {
                                      setRenaming(null);
                                      const typed = e.currentTarget.value;
                                      if (typed === lane.name) return;
                                      renameLane(tid(), lane.id, typed);
                                      commitLaneName(tid(), lane.id, lane.name);
                                    }}
                                  />
                                </Show>
                                <span>{lane.fixtureRef && isParked(lane.fixtureRef.fixtureId) ? 'Parked on OPBridge: shows nothing' : laneNow(lane) ? `Now ${laneNow(lane)}` : laneDrives(lane)}</span>
                              </div>
                              <Show when={lane.mute || lane.solo}>
                                <span class="tl2-ms">
                                  <Show when={lane.mute}>
                                    <button type="button" data-k="m" title="Muted: click to unmute" aria-label={`Unmute ${lane.name}`} data-info={`${lane.name} is muted: it does not play. Click to unmute.`} onClick={(e) => { e.stopPropagation(); toggleLaneFlag([lane.id], 'mute'); }}>M</button>
                                  </Show>
                                  <Show when={lane.solo}>
                                    <button type="button" data-k="s" title="Soloed: click to unsolo" aria-label={`Unsolo ${lane.name}`} data-info={`${lane.name} is soloed: only soloed lanes play. Click to unsolo.`} onClick={(e) => { e.stopPropagation(); toggleLaneFlag([lane.id], 'solo'); }}>S</button>
                                  </Show>
                                </span>
                              </Show>
                              <Show when={wont() || (laneRole(lane) === 'lights' && lane.steps.length === 0 && !lightsPlay()) || (laneRole(lane) === 'drums' && !drumsPlay())}>
                                <i class="tl2-wont" title={`Won't play: ${wont() ?? (laneRole(lane) === 'drums' ? DRUMS_WONT : LIGHTS_WONT)}`} data-info={`Won't play: ${wont() ?? (laneRole(lane) === 'drums' ? DRUMS_WONT : LIGHTS_WONT)}`}>⊘</i>
                              </Show>
                              {/* The bottom edge under the name (2026-10-20): the row's own edge now paints below this sticky
                                  header (one layer for the whole column instead of one per lane: the playhead's smoothness). */}
                              <div
                                class="tl2-rz"
                                onPointerDown={(e) => {
                                  const h0 = laneH(lane.id);
                                  startResize(e, (_dx, dy) => setLaneHeights((m) => ({ ...m, [lane.id]: Math.max(LANE_H_MIN, Math.min(LANE_H_MAX, h0 + dy)) })));
                                }}
                              />
                            </div>
                            <div
                              class="tl2-c tl2-grid"
                              style={gridStyle()}
                              data-tl2-cell={lane.id}
                              data-info={
                                picked() ? `Click to put ${payloadLabel(picked()!)} here.`
                                  : laneRole(lane) === 'opvcolour' ? tip(`Double-click (or ${isMac ? 'Cmd' : 'Ctrl'}+click) to add a ${clickColour().label} keyframe: every OPVisual clip set to follow OPButtons turns that colour. Right-click for "Back to each clip's own look".`,
                                    `Tap to add a ${clickColour().label} keyframe: every OPVisual clip set to follow OPButtons turns that colour. Long-press for "Back to each clip's own look".`)
                                  : laneRole(lane) === 'opvpalette' ? tip('Double-click to choose the palette OPVisual changes to there; a click moves the playhead.', 'Tap to choose the palette OPVisual changes to there.')
                                  : streamsLane(lane) ? tip(`Stream: the colour this light shows, black where it is off, a dotted line where the timeline leaves it as it was. A click moves the playhead; double-click adds a ${clickColour().label} keyframe. Turn Stream off to move or change keyframes.`,
                                    `Stream: the colour this light shows, black where it is off, a dotted line where the timeline leaves it as it was. Tap adds a ${clickColour().label} keyframe. Turn Stream off to move or change keyframes.`)
                                  : laneRole(lane) === 'lights' ? tip(`Double-click (or ${isMac ? 'Cmd' : 'Ctrl'}+click) to add a ${clickColour().label} keyframe there (the colour ringed on the left); a click moves the playhead. Click the mark between two keyframes to choose Cut or Fade.`,
                                    `Tap to add a ${clickColour().label} keyframe there (the colour ringed on the left); tap the ruler to move the playhead. Tap the mark between two keyframes to choose Cut or Fade.`)
                                  : laneRole(lane) === 'guitar' ? tip('Double-click to choose the guitar preset or snapshot from there on; a click moves the playhead.',
                                    'Tap to choose the guitar preset or snapshot from there on; tap the ruler to move the playhead.')
                                    : laneRole(lane) === 'controls' ? tip('Double-click a bar to choose what happens there; a click moves the playhead.',
                                      'Tap a bar to choose what happens there; tap the ruler to move the playhead.')
                                      : laneRole(lane) === 'automation' ? tip('Double-click to add a point there, at that height; a click moves the playhead. Or press Record and move the control.',
                                        'Tap to add a point there, at that height. Or press Record and move the control.')
                                        : laneRole(lane) === 'drums' ? tip('Double-click to add a drum lights block across the section there; right-click for the presets. A click moves the playhead.',
                                          'Tap to add a drum lights block across the section there; long-press for the presets.') : ''
                              }
                              onPointerDown={onCellPointerDown}
                              onClick={(e) => {
                                if (suppressClick) { suppressClick = false; return; }
                                if ((e.target as Element).closest('.tl2-clip, .tl2-kf, .tl2-kfh, .tl2-gflag, .tl2-gsnap, .tl2-hb, .tl2-leadh, .tl2-ap')) return;
                                onCellClick(e, lane, e.currentTarget);
                              }}
                            >
                              <Show when={editsColour(lane) && !streamsLane(lane)}>
                                {keyframeLayer(lane)}
                              </Show>
                              <Show when={streamsLane(lane)}>
                                {streamLayer(lane)}
                              </Show>
                              <Show when={laneRole(lane) === 'opvcolour'}>
                                {opvReleaseLayer(lane)}
                              </Show>
                              <Show when={laneRole(lane) === 'opvpalette'}>
                                {opvPaletteLayer(lane)}
                              </Show>
                              <Show when={laneRole(lane) === 'guitar'}>
                                {guitarLayer(lane)}
                              </Show>
                              <Show when={laneRole(lane) === 'automation'}>
                                {automationLayer(lane)}
                              </Show>
                              <Show when={laneRole(lane) === 'drums' && lane.steps.some(isHitStep)}>
                                {hitLayer(lane)}
                              </Show>
                              <For each={all()}>
                                {(c) => {
                                  const s = c.step;
                                  const preview = () => (dragPreview()?.stepId === s.id && !dragPreview()!.copy ? dragPreview() : null);
                                  const left = () => (preview() && preview()!.laneId === lane.id ? preview()!.position : c.start + shiftOf(s.id)) * ppb();
                                  const width = () => {
                                    const p = preview();
                                    if (p && p.laneId === lane.id) return Math.max(6, p.durationBars * ppb() - 2);
                                    return Math.max(6, (c.end - c.start) * ppb() - 2);
                                  };
                                  const why = () => warnings().get(s.id);
                                  const durational = s.kind === 'buttonRef' || s.kind === 'animation' || s.kind === 'pattern' || s.kind === 'hitBlock';
                                  return (
                                    <div
                                      class="tl2-clip"
                                      data-step-id={s.id}
                                      data-kind={s.kind}
                                      data-glow={s.kind === 'buttonRef' && hoverItemId() === s.buttonId ? 'true' : undefined}
                                      onPointerEnter={() => { if (s.kind === 'buttonRef') setHoverItemId(s.buttonId); }}
                                      onPointerLeave={() => { if (s.kind === 'buttonRef') setHoverItemId((h) => (h === s.buttonId ? null : h)); }}
                                      data-wont={why() ? 'true' : undefined}
                                      data-away={preview() && preview()!.laneId !== lane.id ? 'true' : undefined}
                                      aria-selected={isStepSel(s.id)}
                                      style={{ left: `${left() + 1}px`, width: `${width()}px` }}
                                      title={why() ? `Won't play: ${why()}` : undefined}
                                      data-info={isPatternStep(s) ? `${stepTitle(s)}, from ${barBeat(s.position, sig())}: ${s.keys.length} colour changes a cycle, played exactly as written out. Drag to move it; drag its right edge to repeat it more or less; right-click to unpack it into keyframes.`
                                        : isHitBlockStep(s) ? `${stepTitle(s)}, from ${barBeat(s.position, sig())}: each ${sourceLabel(s.source).toLowerCase()} hit lights its look${hitBlockFill(s) ? ', and in the fill its fill look' : ''}. Click it to change it and try it; drag to move; drag its right edge to repeat it more or less.`
                                        : `${stepTitle(s)} at ${barBeat(s.position, sig())}. Drag to move${durational ? ', drag the right edge to change its length' : ''}; Delete removes it.`}
                                      onPointerDown={(e) => onClipPointerDown(e, lane, s, 'move')}
                                    >
                                      <div class="tl2-ct"><Show when={why()}><i class="tl2-wont">⊘</i></Show>{stepTitle(s)}</div>
                                      <div class="tl2-cb" style={isPatternStep(s) ? patternPaint(s) : isHitBlockStep(s) ? drumPaint(s) : undefined}>{clipBody(s)}</div>
                                      <Show when={isHitBlockStep(s) && s.cycleBars * ppb() >= 54}>
                                        <For each={isHitBlockStep(s) ? hitBlockCycles(s).filter((c) => c.fillAt !== null) : []}>
                                          {(c) => <i class="tl2-fillmark" style={{ left: `${(c.fillAt! - s.position) * ppb()}px` }}>fill</i>}
                                        </For>
                                      </Show>
                                      <Show when={durational}>
                                        <i class="tl2-edge" onPointerDown={(e) => onClipPointerDown(e, lane, s, 'resize')} />
                                      </Show>
                                    </div>
                                  );
                                }}
                              </For>
                              {/* a block dragged in from another lane (a keyframe's ghost is `keyframeLayer`'s) */}
                              <Show when={dragPreview() && dragPreview()!.laneId === lane.id && (dragPreview()!.copy || !lane.steps.some((x) => x.id === dragPreview()!.stepId)) && !draggingKeyframe() && laneRole(lane) !== 'guitar'}>
                                <div class="tl2-clip tl2-ghost" style={{ left: `${dragPreview()!.position * ppb() + 1}px`, width: `${Math.max(6, dragPreview()!.durationBars * ppb() - 2)}px` }} />
                              </Show>
                              <Show when={browserDrag()?.laneId === lane.id && browserDrag()!.ok}>
                                <i class="tl2-dropline" style={{ left: `${browserDrag()!.position * ppb()}px` }} />
                              </Show>
                              <Show when={lane.steps.length === 0}>
                                <span class="tl2-hint">
                                  {laneRole(lane) === 'opvcolour' ? `${tip('Double-click', 'Tap')} to add a ${clickColour().label} keyframe: OPVisual's clips that follow OPButtons take it on`
                                    : laneRole(lane) === 'opvpalette' ? `${tip('Double-click', 'Tap')} a bar to choose the palette OPVisual changes to there`
                                    : laneRole(lane) === 'lights' ? `${tip('Double-click', 'Tap')} to add a ${clickColour().label} keyframe.${lightsWontNote()}`
                                    : laneRole(lane) === 'guitar' ? `${tip('Double-click', 'Tap')} a bar to choose a guitar preset, or drag one in from your canvas`
                                    : laneRole(lane) === 'controls' ? `${tip('Double-click', 'Tap')} a bar to choose a control, or drag one in from the browser`
                                      : laneRole(lane) === 'automation' ? `${tip('Double-click', 'Tap')} to add a point, or press Record and move ${laneDrives(lane).replace(/^Moves /, '')}`
                                        : laneRole(lane) === 'drums' ? `${tip('Double-click', 'Tap')} to add a drum lights block, or ${tip('right-click', 'long-press')} for the presets` : 'Notes'}
                                </span>
                              </Show>
                            </div>
                            <div
                              class="tl2-rz"
                              onPointerDown={(e) => {
                                const h0 = laneH(lane.id);
                                startResize(e, (_dx, dy) => setLaneHeights((m) => ({ ...m, [lane.id]: Math.max(LANE_H_MIN, Math.min(LANE_H_MAX, h0 + dy)) })));
                              }}
                            />
                          </div>
                        );
                      }}
                    </For>

                    {/* Shown on an empty timeline too (operator, 2026-10-14). */}
                    <Show when={true}>
                      <div class="tl2-row tl2-addrow">
                        <div class="tl2-h tl2-addwrap">
                          <button type="button" class="tl2-addlane" data-testid="tl2-addlane" data-info="Add a lane: one for each thing this timeline moves." onClick={(e) => openAddLaneMenu(e.currentTarget)}>
                            {ICON.add}Add a lane
                          </button>
                          <Show when={addLaneMenu() && addLaneAt()}>
                            {(at) => (
                              <div class="tl2-menu tl2-menu-up" role="menu" data-testid="tl2-addlane-menu"
                                style={{ position: 'fixed', left: `${at().left}px`, top: at().top != null ? `${at().top}px` : 'auto', bottom: at().bottom != null ? `${at().bottom}px` : 'auto', 'max-height': `${at().maxH}px`, 'overflow-y': 'auto' }}>
                                <LaneChoices lightsOnly={lightingOnly()} fixtures={fixtures().map((f) => ({ id: f.id, name: f.name }))} lightsPlay={lightsPlay()} drumsPlay={drumsPlay()} onDrums={openDrumPresets} onMotion={openMotionTargets} onOpvisual={openOpvMenu} onPick={addLaneOf} onEveryLight={addEveryLight} autos={autoTargets()} onAuto={(a) => void addAutomationLane(a.itemId, a.axis)} {...(!songRowShown() ? { onSong: addSongRow } : {})} />
                              </div>
                            )}
                          </Show>
                        </div>
                        <div class="tl2-c tl2-grid" style={gridStyle()} {...newLaneCell()}>
                          <Show when={browserDrag()?.newLane}>
                            <i class="tl2-dropline" style={{ left: `${browserDrag()!.position * ppb()}px` }} />
                          </Show>
                        </div>
                      </div>
                    </Show>
                    <div class="tl2-row tl2-fill">
                      <div class="tl2-h" />
                      <div class="tl2-c tl2-grid" style={gridStyle()} {...newLaneCell()}>
                        <Show when={browserDrag()?.newLane}>
                          <i class="tl2-dropline" style={{ left: `${browserDrag()!.position * ppb()}px` }} />
                        </Show>
                        {/* Only a COMPLETELY empty timeline (operator, 2026-10-16: "having a song in there isn't empty"). */}
                        <Show when={timeline()!.lanes.length === 0 && !timeline()!.testTrack}>
                          <div class="tl2-start">
                            <h2>{lightingOnly() ? 'An empty lighting timeline' : 'An empty timeline'}</h2>
                            {/* A click on a lane moves the playhead; adding is a double-click, or a tap on touch (2026-10-16: this said "click"). */}
                            <p>{tip('Add a lane for each thing you want this timeline to move. Then double-click a bar in the lane to put a change there, and press Space to play it.',
                              'Add a lane for each thing you want this timeline to move. Then tap a bar in the lane to put a change there, and press Play.')}</p>
                            <div class="tl2-qa">
                              <LaneChoices lightsOnly={lightingOnly()} fixtures={fixtures().map((f) => ({ id: f.id, name: f.name }))} lightsPlay={lightsPlay()} drumsPlay={drumsPlay()} onDrums={openDrumPresets} onMotion={openMotionTargets} onOpvisual={openOpvMenu} onPick={addLaneOf} onEveryLight={addEveryLight} autos={autoTargets()} onAuto={(a) => void addAutomationLane(a.itemId, a.axis)} {...(!songRowShown() ? { onSong: addSongRow } : {})} />
                            </div>
                          </div>
                        </Show>
                      </div>
                    </div>
                    <Show when={!!timeline()}>
                      <i class="tl2-ph" style={{ left: `${HEADER_W + shownBar() * ppb()}px` }} data-armed={armed() ? 'true' : undefined} data-cursor={!playing() && !armed() ? 'true' : undefined} data-scrub={scrubBar() !== null ? 'true' : undefined} />
                    </Show>
                  </div>
                </Show>
              </div>
              <Show when={!!palChooser()}>
                {(() => {
                  const c = () => palChooser()!;
                  return (
                    <div class="tl2-pop tl2-cpop tl2-palpop" data-testid="tl2-palpop" style={popAt(c().x, c().y, 340, 330, c().laneId)} role="menu">
                      <div class="tl2-pop-h">The palette OPVisual changes to at {barBeat(c().position, sig())}</div>
                      <OpvPalettePicker target={autoPickOpVisualBridge() ?? undefined} value="" onPick={(id, name) => placePalette(c().laneId, c().position, id, name)} />
                    </div>
                  );
                })()}
              </Show>
              <Show when={!!ovChooser()}>
                {(() => {
                  const c = () => ovChooser()!;
                  // OPVisual's grid as it reports itself, on the machine it runs on (its bank: names and size).
                  const target = autoPickOpVisualBridge() ?? undefined;
                  const release = watchOpvListing(target);
                  onCleanup(release);
                  const listing = () => opvListing(target);
                  const cols = () => Math.max(1, Math.min(OPV_COLUMNS, listing()?.cols ?? OPV_COLUMNS));
                  const rows = () => listing()?.rows ?? 0;
                  const cellName = (col: number, row: number) => listing()?.cells[cellKey(col, row)]?.label ?? '';
                  return (
                    <div class="tl2-pop tl2-cpop tl2-ovpop" style={popAt(c().x, c().y, 360, 420, c().laneId)} role="menu">
                      <div class="tl2-pop-h">What OPVisual plays at {barBeat(c().position, sig())}</div>
                      <div class="tl2-pop-g">Columns: every layer's clip in that column</div>
                      <div class="tl2-ov-cols">
                        <For each={Array.from({ length: cols() }, (_, i) => i + 1)}>
                          {(col) => <button type="button" role="menuitem" data-ov-col={col} onClick={() => placeOpv(c().laneId, c().position, opvColumnId(col), `Column ${col}`)}>{col}</button>}
                        </For>
                      </div>
                      <Show
                        when={rows() > 0}
                        fallback={<div class="tl2-text tl2-ov-note">{target ? "OPVisual's grid is not reachable right now, so only columns are offered." : 'No computer is set for OPVisual yet, so only columns are offered.'}</div>}
                      >
                        <div class="tl2-pop-g">Cells: one clip (OPVisual publishes every filled cell from its 2026-10-13 build; an older OPVisual only fires cells bound to a controller)</div>
                        <div class="tl2-ov-grid" style={{ 'grid-template-columns': `repeat(${cols()}, 1fr)` }}>
                          <For each={Array.from({ length: rows() }, (_, r) => r + 1)}>
                            {(row) => (
                              <For each={Array.from({ length: cols() }, (_, i) => i + 1)}>
                                {(col) => (
                                  <button
                                    type="button"
                                    role="menuitem"
                                    data-ov-cell={`${row}-${col}`}
                                    disabled={!cellName(col, row)}
                                    title={cellName(col, row) ? `Layer ${row}, column ${col}: ${cellName(col, row)}` : `Layer ${row}, column ${col}: empty`}
                                    onClick={() => placeOpv(c().laneId, c().position, opvCellId(row, col), `L${row} C${col}: ${cellName(col, row)}`)}
                                  >{cellName(col, row)}</button>
                                )}
                              </For>
                            )}
                          </For>
                        </div>
                      </Show>
                    </div>
                  );
                })()}
              </Show>
              <Show when={!!chooser()}>
                {(() => {
                  const c = () => chooser()!;
                  const lane = () => timeline()?.lanes.find((l) => l.id === c().laneId) ?? null;
                  // A Guitar lane offers guitar presets and snapshots only.
                  const groups = () => (lane() && laneRole(lane()!) === 'guitar' ? controls().filter((g) => GUITAR_KINDS.has(g.kind)) : controls());
                  const swapping = () => {
                    const id = c().replaceStepId;
                    const s = id ? lane()?.steps.find((x) => x.id === id) : undefined;
                    return s ? stepTitle(s) : null;
                  };
                  return (
                    <div class="tl2-pop tl2-cpop" style={{ ...popAt(c().x, c().y, 340, 480, c().laneId) }} role="menu">
                      <div class="tl2-pop-h">{swapping() ? `Swap ${swapping()} at ${barBeat(c().position, sig())} for:` : `What happens at ${barBeat(c().position, sig())}?`}</div>
                      <Show when={!swapping()}>
                        <button
                          type="button"
                          class="tl2-aimbtn"
                          data-info="Your canvas at full size: tap the control to put it here. Nothing is sent."
                          onClick={() => startCanvasPick(c().laneId, c().position)}
                        >{ICON.target}Point at it on your canvas</button>
                      </Show>
                      {canvasMap('chooser')}
                      <div class="tl2-pop-list">
                        <For each={groups()}>
                          {(g) => (
                            <>
                              <div class="tl2-pop-g">{g.label}</div>
                              <For each={g.items}>
                                {(it) => (
                                  <button type="button" role="menuitem" onClick={() => chooseFromChooser({ kind: 'item', itemId: it.id, label: itemText(it).label, itemKind: it.kind })}>
                                    <b>{itemText(it).label}</b><span>{itemText(it).meta}</span>
                                  </button>
                                )}
                              </For>
                            </>
                          )}
                        </For>
                        <Show when={groups().length === 0}>
                          <p class="tl2-bnote">{lane() && laneRole(lane()!) === 'guitar' ? "No guitar presets or snapshots on this project's canvas yet. Add one from Navigate, Add a control." : "No buttons or presets on this project's canvas yet. Add one from Navigate, Add a control."}</p>
                        </Show>
                        {/* Hue scenes (2026-10-23): a cue recalling one, straight from the Hue app's list. */}
                        <Show when={!swapping() && lane() && laneRole(lane()!) === 'controls'}>
                          <div class="tl2-pop-g">Hue scenes</div>
                          <HueSceneList testid="tl2-hue-chooser"
                            onPick={(e) => { const ref = hueRefOf(e); chooseFromChooser({ kind: 'hue', ref, label: hueSceneLabel(ref) }); }} />
                        </Show>
                      </div>
                    </div>
                  );
                })()}
              </Show>
              <Show when={!!transPop()}>
                {(() => {
                  const tp = () => transPop()!;
                  const lane = () => timeline()?.lanes.find((l) => l.id === tp().laneId) ?? null;
                  const from = () => {
                    const s = lane()?.steps.find((x) => x.id === tp().stepId);
                    return s && isColorMarkerStep(s) ? s : null;
                  };
                  const next = () => (lane() && from() ? nextKeyframe(lane()!, from()!) : null);
                  return (
                    <Show when={lane() && from() && next()}>
                      <div
                        class="tl2-pop tl2-tpop"
                        role="dialog"
                        aria-label="Transition"
                        style={{
                          ...popAt(tp().x - 150, tp().y, 300, 190, tp().laneId),
                        }}
                      >
                        <div class="tl2-pop-h">{colourName(from()!.color, from()!.label)} to {colourName(next()!.color, next()!.label)}</div>
                        <div class="tl2-tbody">
                          <TransitionControls
                            timelineId={tid()}
                            laneId={lane()!.id}
                            step={from()!}
                            next={next()}
                            sig={sig()}
                            more={fadeMore()}
                            onMore={setFadeMore}
                          />
                        </div>
                      </div>
                    </Show>
                  );
                })()}
              </Show>
            </section>

            <Show when={detailOpen()}>
              <div
                class="tl2-split-h"
                data-info="Drag to resize the detail panel."
                onPointerDown={(e) => { const h0 = detailH(); startResize(e, (_dx, dy) => setDetailH(Math.max(120, Math.min(520, h0 - dy)))); }}
              />
            </Show>
            <section class="tl2-detail">
              <div class="tl2-ptitle">
                {/* Back to the timeline's own settings from a lane, step, section or the song (2026-10-28, operator: "no
                    way of delecting a track to get to the timeline info"; Escape did it, unsaid). */}
                <Show when={timeline() && sel().type !== 'timeline'}>
                  <button type="button" class="tl2-crumb" data-testid="tl2-crumb" title="Back to this timeline's settings (Esc)"
                    data-info="Back to this timeline's own settings: its length, loop, lights at start and end. Esc does the same."
                    onClick={() => { setSel({ type: 'timeline' }); setDetailOpen(true); }}>
                    <span aria-hidden="true">‹</span>{timeline()!.name}
                  </button>
                </Show>
                <b>{detailTitle()}</b>
                <span>{detailSub()}</span>
                <span class="tl2-sp" />
                <button type="button" class="tl2-fold" title={detailOpen() ? 'Fold the detail panel' : 'Show the detail panel'} data-info="Fold the detail panel to its title." onClick={() => setDetailOpen(!detailOpen())}>
                  <span style={{ display: 'inline-flex', transform: detailOpen() ? 'rotate(180deg)' : undefined }}>{ICON.up}</span>
                </button>
              </div>
              <Show when={detailOpen()}>
                <div class="tl2-dbody">
                  <div class="tl2-props">{detailProps()}</div>
                  <div class="tl2-vs" />
                  <div class="tl2-big">{detailList()}</div>
                </div>
              </Show>
            </section>
          </div>
        </div>

        {/* ───────── performance strip ───────── */}
        <section class="tl2-perf" data-open={perfOpen() ? 'true' : undefined}>
          <button type="button" class="tl2-ptitle tl2-perfbar" data-info="Performance strip: click to open or close." onClick={() => setPerfOpen(!perfOpen())}>
            <span style={{ display: 'inline-flex', transform: perfOpen() ? undefined : 'rotate(180deg)' }}>{ICON.up}</span>
            <b>Performance</b><span class="tl2-sp" /><span>Section pads</span>
          </button>
          <Show when={perfOpen() && timeline()}>
            <div class="tl2-pbody">
              <For each={timeline()!.sections}>
                {(s) => (
                  <button
                    type="button" class="tl2-pad"
                    data-playing={playingSectionId() === s.id ? 'true' : undefined}
                    data-queued={queuedSectionId() === s.id ? 'true' : undefined}
                    aria-pressed={playingSectionId() === s.id}
                    data-info={`${s.name}: while the song plays, jump to it at the next bar.`}
                    onClick={() => jumpTo(s.id)}
                  >{s.name}</button>

                )}
              </For>
              <span class="tl2-vsep" />
              <button type="button" class="tl2-key" onClick={togglePlay}>{playing() || armed() ? 'Stop' : 'Play'}</button>
            </div>
          </Show>
        </section>

        <footer class="tl2-status">
          <span class="tl2-info">{info() || defaultInfo()}</span>
          {/* Where it plays and whether OPBridge has it, down here with the other status (2026-10-16): in the toolbar
              they had what the tools left, 77 px of the 254 they need at 1500 px and 15 at 1280, so they were cut off
              mid-word or not there at all, sync failures included. */}
          <Show when={timeline()}>
            <div class="tl2-pills">
              <TimelinePlaysOnPill timeline={timeline()!} />
              <TimelineSyncPill id={tid()} />
            </div>
          </Show>
          <Show when={timeline()}>
            <span class="tl2-counts">{timeline()!.lanes.length} lane{timeline()!.lanes.length === 1 ? '' : 's'}, {timeline()!.lanes.reduce((n, l) => n + l.steps.length, 0)} changes</span>
          </Show>
          <span><b>{Math.round(shownBpm() || 0)} BPM</b></span>
        </footer>

        {/* the thing being dragged from the browser */}
        <Show when={browserDrag()}>
          <div class="tl2-drag" data-ok={browserDrag()!.ok ? 'true' : undefined} style={{ left: `${browserDrag()!.x + 12}px`, top: `${browserDrag()!.y + 10}px` }}>
            <Show when={browserDrag()!.payload.kind === 'colour'}>
              <i class="tl2-dot" style={{ background: rgbCss((browserDrag()!.payload as { colour: StageColor }).colour.rgb) }} />
            </Show>
            {payloadLabel(browserDrag()!.payload)}
          </div>
        </Show>
        {/* the box being dragged over the lanes */}
        <Show when={box()}>
          <div
            class="tl2-box"
            style={{
              left: `${Math.min(box()!.x0, box()!.x1)}px`, top: `${Math.min(box()!.y0, box()!.y1)}px`,
              width: `${Math.abs(box()!.x1 - box()!.x0)}px`, height: `${Math.abs(box()!.y1 - box()!.y0)}px`,
            }}
          />
        </Show>
        {/* point at it on the real canvas: portalled out, so it shows while the editor is hidden */}

        <Show when={canvasPick()}>{canvasPickVeil()}</Show>
        {/* Recording: the editor steps aside so the canvas can be played; this bar says what is happening. */}
        <Show when={recTake()}>
          <Portal>
            <div class="tl2-recbar" role="status">
              <i class="tl2-recdot" aria-hidden="true" />
              <b>{(() => {
                const armedNames = (timeline()?.lanes ?? []).filter((l) => recArmed().has(l.id) && recordable(l)).map((l) => l.name);
                return armedNames.length ? `Recording into ${armedNames.join(', ')}` : 'Recording what you press and move';
              })()}</b>
              <span>{playing() ? `bar ${barBeat(playBar(), sig())}` : 'starting at the next bar'}, {recordedCount()} press{recordedCount() === 1 ? '' : 'es'}{recordedMoveCount() ? `, ${recordedMoveCount()} moved` : ''}</span>
              <button type="button" class="tl2-recrep" aria-pressed={recReplace()} title="Replace: wipe what this take records into, in the bars it covers" onClick={() => setRecReplace(!recReplace())}>Replace</button>
              <button type="button" class="tl2-recstop" onClick={stopRecording}>Stop</button>
            </div>
          </Portal>
        </Show>
        {/* the song import window (2026-10-13): upload, analyse, ready, then mark the sections */}
        <Show when={timeline()}>
          <SongImportModal timeline={timeline()!} onMark={() => {}} />
        </Show>
        {/* the right-click (or long-press) menu, DEC-245 */}
        <Show when={ctxMenu()}>
          {(() => {
            const m = () => ctxMenu()!;
            const run = (fn: () => void) => {
              if (performance.now() < menuGuardUntil) return;
              setCtxMenu(null);
              fn();
            };
            return (
              <div class="tl2-ctx" role="menu" aria-label={m().title} style={{ left: `${m().x}px`, top: `${m().y}px` }}>
                <div class="tl2-ctx-h">{m().title}</div>
                <For each={m().entries}>
                  {(en) => (
                    'sep' in en ? <i class="tl2-ctx-sep" />
                      : 'hues' in en ? (
                        <div class="tl2-ctx-sw">
                          <span>{en.hues}</span>
                          <LaneHueRow current={en.current} pick={(h) => run(() => en.pick(h))} />
                        </div>
                      )
                      : 'swatches' in en ? (
                        <div class="tl2-ctx-sw">
                          <span>{en.swatches}</span>
                          <div class="tl2-swatches tl2-swatches-d">
                            <For each={STAGE_COLORS}>
                              {(c) => (
                                <button
                                  type="button"
                                  class="tl2-swatch"
                                  style={{ background: c.label === 'Off' ? undefined : rgbCss(c.rgb) }}
                                  data-off={c.label === 'Off' ? 'true' : undefined}
                                  aria-pressed={!!en.current && sameRgb(c.rgb, en.current)}
                                  title={c.label}
                                  aria-label={c.label}
                                  onClick={() => run(() => en.pick(c))}
                                />
                              )}
                            </For>
                          </div>
                        </div>
                      ) : 'open' in en ? (
                        <button
                          type="button"
                          role="menuitem"
                          class="tl2-ctx-it"
                          aria-haspopup="menu"
                          data-menu-folder={en.label === 'Back' ? undefined : 'true'}
                          onClick={() => { if (performance.now() >= menuGuardUntil) placeMenu(en.open(), m().x, m().y); }}
                        >
                          <span class="tl2-ctx-ck">{en.label === 'Back' ? '\u2039' : null}</span>
                          <span>{en.label}</span>
                          <em>{en.hint ? `${en.hint} ` : ''}{en.label === 'Back' ? '' : '\u203A'}</em>
                        </button>
                      ) : (
                        <button
                          type="button"
                          role="menuitem"
                          class="tl2-ctx-it"
                          data-danger={en.danger ? 'true' : undefined}
                          aria-checked={en.checked === undefined ? undefined : en.checked}
                          disabled={en.disabled}
                          onClick={() => run(en.run)}
                        >
                          <span class="tl2-ctx-ck">{en.checked ? ICON.check : null}</span>
                          <span>{en.label}</span>
                          <Show when={en.hint}><em>{en.hint}</em></Show>
                        </button>
                      )
                  )}
                </For>
              </div>
            );
          })()}
        </Show>
      </div>
    </Portal>
  );

  // ── the detail panel: whatever is selected, else the timeline ──
  function detailTitle(): string {
    const s = sel(); const t = timeline();
    if (!t) return 'Detail';
    if (s.type === 'section') return t.sections.find((x) => x.id === s.id)?.name ?? 'Section';
    if (s.type === 'track') return t.testTrack?.name ?? 'Song';
    if (s.type === 'step') return selStep() ? stepTitle(selStep()!) : 'Change';
    if (s.type === 'lane') return selLane()?.name ?? 'Lane';
    if (s.type === 'lanes') return `${s.ids.length} lanes`;
    if (s.type === 'steps') return `${s.refs.length} change${s.refs.length === 1 ? '' : 's'}`;
    return t.name;
  }
  function detailSub(): string {
    const s = sel(); const t = timeline();
    if (!t) return '';
    if (s.type === 'section') return 'Section';
    if (s.type === 'track') return 'The song under this timeline';
    if (s.type === 'step') return `${selLane()?.name ?? ''}, ${barBeat(selStep()?.position ?? 0, sig())}`;
    // The lane can be gone for a moment (an undo removed the selected lane)
    // before the selection follows; say nothing rather than throw.
    if (s.type === 'lane') { const l = selLane(); return l ? laneDrives(l) : ''; }
    if (s.type === 'lanes') return t.lanes.filter((l) => s.ids.includes(l.id)).map((l) => l.name).join(', ');
    if (s.type === 'steps') {
      const n = new Set(s.refs.map((r) => r.laneId)).size;
      const where = s.range ? `from ${barBeat(s.range.start, sig())} to ${barBeat(s.range.end, sig())}` : '';
      return [`on ${n} lane${n === 1 ? '' : 's'}`, where].filter(Boolean).join(', ');
    }
    return t.tier === 'song' ? 'Song timeline' : t.tier === 'action' ? 'Action timeline' : 'Genre timeline';
  }
  function detailProps(): JSX.Element {
    return (
      <Show when={timeline()} fallback={<p class="tl2-none">Make a timeline to see it here.</p>}>
        <Show when={sel().type === 'timeline'}>
          <TimelineProps
            timeline={timeline()!}
            onDuplicate={duplicateTimelineNow}
            onDelete={deleteTimelineNow}
          />
        </Show>
        <Show when={sel().type === 'section'}>
          <SectionProps timeline={timeline()!} sectionId={(sel() as { id: string }).id} onDuplicate={() => duplicateSectionHere((sel() as { id: string }).id)} />
        </Show>
        <Show when={sel().type === 'track'}>
          <TestTrackProps
            timeline={timeline()!}
            selectedSectionName={lastSectionName()}
            Prop={Prop}
          />
        </Show>
        <Show when={sel().type === 'lane' && !!selLane()}>
          <LaneProps
            timeline={timeline()!}
            lane={selLane()!}
            drives={laneDrives(selLane()!)}
            wont={laneWontPlay(selLane()!)}
            silent={!laneAudible(timeline()!, selLane()!) && !selLane()!.mute ? 'Another lane is soloed, so this one does not play.' : null}
            onRemove={() => removeLaneHere(selLane()!.id)}
            onDuplicate={() => duplicateLanes([selLane()!.id])}
            onHue={(h) => setLaneHue([selLane()!.id], h)}
            onFlag={(k) => toggleLaneFlag([selLane()!.id], k)}
            onAllLights={() => toggleAllLights(selLane()!.id)}
          />
        </Show>
        <Show when={sel().type === 'lanes'}>
          {(() => {
            const ids = () => (sel() as { ids: string[] }).ids;
            const lanes = () => timeline()!.lanes.filter((l) => ids().includes(l.id));
            return (
              <>
                <Prop label="Lanes"><div class="tl2-text">{lanes().map((l) => l.name).join(', ')}</div></Prop>
                <Prop label="Plays">
                  <div class="tl2-seg">
                    <button type="button" aria-pressed={lanes().every((l) => l.mute)} data-info="Mute: these lanes do not play." onClick={() => toggleLaneFlag(ids(), 'mute')}>Mute</button>
                    <button type="button" aria-pressed={lanes().every((l) => l.solo)} data-info="Solo: while any lane is soloed, only soloed lanes play." onClick={() => toggleLaneFlag(ids(), 'solo')}>Solo</button>
                  </div>
                </Prop>
                <div class="tl2-actions">
                  <button type="button" class="tl2-btn" onClick={() => selectChangesOf(ids())}>Select their changes</button>
                  <button type="button" class="tl2-btn" onClick={() => duplicateLanes(ids())}>Duplicate</button>
                  <button type="button" class="tl2-btn tl2-danger" onClick={() => removeLanes(ids())}>Delete {ids().length} lanes</button>
                </div>
              </>
            );
          })()}
        </Show>
        <Show when={sel().type === 'steps'}>
          <Prop label="Selected"><div class="tl2-text">{detailSub()}</div></Prop>
          <div class="tl2-actions">
            <button type="button" class="tl2-btn" data-info={`Repeat them straight after (${keyHint('D')}).`} onClick={() => duplicateSelectedSteps()}>Duplicate</button>
            <button type="button" class="tl2-btn" onClick={() => void copyRefs(selRefs(), false)}>Copy</button>
            <button type="button" class="tl2-btn tl2-danger" onClick={() => removeSelectedSteps()}>Delete {(sel() as { refs: StepRef[] }).refs.length} changes</button>
          </div>
        </Show>
        <Show when={sel().type === 'step' && !!selStep() && isHitBlockStep(selStep()!)}>
          <DrumBlockPanel
            timeline={timeline()!}
            lane={selLane()!}
            step={selStep() as HitBlockStep}
            sig={sig()}
            bpm={shownBpm()}
            wont={warnings().get(selStep()!.id) ?? null}
            playsHere={drumsPlay()}
            Prop={Prop}
            onRemove={deleteSelectedStep}
            onDuplicate={() => void duplicateSelectedStep()}
          />
        </Show>
        <Show when={sel().type === 'step' && !!selStep() && isHitRouteStep(selStep()!)}>
          <DrumRoutePanel timeline={timeline()!} step={selStep() as { source: HitBlockStep['source']; lookId: string }} Prop={Prop} onRemove={deleteSelectedStep} />
        </Show>
        <Show when={sel().type === 'step' && !!selStep() && !isHitBlockStep(selStep()!) && !isHitRouteStep(selStep()!)}>
          <StepProps
            timeline={timeline()!}
            lane={selLane()!}
            step={selStep()!}
            sig={sig()}
            unit={unit()}
            title={stepTitle(selStep()!)}
            meta={selStep()!.kind === 'buttonRef' ? (controlById((selStep() as { buttonId: string }).buttonId) ? itemText(controlById((selStep() as { buttonId: string }).buttonId)!).meta : 'Not on this project\'s canvas') : ''}
            wont={warnings().get(selStep()!.id) ?? null}
            onRemove={deleteSelectedStep}
            fadeMore={fadeMore()}
            onFadeMore={setFadeMore}
            lead={leadInfo(selLane()!, selStep()!)}
            onLead={(bars, curve) => setLeadIn(selLane()!.id, selStep()!.id, bars, curve)}
          />
        </Show>
      </Show>
    );
  }
  function detailList(): JSX.Element {
    const lane = selLane();
    if (!timeline()) return <div class="tl2-none">Nothing selected.</div>;
    if (!lane) {
      const t = timeline()!;
      return (
        <div class="tl2-overview">
          <For each={laneOrder()} fallback={<div class="tl2-none">No lanes yet. Add one from the browser or the empty timeline.</div>}>
            {(l) => (
              <button type="button" class="tl2-cue" onClick={() => setSel({ type: 'lane', id: l.id })}>
                <span class="tl2-cue-at">{roleIcon(l)}</span>
                <b>{l.name}</b>
                <em>{l.steps.length} change{l.steps.length === 1 ? '' : 's'}</em>
              </button>
            )}
          </For>
        </div>
      );
    }
    // A CUE LIST for the selected lane (the review's pro ask): one row per
    // change, in time order.
    const sorted = [...lane.steps].sort((a, b) => a.position - b.position);
    return (
      <div class="tl2-cues">
        <For each={sorted} fallback={<div class="tl2-none">No changes on this lane yet. {tip('Double-click', 'Tap')} a bar in the lane.</div>}>
          {(s) => (
            <div
              class="tl2-cue"
              data-step-id={s.id}
              data-lane-id={lane.id}
              role="button"
              tabindex={0}
              aria-selected={isStepSel(s.id)}
              data-wont={warnings().get(s.id) ? 'true' : undefined}
              onClick={() => setSel({ type: 'step', laneId: lane.id, stepId: s.id })}
            >
              <span class="tl2-cue-at">{barBeat(s.position, sig())}</span>
              <Show when={s.kind === 'colorMarker'}>
                <i class="tl2-dot" style={{ background: rgbCss((s as ColorMarkerStep).color) }} />
              </Show>
              <b>{stepTitle(s)}</b>
              <em>{s.kind === 'colorMarker' ? thenText(lane, s, sig()) : s.kind === 'lightRelease' ? 'Every light back to its own lane' : s.kind === 'automationPoint' ? (s.curve === 'hold' ? 'Holds, then jumps' : `${curveLabel(s.curve)} to the next`) : s.kind === 'pattern' ? `Repeats for ${barsText(s.durationBars)}` : s.kind === 'hitBlock' ? `Every ${barsText(s.cycleBars)} for ${barsText(s.durationBars)}` : s.kind === 'hitRoute' ? 'Until the next for that drum' : (s.kind === 'buttonRef' || s.kind === 'animation') && (s as { durationBars?: number }).durationBars ? `Held ${barsText(stepDuration(s))}` : 'Instant'}</em>
              <Show when={warnings().get(s.id)}><i class="tl2-wont" title={warnings().get(s.id)}>⊘</i></Show>
              <button type="button" class="tl2-x" title="Delete" aria-label="Delete" onClick={(e) => { e.stopPropagation(); removeStep(tid(), lane.id, s.id); }}>{ICON.close}</button>
            </div>
          )}
        </For>
      </div>
    );
  }
}

// ─── pieces ─────────────────────────────────────────────────────────────────

function BrowserGroup(props: { title: string; children: JSX.Element }) {
  const [open, setOpen] = createSignal(true);
  return (
    <div class="tl2-bgroup" data-open={open() ? 'true' : undefined}>
      <button type="button" class="tl2-bcat" onClick={() => setOpen(!open())}>
        <span class="tl2-tri">{ICON.tri}</span>{props.title}
      </button>
      <Show when={open()}>{props.children}</Show>
    </div>
  );
}

function LiveTargetRow(props: { target: LiveTarget; picked: Payload | null; onPointerDown: (e: PointerEvent, p: Payload) => void }) {
  const reading = () => props.target.read();
  return (
    <button
      type="button"
      class="tl2-bitem"
      disabled={!reading()}
      data-picked={props.picked?.kind === 'live' && props.picked.targetId === props.target.id ? 'true' : undefined}
      data-info={reading()
        ? `${props.target.label}, now ${reading()!.display}: drag onto a Controls lane to set it back to this there.`
        : `${props.target.label}: not known yet, so there is nothing to capture.`}
      onPointerDown={(e) => {
        const r = reading();
        if (r) props.onPointerDown(e, { kind: 'live', targetId: props.target.id, value: r.value, label: `${props.target.label}: ${r.display}` });
      }}
    >
      {ICON.knob}<span>{props.target.label}</span><em>{reading()?.display ?? 'not known'}</em>
    </button>
  );
}

/** Controls first: it is the lane that plays today. */
function LaneChoices(props: { lightsOnly?: boolean; fixtures: Array<{ id: string; name: string }>; lightsPlay: boolean; drumsPlay: boolean; onDrums: (x: number, y: number) => void; onMotion: (x: number, y: number) => void; onOpvisual: (x: number, y: number) => void; onPick: (role: NewLaneRole, fixtureId?: string) => void; onEveryLight: () => void; onSong?: () => void; autos: AutoTarget[]; onAuto: (a: AutoTarget) => void }) {
  return (
    <>
      <Show when={props.onSong && !props.lightsOnly}>
        <button type="button" data-testid="tl2-add-song" data-info="Song: an audio file under the timeline, to build the show against and play with it." onClick={() => props.onSong!()}><span class="tl2-song-i" aria-hidden="true">♪</span>Song</button>
      </Show>
      <Show when={props.fixtures.length > 0}>
        <button type="button" class="tl2-everylight" data-info={`The All lights master, and a lane for each of your ${props.fixtures.length} lights.`} onClick={() => props.onEveryLight()}>{ICON.lights}Add every light in your rig<em>{props.fixtures.length}</em></button>
      </Show>
      {/* A lighting timeline (DEC-323) takes light lanes only. */}
      <Show when={!props.lightsOnly}>
        <button type="button" data-testid="tl2-add-controls" data-info="Controls: presses of the buttons on your canvas." onClick={() => props.onPick('controls')}>{ICON.controls}Controls</button>
        <button type="button" data-testid="tl2-add-guitar" data-info="Guitar: the guitar's preset and snapshot changes, each one's name running until the next." onClick={() => props.onPick('guitar')}>{ICON.guitar}Guitar</button>
      </Show>
      <button type="button" class={props.lightsPlay ? undefined : 'tl2-later'} data-info={`Lights: colour changes for every light at once.${props.lightsPlay ? '' : ` ${LIGHTS_WONT}.`}`} onClick={() => props.onPick('lights')}>{ICON.lights}Lights<Show when={!props.lightsPlay}><em>won't play yet</em></Show></button>
      <button type="button" data-testid="tl2-add-drums" class={props.drumsPlay ? undefined : 'tl2-later'}
        data-info={`Drum lights: your kick, snare and hi-hat light the rig as you play, a look for most of each cycle and a fill at its end. Pick a ready-made one.${props.drumsPlay ? '' : ` ${DRUMS_WONT[0]!.toUpperCase()}${DRUMS_WONT.slice(1)}.`}`}
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); props.onDrums(r.left, r.bottom); }}>{ICON.drum}Drum lights<Show when={!props.drumsPlay}><em>won't play yet</em></Show></button>
      <Show when={!props.lightsOnly}>
        <button type="button" data-testid="tl2-add-motion"
          data-info="OP-Motion: Tone, Air, a Move or Live's crossfader, moved over time on your submixes or Main. Pick what it moves."
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); props.onMotion(r.left, r.bottom); }}>{ICON.auto}OP-Motion</button>
        <button type="button" data-testid="tl2-add-opv"
          data-info="OPVisual: pick what a new lane controls: cells and columns, colour, palette, visualiser knobs, layers and the whole picture."
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); props.onOpvisual(r.left, r.bottom); }}>{ICON.lights}OPVisual</button>
      </Show>
      <For each={props.lightsOnly ? props.fixtures : props.fixtures.slice(0, 6)}>
        {(f) => <button type="button" class={props.lightsPlay ? undefined : 'tl2-later'} data-info={`${f.name} on its own.${props.lightsPlay ? '' : ` ${LIGHTS_WONT}.`}`} onClick={() => props.onPick('lights', f.id)}>{ICON.lights}{f.name}<Show when={!props.lightsPlay}><em>won't play yet</em></Show></button>}
      </For>
      <For each={props.lightsOnly ? [] : props.autos.slice(0, 8)}>
        {(a) => <button type="button" data-testid="tl2-add-auto" data-info={`Automation: ${a.label}'s moves over time, as a curve (${a.hint}). Press Record and move it, or double-click the lane to add points.`} onClick={() => props.onAuto(a)}>{ICON.auto}{a.label}<em>automation</em></button>}
      </For>
    </>
  );
}

/** The presets in the length dropdown (operator, 2026-10-15). */
const LENGTH_PRESETS = [4, 8, 16, 32, 64] as const;

/**
 * The timeline's length in the toolbar display (operator, 2026-10-15: "bars should be adjustable in the bars
 * display. add a - + to the number. also add a dropdown with 4/8/16/32/64/custom"). Same rule as the Length row
 * in the timeline's properties (`setTimelineLength`): the LAST section takes the difference.
 *
 *   - / +     one bar.
 *   number    always typeable; Enter or leaving the box sets it, Esc puts it back.
 *   dropdown  4/8/16/32/64; reads Custom when the length is none of them, and Custom puts the cursor in the
 *             number. A preset shorter than the other sections allow is greyed out, never reached by trimming.
 *   hidden    steps at or past the end are KEPT (lengthen again and they are back) and counted here. OPBridge
 *             ends the timeline at the sections' total and never reaches them.
 */
function LengthControl(props: { timeline: Timeline }) {
  let input: HTMLInputElement | undefined;
  const total = () => timelineTotalBars(props.timeline);
  const min = () => minTimelineLength(props.timeline);
  const max = () => maxTimelineLength(props.timeline);
  const hasSections = () => props.timeline.sections.length > 0;
  /** A timeline following its song is the song's length (2026-10-21). */
  const editable = () => hasSections() && !followsSong(props.timeline);
  const preset = () => (LENGTH_PRESETS as readonly number[]).includes(total()) ? String(total()) : 'custom';
  const hidden = () => props.timeline.lanes.reduce((n, l) => n + l.steps.filter((st) => st.position >= total() - 1e-6).length, 0);
  const set = (n: number) => setTimelineLength(props.timeline.id, n);
  function commit(el: HTMLInputElement) {
    const n = Math.round(Number(el.value));
    if (Number.isFinite(n) && el.value.trim() !== '') set(Math.max(min(), Math.min(max(), n)));
    el.value = String(total());
  }
  return (
    <div
      class="tl2-len"
      data-testid="tl2-lcd-length"
      data-info={followsSong(props.timeline)
        ? `How long this timeline is: ${barsText(total())}, the song's length. Its sections are the song's parts.`
        : hasSections()
        ? `How long this timeline is: ${barsText(total())}. The last section grows or shrinks to fit${hidden() ? `; ${hidden()} change${hidden() === 1 ? '' : 's'} past the end ${hidden() === 1 ? 'is' : 'are'} kept, hidden, and come${hidden() === 1 ? 's' : ''} back if you make it longer` : ''}.`
        : 'How long this timeline is: add a section to give it a length.'}
    >
      <button type="button" class="tl2-lenbtn" aria-label="One bar shorter" disabled={!editable() || total() <= min()} onClick={() => set(total() - 1)}>{ICON.minus}</button>
      <input
        ref={input}
        class="tl2-lennum"
        type="text"
        inputmode="numeric"
        aria-label="Length in bars"
        value={total()}
        disabled={!editable()}
        onFocus={(e) => e.currentTarget.select()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); e.currentTarget.value = String(total()); e.currentTarget.blur(); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); set(total() + 1); e.currentTarget.value = String(total()); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); set(total() - 1); e.currentTarget.value = String(total()); }
        }}
        onBlur={(e) => commit(e.currentTarget)}
      />
      <button type="button" class="tl2-lenbtn" aria-label="One bar longer" disabled={!editable() || total() >= max()} onClick={() => set(total() + 1)}>{ICON.add}</button>
      <small>{total() === 1 ? 'bar' : 'bars'}</small>
      <select
        class="tl2-lenpick"
        aria-label="Length preset"
        value={preset()}
        disabled={!editable()}
        onChange={(e) => {
          const v = e.currentTarget.value;
          if (v === 'custom') { input?.focus(); return; }
          set(Number(v));
          e.currentTarget.value = preset();
        }}
      >
        <For each={LENGTH_PRESETS}>
          {(n) => <option value={String(n)} disabled={n < min() || n > max()}>{n}</option>}
        </For>
        <option value="custom">Custom</option>
      </select>
      <Show when={hidden() > 0}>
        <small class="tl2-lenhid">{hidden()} hidden</small>
      </Show>
    </div>
  );
}

function Prop(props: { label: string; children: JSX.Element }) {
  return <div class="tl2-prop"><span>{props.label}</span><div class="tl2-pv">{props.children}</div></div>;
}

function TimelineProps(props: { timeline: Timeline; onDuplicate: () => void; onDelete: () => void }) {
  let before = '';
  return (
    <>
      <Prop label="Name">
        <input
          class="tl2-field"
          value={props.timeline.name}
          onFocus={() => { before = props.timeline.name; }}
          onInput={(e) => renameTimeline(props.timeline.id, e.currentTarget.value)}
          onBlur={() => commitTimelineName(props.timeline.id, before)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label="Timeline name"
        />
      </Prop>
      <Prop label="Kind"><div class="tl2-text" data-testid="tl2-kind">{props.timeline.purpose === 'lights' ? 'Lighting: your lights only, on its own or as the default lights' : props.timeline.tier === 'song' ? 'Song: sections, launched from a pad' : props.timeline.tier === 'action' ? 'Action: fired from one button' : "Genre: OPBridge can't play these yet"}</div></Prop>
      <Prop label="Length">
        {/* Editable (2026-10-14): the last section takes the difference. */}
        <div class="tl2-stepper" data-testid="tl2-length">
          <button type="button" aria-label="One bar shorter" disabled={followsSong(props.timeline)} onClick={() => setTimelineLength(props.timeline.id, timelineTotalBars(props.timeline) - 1)}>{ICON.minus}</button>
          <input class="tl2-field tl2-num" type="number" min="1" max="1024" aria-label="Length in bars" disabled={followsSong(props.timeline)} value={timelineTotalBars(props.timeline)}
            onChange={(e) => { const n = Number(e.currentTarget.value); if (Number.isFinite(n) && n >= 1) setTimelineLength(props.timeline.id, n); else e.currentTarget.value = String(timelineTotalBars(props.timeline)); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
          <button type="button" aria-label="One bar longer" disabled={followsSong(props.timeline)} onClick={() => setTimelineLength(props.timeline.id, timelineTotalBars(props.timeline) + 1)}>{ICON.add}</button>
          <span class="tl2-text">bars, {props.timeline.sections.length} section{props.timeline.sections.length === 1 ? '' : 's'}</span>
        </div>
      </Prop>
      <Prop label="Loop">
        <div class="tl2-seg">
          <button type="button" aria-pressed={!timelineLoops(props.timeline)} onClick={() => setTimelineLoop(props.timeline.id, false)}>Off</button>
          <button type="button" aria-pressed={timelineLoops(props.timeline)} onClick={() => setTimelineLoop(props.timeline.id, true)}>On</button>
        </div>
      </Prop>
      {/* Every light dark at the start, or left as it was (2026-10-20); only on a timeline that drives lights. */}
      <Show when={props.timeline.lanes.some((l) => l.laneKind === 'dmx-fixture' || l.laneKind === 'dmx-rig' || l.laneKind === 'instrument')}>
        <Prop label="Lights at start">
          <div class="tl2-seg" data-testid="tl2-lights-at-start">
            <button type="button" aria-pressed={timelineStartsDark(props.timeline)}
              title="Every light goes dark when the timeline starts and stays dark until its own lane colours it, so each play starts the same."
              data-info="Every light goes dark when the timeline starts and stays dark until its own lane colours it, so each play starts the same."
              onClick={() => setTimelineLightsAtStart(props.timeline.id, props.timeline.tier === 'song' ? undefined : 'off')}>Off</button>
            <button type="button" aria-pressed={!timelineStartsDark(props.timeline)}
              title="Lights keep whatever they were showing until their own lane colours them."
              data-info="Lights keep whatever they were showing until their own lane colours them."
              onClick={() => setTimelineLightsAtStart(props.timeline.id, props.timeline.tier === 'song' ? 'asIs' : undefined)}>As they are</button>
          </div>
        </Prop>
        <Prop label="Lights at end">
          <div class="tl2-seg" data-testid="tl2-lights-at-end">
            <button type="button" aria-pressed={timelineEndsDark(props.timeline)}
              title="When the timeline ends or is stopped, every light it drove goes dark."
              data-info="When the timeline ends or is stopped, every light it drove goes dark."
              onClick={() => setTimelineLightsAtEnd(props.timeline.id, props.timeline.tier === 'song' ? undefined : 'off')}>Off</button>
            <button type="button" aria-pressed={!timelineEndsDark(props.timeline)}
              title="When the timeline ends or is stopped, every light it drove goes back to how it was before it started."
              data-info="When the timeline ends or is stopped, every light it drove goes back to how it was before it started."
              onClick={() => setTimelineLightsAtEnd(props.timeline.id, props.timeline.tier === 'song' ? 'restore' : undefined)}>Back to how they were</button>
          </div>
          <Show when={!resolveBridges(timelineHomeTarget(props.timeline)).some(bridgeEndsLightsDark)}>
            <div class="tl2-text tl2-dim">Its computer's OPBridge needs an update before this applies; until then the lights go back to how they were.</div>
          </Show>
        </Prop>
      </Show>
      <Show when={paintsLights(props.timeline)}>
        <ArrivalRow label="When started" testId="tl2-start-arrival" timeline={props.timeline} value={props.timeline.arrival ?? 'cut'}
          slam={startSlam(props.timeline)} onPick={(a) => setTimelineArrival(props.timeline.id, a)} />
      </Show>
      {/* A hand on a fader wins over its automation (OPBridge S324); when the timeline takes it back (2026-10-16). */}
      <Show when={props.timeline.lanes.some((l) => l.laneKind === 'automation')}>
        <Prop label="Fader grabbed">
          <div class="tl2-seg" data-testid="tl2-touchmode">
            <For each={TOUCH_MODES}>
              {(m) => (
                <button type="button" title={m.info} data-info={m.info}
                  aria-pressed={(props.timeline.touchMode ?? 'nextBarAfterRelease') === m.mode}
                  onClick={() => setTimelineTouchMode(props.timeline.id, m.mode === 'nextBarAfterRelease' ? undefined : m.mode)}>{m.label}</button>
              )}
            </For>
          </div>
          <Show when={!resolveBridges(timelineHomeTarget(props.timeline)).some(bridgeTouches)}>
            <div class="tl2-text tl2-dim">Its computer's OPBridge needs an update before a hand on a fader wins; until then the timeline keeps moving it.</div>
          </Show>
        </Prop>
      </Show>
      <div class="tl2-actions">
        <button type="button" class="tl2-btn" onClick={() => props.onDuplicate()}>Duplicate</button>
        <button type="button" class="tl2-btn tl2-danger" onClick={() => props.onDelete()}>Delete timeline</button>
      </div>
    </>
  );
}

/** The lights a slam lands on at the timeline's first section (a start at bar 0 lands there), or none. */
function startSlam(t: Timeline): string[] {
  const first = timelinePlaySections(t).find((x) => x.id !== leadInSectionId(t));
  return first ? sectionSlam(t, first.id) : [];
}

/** Whether a timeline paints any light with colour: what an arrival blends (OPBridge S351 blends the colour lanes). */
const paintsLights = (t: Timeline): boolean => t.lanes.some((l) => l.laneKind === 'dmx-fixture' || l.laneKind === 'dmx-rig');

/** How the lights ARRIVE (2026-11-04, OPBridge S351): cut on the bar line, or blend into it from the press. `slam` lists
 *  the lights a slam lands on, when the moment opens with one: Blend is then refused, with the reason (operator's
 *  choice), and the cut shows as chosen whatever is stored, because that is what plays. */
function ArrivalRow(props: { label: string; testId: string; timeline: Timeline; value: 'cut' | 'blend'; slam: string[]; onPick: (a: 'cut' | 'blend') => void }) {
  const blends = () => props.value === 'blend' && props.slam.length === 0;
  return (
    <Prop label={props.label}>
      <div class="tl2-seg" data-testid={props.testId} data-arrival={blends() ? 'blend' : 'cut'} data-slam={props.slam.length > 0 ? 'true' : undefined}>
        <button type="button" aria-pressed={!blends()}
          title="The lights change to the new look exactly on the bar line."
          data-info="The lights change to the new look exactly on the bar line."
          onClick={() => props.onPick('cut')}>Cut on the bar</button>
        <button type="button" aria-pressed={blends()} disabled={props.slam.length > 0}
          title="From the moment you press, the lights fade into the new look, and are there on the bar line."
          data-info="From the moment you press, the lights fade into the new look, and are there on the bar line."
          onClick={() => props.onPick('blend')}>Blend from the press</button>
      </div>
      <Show when={props.slam.length > 0}>
        <div class="tl2-text tl2-dim" data-testid={`${props.testId}-slam`}>It opens with a slam ({props.slam.length} lights land at full on its first beat), so it cuts: a blend would soften it.</div>
      </Show>
      <Show when={blends() && !resolveBridges(timelineHomeTarget(props.timeline)).some(bridgeBlendsArrival)}>
        <div class="tl2-text tl2-dim">Its computer's OPBridge needs an update before this applies; until then the lights cut on the bar.</div>
      </Show>
    </Prop>
  );
}

function SectionProps(props: { timeline: Timeline; sectionId: string; onDuplicate: () => void }) {
  const section = () => props.timeline.sections.find((s) => s.id === props.sectionId);
  let before = '';
  /** Following its song (2026-10-21): name and length are the song part's, changed on the Song parts row. */
  const f = () => followsSong(props.timeline);
  return (
    <Show when={section()}>
      <Show when={f()}>
        <Prop label="Part"><div class="tl2-text" data-testid="tl2-sec-part">{section()!.name || 'Not a part yet'}, {barsText(section()!.lengthBars)}. It is a part of the song: rename it, move it or change its length on the Song parts row, or right-click it here.</div></Prop>
      </Show>
      <Show when={!f()}>
      <Prop label="Name">
        <input
          class="tl2-field"
          value={section()!.name}
          onFocus={() => { before = section()!.name; }}
          onInput={(e) => renameSection(props.timeline.id, props.sectionId, e.currentTarget.value)}
          onBlur={() => commitSectionName(props.timeline.id, props.sectionId, before)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label="Section name"
        />
      </Prop>
      <Prop label="Length">
        <div class="tl2-stepper">
          <button type="button" aria-label="Shorter" onClick={() => setSectionLength(props.timeline.id, props.sectionId, section()!.lengthBars - 1)}>{ICON.minus}</button>
          <span>{barsText(section()!.lengthBars)}</span>
          <button type="button" aria-label="Longer" onClick={() => setSectionLength(props.timeline.id, props.sectionId, section()!.lengthBars + 1)}>{ICON.add}</button>
        </div>
      </Prop>
      </Show>
      <Show when={props.timeline.tier === 'song'}>
        <Prop label="At its end">
          <div class="tl2-seg" data-section-end={section()!.onEnd ?? 'auto-advance'}>
            <button type="button" aria-pressed={section()!.onEnd === 'loop'} data-info="Loop: this section plays again and again until you jump to another one." onClick={() => setSectionOnEnd(props.timeline.id, props.sectionId, 'loop')}>Loop</button>
            <button type="button" aria-pressed={(section()!.onEnd ?? 'auto-advance') === 'auto-advance'} data-info="Next: carries straight on into the next section." onClick={() => setSectionOnEnd(props.timeline.id, props.sectionId, 'auto-advance')}>Next</button>
            <button type="button" aria-pressed={section()!.onEnd === 'stop'} data-info="Stop: the song holds at the end of this section until you jump on." onClick={() => setSectionOnEnd(props.timeline.id, props.sectionId, 'stop')}>Stop</button>
          </div>
        </Prop>
      </Show>
      <Show when={paintsLights(props.timeline)}>
        <ArrivalRow label="Lights arrive" testId="tl2-sec-arrival" timeline={props.timeline} value={section()!.arrival ?? 'cut'}
          slam={sectionSlam(props.timeline, props.sectionId)} onPick={(a) => setSectionArrival(props.timeline.id, props.sectionId, a)} />
      </Show>
      {/* What its menu offers, here too (2026-10-16: the panel had only Delete). */}
      <Show when={!f()}><div class="tl2-actions">
        <button type="button" class="tl2-btn" data-info="Duplicate: a copy with what is in it, straight after." onClick={() => props.onDuplicate()}>Duplicate</button>
        <button type="button" class="tl2-btn" disabled={props.timeline.sections[0]?.id === props.sectionId} onClick={() => moveSection(props.timeline.id, props.sectionId, -1)}>Move earlier</button>
        <button type="button" class="tl2-btn" disabled={props.timeline.sections.at(-1)?.id === props.sectionId} onClick={() => moveSection(props.timeline.id, props.sectionId, 1)}>Move later</button>
        <button type="button" class="tl2-btn tl2-danger" disabled={props.timeline.sections.length <= 1} onClick={() => removeSection(props.timeline.id, props.sectionId)}>Delete section</button>
      </div></Show>
    </Show>
  );
}

/** The lane colours as a row of swatches, "None" first. */
function LaneHueRow(props: { current: string | undefined; pick: (h: string | undefined) => void }) {
  return (
    <div class="tl2-hues" role="group" aria-label="Lane colour">
      <button type="button" class="tl2-hue" data-none="true" aria-pressed={!props.current} title="No colour" aria-label="No colour" onClick={() => props.pick(undefined)} />
      <For each={LANE_HUES as unknown as string[]}>
        {(h) => (
          <button type="button" class="tl2-hue" style={{ background: `var(--page-hue-${h})` }} aria-pressed={props.current === h}
            title={h[0]!.toUpperCase() + h.slice(1)} aria-label={h[0]!.toUpperCase() + h.slice(1)} onClick={() => props.pick(h)} />
        )}
      </For>
    </div>
  );
}

/** An OP-Motion lane's own settings (2026-10-23): what it moves, on which buses, and how it glides off at the end.
 *  The device lists are data (`timeline/opMotion.ts`), so a new target or Move is one line there. */
function MotionLaneFields(props: { timeline: Timeline; lane: TimelineLane }) {
  const m = () => props.lane.motion!;
  const target = () => motionTarget(m().target);
  const set = (patch: Partial<NonNullable<TimelineLane['motion']>>) => {
    const t = getTimeline(props.timeline.id);
    if (!t) return;
    upsertTimeline({ ...t, lanes: t.lanes.map((l) => {
      if (l.id !== props.lane.id || !l.motion) return l;
      const motion = { ...l.motion, ...patch };
      // The crossfader's "off" is its home side, an effect's is off: a glide-out chosen for one never carries to the other.
      if (patch.target !== undefined && (patch.target === 'xfade') !== (l.motion.target === 'xfade')) delete motion.glideOut;
      // A lane still called what it was made as follows its target and buses; a name the operator typed stays.
      const auto = l.name === motionLaneName(l.motion.target, l.motion.buses);
      return { ...l, motion, ...(auto ? { name: motionLaneName(motion.target, motion.buses) } : {}) };
    }) });
  };
  const all = () => m().buses.length === 0 || m().buses.includes('all');
  const toggleBus = (id: string) => {
    if (id === 'all') { set({ buses: ['all'] }); return; }
    const now = all() ? [] : m().buses;
    const next = now.includes(id) ? now.filter((b) => b !== id) : [...now, id];
    set({ buses: next.length > 0 ? next : ['all'] });
  };
  const groups = [
    { label: 'Effects', ids: MOTION_TARGETS.filter((x) => x.family === 'Effect') },
    ...MOVE_FAMILIES.map((f) => ({ label: `Moves: ${f}`, ids: MOTION_TARGETS.filter((x) => x.family === f) })),
    { label: 'Mix', ids: MOTION_TARGETS.filter((x) => x.family === 'Crossfader') },
  ];
  const live = () => primaryBridge();
  return (
    <div data-motion-lane={props.lane.id}>
      <Prop label="Moves">
        <select class="tl2-field" data-testid="tl2-motion-target" value={m().target} onChange={(e) => set({ target: e.currentTarget.value })} aria-label="What this lane moves">
          <For each={groups}>
            {(g) => (
              <optgroup label={g.label}>
                <For each={g.ids}>{(x) => <option value={x.id}>{x.label}</option>}</For>
              </optgroup>
            )}
          </For>
        </select>
      </Prop>
      <Show when={target()}>
        <Prop label=""><div class="tl2-text">{target()!.hint}.</div></Prop>
      </Show>
      <Prop label="On">
        <Show when={!target()?.mainOnly} fallback={<div class="tl2-text">Main (only the OP-Motion on Main moves the crossfader)</div>}>
          <div class="tl2-togs tl2-wrap" data-testid="tl2-motion-buses">
            <button type="button" class="tl2-btn" data-bus="all" aria-pressed={all()} data-info="Every OP-Motion in the set, on every submix and on Main." onClick={() => toggleBus('all')}>Every bus</button>
            <For each={MOTION_BUSES}>
              {(b) => (
                <button type="button" class="tl2-btn" data-bus={b.id} aria-pressed={!all() && m().buses.includes(b.id)}
                  data-info={`The OP-Motion on your ${b.label} ${b.id === 'master' ? 'track' : 'submix'}.`} onClick={() => toggleBus(b.id)}>{b.label}</button>
              )}
            </For>
          </div>
        </Show>
      </Prop>
      <Prop label="At the end">
        <div class="tl2-seg" data-testid="tl2-motion-glideout">
          <For each={GLIDE_OUT_CHOICES}>
            {(c) => (
              <button type="button" aria-pressed={motionGlideOut(props.lane) === c.id} data-glide={c.id}
                data-info={c.id === 'none'
                  ? (m().target === 'xfade' ? 'None: the crossfader stays where the timeline leaves it.' : 'None: it holds to the end, and OP-Motion lets it go about 2 seconds later.')
                  : `Glides back to ${m().target === 'xfade' ? 'its home side' : 'off'} over ${c.label}, landing on the timeline's last bar line.`}
                onClick={() => set({ glideOut: c.id })}>{c.label}</button>
            )}
          </For>
        </div>
      </Prop>
      <Prop label="">
        <div class="tl2-text">
          Where the timeline ends, this lane glides off. Stopped early, or on a section that loops, OP-Motion lets it go
          on its own once its last change has run, about 2 seconds after.
        </div>
      </Prop>
      <Prop label="Glide in">
        <div class="tl2-text">Not yet: it starts on bar 1. Gliding in from the moment you press needs OPBridge to send at
          the press (asked 2026-10-23).</div>
      </Prop>
      <Prop label="Plays on">
        <div class="tl2-text">{live() ? `${live()!.name}, the computer running Live` : 'The computer running Live (none paired yet)'}</div>
      </Prop>
    </div>
  );
}

function LaneProps(props: {
  timeline: Timeline; lane: TimelineLane; drives: string; wont: string | null; silent: string | null;
  onRemove: () => void; onDuplicate: () => void; onFlag: (k: 'mute' | 'solo') => void; onHue: (h: string | undefined) => void; onAllLights: () => void;
}) {
  let before = '';
  return (
    <>
      <Prop label="Name">
        <input
          class="tl2-field"
          value={props.lane.name}
          onFocus={() => { before = props.lane.name; }}
          onInput={(e) => renameLane(props.timeline.id, props.lane.id, e.currentTarget.value)}
          onBlur={() => commitLaneName(props.timeline.id, props.lane.id, before)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label="Lane name"
        />
      </Prop>
      <Prop label="Drives"><div class="tl2-text">{props.drives}</div></Prop>
      <Show when={props.lane.laneKind === 'dmx-rig'}>
        <Prop label="Reach">
          <div class="tl2-togs">
            <button type="button" class="tl2-btn" data-testid="tl-master-all-lights" aria-pressed={!!props.lane.allLights} data-info="Every light: this master takes over every light in every group, above their masters, plus the Hue, Elgato and WLED lights of the computer playing it. Lights marked Skip All lights in OPBridge are left alone." onClick={props.onAllLights}>Every light</button>
          </div>
        </Prop>
      </Show>
      <Show when={props.lane.motion}>
        <MotionLaneFields timeline={props.timeline} lane={props.lane} />
      </Show>
      <Prop label="Changes"><div class="tl2-text">{props.lane.steps.length}</div></Prop>
      <Show when={props.wont}>
        <Prop label="Plays"><div class="tl2-text tl2-warn">Won't play: {props.wont}</div></Prop>
      </Show>
      <Prop label="Colour"><LaneHueRow current={props.lane.color} pick={props.onHue} /></Prop>
      {/* Two switches, each on or off by itself (2026-10-16: drawn as one segmented choice, as if picking one). */}
      <Prop label="Mute">
        <div class="tl2-togs">
          <button type="button" class="tl2-btn" aria-pressed={!!props.lane.mute} data-info="Mute: this lane does not play." onClick={() => props.onFlag('mute')}>Mute</button>
          <button type="button" class="tl2-btn" aria-pressed={!!props.lane.solo} data-info="Solo: while any lane is soloed, only soloed lanes play." onClick={() => props.onFlag('solo')}>Solo</button>
        </div>
      </Prop>
      <Show when={props.silent}>
        <Prop label=""><div class="tl2-text tl2-warn">{props.silent}</div></Prop>
      </Show>
      <div class="tl2-actions">
        <button type="button" class="tl2-btn" data-info="Duplicate: a copy of this lane with its changes, under it." onClick={() => props.onDuplicate()}>Duplicate</button>
        <button type="button" class="tl2-btn tl2-danger" onClick={() => props.onRemove()}>Delete lane</button>

      </div>
    </>
  );
}

function StepProps(props: {
  timeline: Timeline; lane: TimelineLane; step: TimelineStep; sig: number; unit: number;
  title: string; meta: string; wont: string | null; onRemove: () => void;
  fadeMore: boolean; onFadeMore: (open: boolean) => void;
  lead: { kind: 'in' | 'back'; bars: number; curve: ValuePointCurve; max: number } | null;
  onLead: (bars: number, curve?: ValuePointCurve) => void;
}) {
  const s = () => props.step;
  const nudge = (d: number) => placeStep(props.timeline.id, props.lane.id, s().id, { position: Math.max(0, s().position + d) });
  const durational = () => s().kind === 'buttonRef' || s().kind === 'animation' || s().kind === 'pattern';
  // A pattern block always has a length (2026-10-23: "Instant" stripped it and left a block that plays nothing).
  const alwaysHeld = () => s().kind === 'pattern';
  const dur = () => (s() as { durationBars?: number }).durationBars;
  return (
    <>
      <Show when={s().kind === 'colorMarker'}>
        <Prop label="Colour">
          <div class="tl2-swatches tl2-swatches-d">
            <For each={STAGE_COLORS}>
              {(c) => (
                <button
                  type="button"
                  class="tl2-swatch"
                  style={{ background: c.label === 'Off' ? undefined : rgbCss(c.rgb) }}
                  data-off={c.label === 'Off' ? 'true' : undefined}
                  aria-pressed={sameRgb(c.rgb, (s() as ColorMarkerStep).color)}
                  title={c.label}
                  aria-label={c.label}
                  onClick={() => { useClickColour(c); setColorMarkerColor(props.timeline.id, props.lane.id, s().id, c.rgb); }}
                />
              )}
            </For>
          </div>
        </Prop>
        {/* A split keyframe (2026-11-02): the colour the fade before it arrives at, then its own from its beat. */}
        <Prop label="Fade arrives at">
          <div class="tl2-swatches tl2-swatches-d" data-testid="tl2-arrive">
            <For each={STAGE_COLORS}>
              {(c) => (
                <button
                  type="button"
                  class="tl2-swatch"
                  style={{ background: c.label === 'Off' ? undefined : rgbCss(c.rgb) }}
                  data-off={c.label === 'Off' ? 'true' : undefined}
                  aria-pressed={!!(s() as ColorMarkerStep).arrive && sameRgb(c.rgb, (s() as ColorMarkerStep).arrive!)}
                  title={`Arrives at ${c.label}`}
                  aria-label={`Arrives at ${c.label}`}
                  onClick={() => setKeyArrive(props.timeline.id, props.lane.id, s().id, c.rgb)}
                />
              )}
            </For>
          </div>
          <Show when={(s() as ColorMarkerStep).arrive}>
            <button type="button" class="tl2-btn tl2-join" onClick={() => setKeyArrive(props.timeline.id, props.lane.id, s().id, null)}>Join the halves</button>
          </Show>
        </Prop>
        <TransitionControls
          timelineId={props.timeline.id}
          laneId={props.lane.id}
          step={s() as ColorMarkerStep}
          next={nextKeyframe(props.lane, s())}
          sig={props.sig}
          more={props.fadeMore}
          onMore={props.onFadeMore}
          until={(() => { const hb = handBackAfter(props.lane, s()); return hb && !nextKeyframe(props.lane, s()) ? `Until the Hand back at ${barBeat(hb.position, props.sig)}` : null; })()}
        />
      </Show>
      <Show when={s().kind === 'lightRelease'}>
        <Show when={props.lane.laneKind === 'opvisual-colour'} fallback={<Prop label="Hand back"><div class="tl2-text">Every light goes back to its own lane here.</div></Prop>}>
          <Prop label="Own look"><div class="tl2-text">Every OPVisual clip goes back to its own look here, fading over a bar unless you set a fade below.</div></Prop>
        </Show>
      </Show>
      <Show when={opvPaletteOf(s()) !== null}>
        <Prop label="Palette">
          <OpvPalettePicker target={autoPickOpVisualBridge() ?? undefined} value={opvPaletteOf(s()) ?? ''}
            onPick={(id, name) => setOpvPalette(props.timeline.id, props.lane.id, s().id, id, name)} />
        </Prop>
      </Show>
      <Show when={props.lead}>
        <LeadInControls lead={props.lead!} sig={props.sig} onSet={props.onLead} />
      </Show>
      <Show when={s().kind === 'buttonRef'}>
        <Prop label="Fires"><div class="tl2-text">{props.title}<em>{props.meta}</em></div></Prop>
      </Show>
      <Show when={s().kind === 'targetValue'}>
        <Prop label="Sets"><div class="tl2-text">{props.title}</div></Prop>
      </Show>
      <Show when={hueCueRef(s())}>
        <Prop label="Recalls"><div class="tl2-text">{props.title}<em>A Hue scene. The room changes to it here; OPBridge sends it to the Hue bridge from whichever computer holds it.</em></div></Prop>
      </Show>
      {/* OPVisual cell / column (2026-10-18, OPVisual DEC-148): off by default -
          the cell plays and ends; the timeline is the script. */}
      <Show when={opvActionId(s())}>
        <Prop label="Follow">
          <div class="tl2-seg" data-testid="tl2-opv-follow">
            <button type="button" aria-pressed={!opvFollows(s())} data-info="Off: the cell plays its count in OPVisual and stops there."
              onClick={() => setCueFollow(props.timeline.id, props.lane.id, s().id, false)}>Off</button>
            <button type="button" aria-pressed={opvFollows(s())} data-info="On: the cell moves on as it is set to in OPVisual (right, down, a named cell...)."
              onClick={() => setCueFollow(props.timeline.id, props.lane.id, s().id, true)}>On</button>
          </div>
        </Prop>
      </Show>
      <Prop label="At">
        <div class="tl2-stepper">
          <button type="button" aria-label="Earlier" onClick={() => nudge(-props.unit)}>{ICON.left}</button>
          <span>{barBeat(s().position, props.sig)}</span>
          <button type="button" aria-label="Later" onClick={() => nudge(props.unit)}>{ICON.right}</button>
        </div>
      </Prop>
      <Show when={durational()}>
        <Show when={!alwaysHeld()}>
        <Prop label="Length">
          <div class="tl2-seg">
            <button type="button" aria-pressed={!dur()} onClick={() => {
              const t = getTimeline(props.timeline.id);
              if (!t) return;
              upsertTimeline({ ...t, lanes: t.lanes.map((l) => l.id !== props.lane.id ? l : { ...l, steps: l.steps.map((x) => { if (x.id !== s().id) return x; const { durationBars: _d, ...rest } = x as TimelineStep & { durationBars?: number }; void _d; return rest as TimelineStep; }) }) });
            }}>Instant</button>
            <button type="button" aria-pressed={!!dur()} onClick={() => placeStep(props.timeline.id, props.lane.id, s().id, { durationBars: dur() ?? 1 })}>Held</button>
          </div>
        </Prop>
        </Show>
        <Show when={dur()}>
          <Prop label={alwaysHeld() ? 'Repeats for' : 'Held for'}>
            <div class="tl2-stepper">
              <button type="button" aria-label="Shorter" onClick={() => placeStep(props.timeline.id, props.lane.id, s().id, { durationBars: Math.max(props.unit, (dur() ?? 1) - props.unit) })}>{ICON.minus}</button>
              <span>{barsText(dur() ?? 1)}</span>
              <button type="button" aria-label="Longer" onClick={() => placeStep(props.timeline.id, props.lane.id, s().id, { durationBars: (dur() ?? 1) + props.unit })}>{ICON.add}</button>
            </div>
          </Prop>
        </Show>
      </Show>
      <Show when={props.wont}>
        <Prop label="Plays"><div class="tl2-text tl2-warn">Won't play: {props.wont}</div></Prop>
      </Show>
      <div class="tl2-actions">
        <button type="button" class="tl2-btn tl2-danger" onClick={() => props.onRemove()}>Delete</button>
      </div>
    </>
  );
}

/** How the lights go into a master's takeover, or back out of it: a cut on
 *  the beat, or a fade of a length that ARRIVES on the beat, in a shape
 *  (2026-10-02). Lengths that do not fit the room before are greyed. */
function LeadInControls(props: {
  lead: { kind: 'in' | 'back'; bars: number; curve: ValuePointCurve; max: number }; sig: number;
  onSet: (bars: number, curve?: ValuePointCurve) => void;
}) {
  const opts = () => [
    { bars: 0, label: 'Cut' }, { bars: 1 / props.sig, label: '1 beat' }, { bars: 2 / props.sig, label: '2 beats' },
    { bars: 1, label: '1 bar' }, { bars: 2, label: '2 bars' }, { bars: 4, label: '4 bars' },
  ];
  const what = () => (props.lead.kind === 'in' ? 'into this colour' : 'back to their own lanes');
  const known = () => opts().some((o) => Math.abs(o.bars - props.lead.bars) < 1e-6);
  return (
    <>
      <Prop label={props.lead.kind === 'in' ? 'Fade in from the lights' : 'Fade back to the lights'}>
        <div class="tl2-trow">
          <div class="tl2-seg tl2-leadseg">
            <For each={opts()}>
              {(o) => (
                <button
                  type="button"
                  aria-pressed={Math.abs(o.bars - props.lead.bars) < 1e-6}
                  disabled={o.bars > props.lead.max + 1e-9}
                  data-info={o.bars === 0 ? `Cut: every light switches ${what()} on the beat.` : `Every light fades ${what()} over ${o.label}, arriving on the beat.`}
                  onClick={() => props.onSet(o.bars)}
                >{o.label}</button>
              )}
            </For>
          </div>
          <Show when={!known()}><em class="tl2-leadnow">{barsText(props.lead.bars)}</em></Show>
        </div>
      </Prop>
      <Show when={props.lead.bars > 0}>
        <Prop label="Shape">
          <div class="tl2-seg tl2-shapes">
            <For each={FADE_SHAPES}>
              {(sh) => (
                <button type="button" aria-pressed={props.lead.curve === sh.curve} data-info={sh.info} onClick={() => props.onSet(props.lead.bars, sh.curve)}>
                  {ICON.shape(sh.curve)}{sh.label}
                </button>
              )}
            </For>
          </div>
        </Prop>
      </Show>
    </>
  );
}

/** The stretch out of a colour keyframe: Cut or Fade up front; a fade's shape
 *  and its route round the colour wheel one tap deeper, behind More (operator,
 *  2026-10-01: "Cut + Fade, shapes deeper"). The chooser on the lane and the
 *  detail panel both show this, so the two can never disagree. */
function TransitionControls(props: {
  timelineId: string; laneId: string; step: ColorMarkerStep; next: ColorMarkerStep | null; sig: number;
  more: boolean; onMore: (open: boolean) => void;
  /** "Until the Hand back at 10.1" when a Hand back, not a keyframe, comes next. */
  until?: string | null;
}) {
  const set = (change: Parameters<typeof setColorMarkerTransition>[3]) => setColorMarkerTransition(props.timelineId, props.laneId, props.step.id, change);
  const fade = () => transitionOf(props.step) === 'fade';
  const curve = () => props.step.curve ?? 'linear';
  const rainbow = () => (props.step.path ?? 'short') === 'long';
  // A chosen shape or route is never hidden behind a folded More.
  const deeper = () => fade() && (props.more || curve() !== 'linear' || rainbow());
  const fromName = () => colourName(props.step.color, props.step.label);
  const toName = () => (props.next ? colourName(props.next.color, props.next.label) : '');
  const at = () => (props.next ? barBeat(props.next.position, props.sig) : '');
  const noRainbow = () => !props.next || isGrey(props.step.color) || isGrey(props.next.color);
  return (
    <Show
      when={props.next}
      fallback={<Prop label="Then"><div class="tl2-text">{props.until ?? 'Holds to the end'}<em>{props.until ? 'Then every light goes back to its own lane.' : 'Add a keyframe after it to cut or fade to.'}</em></div></Prop>}
    >
      <Prop label={`To ${toName()}`}>
        <div class="tl2-trow">
          <div class="tl2-seg tl2-tseg">
            <button
              type="button"
              aria-pressed={!fade()}
              data-info={`Cut: stays ${fromName()} until ${at()}, then switches to ${toName()} on the beat.`}
              onClick={() => { if (fade()) set({ curve: 'hold' }); }}
            >{ICON.cut}Cut</button>
            <button
              type="button"
              aria-pressed={fade()}
              data-info={`Fade: blends from ${fromName()} into ${toName()}, arriving at ${at()}.`}
              // A fade from a Cut starts Even, the short way round, in the
              // fade space; a space the Legacy timeline chose is kept.
              onClick={() => { if (!fade()) set(props.step.space === 'HSL' || props.step.space === 'OKLCH' ? { curve: 'linear' } : { curve: 'linear', space: FADE_SPACE }); }}
            >{ICON.fade}Fade</button>
          </div>
          <Show when={fade()}>
            <button
              type="button"
              class="tl2-more"
              aria-expanded={deeper()}
              data-info="More: the fade's shape, and which way round the colour wheel it goes."
              onClick={() => props.onMore(!deeper())}
            >More<span style={{ display: 'inline-flex', transform: deeper() ? 'rotate(180deg)' : undefined }}>{ICON.down}</span></button>
          </Show>
        </div>
      </Prop>
      <Show when={deeper()}>
        <Prop label="Shape">
          <div class="tl2-seg tl2-shapes">
            <For each={FADE_SHAPES}>
              {(sh) => (
                <button type="button" aria-pressed={curve() === sh.curve} data-info={sh.info} onClick={() => set({ curve: sh.curve })}>
                  {ICON.shape(sh.curve)}{sh.label}
                </button>
              )}
            </For>
          </div>
        </Prop>
        <Prop label="Route">
          <div class="tl2-seg">
            <button type="button" aria-pressed={!rainbow()} data-info="Direct: the short way round the colour wheel." onClick={() => set({ path: 'short' })}>Direct</button>
            <button
              type="button"
              aria-pressed={rainbow()}
              disabled={noRainbow()}
              data-info={noRainbow()
                ? 'Rainbow needs a colour at both ends: White and Off have no place on the colour wheel.'
                : `Rainbow: the long way round the colour wheel, through every colour between ${fromName()} and ${toName()}.`}
              onClick={() => set({ path: 'long', space: FADE_SPACE })}
            >Rainbow</button>
          </div>
        </Prop>
      </Show>
    </Show>
  );
}

// ─── icons: one drawn set, one stroke weight (DEC-239's rule) ───────────────

function svg(children: JSX.Element, fill = false): JSX.Element {
  return (
    <svg class="tl2-i" viewBox="0 0 24 24" aria-hidden="true" data-fill={fill ? 'true' : undefined}>{children}</svg>
  );
}
const ICON = {
  get play() { return svg(<path d="M7.5 5v14l11-7z" />, true); },
  get stop() { return svg(<rect x="6.5" y="6.5" width="11" height="11" rx="1" />, true); },
  get loop() { return svg(<path d="M4.5 11.5a7.5 7.5 0 0 1 12.9-4.9l1.6 1.8M19.3 3.9v4.8h-4.8M19.5 12.5a7.5 7.5 0 0 1-12.9 4.9L5 15.6M4.7 20.1v-4.8h4.8" />); },
  get browser() { return svg(<><rect x="3.5" y="4" width="17" height="16" rx="1.5" /><path d="M9.5 4v16" /></>); },
  get detail() { return svg(<><rect x="3.5" y="4" width="17" height="16" rx="1.5" /><path d="M3.5 14h17" /></>); },
  get perf() { return svg(<><rect x="3.5" y="13" width="5" height="6" rx="1" /><rect x="9.5" y="13" width="5" height="6" rx="1" /><rect x="15.5" y="13" width="5" height="6" rx="1" /><path d="M3.5 8h17" /></>); },
  /** Three lanes drawn as strips of colour: the Stream view. */
  get stream() { return svg(<><rect x="3.5" y="5" width="17" height="3.4" rx="1.2" /><rect x="3.5" y="10.3" width="17" height="3.4" rx="1.2" /><rect x="3.5" y="15.6" width="17" height="3.4" rx="1.2" /></>); },
  get up() { return svg(<path d="M6 15l6-6 6 6" />); },
  get down() { return svg(<path d="M6 9l6 6 6-6" />); },
  get target() { return svg(<><circle cx="12" cy="12" r="7.5" /><circle cx="12" cy="12" r="2.5" /><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" /></>); },
  /** A transition as its curve: a cut holds, then steps up on the beat. */
  get cut() { return svg(<path d="M3.5 17h9V7h8" />); },
  get fade() { return svg(<path d="M3.5 17 20.5 7" />); },
  shape(curve: ValuePointCurve) {
    const d = curve === 'ease-in' ? 'M3.5 17C12 17 16 13 20.5 7'
      : curve === 'ease-out' ? 'M3.5 17C8 11 12 7 20.5 7'
        : curve === 'ease-in-out' ? 'M3.5 17C11 17 13 7 20.5 7'
          : 'M3.5 17 20.5 7';
    return svg(<path d={d} />);
  },
  get left() { return svg(<path d="M15 5l-7 7 7 7" />); },
  get right() { return svg(<path d="M9 5l7 7-7 7" />); },
  get tri() { return svg(<path d="M8 5l9 7-9 7z" />, true); },
  get add() { return svg(<path d="M12 5v14M5 12h14" />); },
  get undo() { return svg(<path d="M9 7 4.5 11.5 9 16M4.5 11.5h10a5 5 0 0 1 0 10H12" />); },
  get redo() { return svg(<path d="M15 7l4.5 4.5L15 16M19.5 11.5h-10a5 5 0 0 0 0 10H12" />); },
  get minus() { return svg(<path d="M5 12h14" />); },
  get close() { return svg(<path d="M6 6l12 12M18 6L6 18" />); },
  get search() { return svg(<><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" /></>); },
  get lights() { return svg(<><path d="M8.2 3.5h7.6l-1.3 6.2H9.5z" /><path d="M10.2 9.7 6 20.5M13.8 9.7l4.2 10.8M12 9.7v10.8" /></>); },
  get controls() { return svg(<><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>); },
  get marker() { return svg(<path d="M6 3.5v17M6 4.5h11l-2.5 4 2.5 4H6" />); },
  /** A stompbox: the guitar's sound, which is what a Guitar lane changes. */
  get guitar() { return svg(<><rect x="5" y="3.5" width="14" height="17" rx="2" /><circle cx="9" cy="8" r="1.6" /><circle cx="15" cy="8" r="1.6" /><rect x="9" y="13.5" width="6" height="4" rx="1" /></>); },
  get check() { return svg(<path d="M5 12.5l4.5 4.5L19 7.5" />); },
  get knob() { return svg(<><circle cx="12" cy="12" r="8" /><path d="M12 12l4.4-4.4" /></>); },
  /** A curve through points: an automation lane. */
  get auto() { return svg(<><path d="M3.5 17.5 9 9l5.5 5 6-8.5" /><circle cx="9" cy="9" r="1.6" /><circle cx="14.5" cy="14" r="1.6" /></>); },
  get pencil() { return svg(<path d="M14.8 4.8l4.4 4.4M4.5 19.5l1-4.4L15.6 5a1.6 1.6 0 0 1 2.2 0l1.2 1.2a1.6 1.6 0 0 1 0 2.2L8.9 18.5z" />); },
  get trash() { return svg(<><path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5" /><path d="M10.5 10.5v6M13.5 10.5v6" /></>); },
  get sections() { return svg(<><rect x="3" y="7" width="5" height="10" rx="1" /><rect x="9.5" y="7" width="5" height="10" rx="1" /><rect x="16" y="7" width="5" height="10" rx="1" /></>); },
  /** A drum and its sticks: a drum lights lane. */
  get drum() { return svg(<><ellipse cx="12" cy="10" rx="7.5" ry="2.8" /><path d="M4.5 10v6.2c0 1.6 3.4 2.8 7.5 2.8s7.5-1.2 7.5-2.8V10M8 12.6v5.6M16 12.6v5.6" /><path d="M6.5 3.5l4 4.6M17.5 3.5l-4 4.6" /></>); },
};
